import { BarcodeOverrideDialog } from '../components/barcodeOverride/BarcodeOverrideDialog';
import { BarcodeOverrideHistory } from '../components/barcodeOverride/BarcodeOverrideHistory';
import { barcodeOverrideApi } from '../components/barcodeOverride/barcodeOverrideApi';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { getDealerUrl } from '../utils/dealerUrl';
import { brandedProductName, brandsMatchIdentity, mergeBrandLabels } from '../utils/brandIdentity';
import { BRAND_CATALOG_IDENTITY } from '../utils/brandCatalogIdentity';
import { countryOfOriginFromProduct, japanArticleNumber } from '../utils/productIdentityExtras';
import { resolveStagedSearchBrandId } from '../utils/stagedBrandQuery';
import { useBrandRegistry } from '../hooks/useBrandRegistry';
import { formatRelativeDate, isMarketPricingStale } from '../utils/productDetailHelpers';
import { isSelfVariantRow, normalizeProductVariants } from '../utils/productVariants';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  TextField,
  LinearProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Fade,
  FormControlLabel,
  Switch,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  ButtonGroup,
  Collapse,
  Popover,
  Avatar,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  Link,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  RocketLaunch as PromoteIcon,
  SmartToy as AIIcon,
  NewReleases as NewIcon,
  PriceChange as PriceChangeIcon,
  Edit as ContentUpdateIcon,
  SwapHoriz as StatusChangeIcon,
  SystemUpdateAlt as FullUpdateIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as SuccessOutlineIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
  CloudDownload as ScraperIcon,
  Fingerprint as IdentifierIcon,
  AutoFixHigh as GenerateIcon,
  Save as SaveIcon,
  EditOff as EditOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  BuildCircle as FixIcon,
  PriceCheck as PriceCheckIcon,
  CalendarMonth as CalendarIcon,
  VideoLibrary as VideoIcon,
  PhotoLibrary as PhotoIcon,
  CloudUpload as CloudUploadIcon,
  RateReview as SendToReviewIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Stop as StopIcon,
  Inventory2 as BoxIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as MenuArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DocumentScanner as SnapshotIcon,
  TravelExplore as DiscoveryIcon,
  TrendingUp as MarketIcon,
  LocalOffer as SaleIcon,
  CleaningServices as ReaperIcon,
  Campaign as AnnounceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ViewHeadline as CompactDensityIcon,
  TableRows as ComfortableDensityIcon,
  ImageOutlined as NoImageIcon,
  ZoomIn as ZoomInIcon,
  CloudDone as StorageHostedIcon,
  Link as CdnLinkIcon,
  BrokenImage as BrokenImageIcon,
  MonitorWeight as WeightIcon,
  OpenInNew as OpenInNewIcon,
  ArrowForward as ArrowForwardIcon,
  ContentCopy as CopyFromParentIcon,
  Storefront as BhFillIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import {
  PermalinkNotFoundError,
  usePermalinkNavigation,
  useProductPermalink,
} from '../hooks/useProductPermalink';
import { PermalinkNotFound } from '../components/productPermalink/PermalinkNotFound';
import { PermalinkLoadFailed } from '../components/productPermalink/PermalinkLoadFailed';
import { ImageGallery } from '../components/ImageGallery';
import { useContentScopes } from '../hooks/useContentScopes';
import { makeApiCall, getAuthHeaders, readJsonResponse } from '../utils/apiConfig';
import { formatFirestoreDate, parseFirestoreTimestamp } from '../utils/firestoreDate';
import {
  collapseLetterSpacedWords,
  formatSpecFieldLabel,
  formatSpecSectionTitle,
  stripUnicodeSuperscripts,
} from '../utils/specLabelDisplay';
import {
  getStagedApprovalGaps,
  stagedHasBarcode,
} from '../utils/stagedApprovalGaps';
import { stagedIntegrityIssueIds } from '../utils/catalogIntegrity';
import { comparePromotePriority, isPriorityNewStaged } from '../utils/stagedPromotePriority';
import {
  EMPTY_STAGED_FILTER_STATE,
  STAGED_FILTER_KEYS,
  addStagedFilterValue,
  hasActiveStagedFilters,
  removeStagedFilter,
  setStagedFilterValue,
  stagedFilterDefault,
  stagedFiltersFromParams,
  stagedFiltersToParams,
  stagedIssueFilterBase,
  stagedIssueFilterMode,
  stagedRowMatchesIssues,
  type StagedFilterKey,
  type StagedFilterState,
  type StagedFilterValue,
} from '../utils/stagedFilterState';
import StagedProductsFilterBar, {
  type TopIssueShortcut,
} from '../components/stagedProducts/StagedProductsFilterBar';
import StagedTabCount from '../components/stagedProducts/StagedTabCount';
import { StagedVariantLinkCard } from '../components/stagedProducts/StagedVariantLinkCard';
import { parseLocalizedNumber } from '../utils/localizedNumber';
import { buildTagsFromAiSuggestedFix } from '../utils/aiTagSuggestedFix';
import {
  buildAcceptAllAiPatch,
  hasPendingAiSuggestions,
} from '../utils/acceptAllAiSuggestions';
import {
  pickPipelineOwnedDetailActions,
  PIPELINE_OWNED_STATUSES,
  type StagedDetailAction,
  type StagedDetailActionId,
} from '../utils/stagedDetailActions';
import { mpnMergeCandidateKind, visibleMpnMergeCandidates } from '../utils/mpnMergeCandidates';
import {
  getPromoteBlockers,
  overlayMergePreviewBarcodes,
  promoteStateLabel,
  promoteWillFail,
  summarizePromoteBlockers,
  type PromoteBlocker,
} from '../utils/promoteBlockers';
import {
  describeGapSteps,
  describeGapWhen,
  describeProductPipeline,
  describeRemediationQueueStatus,
  type RemediationBudgetLine,
  REMEDIATION_LANE_COPY,
  REMEDIATION_LANE_ORDER,
  remediationActionLabel,
  remediationGapLabel,
} from '../utils/remediationPipelineCopy';
import {
  annotateIdentifiers,
  identifierAreaPrimers,
  isIdentifierFlagAcknowledged,
  type IdentifierFieldNote,
  type IdentifierReviewField,
  type ReviewPrimerField,
} from '../utils/identifierReviewNotes';
import { barcodeModelLabel, brandIdentifierPolicy } from '../utils/brandIdentifierPolicy';
import { BrandCatalogProfileButton } from '../components/BrandCatalogProfile';
import { stagedFieldSource, stagedSectionSource, stagedTagSources } from '../utils/stagedFieldSource';
import { stagedStorefrontPlatform } from '../utils/stagedStorefrontPlatform';
import { SectionTitleWithSource, StagedSourceBadge } from '../components/StagedSourceBadge';
import {
  buildMergeSelectionState,
  defaultSelectedMergePaths,
  isSelectableMergeField,
  resolvePromoteMergeSelection,
  selectedMergePayload,
  type MergeSelectionState,
} from '../utils/mergeApplySelection';
import { snapshotScrapedFieldsIfNeeded } from '../utils/scrapedContentBackup';
import {
  clampShortDescription,
  isShortDescriptionIssueField,
  STAGED_SHORT_DESCRIPTION_MAX_CHARS,
} from '../utils/shortDescription';
import { enrichCategoryVerificationIssue } from '../utils/categorySuggestedFix';
import { PRODUCT_CATEGORY_SELECT_GROUPS } from '../constants/productCategories';
import {
  applyBrokenImageRemediation,
  summarizeImageHealthFix,
} from '../utils/productImageHealth';
import { PageContainer, PageHeader, NumberField, BrandSelect, ChipInput, InlineMessage, parseNumberInput } from '../components/ui';
import { BarcodeTextField } from '../components/admin/BarcodeTextField';
import { IdentifierCandidatesChip } from '../components/admin/IdentifierCandidatesChip';
import { ProductLifecycleEditor, type ProductLifecycleInput, type ProductLifecycleValue } from '../components/admin/ProductLifecycleEditor';
import { withCardPending } from '../components/admin/panelSavePayload';
import {
  ProductLaunchDatesEditor,
  type ProductLaunchDatesInput,
  type ProductLaunchDatesValue,
} from '../components/admin/ProductLaunchDatesEditor';
import { StagedProductScrapeDialog } from '../components/stagedProducts/StagedProductScrapeDialog';
import StagedBundleComponents, {
  type StagedBundleComponentRow,
} from '../components/stagedProducts/StagedBundleComponents';
import { tokens } from '../theme/tokens';

/** Rows fetched per page; further pages stream in via infinite scroll (cursor-based). */
const STAGED_PRODUCTS_PAGE_SIZE_OPTIONS = [100, 250, 500, 1000];
const STAGED_PRODUCTS_DEFAULT_PAGE_SIZE = 250;
const STAGED_PRODUCTS_PAGE_SIZE_KEY = 'stagedProducts.pageSize';
/** Backend cap for bulk Accept AI Features / Accept All AI Suggestions. */
const BULK_AI_BATCH_MAX = 500;

type StagedEditMeta = {
  source: 'ai_fix' | 'admin_edit' | 'accept_all_ai' | 'ai_suggestion';
  summary?: string;
  clearedIssueFields?: string[];
};

function withEditMeta(data: Record<string, any>, meta: StagedEditMeta): Record<string, any> {
  const { editMeta: _drop, ...fields } = data;
  return { ...fields, editMeta: meta };
}

function stagedProductApiPath(id: string, suffix = ''): string {
  return `/staged-products/${encodeURIComponent(id)}${suffix}`;
}

function evictStagedIdFromListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
) {
  queryClient.setQueriesData<{ pages: Array<{ data: StagedProduct[]; nextCursor: string | null }> }>(
    { queryKey: ['staged-products'] },
    (old) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: page.data.filter((row) => row.id !== id),
        })),
      };
    },
  );
}

function editHistorySourceLabel(source: string): string {
  switch (source) {
    case 'ai_fix':
      return 'AI fix';
    case 'accept_all_ai':
      return 'Accept all AI';
    case 'ai_suggestion':
      return 'AI suggestion';
    case 'admin_edit':
      return 'Admin edit';
    case 'submitter_answer':
      return 'Submitter answer';
    case 'submitter_edit':
      return 'Submitter edit';
    default:
      return source || 'Edit';
  }
}

// ── Pipeline status board ───────────────────────────────────────────────────
// Every scheduled function records into pipelineRuns; the board groups those
// docs by domain and derives freshness per pipeline from its real cadence, so
// a weekly job isn't flagged an hour after its (perfectly healthy) last run
// while a 10-minute loop that's been quiet for 2 hours is.

type StatusTab = 'review' | 'remediating' | 'pending' | 'approved' | 'rejected' | 'promoted' | 'all' | 'upc_review';
type PipelineGroup = 'Staging & Remediation' | 'Scraping & Discovery' | 'Pricing' | 'Housekeeping';
const PIPELINE_GROUP_ORDER: PipelineGroup[] = [
  'Staging & Remediation',
  'Scraping & Discovery',
  'Pricing',
  'Housekeeping',
];

interface PipelineMeta {
  label: string;
  group: PipelineGroup;
  /** Scheduled cadence in minutes; undefined = event-driven or retired (never "overdue"). */
  intervalMin?: number;
  icon: React.ReactNode;
  /** Short description shown in the pipeline detail dialog. */
  description?: string;
  /** Optional admin route for deeper inspection. */
  href?: string;
  hrefLabel?: string;
  /** Optional staged-products tab to switch to (same page). */
  statusTab?: StatusTab;
}

/** Cadences mirror the .schedule(...) of each function in src/functions. */
const PIPELINE_META: Record<string, PipelineMeta> = {
  ai_verification: {
    label: 'AI Verification',
    group: 'Staging & Remediation',
    intervalMin: 2,
    icon: <AIIcon fontSize="small" />,
    description: 'Scores staged products and routes them to approved, review, remediating, or failed.',
    statusTab: 'pending',
  },
  remediation_dispatcher: {
    label: 'Remediation Dispatcher',
    group: 'Staging & Remediation',
    intervalMin: 15,
    icon: <GenerateIcon fontSize="small" />,
    description: 'Closes open data gaps. Free fills run 8 at a time, up to 1,000 a run; paid work uses the time left.',
    statusTab: 'remediating',
  },
  product_enrichment: {
    label: 'Snapshot Enrichment',
    group: 'Staging & Remediation',
    intervalMin: 10,
    icon: <SnapshotIcon fontSize="small" />,
    description: 'Enriches staged product snapshots from source pages and stored scrape payloads.',
    statusTab: 'review',
  },
  identifier_enrichment: {
    label: 'Identifier Enrichment (retired)',
    group: 'Staging & Remediation',
    icon: <IdentifierIcon fontSize="small" />,
    description: 'Retired identifier enrichment job — kept for historical pipelineRuns only.',
  },
  ai_enhance: {
    label: 'AI Enhance',
    group: 'Staging & Remediation',
    icon: <AIIcon fontSize="small" />,
    description: 'Admin-triggered descriptions and auto-fixable name/verification fixes on staged products.',
    statusTab: 'review',
  },
  brand_scraper: {
    label: 'Brand Scraper',
    group: 'Scraping & Discovery',
    intervalMin: 15,
    icon: <ScraperIcon fontSize="small" />,
    description: 'Checks brand schedule configs and queues enabled scraper jobs.',
    href: '/app/admin/jobs',
    hrefLabel: 'Open Jobs',
  },
  product_discovery: {
    label: 'Product Discovery',
    group: 'Scraping & Discovery',
    intervalMin: 24 * 60,
    icon: <DiscoveryIcon fontSize="small" />,
    description: 'Discovers new product listings across brand catalogs.',
    href: '/app/admin/jobs',
    hrefLabel: 'Open Jobs',
  },
  promaster_warehouse_sync: {
    label: 'Promaster Warehouse Sync',
    group: 'Scraping & Discovery',
    intervalMin: 24 * 60,
    icon: <IdentifierIcon fontSize="small" />,
    description: 'Daily pull of Promaster warehouse-products for Enrich IDs, then refreshes fallback list/MAP/cost on every matching product with no manufacturer price.',
  },
  promaster_dealer_cost_refresh: {
    label: 'Promaster Dealer Cost Refresh',
    group: 'Pricing',
    intervalMin: 24 * 60,
    icon: <PriceChangeIcon fontSize="small" />,
    description: 'Links the Promaster warehouse cost to catalog products so verified dealers see it as their dealer cost.',
  },
  stock_refresh: {
    label: 'Stock Refresh (retired)',
    group: 'Scraping & Discovery',
    intervalMin: 7 * 24 * 60,
    icon: <BoxIcon fontSize="small" />,
    description: 'Scheduler is idle. Manufacturer stock is collected on the daily price refresh; use admin Stock & Price Re-check to run manually.',
    href: '/app/admin/jobs',
    hrefLabel: 'Open Jobs',
  },
  price_refresh: {
    label: 'Price Refresh',
    group: 'Pricing',
    intervalMin: 24 * 60,
    icon: <PriceChangeIcon fontSize="small" />,
    description: 'Queues staggered price_only scrape jobs for every enabled brand (daily). Skips brands whose monthly full scrape is today.',
    href: '/app/admin/catalog-price-changes',
    hrefLabel: 'Catalog price changes',
  },
  market_pricing_refresh: {
    label: 'Market Pricing Refresh',
    group: 'Pricing',
    intervalMin: 7 * 24 * 60,
    icon: <MarketIcon fontSize="small" />,
    description: 'Refreshes street/dealer market pricing across brands.',
    href: '/app/admin/catalog-price-changes',
    hrefLabel: 'Catalog price changes',
  },
  pricing_coverage_audit: {
    label: 'Pricing Coverage Audit',
    group: 'Pricing',
    intervalMin: 24 * 60,
    icon: <PriceCheckIcon fontSize="small" />,
    description: 'Audits pricing freshness and enqueues stale SKUs for the price refresh drain.',
    href: '/app/admin/pricing-freshness',
    hrefLabel: 'Pricing freshness',
  },
  shopify_map_audit: {
    label: 'Shopify MAP Audit',
    group: 'Pricing',
    intervalMin: 30,
    icon: <PriceCheckIcon fontSize="small" />,
    description: 'Pulls live Shopify variant prices and flags stale or below-MAP listings against Motzu.',
  },
  map_violation_detection: {
    label: 'MAP Violation Detection',
    group: 'Pricing',
    intervalMin: 6 * 60,
    icon: <PriceCheckIcon fontSize="small" />,
    description: 'Compares catalog advertised prices to active vendor MAP rows.',
  },
  sale_expiration: {
    label: 'Sale Expiration',
    group: 'Pricing',
    intervalMin: 24 * 60,
    icon: <SaleIcon fontSize="small" />,
    description: 'Clears expired sale prices from the catalog.',
    href: '/app/admin/catalog-price-changes',
    hrefLabel: 'Catalog price changes',
  },
  lens_compatibility: {
    label: 'Lens Compatibility',
    group: 'Housekeeping',
    intervalMin: 24 * 60,
    icon: <TimeIcon fontSize="small" />,
    description:
      'Rebuilds camera, lens and accessory compatibility edges from mounts and structured specs, a bounded page per night.',
  },
  stale_job_reaper: {
    label: 'Stale Job Reaper',
    group: 'Housekeeping',
    intervalMin: 30,
    icon: <ReaperIcon fontSize="small" />,
    description: 'Marks abandoned running/pending ingestion jobs as failed.',
    href: '/app/admin/jobs',
    hrefLabel: 'Open Jobs',
  },
  announcement_monitor: {
    label: 'Announcement Monitor',
    group: 'Housekeeping',
    intervalMin: 4 * 60,
    icon: <AnnounceIcon fontSize="small" />,
    description: 'Watches brand announcement channels for new product signals.',
    href: '/app/admin/system',
    hrefLabel: 'System',
  },
};

function pipelineMetaFor(key: string, run: { brand?: string; brandId?: string }): PipelineMeta {
  if (PIPELINE_META[key]) return PIPELINE_META[key];
  if (key.startsWith('scraper_')) {
    return {
      label: `Scraper: ${run.brand || run.brandId || key.replace('scraper_', '')}`,
      group: 'Scraping & Discovery',
      icon: <ScraperIcon fontSize="small" />,
      description: 'Per-brand scraper run recorded by the ingestion pipeline.',
      href: '/app/admin/jobs',
      hrefLabel: 'Open Jobs',
    };
  }
  return { label: key, group: 'Housekeeping', icon: <TimeIcon fontSize="small" /> };
}

/** Scalar/array fields worth surfacing in the pipeline detail dialog. */
const PIPELINE_DETAIL_SKIP = new Set([
  'name',
  'lastRunAt',
  'status',
  'message',
  'pipeline',
  'pauseRequested',
  'cancelRequested',
  'batchProductIds',
  'type',
  'brand',
  'brandId',
  // Shown in a dedicated troubleshooting list below metrics.
  'errors',
  'warnings',
]);

function pipelineMessageList(raw: unknown): string[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (entry && typeof entry === 'object') {
        const obj = entry as { message?: unknown; error?: unknown; detail?: unknown };
        const msg = obj.message ?? obj.error ?? obj.detail;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
        try {
          return JSON.stringify(entry);
        } catch {
          return String(entry);
        }
      }
      return String(entry);
    })
    .filter(Boolean);
}

function pipelineDetailEntries(run: PipelineRun): Array<{ key: string; value: string }> {
  const out: Array<{ key: string; value: string }> = [];
  for (const [key, raw] of Object.entries(run as unknown as Record<string, unknown>)) {
    if (PIPELINE_DETAIL_SKIP.has(key) || raw == null) continue;
    if (typeof raw === 'number' || typeof raw === 'boolean' || typeof raw === 'string') {
      out.push({ key, value: String(raw) });
      continue;
    }
    if (Array.isArray(raw)) {
      out.push({ key, value: `${raw.length} item${raw.length === 1 ? '' : 's'}` });
      continue;
    }
    if (typeof raw === 'object') {
      const keys = Object.keys(raw as object);
      out.push({ key, value: `${keys.length} field${keys.length === 1 ? '' : 's'}` });
    }
  }
  return out.sort((a, b) => a.key.localeCompare(b.key));
}

type PipelineHealth = 'error' | 'partial' | 'overdue' | 'running' | 'paused' | 'ok';

/** States that warrant color and a chip in the collapsed header. */
const ATTENTION_HEALTH: PipelineHealth[] = ['error', 'partial', 'overdue', 'running', 'paused'];

function pipelineHealthFor(
  run: { status: string; lastRunAt?: { _seconds?: number } },
  meta: PipelineMeta,
  nowMs: number,
): { health: PipelineHealth; lastRunMs: number | null; overdueMs: number } {
  const lastRunMs = run.lastRunAt?._seconds ? run.lastRunAt._seconds * 1000 : null;
  if (run.status === 'running') return { health: 'running', lastRunMs, overdueMs: 0 };
  if (run.status === 'paused') return { health: 'paused', lastRunMs, overdueMs: 0 };
  if (run.status === 'error') return { health: 'error', lastRunMs, overdueMs: 0 };
  if (run.status === 'partial' || run.status === 'completed_with_errors') {
    return { health: 'partial', lastRunMs, overdueMs: 0 };
  }
  if (meta.intervalMin && lastRunMs) {
    // 1.5× cadence with a 30-minute floor absorbs cold starts and clock skew.
    const allowanceMs = Math.max(meta.intervalMin * 1.5, 30) * 60_000;
    const elapsed = nowMs - lastRunMs;
    if (elapsed > allowanceMs) {
      return { health: 'overdue', lastRunMs, overdueMs: elapsed - meta.intervalMin * 60_000 };
    }
  }
  return { health: 'ok', lastRunMs, overdueMs: 0 };
}

/** "5m" / "3h" / "2d" — used for both ages and overdue amounts. */
function shortDuration(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

const PIPELINE_HEALTH_UI: Record<PipelineHealth, { label: string; color: string; icon: React.ReactNode }> = {
  error: { label: 'error', color: 'error.main', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  partial: { label: 'partial', color: 'warning.main', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  overdue: { label: 'overdue', color: 'warning.main', icon: <TimeIcon sx={{ fontSize: 14 }} /> },
  running: { label: 'running', color: 'info.main', icon: <ResumeIcon sx={{ fontSize: 14 }} /> },
  paused: { label: 'paused', color: 'info.main', icon: <PauseIcon sx={{ fontSize: 14 }} /> },
  ok: { label: 'healthy', color: 'text.disabled', icon: <SuccessOutlineIcon sx={{ fontSize: 14 }} /> },
};

function stagedProductIsDiscontinued(p: StagedProduct): boolean {
  if (p.detectedStatus === 'discontinued') return true;
  return p.manufacturerStock?.status === 'discontinued';
}

interface StagedProduct {
  id: string;
  stagingStatus: string;
  changeType: string;
  existingCanonicalId?: string;
  existingCanonicalSku?: string;
  sku: string;
  upc?: string;
  ean?: string;
  jan?: string;
  asin?: string;
  mpn?: string;
  gtin?: string;
  countryOfOrigin?: string;
  condition?: 'new' | 'used' | 'refurbished' | 'open-box';
  skuSource?: 'vendor' | 'handle';
  /** Body SKU when this staged row is a kit configuration. */
  parentSku?: string;
  /** Family id shared with the parent (`nikon_<parentSku>`). The parent has this and no parentSku. */
  variantGroupId?: string;
  /** Web bundle sold under its handle SKU; its real SKUs are `bundleComponents` (MOT-216). */
  isBundle?: boolean;
  bundleResolution?: 'resolved' | 'needs_review';
  bundleResolutionReason?: string | null;
  bundleOptionName?: string | null;
  bundleContentsText?: string | null;
  bundleComponents?: StagedBundleComponentRow[];
  bundleComponentTotal?: number | null;
  bundleCompareAtPrice?: number | null;
  acknowledgedReviewFlags?: Record<string, { by: string; at: string | Date; value: string }>;
  upcVerification?: string;
  name: string;
  brand: string;
  brandId: string;
  /** Who sells it when the source resells another maker's product (MOT-633); `brand` is the maker. */
  distributor?: { id: string; name: string } | null;
  model: string;
  category: string;
  categorySlug?: string;
  productType: string;
  productTypeExpanded?: string;
  detectedStatus?: string;
  /** Scraped listing signal — discontinued SKUs still appear when staging from full-catalog hubs */
  manufacturerStock?: {
    status?: 'in_stock' | 'out_of_stock' | 'backorder' | 'preorder' | 'discontinued' | 'unknown';
    label?: string;
  };
  launchDate?: string;
  /** Pre-release lifecycle (flag `preorder_lifecycle`); carried to canonical on promotion. */
  lifecycle?: ProductLifecycleValue | null;
  launchDates?: {
    announced?: string;
    announcedNote?: string;
    us?: { date: string; precision: string; note?: string };
    canada?: { date: string; precision: string; note?: string };
    global?: { date: string; precision: string; note?: string };
    checkedAt?: string;
    confidence?: number;
  };
  pricing: {
    msrp?: number;
    retail?: number;
    salePrice?: number;
    currency: string;
    priceAsOf?: string;
    priceLastChecked?: string;
    vendorPriceAsOf?: string;
    msrpAsOf?: string;
    retailAsOf?: string;
    salePriceAsOf?: string;
    msrpSource?: string;
    salePriceSource?: 'scrape' | 'map_rebate' | 'manual';
  };
  description?: string;
  shortDescription?: string;
  features?: string[];
  highlights?: string[];
  images?: Array<{
    url: string;
    sourceUrl?: string;
    alt?: string;
    isPrimary?: boolean;
    type?: 'main' | 'alternate' | 'lifestyle' | 'detail' | 'swatch' | 'promotional' | 'icon';
    typeSource?: 'heuristic' | 'ai' | 'admin';
  }>;
  videos?: Array<{ url: string; title?: string; type?: string }>;
  documentation?: Array<{ url: string; title: string; type: string; language?: string }>;
  inTheBox?: Array<{ name: string; quantity?: number }>;
  specifications?: Record<string, any>;
  specificationsRaw?: Record<string, any>;
  weight?: {
    value: number;
    unit: string;
    raw: string;
  };
  shippingWeight?: {
    value: number;
    unit: string;
    raw: string;
  };
  weightEvidence?: {
    method?: string;
    quote?: string;
    sourceField?: string;
    confidence?: number;
    unitInferred?: boolean;
    applied?: boolean;
    value?: number;
    unit?: string;
    note?: string;
  };
  aiWeight?: {
    value: number;
    unit: string;
    raw: string;
  };
  /** Competing per-variant boxed weights awaiting a human pick. Never auto-applied. */
  shippingWeightCandidates?: Array<{
    value: number;
    unit: string;
    raw: string;
    variantSku?: string;
    variantTitle?: string;
  }>;
  tags?: string[];
  verification?: VerificationResult;
  /** Recent admin / AI edits applied during review (newest last). */
  editHistory?: Array<{
    at?: string | { _seconds?: number; seconds?: number };
    by?: string;
    byEmail?: string;
    byName?: string;
    source: 'ai_fix' | 'admin_edit' | 'accept_all_ai' | 'ai_suggestion' | string;
    fields: string[];
    summary?: string;
    changes?: Array<{ field: string; from?: string | null; to?: string | null }>;
    clearedIssueFields?: string[];
  }>;
  /** Remediation ledger — what the pipeline tried, and what closed each gap. */
  remediation?: {
    version?: number;
    openGaps?: string[];
    lane?: string;
    computedAt?: any;
    nextAttemptAt?: any;
    exhaustedAt?: any;
    gaps?: Record<string, {
      chain?: string[];
      attempts?: Array<{ action: string; at?: any; ok?: boolean; detail?: string; budgetSkipped?: boolean }>;
      attemptCounts?: Record<string, number>;
      nextAction?: string | null;
      nextAttemptAt?: any;
      exhausted?: boolean;
      resolvedBy?: string | null;
      resolvedAt?: any;
    }>;
  };
  enrichmentAmbiguous?: boolean;
  enrichmentAmbiguityReason?: string;
  enrichmentConfidence?: number;
  enrichmentSource?: string;
  enrichmentNeedsReview?: boolean;
  enrichment?: { filledFrom?: Array<{ source?: string; fields?: string[] }> };
  marketPricing?: {
    msrp?: number;
    msrpSource?: string;
    streetPriceLow?: number;
    streetPriceHigh?: number;
    streetPriceAvg?: number;
    dealerPrices?: Array<{
      dealer: string;
      price: number;
      url?: string;
      inStock?: boolean;
      observedAt?: string;
    }>;
    checkedAt?: string;
    confidence?: number;
  };
  identifierCandidates?: Array<{
    upc?: string;
    ean?: string;
    mpn?: string;
    gtin?: string;
    title?: string;
    source: string;
    confidence: number;
    matchReason: string;
  }>;
  variants?: Array<{
    sku: string;
    upc?: string;
    ean?: string;
    mpn?: string;
    optionName: string;
    optionValue: string;
    title?: string;
    source?: string;
  }>;
  source: {
    vendorId: string;
    vendorName: string;
    connectorId: string;
    sourceId: string;
    sourceUrl?: string;
  };
  aiDescription?: string;
  aiShortDescription?: string;
  aiFeatures?: string[];
  aiInTheBox?: Array<{ name: string; quantity?: number; sku?: string }>;
  aiContentConfidence?: {
    description?: number;
    shortDescription?: number;
    features?: number;
    inTheBox?: number;
  };
  descriptionSource?: 'scraped' | 'ai_generated' | 'ai_enhanced';
  scrapedDescription?: string;
  scrapedShortDescription?: string;
  scrapedFeatures?: string[];
  reviewedBy?: string;
  reviewNote?: string;
  createdAt: string;
  updatedAt: string;
  ingestedAt: string;
  verifiedAt?: string;
  promotedAt?: string;
}

interface VerificationResult {
  overallScore: number;
  passedThreshold: boolean;
  verifiedAt: string;
  modelUsed: string;
  scores: {
    nameQuality: number;
    descriptionQuality: number;
    specCompleteness: number;
    imageQuality: number;
    pricingReasonableness: number;
    categoryAccuracy: number;
    weightCompleteness?: number;
  };
  issues: Array<{
    field: string;
    severity: 'error' | 'warning' | 'info';
    message: string;
    autoFixable: boolean;
    suggestedFix?: any;
  }>;
  suggestions: {
    suggestedCategory?: string;
    suggestedTags?: string[];
    discontinuedLikelihood?: number;
  };
}

interface PipelineRun {
  name: string;
  type?: string;
  brand?: string;
  brandId?: string;
  lastRunAt: any;
  status: string;
  message?: string;
  processed?: number;
  budgetSkips?: number;
  gapsClosed?: number;
  sentToReview?: number;
  advancedToPending?: number;
  fastTrackedToApproved?: number;
  approved?: number;
  review?: number;
  failed?: number;
  enriched?: number;
  created?: number;
  updated?: number;
  skipped?: number;
  total?: number;
  queued?: number;
  pipeline?: string;
  /** Per-run troubleshooting messages (e.g. announcement_monitor feed failures). */
  errors?: Array<string | { message?: string; error?: string; detail?: string }>;
  warnings?: Array<string | { message?: string; error?: string; detail?: string }>;
  /** Bulk AI verify: cooperative pause between products */
  pauseRequested?: boolean;
  cancelRequested?: boolean;
  batchProductIds?: string[];
}

/**
 * Pipeline order: AI → remediation → human review → approved → promoted,
 * with rejected as the terminal failure path. Tabs follow this sequence so the
 * board reads left-to-right like the workflow.
 */
const CAN_SEND_TO_REVIEW = new Set(['pending', 'remediating', 'verifying', 'failed', 'approved']);

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'pending', label: 'Pending AI' },
  { value: 'remediating', label: 'Remediating' },
  { value: 'review', label: 'Needs Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'promoted', label: 'Promoted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

/**
 * UPC Review is off the tab strip — it duplicated the UPC state filter and its
 * queue is normally empty. The view itself still answers to `?tab=upc_review`
 * for anyone working a backlog of unverified barcodes.
 */
const ALL_STATUS_TABS: StatusTab[] = [...STATUS_TABS.map((t) => t.value), 'upc_review'];

function isStatusTab(value: string | null | undefined): value is StatusTab {
  return !!value && (ALL_STATUS_TABS as string[]).includes(value);
}

const statusColor: Record<string, 'warning' | 'default' | 'success' | 'error' | 'info' | 'primary'> = {
  pending: 'default',
  remediating: 'info',
  verifying: 'info',
  review: 'warning',
  approved: 'success',
  promoted: 'primary',
  rejected: 'error',
  failed: 'error',
};

const changeTypeConfig: Record<string, { icon: React.ReactNode; label: string; color: 'info' | 'warning' | 'success' | 'default' }> = {
  new: { icon: <NewIcon fontSize="small" />, label: 'New', color: 'success' },
  price_update: { icon: <PriceChangeIcon fontSize="small" />, label: 'Price Update', color: 'info' },
  content_update: { icon: <ContentUpdateIcon fontSize="small" />, label: 'Content Update', color: 'info' },
  spec_update: { icon: <ContentUpdateIcon fontSize="small" />, label: 'Spec Update', color: 'info' },
  status_change: { icon: <StatusChangeIcon fontSize="small" />, label: 'Status Change', color: 'warning' },
  full_update: { icon: <FullUpdateIcon fontSize="small" />, label: 'Full Update', color: 'default' },
};

/** Re-scrape of an existing catalog SKU (vs brand-new discovery). */
function isStagedRescrape(p: Pick<StagedProduct, 'changeType' | 'existingCanonicalId'>): boolean {
  if (p.existingCanonicalId) return true;
  return !!p.changeType && p.changeType !== 'new';
}

/** Gemini / import payloads sometimes store objects in text fields. */
function catalogText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** Pull a human-readable message out of API JSON bodies / Error.message blobs. */
const humanizeErrorText = (raw: string, fallback = 'Request failed'): string => {
  const text = String(raw || '').trim();
  if (!text || text === '[object Object]') return fallback;
  // A gateway error page (e.g. the Hosting rewrite's 502 at 60 s) is never shown as text.
  if (/^<(!doctype|html)/i.test(text)) return 'The server did not finish the request. Try again in a minute.';
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      const nestedError = parsed.error;
      const nestedMsg =
        nestedError && typeof nestedError === 'object' && !Array.isArray(nestedError)
          ? (nestedError as Record<string, unknown>).message
          : undefined;
      const candidate =
        (typeof nestedError === 'string' && nestedError) ||
        (typeof nestedMsg === 'string' && nestedMsg) ||
        (typeof parsed.message === 'string' && parsed.message) ||
        (typeof (parsed as any).data?.error === 'string' && (parsed as any).data.error) ||
        (typeof (parsed as any).data?.message === 'string' && (parsed as any).data.message);
      if (candidate && candidate.trim()) return candidate.trim();
    } catch {
      // not JSON — fall through
    }
  }
  return text;
};

/** Prefer API `{ error: { message } }` / `{ error: string }` shapes over `[object Object]`. */
const messageFromApiBody = (body: unknown, fallback: string): string => {
  if (!body || typeof body !== 'object') return fallback;
  const b = body as Record<string, unknown>;
  if (typeof b.error === 'string' && b.error.trim()) return humanizeErrorText(b.error, fallback);
  if (b.error && typeof b.error === 'object' && !Array.isArray(b.error)) {
    const msg = (b.error as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) return humanizeErrorText(msg, fallback);
  }
  if (typeof b.message === 'string' && b.message.trim()) return humanizeErrorText(b.message, fallback);
  return fallback;
};

/** A failed response as an Error with a readable message, whatever the body is. */
const responseError = async (res: Response, fallback: string): Promise<Error> => {
  try {
    return new Error(messageFromApiBody(await readJsonResponse(res, fallback), fallback));
  } catch (err) {
    return err instanceof Error ? err : new Error(fallback);
  }
};

const getErrorMessage = (error: unknown): string => {
  if (!error) return 'Request failed';
  if (typeof error === 'string') return humanizeErrorText(error);
  if (error instanceof Error) return humanizeErrorText(error.message);
  if (typeof error === 'object') {
    const e = error as Record<string, any>;
    if (typeof e.error === 'string') return humanizeErrorText(e.error);
    if (e.error && typeof e.error === 'object' && typeof e.error.message === 'string') {
      return humanizeErrorText(e.error.message);
    }
    if (typeof e.message === 'string') return humanizeErrorText(e.message);
  }
  return 'Request failed';
};

const formatDate = (value?: any): string => {
  if (!value) return '—';
  let d: Date;
  if (typeof value === 'string') {
    d = new Date(value);
  } else if (value._seconds != null) {
    d = new Date(value._seconds * 1000);
  } else if (value.seconds != null) {
    d = new Date(value.seconds * 1000);
  } else if (value instanceof Date) {
    d = value;
  } else {
    d = new Date(value);
  }
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const formatPrice = (val?: number, currency = 'USD'): string => {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val);
};

const STAGED_LIST_DENSITY_KEY = 'stagedProducts.listDensity';

type StagedListDensity = 'comfortable' | 'compact';

function parseStagedDate(value?: any): Date | null {
  if (!value) return null;
  let d: Date;
  if (typeof value === 'string') d = new Date(value);
  else if (value._seconds != null) d = new Date(value._seconds * 1000);
  else if (value.seconds != null) d = new Date(value.seconds * 1000);
  else if (value instanceof Date) d = value;
  else d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRelativeTime(value?: any): string {
  const d = parseStagedDate(value);
  if (!d) return '—';
  const diffMs = Date.now() - d.getTime();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return diffMs >= 0 ? 'just now' : 'in a moment';
  if (mins < 60) return diffMs >= 0 ? `${mins}m ago` : `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return diffMs >= 0 ? `${hours}h ago` : `in ${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 14) return diffMs >= 0 ? `${days}d ago` : `in ${days}d`;
  return d.toLocaleDateString();
}

function statusDisplayLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending',
    remediating: 'Remediating',
    verifying: 'Verifying',
    review: 'Needs Review',
    approved: 'Approved',
    promoted: 'Promoted',
    rejected: 'Rejected',
    failed: 'Failed',
  };
  return labels[status] || status;
}

type MergeDiffKind = 'added' | 'changed' | 'unchanged' | 'kept';

interface MergeDiffArrayItems {
  kept?: string[];
  removed?: string[];
  added?: string[];
  metadataOnly?: boolean;
}

interface MergeDiffField {
  path: string;
  kind: MergeDiffKind;
  before?: string;
  after?: string;
  /** Provenance for the change, e.g. how a status flip was detected and why. */
  note?: string;
  items?: MergeDiffArrayItems;
}

interface MergeDiffSummary {
  added: number;
  changed: number;
  unchanged: number;
  keptFromTarget: number;
}

interface MergeDiffResult {
  summary: MergeDiffSummary;
  fields: MergeDiffField[];
}

function formatMergeDiffSummary(summary: MergeDiffSummary): string {
  const parts: string[] = [];
  if (summary.added) parts.push(`+${summary.added} added`);
  if (summary.changed) parts.push(`${summary.changed} changed`);
  if (summary.keptFromTarget) parts.push(`${summary.keptFromTarget} kept`);
  if (summary.unchanged) parts.push(`${summary.unchanged} unchanged`);
  return parts.length > 0 ? parts.join(' · ') : 'No field differences';
}

type MergeIdentityIssue = 'kit' | 'upc' | 'ean' | null;

function mergeIdentityIssue(
  preview?: { kitMismatch?: boolean; upcMismatch?: boolean; eanMismatch?: boolean } | null,
): MergeIdentityIssue {
  if (preview?.kitMismatch) return 'kit';
  if (preview?.upcMismatch) return 'upc';
  if (preview?.eanMismatch) return 'ean';
  return null;
}

const KIT_VS_BODY_APPLY_BLOCK =
  'This scrape is a camera body (or non-kit) and the catalog row is a lens kit — or the reverse. They are different products. Do not merge; fix the SKU or reject the row.';

function mergeDiffKindColor(kind: MergeDiffKind): 'success' | 'warning' | 'info' | 'default' {
  if (kind === 'added') return 'success';
  if (kind === 'changed') return 'warning';
  if (kind === 'kept') return 'info';
  return 'default';
}

/**
 * Merge preview is fetched once per dialog open unless this revision changes.
 * In-dialog edits (Accept all AI, PATCH, identifier chips) update `detailProduct`
 * without closing, so the query key must include the compared fields — not just id.
 */
function stagedMergePreviewRevision(product: StagedProduct | null | undefined): string {
  if (!product) return '';
  return JSON.stringify({
    updatedAt: parseFirestoreTimestamp(product.updatedAt)?.getTime() ?? product.updatedAt ?? '',
    name: product.name ?? '',
    model: product.model ?? '',
    brand: product.brand ?? '',
    sku: product.sku ?? '',
    upc: product.upc ?? '',
    ean: product.ean ?? '',
    mpn: product.mpn ?? '',
    gtin: product.gtin ?? '',
    category: product.category ?? '',
    categorySlug: product.categorySlug ?? '',
    productType: product.productType ?? '',
    detectedStatus: product.detectedStatus ?? '',
    description: product.description ?? '',
    shortDescription: product.shortDescription ?? '',
    tags: product.tags ?? [],
    features: product.features ?? [],
    specifications: product.specifications ?? null,
    pricing: product.pricing ?? null,
    weight: product.weight ?? null,
    shippingWeight: product.shippingWeight ?? null,
    images: product.images ?? [],
    videos: product.videos ?? [],
    documentation: product.documentation ?? [],
    inTheBox: product.inTheBox ?? [],
    // Launch / lifecycle edits must bust the promote merge preview (MOT-647).
    launchDates: product.launchDates ?? null,
    lifecycle: product.lifecycle ?? null,
  });
}

function MergeDiffItemLines({ items }: { items: MergeDiffArrayItems }) {
  if (items.metadataOnly) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontSize: 10.5, mt: 0.25, fontStyle: 'italic' }}
      >
        Same items; order or metadata changed
      </Typography>
    );
  }

  const groups: Array<{ key: 'kept' | 'removed' | 'added'; label: string; color: string }> = [
    { key: 'kept', label: 'kept', color: 'text.secondary' },
    { key: 'removed', label: 'removed', color: 'error.main' },
    { key: 'added', label: 'added', color: 'success.main' },
  ];

  return (
    <Box sx={{ mt: 0.35 }}>
      {groups.map(({ key, label, color }) => {
        const values = items[key];
        if (!values?.length) return null;
        return (
          <Typography
            key={key}
            variant="caption"
            sx={{ display: 'block', fontSize: 10.5, color, lineHeight: 1.35 }}
          >
            {label}: {values.join(' · ')}
          </Typography>
        );
      })}
    </Box>
  );
}

type MergeApplyInput = {
  preset?: 'specifications' | 'added_and_changed';
  paths?: string[];
};

type MergeApplyPending =
  | { kind: 'specifications' }
  | { kind: 'added_and_changed'; count: number }
  | { kind: 'selected'; count: number; payload: MergeApplyInput };

function detailPromoteButtonLabel(
  kind: 'approve' | 'promote',
  hasCanonical: boolean,
  selection: MergeSelectionState | null,
  noCatalogDiff = false,
): string {
  if (hasCanonical && noCatalogDiff) {
    return kind === 'approve' ? 'Approve & Close (no changes)' : 'Close (no catalog changes)';
  }
  if (!hasCanonical) return kind === 'approve' ? 'Approve & Promote' : 'Promote to Canonical';
  const selected = selection?.selectedPaths.length ?? 0;
  const selectable = selection?.selectablePaths.length ?? 0;
  if (selectable > 0 && selected === 0) {
    return kind === 'approve' ? 'Approve & Promote (0 fields)' : 'Promote without scrape fields';
  }
  if (selectable > 0 && selected < selectable) {
    return kind === 'approve'
      ? `Approve & Apply selected (${selected})`
      : `Apply selected to Canonical (${selected})`;
  }
  return kind === 'approve' ? 'Approve & Apply Update' : 'Apply Update to Canonical';
}

/** Expandable field-level merge preview (promote / MPN keeper). */
function MergeDiffPanel({
  diff,
  loading,
  emptyMessage,
  defaultExpanded = false,
  selectable = false,
  canApply = false,
  applying = false,
  canonicalId,
  applyBlockedReason,
  summaryLabel = 'Merge diff',
  onApply,
  onSelectionChange,
}: {
  diff?: MergeDiffResult | null;
  loading?: boolean;
  emptyMessage?: string;
  defaultExpanded?: boolean;
  /** Full Update preview: pick specs or a subset and write to catalog without promote. */
  selectable?: boolean;
  canApply?: boolean;
  applying?: boolean;
  canonicalId?: string;
  /** When set, selection is visible but Apply is disabled (e.g. kit vs body). */
  applyBlockedReason?: string;
  summaryLabel?: string;
  onApply?: (input: MergeApplyInput) => void;
  onSelectionChange?: (state: MergeSelectionState) => void;
}) {
  const [open, setOpen] = useState(defaultExpanded || selectable);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<MergeApplyPending | null>(null);
  const previewFields = diff?.fields;
  useEffect(() => {
    setSelected(new Set(defaultSelectedMergePaths(previewFields || [])));
    setPending(null);
  }, [previewFields]);
  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange(buildMergeSelectionState(previewFields || [], selected));
  }, [onSelectionChange, previewFields, selected]);

  const meaningful = (diff?.fields || []).filter((f) => f.kind !== 'unchanged');
  const rows = open
    ? (diff?.fields || []).filter((f) => f.kind === 'added' || f.kind === 'changed' || f.kind === 'kept')
    : [];
  const selectableRows = (diff?.fields || []).filter(isSelectableMergeField);
  const selectablePaths = selectableRows.map((f) => f.path);
  const specSelectable = selectableRows.filter((f) => f.path === 'specifications' || f.path.startsWith('specifications.'));
  const selectedCount = selectablePaths.filter((p) => selected.has(p)).length;
  const allSelectableChecked = selectablePaths.length > 0 && selectablePaths.every((p) => selected.has(p));
  const someSelectableChecked = selectablePaths.some((p) => selected.has(p));
  const showSelection = Boolean(selectable && canApply && onApply);
  const applyEnabled = showSelection && !applyBlockedReason;

  const togglePath = (path: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(path);
      else next.delete(path);
      return next;
    });
    setPending(null);
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(selectablePaths) : new Set());
    setPending(null);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Computing merge difference…
        </Typography>
      </Box>
    );
  }

  if (!diff) {
    if (!emptyMessage) return null;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {emptyMessage}
      </Typography>
    );
  }

  const confirmCopy =
    pending?.kind === 'specifications'
      ? `Apply the merged specification sheet to ${canonicalId || 'the catalog product'}? This does not promote the staged row.`
      : pending?.kind === 'added_and_changed'
        ? `Apply all ${pending.count} added/changed field(s) to ${canonicalId || 'the catalog product'}? This does not promote the staged row.`
        : pending?.kind === 'selected'
          ? `Apply ${pending.count} selected field(s) to ${canonicalId || 'the catalog product'}? This does not promote the staged row.`
          : null;

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        color="inherit"
        onClick={() => setOpen((v) => !v)}
        endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        sx={{
          textTransform: 'none',
          px: 0.5,
          minHeight: 28,
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {summaryLabel}: {formatMergeDiffSummary(diff.summary)}
      </Button>
      <Collapse in={open}>
        {meaningful.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5, mt: 0.5 }}>
            Keeper and staged row match on compared fields.
          </Typography>
        ) : (
          <>
            <TableContainer
              sx={{
                mt: 0.75,
                maxHeight: showSelection ? 320 : 240,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showSelection ? (
                      <TableCell padding="checkbox" sx={{ py: 0.25, width: 36 }}>
                        <Checkbox
                          size="small"
                          checked={allSelectableChecked}
                          indeterminate={someSelectableChecked && !allSelectableChecked}
                          disabled={!applyEnabled || applying || selectablePaths.length === 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                          inputProps={{ 'aria-label': 'Select all added and changed fields' }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ py: 0.5, fontSize: 11, width: 72 }}>Change</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: 11 }}>Field</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: 11 }}>Before → after</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const canSelect = showSelection && isSelectableMergeField(row);
                    return (
                      <TableRow key={`${row.kind}:${row.path}:${row.before || ''}:${row.after || ''}`}>
                        {showSelection ? (
                          <TableCell padding="checkbox" sx={{ py: 0.25, verticalAlign: 'top' }}>
                            {canSelect ? (
                              <Checkbox
                                size="small"
                                checked={selected.has(row.path)}
                                disabled={!applyEnabled || applying}
                                onChange={(e) => togglePath(row.path, e.target.checked)}
                                inputProps={{ 'aria-label': `Select ${row.path}` }}
                              />
                            ) : null}
                          </TableCell>
                        ) : null}
                        <TableCell sx={{ py: 0.4, verticalAlign: 'top' }}>
                          <Chip
                            size="small"
                            label={row.kind}
                            color={mergeDiffKindColor(row.kind)}
                            variant="outlined"
                            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.4, verticalAlign: 'top' }}>
                          <Typography
                            variant="caption"
                            component="code"
                            sx={{ fontSize: 11, wordBreak: 'break-word' }}
                          >
                            {row.path}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.4, verticalAlign: 'top' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
                            {row.before || '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 11 }}>
                            → {row.after || '—'}
                          </Typography>
                          {row.items && <MergeDiffItemLines items={row.items} />}
                          {row.note && (
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: 'block', fontSize: 10.5, mt: 0.25, fontStyle: 'italic' }}
                            >
                              {row.note}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {showSelection && selectablePaths.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {applyBlockedReason ? (
                  <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                    <Typography variant="caption">{applyBlockedReason}</Typography>
                  </Alert>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Checked fields are written to the catalog; unchecked added/changed fields keep the
                    current catalog value. Specs are selected by default. Apply selected writes without
                    promoting. Apply Update to Canonical uses this same selection and still requires
                    listing completeness.
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!applyEnabled || applying || specSelectable.length === 0}
                    onClick={() => {
                      setSelected(new Set(specSelectable.map((f) => f.path)));
                      setPending({ kind: 'specifications' });
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Specs only
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!applyEnabled || applying}
                    onClick={() => {
                      setSelected(new Set(selectablePaths));
                      setPending({ kind: 'added_and_changed', count: selectablePaths.length });
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    All added/changed
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!applyEnabled || applying || selectedCount === 0}
                    onClick={() => {
                      const payload = selectedMergePayload(
                        selectablePaths.filter((p) => selected.has(p)),
                        diff.fields,
                      );
                      setPending({ kind: 'selected', count: selectedCount, payload });
                    }}
                    sx={{ textTransform: 'none', boxShadow: 'none' }}
                  >
                    Apply selected ({selectedCount})
                  </Button>
                </Stack>
                {pending && confirmCopy ? (
                  <Alert
                    severity="warning"
                    sx={{ mt: 1, py: 0.5 }}
                    action={
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          color="inherit"
                          disabled={applying}
                          onClick={() => setPending(null)}
                          sx={{ textTransform: 'none' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={applying}
                          onClick={() => {
                            if (!onApply) return;
                            if (pending.kind === 'specifications') onApply({ preset: 'specifications' });
                            else if (pending.kind === 'added_and_changed') onApply({ preset: 'added_and_changed' });
                            else onApply(pending.payload);
                          }}
                          sx={{ textTransform: 'none', boxShadow: 'none' }}
                        >
                          {applying ? 'Applying…' : 'Confirm'}
                        </Button>
                      </Stack>
                    }
                  >
                    <Typography variant="caption">{confirmCopy}</Typography>
                  </Alert>
                ) : null}
              </Box>
            ) : null}
          </>
        )}
      </Collapse>
    </Box>
  );
}

function aiScoreTone(score?: number): 'success' | 'warning' | 'error' | 'default' {
  if (score == null || Number.isNaN(score)) return 'default';
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

/** True when `url` points at Motzu Firebase / GCS hosting (vs manufacturer CDN). */
function isFirebaseHostedImageUrl(url?: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const u = url.toLowerCase();
  return (
    u.includes('firebasestorage.googleapis.com') ||
    u.includes('.firebasestorage.app/') ||
    u.includes('storage.googleapis.com/')
  );
}

function stagedImageHostMeta(img: { url?: string; sourceUrl?: string }): {
  hosted: boolean;
  shortLabel: string;
  caption: string;
  detail: string;
} {
  const hosted = isFirebaseHostedImageUrl(img.url);
  if (hosted) {
    const origin = img.sourceUrl && img.sourceUrl !== img.url ? img.sourceUrl : undefined;
    return {
      hosted: true,
      shortLabel: 'Storage',
      caption: 'Firebase Storage',
      detail: origin
        ? `Downloaded into Firebase Storage (original: ${origin})`
        : 'Hosted in Firebase Storage',
    };
  }
  return {
    hosted: false,
    shortLabel: 'CDN',
    caption: 'CDN URL',
    detail: 'External CDN / manufacturer URL — not downloaded to Firebase Storage',
  };
}

const STAGED_IMAGE_TYPE_OPTIONS: Array<{
  value: 'main' | 'alternate' | 'lifestyle' | 'detail' | 'promotional' | 'icon' | '';
  label: string;
}> = [
  { value: 'main', label: 'Product (main)' },
  { value: 'alternate', label: 'Product (alternate)' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'icon', label: 'Icon / non-product' },
  { value: 'detail', label: 'Detail' },
  { value: '', label: 'Clear type' },
];

function imageTypeChipLabel(type?: string): string | null {
  if (!type) return null;
  if (type === 'promotional') return 'Promo';
  if (type === 'lifestyle') return 'Lifestyle';
  if (type === 'icon') return 'Icon';
  if (type === 'main') return 'Main';
  if (type === 'detail') return 'Detail';
  if (type === 'alternate') return 'Alt';
  if (type === 'swatch') return 'Swatch';
  return type;
}

function primaryProductImage(product: { images?: Array<{ url?: string; isPrimary?: boolean }> }): string | undefined {
  const images = product.images || [];
  const primary = images.find((img) => img.isPrimary && img.url);
  return primary?.url || images.find((img) => img.url)?.url;
}

function namedBoxItemCount(items?: Array<{ name?: string; item?: string }>): number {
  // Bad docs have shipped inTheBox as a JSON string; never let one row crash the queue.
  if (!Array.isArray(items)) return 0;
  return items.filter((item) => String(item?.name || item?.item || '').trim().length > 0).length;
}

function specFieldCount(product: StagedProduct): number {
  const countKeys = (obj?: Record<string, unknown>) => {
    if (!obj || typeof obj !== 'object') return 0;
    return Object.keys(obj).filter((k) => k !== 'raw' && k !== '_meta').length;
  };
  return countKeys(product.specifications as Record<string, unknown> | undefined)
    || countKeys(product.specificationsRaw as Record<string, unknown> | undefined);
}

function hasListPrice(product: StagedProduct): boolean {
  const p = product.pricing;
  if (!p) return false;
  return [p.msrp, p.retail, p.salePrice].some(
    (v) => typeof v === 'number' && Number.isFinite(v) && v > 0,
  );
}

function dataCompletenessItems(product: StagedProduct): Array<{ key: string; label: string; ok: boolean; detail?: string }> {
  const imageCount = product.images?.length ?? 0;
  const videoCount = product.videos?.length ?? 0;
  const boxCount = namedBoxItemCount(product.inTheBox);
  const aiBoxCount = namedBoxItemCount(product.aiInTheBox);
  const specCount = specFieldCount(product);
  const listPrice = product.pricing?.retail || product.pricing?.msrp || product.pricing?.salePrice;
  return [
    {
      key: 'upc',
      label: 'UPC',
      ok: !!product.upc,
      detail: product.upc || 'Missing UPC',
    },
    {
      key: 'photo',
      label: 'Photo',
      ok: imageCount > 0,
      detail: imageCount > 0 ? `${imageCount} photo${imageCount === 1 ? '' : 's'}` : 'No photos',
    },
    {
      key: 'price',
      label: 'Price',
      ok: hasListPrice(product),
      detail: hasListPrice(product)
        ? formatPrice(listPrice, product.pricing?.currency)
        : 'No MSRP / retail / sale price',
    },
    {
      key: 'specs',
      label: 'Specs',
      ok: specCount > 0,
      detail: specCount > 0 ? `${specCount} spec field${specCount === 1 ? '' : 's'}` : 'No specifications',
    },
    {
      key: 'inTheBox',
      label: 'In the box',
      ok: boxCount > 0,
      detail: boxCount > 0
        ? `${boxCount} item${boxCount === 1 ? '' : 's'}`
        : aiBoxCount > 0
          ? `Missing (AI suggested ${aiBoxCount})`
          : 'Missing in the box',
    },
    {
      key: 'market',
      label: 'Market',
      ok: !!product.marketPricing && !isMarketPricingStale(product.marketPricing),
      detail: !product.marketPricing
        ? 'No market pricing'
        : isMarketPricingStale(product.marketPricing)
          ? `Stale street prices${product.marketPricing.checkedAt ? ` (${formatRelativeDate(product.marketPricing.checkedAt) || formatDate(product.marketPricing.checkedAt)})` : ''}`
          : `Street ${formatPrice(product.marketPricing.streetPriceLow)}–${formatPrice(product.marketPricing.streetPriceHigh)}`,
    },
    {
      key: 'launch',
      label: 'Launch',
      ok: !!product.launchDates,
      detail: product.launchDates?.us?.date || product.launchDates?.announced || (product.launchDates ? 'Launch dates set' : 'No launch dates'),
    },
    {
      key: 'video',
      label: 'Video',
      ok: videoCount > 0,
      detail: videoCount > 0 ? `${videoCount} video${videoCount === 1 ? '' : 's'}` : 'No videos',
    },
  ];
}

/**
 * Remediation provenance — what the automated pipeline did to this product.
 *
 * Spot-checking machine-advanced products needs the ledger visible: which
 * gaps were open, which action closed each one, what was tried and failed,
 * and whether any content is AI-generated. `resolvedBy` distinguishes a
 * dispatcher action from an `external` close (a scrape, admin edit, or fill).
 */
const actionLabel = remediationActionLabel;

function stagedDetailActionIcon(id: StagedDetailActionId) {
  switch (id) {
    case 'accept_all_ai':
      return <FixIcon />;
    case 'enrich_ids':
      return <IdentifierIcon />;
    case 'generate_ai':
      return <GenerateIcon />;
    case 'send_to_review':
      return <SendToReviewIcon />;
    case 'approve':
      return <ApproveIcon />;
    case 'approve_and_promote':
      return <PromoteIcon />;
    case 'reject':
      return <RejectIcon />;
  }
}

const PROMOTE_DETAIL_ACTIONS = new Set<StagedDetailActionId>(['approve', 'approve_and_promote']);

function promoteBlockerTooltip(blockers: PromoteBlocker[]): React.ReactNode {
  return (
    <Box>
      {blockers.map((b) => (
        <Typography key={b.id} variant="caption" display="block" sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
          {b.blocksPromote ? 'Blocks promote — ' : 'Holds auto-approve — '}
          {b.message}
        </Typography>
      ))}
    </Box>
  );
}

/** Chip and alert tone: red when promote fails, blue for the pre-release UPC waiver, amber when held. */
function promoteStateColor(blockers: PromoteBlocker[]): 'error' | 'info' | 'warning' {
  if (promoteWillFail(blockers)) return 'error';
  return blockers.some((b) => b.id === 'upcPendingPreRelease') ? 'info' : 'warning';
}

function PromoteHoldIcon({ blockers }: { blockers: PromoteBlocker[] }) {
  if (blockers.length === 0) return null;
  const hard = promoteWillFail(blockers);
  return (
    <Tooltip title={promoteBlockerTooltip(blockers)}>
      <WarningIcon
        color={hard ? 'error' : 'warning'}
        fontSize="small"
        aria-label={summarizePromoteBlockers(blockers)}
      />
    </Tooltip>
  );
}

/** Split control: recommended next step as the main click, overrides in the chevron. */
const PipelineOwnedActionSplit: React.FC<{
  primary: StagedDetailAction | null;
  overflow: StagedDetailAction[];
  busy: boolean;
  menuAnchor: HTMLElement | null;
  onMenuAnchor: (el: HTMLElement | null) => void;
  onRun: (id: StagedDetailActionId) => void;
  promoteBlockers?: PromoteBlocker[];
}> = ({ primary, overflow, busy, menuAnchor, onMenuAnchor, onRun, promoteBlockers = [] }) => {
  if (!primary && overflow.length === 0) return null;
  const showPromoteHold =
    promoteBlockers.length > 0 &&
    [primary, ...overflow].some((a) => a && PROMOTE_DETAIL_ACTIONS.has(a.id));
  if (!primary || overflow.length === 0) {
    const only = primary || overflow[0];
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Button
          variant={only.variant}
          color={only.color}
          startIcon={stagedDetailActionIcon(only.id)}
          onClick={() => onRun(only.id)}
          disabled={busy}
          sx={{ textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
        >
          {only.label}
        </Button>
        {showPromoteHold && PROMOTE_DETAIL_ACTIONS.has(only.id) && (
          <PromoteHoldIcon blockers={promoteBlockers} />
        )}
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <ButtonGroup
        variant="contained"
        color={primary.color}
        disabled={busy}
        sx={{ boxShadow: 'none' }}
      >
        <Button
          title={primary.description || 'Recommended next step'}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              stagedDetailActionIcon(primary.id)
            )
          }
          onClick={() => onRun(primary.id)}
          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
        >
          {primary.label}
        </Button>
        <Button
          size="small"
          title="Other actions, including approve"
          aria-label="More actions"
          aria-haspopup="menu"
          onClick={(e) => onMenuAnchor(e.currentTarget)}
          sx={{
            minWidth: 36,
            px: 0.5,
            boxShadow: 'none',
            borderLeft: '1px solid',
            borderColor: 'rgba(255,255,255,0.3)',
          }}
        >
          <MenuArrowIcon />
        </Button>
      </ButtonGroup>
      {showPromoteHold && <PromoteHoldIcon blockers={promoteBlockers} />}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => onMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {overflow.map((action) => (
          <MenuItem key={action.id} onClick={() => onRun(action.id)} disabled={busy}>
            <ListItemIcon sx={{ color: `${action.color}.main` }}>
              {stagedDetailActionIcon(action.id)}
            </ListItemIcon>
            <ListItemText
              primary={action.label}
              secondary={
                PROMOTE_DETAIL_ACTIONS.has(action.id) && promoteBlockers.length > 0
                  ? summarizePromoteBlockers(promoteBlockers)
                  : action.description
              }
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 },
              }}
            />
            {PROMOTE_DETAIL_ACTIONS.has(action.id) && <PromoteHoldIcon blockers={promoteBlockers} />}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

/**
 * True when an automated action (not a scrape/admin edit) closed a gap on this
 * product — i.e. the pipeline advanced it without a human looking. These are
 * the rows worth sampling when spot-checking machine output.
 */
function wasMachineAdvanced(p: StagedProduct): boolean {
  const gaps = p.remediation?.gaps || {};
  return Object.values(gaps).some((st) => !!st?.resolvedAt && !!st?.resolvedBy && st.resolvedBy !== 'external');
}

function RemediationLaneChip({ lane }: { lane?: string | null }) {
  const copy = (lane && REMEDIATION_LANE_COPY[lane]) || REMEDIATION_LANE_COPY.unlabeled;
  if (!copy) return null;
  return (
    <Tooltip title={copy.detail}>
      <Chip size="small" variant="outlined" label={copy.label} sx={{ borderRadius: 1.5 }} />
    </Tooltip>
  );
}

const RemediationPanel: React.FC<{ product: StagedProduct }> = ({ product }) => {
  const led = product.remediation;
  if (!led) return null;

  const gapEntries = Object.entries(led.gaps || {});
  const open = new Set(led.openGaps || []);
  const openEntries = gapEntries.filter(([g]) => open.has(g));
  const resolvedEntries = gapEntries.filter(([, st]) => !!st?.resolvedAt);
  const machineResolved = resolvedEntries.filter(([, st]) => st?.resolvedBy && st.resolvedBy !== 'external');
  const pipeline = describeProductPipeline({
    stagingStatus: product.stagingStatus,
    nextAttemptAt: parseStagedDate(led.nextAttemptAt),
    openGaps: led.openGaps,
    gapStates: led.gaps,
  });
  const aiGenerated =
    !!product.aiDescription ||
    !!product.aiShortDescription ||
    !!(product.aiFeatures || []).length ||
    !!(product.aiInTheBox || []).length;

  if (gapEntries.length === 0 && !aiGenerated) return null;

  return (
    <DetailSection
      title="Product pipeline"
      action={
        machineResolved.length > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={`${machineResolved.length} gap${machineResolved.length === 1 ? '' : 's'} closed automatically`}
          />
        ) : undefined
      }
    >
      <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" display="block" fontWeight={600}>
            {pipeline.stage}
          </Typography>
          {(product.stagingStatus === 'remediating' || Boolean(led.lane && REMEDIATION_LANE_COPY[led.lane])) && (
            <RemediationLaneChip lane={led.lane} />
          )}
        </Stack>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {pipeline.nextWork}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
          {pipeline.after}
        </Typography>
      </Alert>

      {aiGenerated && (
        <Alert severity="info" icon={<AIIcon fontSize="small" />} sx={{ mb: 1.5, py: 0.25 }}>
          <Typography variant="caption">
            Contains AI-generated content:{' '}
            {[
              product.aiDescription ? 'description' : null,
              product.aiShortDescription ? 'short description' : null,
              (product.aiFeatures || []).length ? 'features' : null,
              (product.aiInTheBox || []).length ? 'in the box' : null,
            ].filter(Boolean).join(', ')}
            . Scraped originals are preserved separately.
          </Typography>
        </Alert>
      )}

      {openEntries.length > 0 && (
        <>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
            Open gaps
          </Typography>
          <Stack spacing={1.25} sx={{ mb: 1.5, mt: 0.5 }}>
            {openEntries.map(([gap, st]) => {
              const steps = describeGapSteps(st || {});
              const when = describeGapWhen(parseStagedDate(st?.nextAttemptAt));
              return (
                <Box key={gap} sx={{ borderLeft: 2, borderColor: st?.exhausted ? 'warning.main' : 'info.main', pl: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>{remediationGapLabel(gap)}</Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={st?.exhausted ? 'exhausted' : `next: ${steps.next}`}
                      sx={{ height: 18, fontSize: '0.65rem', color: st?.exhausted ? 'warning.main' : 'text.secondary' }}
                    />
                  </Stack>
                  {steps.then && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Then if still missing: {steps.then}
                    </Typography>
                  )}
                  {steps.exhaustedNote && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {steps.exhaustedNote}
                    </Typography>
                  )}
                  {!st?.exhausted && (
                    <>
                      <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                        {when.headline}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" display="block">
                        {when.detail}
                      </Typography>
                    </>
                  )}
                  {(st?.attempts || []).slice().reverse().map((a, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ pl: 0.5 }}>
                      {a.budgetSkipped ? '⏸' : a.ok ? '✓' : '✗'} {actionLabel(a.action)}
                      {a.detail ? ` — ${a.detail}` : ''}
                      {a.at ? ` · ${formatRelativeTime(a.at)}` : ''}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      {resolvedEntries.length > 0 && (
        <>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
            Closed gaps
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {resolvedEntries.map(([gap, st]) => (
              <Stack key={gap} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <SuccessOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="body2">{gap}</Typography>
                <Typography variant="caption" color="text.secondary">
                  closed by {actionLabel(st?.resolvedBy)}
                  {st?.resolvedAt ? ` · ${formatRelativeTime(st.resolvedAt)}` : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      {openEntries.length === 0 && resolvedEntries.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No gaps recorded — this product met the approval bar as ingested.
        </Typography>
      )}
    </DetailSection>
  );
};

/** Shared chrome for detail-dialog content blocks */
const DetailSection: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}> = ({ title, action, children, sx }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 2,
      borderRadius: 2,
      borderColor: 'divider',
      minWidth: 0,
      overflow: 'hidden',
      ...sx,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const InfoField: React.FC<{
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  primer?: string;
  note?: IdentifierFieldNote | null;
  acknowledged?: { by?: string; at?: Date | string } | null;
  extraChips?: React.ReactNode;
  acknowledging?: boolean;
  onAcknowledge?: () => void;
  source?: ReturnType<typeof stagedFieldSource>;
}> = ({ label, value, mono, primer, note, acknowledged, extraChips, acknowledging, onAcknowledge, source }) => {
  const toneColor =
    note?.tone === 'invalid' ? 'error.main' : note?.tone === 'odd' ? 'warning.main' : 'text.secondary';
  const chipColor =
    note?.tone === 'invalid' ? 'error' : note?.tone === 'odd' ? 'warning' : 'info';
  return (
    <Box sx={{ py: 0.5, minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.35}
        sx={{ mb: 0.25, minWidth: 0, flexWrap: 'wrap' }}
      >
        <Tooltip title={primer || ''} disableHoverListener={!primer}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              fontSize: '0.62rem',
              cursor: primer ? 'help' : undefined,
              borderBottom: primer ? '1px dotted' : undefined,
              borderColor: 'text.disabled',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        {source ? (
          <StagedSourceBadge
            source={
              value == null || value === '' || value === '—'
                ? { source: 'missing' }
                : source
            }
          />
        ) : null}
      </Stack>
      <Typography
        variant="body2"
        fontWeight={500}
        noWrap
        title={typeof value === 'string' ? value : undefined}
        sx={{
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value == null || value === '' ? '—' : value}
      </Typography>
      {note?.chip && !acknowledged && (
        <Chip
          label={note.chip}
          size="small"
          color={chipColor}
          variant="outlined"
          sx={{ mt: 0.4, height: 18, fontSize: '0.62rem' }}
        />
      )}
      {acknowledged && (
        <Tooltip title={acknowledged.by ? `Acknowledged by ${acknowledged.by}` : 'Acknowledged'}>
          <Chip
            label="Acknowledged"
            size="small"
            variant="outlined"
            sx={{ mt: 0.4, height: 18, fontSize: '0.62rem' }}
          />
        </Tooltip>
      )}
      {extraChips}
      {note?.message && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.35, fontSize: 10.5, lineHeight: 1.35, color: toneColor }}
        >
          {note.message}
        </Typography>
      )}
      {note?.suggestion && (
        <Typography
          variant="caption"
          sx={{ display: 'block', fontSize: 10.5, color: 'text.secondary' }}
        >
          Suggested: {note.suggestion}
        </Typography>
      )}
      {note?.blocks && onAcknowledge && !acknowledged && (
        <Button
          size="small"
          color="inherit"
          disabled={acknowledging}
          onClick={onAcknowledge}
          sx={{ mt: 0.25, px: 0.5, minHeight: 22, fontSize: 10.5, textTransform: 'none' }}
        >
          Looks OK
        </Button>
      )}
    </Box>
  );
};

const PRODUCT_INFO_PRIMER_KEYS: Record<string, ReviewPrimerField> = {
  SKU: 'sku',
  MPN: 'mpn',
  UPC: 'upc',
  EAN: 'ean',
  GTIN: 'gtin',
  Brand: 'brand',
  Model: 'model',
  Category: 'category',
  Type: 'productType',
  'Detected Status': 'status',
};

type StagedWeightUnit = 'oz' | 'lb' | 'g' | 'kg';

/**
 * Parse admin "Weight (raw)" input into value + unit. Without a unit suffix, falls back to
 * `fallbackUnit` (avoids wrongly labeling "4.5oz" as grams — previous default was always `g`).
 */
function parseStagedWeightInput(raw: string, fallbackUnit: StagedWeightUnit): { value: number; unit: StagedWeightUnit; raw: string } {
  const trimmed = raw.trim();
  const parseNumeric = (token: string): number => parseLocalizedNumber(token);

  if (!trimmed) {
    return { value: 0, unit: fallbackUnit, raw: trimmed };
  }

  const lbOzMatch = trimmed.match(/([\d,.]+)\s*(?:lbs?|pounds?)[\s,]*([\d,.]+)\s*(?:oz|ounces?)/i);
  if (lbOzMatch) {
    const lbs = parseNumeric(lbOzMatch[1]);
    const oz = parseNumeric(lbOzMatch[2]);
    if (lbs >= 0 && oz >= 0 && !Number.isNaN(lbs) && !Number.isNaN(oz)) {
      return { value: Math.round((lbs * 16 + oz) * 100) / 100, unit: 'oz', raw: trimmed };
    }
  }

  const tokenRegex = /([\d,.]+)\s*(kg|grams?|g\b|lbs?|pounds?|oz|ounces?)/gi;
  const tokenMatch = tokenRegex.exec(trimmed);
  if (tokenMatch) {
    const val = parseNumeric(tokenMatch[1]);
    const unitRaw = tokenMatch[2].toLowerCase();
    if (!Number.isNaN(val) && val >= 0) {
      if (/^kg$/i.test(unitRaw)) return { value: val, unit: 'kg', raw: trimmed };
      if (/^g(?:rams?)?$/i.test(unitRaw)) return { value: val, unit: 'g', raw: trimmed };
      if (/^(?:lbs?|pounds?)$/i.test(unitRaw)) return { value: val, unit: 'lb', raw: trimmed };
      if (/^(?:oz|ounces?)$/i.test(unitRaw)) return { value: val, unit: 'oz', raw: trimmed };
    }
  }

  const n = parseLocalizedNumber(trimmed);
  const value = Number.isFinite(n) ? n : 0;
  return { value, unit: fallbackUnit, raw: trimmed };
}

/** Money box over a numeric state: keeps what is being typed ("12.") while the state holds the number. */
const StagedMoneyField: React.FC<{
  label: string;
  value?: number | null;
  onChange: (next: number | null) => void;
}> = ({ label, value, onChange }) => {
  const [text, setText] = React.useState(value != null ? String(value) : '');
  React.useEffect(() => {
    setText((cur) => {
      const external = value != null ? String(value) : '';
      return (cur.trim() === '' ? null : Number(cur)) === (value ?? null) ? cur : external;
    });
  }, [value]);
  return (
    <NumberField
      label={label}
      min={0}
      step={0.01}
      adornment="$"
      value={text}
      onChange={(v) => {
        setText(v);
        onChange(v.trim() === '' ? null : Number(v) || 0);
      }}
      sx={{ width: '100%' }}
    />
  );
};

/**
 * Weight as a number plus a unit select. Writes the same `{ value, unit, raw }` the
 * old free-text "raw" box produced (via parseStagedWeightInput), so the payload is unchanged.
 */
const WEIGHT_UNITS: StagedWeightUnit[] = ['g', 'kg', 'oz', 'lb'];
export const StagedWeightField: React.FC<{
  label: string;
  helperText?: string;
  weight?: { value?: number; unit?: string; raw?: string } | null;
  fallbackUnit: StagedWeightUnit;
  onChange: (next: { value: number; unit: StagedWeightUnit; raw: string } | null) => void;
}> = ({ label, helperText, weight, fallbackUnit, onChange }) => {
  const storedUnit = (WEIGHT_UNITS.includes(weight?.unit as StagedWeightUnit) ? weight?.unit : fallbackUnit) as StagedWeightUnit;
  const [unit, setUnit] = React.useState<StagedWeightUnit>(storedUnit);
  React.useEffect(() => setUnit(storedUnit), [storedUnit]);
  const [text, setText] = React.useState(weight?.value != null ? String(weight.value) : '');
  // Follow outside changes (a different product, Use AI weight) without fighting what is being typed.
  React.useEffect(() => {
    const external = weight?.value != null ? String(weight.value) : '';
    setText((cur) => (Number(cur || 0) === Number(external || 0) ? cur : external));
  }, [weight?.value]);
  const [textError, setTextError] = React.useState<string | null>(null);
  const hasLetters = (t: string) => /[a-z]/i.test(t);
  const commit = (nextText: string, nextUnit: StagedWeightUnit) => {
    setText(nextText);
    setUnit(nextUnit);
    setTextError(null);
    if (!nextText.trim()) {
      onChange(null);
      return;
    }
    // Text with a unit in it waits for blur or paste; only a plain number commits as typed.
    if (hasLetters(nextText)) return;
    onChange(parseStagedWeightInput(`${nextText} ${nextUnit}`, nextUnit));
  };
  /** "1 lb 4 oz" or "4.5oz": parse it, move the unit select and the box to the result, commit it with the raw text. */
  const commitCompound = (rawText: string) => {
    const trimmed = rawText.trim();
    if (!trimmed) {
      commit('', unit);
      return;
    }
    if (!hasLetters(trimmed)) {
      commit(trimmed, unit);
      return;
    }
    if (!/\d/.test(trimmed) || !/(kg|grams?|g\b|lbs?|pounds?|oz|ounces?)/i.test(trimmed)) {
      setText(trimmed);
      setTextError('Enter a weight like 1 lb 4 oz');
      return;
    }
    const parsed = parseStagedWeightInput(trimmed, unit);
    setText(String(parsed.value));
    setUnit(parsed.unit);
    setTextError(null);
    onChange(parsed);
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      {/* Plain TextField, not NumberField: the shared box strips letters, which a pasted "1 lb 4 oz" needs. */}
      <TextField
        size="small"
        label={label}
        value={text}
        error={Boolean(textError)}
        helperText={textError ?? helperText}
        onChange={(e) => {
          const next = e.target.value;
          const ok = hasLetters(next) ? /^[\d.,\sA-Za-z]*$/.test(next) : /^\d*\.?\d*$/.test(next);
          if (ok) commit(next, unit);
        }}
        onBlur={() => hasLetters(text) && commitCompound(text)}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData('text');
          if (hasLetters(pasted)) {
            e.preventDefault();
            commitCompound(pasted);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === '-' || e.key === '+') e.preventDefault();
        }}
        slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': label } }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <TextField
        select
        size="small"
        value={unit}
        onChange={(e) => commit(hasLetters(text) ? String(parseStagedWeightInput(text, unit).value) : text, e.target.value as StagedWeightUnit)}
        slotProps={{ htmlInput: { 'aria-label': `${label} unit` } }}
        sx={{ width: 76 }}
      >
        {WEIGHT_UNITS.map((u) => (
          <MenuItem key={u} value={u}>{u}</MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

function formatWeightEvidenceLine(evidence?: {
  quote?: string;
  sourceField?: string;
  confidence?: number;
  unitInferred?: boolean;
  applied?: boolean;
  note?: string;
}): string {
  if (!evidence) return '';
  const pct =
    typeof evidence.confidence === 'number' && Number.isFinite(evidence.confidence)
      ? `${Math.round(evidence.confidence <= 1 ? evidence.confidence * 100 : evidence.confidence)}%`
      : null;
  return [
    evidence.quote ? `“${evidence.quote}”` : null,
    evidence.sourceField || null,
    pct,
    evidence.unitInferred ? 'unit inferred' : null,
    evidence.applied === false ? 'not applied' : evidence.applied ? 'applied' : null,
    evidence.note || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function formatStagedWeightDisplay(w: { value?: number; unit?: string; raw?: string }): string {
  const hasValue = typeof w.value === 'number' && Number.isFinite(w.value) && !!w.unit;
  if (!hasValue) return (w.raw || '').trim() || '—';
  const core = `${w.value} ${w.unit}`;
  if (!w.raw?.trim()) return core;
  const compact = (s: string) => s.toLowerCase().replace(/\s+/g, '');
  if (compact(w.raw) === compact(core)) return core;
  return `${core} (${w.raw})`;
}

/** Scraped spec placeholders — not a real measurement (SmallRig "Package Weight: TBC"). */
function isPlaceholderSpecValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'object') return false;
  const s = String(value).trim();
  return !s || /^(tbc|tbd|tba|n\/?a|n\.a\.?|coming soon|unknown|-|—|–|\.)$/i.test(s);
}

function specKeyIsShippingWeight(key: string): boolean {
  return /(package(?:d)?|shipping|gross|boxed)\s*(weight|mass)|(weight|mass)\s*(package(?:d)?|shipping|gross)/i.test(
    String(key || ''),
  );
}

function specKeyIsProductWeight(key: string): boolean {
  if (specKeyIsShippingWeight(key)) return false;
  return /(?:^|\b)((?:product|net|item|body)\s*)?(weight|mass)\b/i.test(String(key || ''));
}

type StagedWeightLike = { value: number; unit: string; raw?: string };

/**
 * Specs often omit Product Weight (PDP) while extract-weight filled `product.weight`
 * from a manual/PDF. Show that value here, and do not leave "Package Weight: TBC"
 * looking like a shipping weight that exists.
 */
function overlayExtractedWeightsOnSpecs(
  entries: Array<[string, unknown]>,
  product: { weight?: StagedWeightLike | null; shippingWeight?: StagedWeightLike | null },
): Array<[string, unknown]> {
  const productLabel =
    Number(product.weight?.value) > 0 ? formatStagedWeightDisplay(product.weight!) : null;
  const shippingLabel =
    Number(product.shippingWeight?.value) > 0
      ? formatStagedWeightDisplay(product.shippingWeight!)
      : null;

  let hasProductWeightRow = false;
  const out: Array<[string, unknown]> = [];

  for (const [key, val] of entries) {
    if (specKeyIsShippingWeight(key) && isPlaceholderSpecValue(val)) {
      if (shippingLabel) out.push([key, shippingLabel]);
      continue;
    }
    if (specKeyIsProductWeight(key)) {
      if (isPlaceholderSpecValue(val)) {
        if (productLabel) {
          out.push([key, productLabel]);
          hasProductWeightRow = true;
        }
        continue;
      }
      hasProductWeightRow = true;
    }
    out.push([key, val]);
  }

  if (productLabel && !hasProductWeightRow) {
    out.unshift(['Weight', productLabel]);
  }
  return out;
}

/** Firestore / pipelines occasionally store list fields as a string; calling .map on them blanks the page. */
function asStringArray(v: unknown): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => (x == null ? '' : String(x)));
  if (typeof v === 'string') return v.trim() ? [v] : [];
  return [];
}

function asObjectArray<T extends Record<string, unknown>>(v: unknown): T[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x) => x != null && typeof x === 'object' && !Array.isArray(x)) as T[];
}

/** Loose doc shape for Firestore payloads (typed list fields use StagedProduct.documentation). */
type StagedProductDoc = { url?: string; title?: string; type?: string; language?: string };
type StagedProductVideo = { url?: string; title?: string; type?: string };
type StagedProductBoxItem = { name?: string; item?: string; quantity?: number };

function stagedAiScore(p: StagedProduct): number | undefined {
  const score = p.verification?.overallScore;
  if (score == null || Number.isNaN(score)) return undefined;
  return score;
}

/**
 * Per-product facts the filter bar works from. Derived once per queue: the
 * approval-gap rules are the expensive part, and eight filters plus their facet
 * counts would otherwise re-run them over and over.
 */
type StagedFilterRow = {
  product: StagedProduct;
  brand: string;
  changeType: string;
  discontinued: boolean;
  rescrape: boolean;
  /** Issue ids, including the derived `any_gap`, `specs` and `inTheBox`. */
  gaps: Set<string>;
  gapCount: number;
  hasBarcode: boolean;
  duplicate: boolean;
  aiBuckets: string[];
  haystack: string;
};

function buildStagedFilterRow(p: StagedProduct, duplicateUpcIds: Set<string>): StagedFilterRow {
  const gapIds = getStagedApprovalGaps(p);
  const gaps = new Set<string>(gapIds);
  if (gapIds.length > 0) gaps.add('any_gap');
  // Specs and in-the-box are filterable issues but not approval gaps.
  if (specFieldCount(p) === 0) gaps.add('specs');
  if (namedBoxItemCount(p.inTheBox) === 0) gaps.add('inTheBox');
  for (const issue of stagedIntegrityIssueIds(p)) gaps.add(issue);
  return {
    product: p,
    brand: p.brand || '',
    changeType: p.changeType || '',
    discontinued: stagedProductIsDiscontinued(p),
    rescrape: isStagedRescrape(p),
    gaps,
    gapCount: gapIds.length,
    hasBarcode: stagedHasBarcode(p),
    duplicate: duplicateUpcIds.has(p.id),
    aiBuckets: stagedAiBuckets(p),
    // Keep in sync with `stagedSearchHaystack` (id + brandId included).
    haystack: [p.id, p.name, p.sku, p.mpn, p.brand, p.brandId, p.model, p.upc, p.ean]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

function stagedRowMatchesValue(row: StagedFilterRow, key: StagedFilterKey, value: string): boolean {
  switch (key) {
    case 'brand':
      return brandsMatchIdentity(row.brand, value) || brandsMatchIdentity(row.product.brandId, value);
    case 'issues':
      return row.gaps.has(value);
    case 'listing':
      return value === 'discontinued_only' ? row.discontinued : !row.discontinued;
    case 'scrape':
      return value === 'updates' ? row.rescrape : !row.rescrape;
    case 'change':
      return row.changeType === value;
    case 'upc':
      return value === 'has' ? row.hasBarcode : !row.hasBarcode;
    case 'dupe':
      return value === 'duplicates_only' ? row.duplicate : !row.duplicate;
    case 'ai':
      return row.aiBuckets.includes(value);
    case 'sort':
      // Ordering, not filtering.
      return true;
    default:
      return true;
  }
}

/** Values inside one field are OR-ed (facet semantics); separate fields are AND-ed. */
function stagedRowMatchesField(
  row: StagedFilterRow,
  key: StagedFilterKey,
  value: StagedFilterValue,
): boolean {
  if (key === 'issues') {
    return !Array.isArray(value) || stagedRowMatchesIssues(row.gaps, value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 || value.some((v) => stagedRowMatchesValue(row, key, v));
  }
  return value === stagedFilterDefault(key) || stagedRowMatchesValue(row, key, value);
}

/** Count each option of one field over rows already narrowed by the other fields. */
function tallyStagedFacet(key: StagedFilterKey, rows: StagedFilterRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  const bump = (value: string) => {
    if (value) counts[value] = (counts[value] || 0) + 1;
  };
  if (key === 'sort') return counts;
  for (const row of rows) {
    switch (key) {
      case 'brand':
        bump(row.brand);
        break;
      case 'issues':
        row.gaps.forEach(bump);
        break;
      case 'listing':
        bump(row.discontinued ? 'discontinued_only' : 'active_only');
        break;
      case 'scrape':
        bump(row.rescrape ? 'updates' : 'new');
        break;
      case 'change':
        bump(row.changeType);
        break;
      case 'upc':
        bump(row.hasBarcode ? 'has' : 'missing');
        break;
      case 'dupe':
        bump(row.duplicate ? 'duplicates_only' : 'hide_duplicates');
        break;
      case 'ai':
        row.aiBuckets.forEach(bump);
        break;
      default:
        break;
    }
  }
  // The leading option of a single-select means "no filter", so it counts everything.
  if (key !== 'brand' && key !== 'issues') counts.all = rows.length;
  return counts;
}

/** Every AI-score bucket a product belongs to (a low score is also "below 80"). */
function stagedAiBuckets(p: StagedProduct): string[] {
  const score = stagedAiScore(p);
  if (score == null) return ['none'];
  if (score >= 80) return ['high'];
  if (score >= 60) return ['medium', 'below_80'];
  return ['low', 'below_80'];
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

type StagedColumnSortId =
  | 'name'
  | 'brand'
  | 'productType'
  | 'changeType'
  | 'stagingStatus'
  | 'aiScore'
  | 'price'
  | 'dataRichness'
  | 'ingestedAt'
  /** Default sort; matches the server's page order so the paged view is correct without loading the full queue. */
  | 'createdAt';

/** API dates arrive as ISO strings or serialized Firestore timestamps (`{_seconds}`). */
function stagedTimeMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'object') {
    const v = value as { _seconds?: number; seconds?: number };
    if (v._seconds != null) return v._seconds * 1000;
    if (v.seconds != null) return v.seconds * 1000;
  }
  const t = new Date(value as string | number | Date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function stagedDataRichness(p: StagedProduct): number {
  let n = 0;
  if (p.marketPricing) n += 2;
  if (p.launchDates) n += 1;
  if (p.upc) n += 1;
  if ((p.images?.length ?? 0) > 0) n += 1;
  if ((p.videos?.length ?? 0) > 0) n += 1;
  return n;
}

function compareStagedProducts(
  a: StagedProduct,
  b: StagedProduct,
  orderBy: StagedColumnSortId,
  direction: 'asc' | 'desc',
): number {
  const dir = direction === 'asc' ? 1 : -1;
  const lc = (s: string | undefined) => (s || '').toLowerCase();
  switch (orderBy) {
    case 'name':
      return lc(a.name).localeCompare(lc(b.name), undefined, { sensitivity: 'base' }) * dir;
    case 'brand':
      return lc(a.brand).localeCompare(lc(b.brand), undefined, { sensitivity: 'base' }) * dir;
    case 'productType':
      return lc(a.productType).localeCompare(lc(b.productType), undefined, { sensitivity: 'base' }) * dir;
    case 'changeType':
      return lc(a.changeType).localeCompare(lc(b.changeType), undefined, { sensitivity: 'base' }) * dir;
    case 'stagingStatus':
      return lc(a.stagingStatus).localeCompare(lc(b.stagingStatus), undefined, { sensitivity: 'base' }) * dir;
    case 'aiScore': {
      const va = a.verification?.overallScore;
      const vb = b.verification?.overallScore;
      const na = va == null || Number.isNaN(va) ? -1 : va;
      const nb = vb == null || Number.isNaN(vb) ? -1 : vb;
      if (na !== nb) return (na - nb) * dir;
      return 0;
    }
    case 'price': {
      const pa = a.pricing?.retail ?? a.pricing?.msrp ?? a.pricing?.salePrice ?? 0;
      const pb = b.pricing?.retail ?? b.pricing?.msrp ?? b.pricing?.salePrice ?? 0;
      if (pa !== pb) return (pa - pb) * dir;
      return 0;
    }
    case 'dataRichness': {
      const da = stagedDataRichness(a);
      const db = stagedDataRichness(b);
      if (da !== db) return (da - db) * dir;
      return 0;
    }
    case 'ingestedAt': {
      const ta = stagedTimeMillis(a.ingestedAt) || stagedTimeMillis(a.createdAt);
      const tb = stagedTimeMillis(b.ingestedAt) || stagedTimeMillis(b.createdAt);
      if (ta !== tb) return (ta - tb) * dir;
      return 0;
    }
    case 'createdAt': {
      const ta = stagedTimeMillis(a.createdAt);
      const tb = stagedTimeMillis(b.createdAt);
      if (ta !== tb) return (ta - tb) * dir;
      return 0;
    }
    default:
      return 0;
  }
}

type UpcReviewRow = {
  id: string;
  name?: string;
  sku?: string;
  brand?: string;
  mpn?: string;
  upc?: string;
  upcVerification?: string;
  enrichmentSource?: string;
  enrichmentConfidence?: number;
  identifierCandidates?: unknown[];
};

type UpcColumnSortId =
  | 'name'
  | 'brand'
  | 'mpn'
  | 'upc'
  | 'status'
  | 'source'
  | 'confidence'
  | 'candidates';

function compareUpcRows(a: UpcReviewRow, b: UpcReviewRow, orderBy: UpcColumnSortId, direction: 'asc' | 'desc'): number {
  const dir = direction === 'asc' ? 1 : -1;
  const lc = (s: string | undefined) => (s || '').toLowerCase();
  switch (orderBy) {
    case 'name':
      return lc(a.name).localeCompare(lc(b.name), undefined, { sensitivity: 'base' }) * dir;
    case 'brand':
      return lc(a.brand).localeCompare(lc(b.brand), undefined, { sensitivity: 'base' }) * dir;
    case 'mpn':
      return lc(a.mpn).localeCompare(lc(b.mpn), undefined, { sensitivity: 'base' }) * dir;
    case 'upc':
      return lc(a.upc).localeCompare(lc(b.upc), undefined, { sensitivity: 'base' }) * dir;
    case 'status':
      return lc(a.upcVerification).localeCompare(lc(b.upcVerification), undefined, { sensitivity: 'base' }) * dir;
    case 'source':
      return lc(a.enrichmentSource).localeCompare(lc(b.enrichmentSource), undefined, { sensitivity: 'base' }) * dir;
    case 'confidence': {
      const ca = a.enrichmentConfidence ?? -1;
      const cb = b.enrichmentConfidence ?? -1;
      if (ca !== cb) return (ca - cb) * dir;
      return 0;
    }
    case 'candidates': {
      const na = a.identifierCandidates?.length ?? 0;
      const nb = b.identifierCandidates?.length ?? 0;
      if (na !== nb) return (na - nb) * dir;
      return 0;
    }
    default:
      return 0;
  }
}

/**
 * Auto-promotion policy (Phase 5). Mirrors `AutoPromoteConfig` on the server;
 * the server re-normalizes everything on write, so this type describes what
 * the form edits, not what is trusted.
 */
type AutoPromoteConfigDraft = {
  enabled: boolean;
  dryRun: boolean;
  soakHours: number;
  minVerificationScore: number;
  maxPerRun: number;
  brandAllowlist: string[];
  allowCanonicalUpdates: boolean;
};

/**
 * Per-brand counterfactual from the server: `eligible` is how many would
 * promote *if this brand were allowlisted*, so the switch shows its own blast
 * radius before it is flipped.
 */
type AutoPromoteBrandCandidate = {
  brandId: string;
  brand: string;
  approved: number;
  eligible: number;
  allowlisted: boolean;
  heldReasons: Record<string, number>;
};

/** Hold reasons, in plain language for the operator reading the panel. */
const AUTO_PROMOTE_REASON_LABELS: Record<string, string> = {
  not_approved: 'not approved',
  brand_not_allowlisted: 'brand off',
  soaking: 'still soaking',
  open_gaps: 'open gaps',
  ai_content_pending: 'AI content unaccepted',
  verification_missing: 'never verified',
  verification_failed: 'verification failed',
  verification_below_threshold: 'score below bar',
  canonical_update_not_allowed: 'canonical update',
  regional_barcode_discrepancy: 'EAN/GTIN mismatch',
};

/** True when the draft differs from what is stored, i.e. Save should be live. */
function autoPromoteDirty(draft: AutoPromoteConfigDraft, saved: AutoPromoteConfigDraft): boolean {
  return (
    draft.enabled !== saved.enabled ||
    draft.dryRun !== saved.dryRun ||
    draft.soakHours !== saved.soakHours ||
    draft.minVerificationScore !== saved.minVerificationScore ||
    draft.maxPerRun !== saved.maxPerRun ||
    draft.allowCanonicalUpdates !== saved.allowCanonicalUpdates ||
    draft.brandAllowlist.slice().sort().join(',') !== saved.brandAllowlist.slice().sort().join(',')
  );
}

const AdminStagedProductsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /**
   * Per-action grants, resolved server-side. Actions the caller cannot
   * perform are hidden rather than shown and then rejected.
   */
  const { can: canContent, isPlatformAdmin } = useContentScopes();
  const { data: brandRegistry } = useBrandRegistry();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Direct staged-product links (MOT-833): the open row lives in the address, `<queue>/:id`.
  const { enabled: permalinksOn, isLoading: permalinkFlagLoading } = useOrgFeatureFlag('product_permalinks');
  const { id: routeStagedId = '' } = useParams<{ id: string }>();
  const permalinkBase = location.pathname.startsWith('/app/store/')
    ? '/app/store/staged-products'
    : '/app/admin/staged-products';
  const permalinkNav = usePermalinkNavigation(permalinkBase, permalinksOn);

  /**
   * The URL is the source of truth for the whole view — tab, search and every
   * applied filter — so a filtered queue can be shared or bookmarked. Filter
   * churn replaces the entry; only picking a tab is worth a history push.
   */
  const filters = useMemo(() => stagedFiltersFromParams(searchParams), [searchParams]);
  const updateFilters = useCallback(
    (updater: (prev: StagedFilterState) => StagedFilterState) => {
      setSearchParams(
        (prev) => stagedFiltersToParams(updater(stagedFiltersFromParams(prev)), prev),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const urlTab = searchParams.get('tab');
  const tabFromUrl = urlTab && isStatusTab(urlTab) ? urlTab : null;
  /**
   * Where the heuristic landed. Only the landing effect writes it, so a URL
   * without `?tab` means "the tab the queue starts on", never "the last tab
   * clicked" — otherwise Back would not leave the tab it just left.
   */
  const [landingTab, setLandingTab] = useState<StatusTab>('pending');
  const activeTab: StatusTab = tabFromUrl ?? landingTab;
  /** Once the user (or a shared link) picks a tab, stop auto-adjusting the landing tab. */
  const userSelectedTabRef = useRef(Boolean(tabFromUrl));
  const setActiveTab = useCallback(
    (tab: StatusTab, options?: { replace?: boolean }) => {
      userSelectedTabRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );
  /** False until stats arrive and the landing tab is chosen (avoids empty Pending flash). */
  const [initialTabReady, setInitialTabReady] = useState(false);
  const [detailProduct, setDetailProduct] = useState<StagedProduct | null>(null);
  const [promoteMergeSelection, setPromoteMergeSelection] = useState<MergeSelectionState | null>(null);
  const [promoteSelectionConfirm, setPromoteSelectionConfirm] = useState<{
    mode: 'promote' | 'approve';
    selected: number;
    excluded: number;
    selectable: number;
  } | null>(null);
  const mergePreviewRevision = useMemo(
    () => stagedMergePreviewRevision(detailProduct),
    [detailProduct],
  );
  /** Catalog-side apply does not always change staged fields; bump so the preview refetches. */
  const [canonicalMergeEpoch, setCanonicalMergeEpoch] = useState(0);
  useEffect(() => {
    setPromoteMergeSelection(null);
    setPromoteSelectionConfirm(null);
  }, [detailProduct?.id, mergePreviewRevision]);
  useEffect(() => {
    setCanonicalMergeEpoch(0);
  }, [detailProduct?.id]);
  /** Drops stale GET responses when the user opens another row or closes the dialog. */
  const detailFetchGen = useRef(0);
  const permalink = useProductPermalink<StagedProduct>({
    enabled: permalinksOn,
    scope: 'staged',
    routeId: routeStagedId,
    fetchById: async (id) => {
      const res = await makeApiCall(stagedProductApiPath(id), { headers: await getAuthHeaders() });
      if (res.status === 404 || res.status === 403) throw new PermalinkNotFoundError();
      if (!res.ok) throw new Error('Failed to load staged product');
      const json = await res.json();
      if (!json.data) throw new PermalinkNotFoundError();
      return json.data as StagedProduct;
    },
  });
  const [reviewNote, setReviewNote] = useState('');
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [dialogAlert, setDialogAlertBanner] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
    href?: string;
    hrefLabel?: string;
  } | null>(null);
  const [scrapeDialogOpen, setScrapeDialogOpen] = useState(false);
  const [detailActionMenuAnchor, setDetailActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  /** In The Box extract/suggest — shown in that section so Alex does not have to scroll to the top banner. */
  const [inTheBoxNotice, setInTheBoxNotice] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
  } | null>(null);
  const [inTheBoxBusy, setInTheBoxBusy] = useState<'extract' | 'suggest' | null>(null);
  /**
   * The banner renders at the top of the detail dialog, but actions fire from
   * sections far down it (In The Box, images, specs), so on its own the result
   * lands off-screen. Mirror it into the viewport-pinned toast; dismissing the
   * banner leaves the toast to time out on its own.
   */
  const setDialogAlert = useCallback(
    (alert: {
      type: 'success' | 'error' | 'warning';
      message: string;
      href?: string;
      hrefLabel?: string;
    } | null) => {
      setDialogAlertBanner(alert);
      if (alert) setToastMessage({ type: alert.type, message: alert.message });
    },
    [],
  );
  /**
   * Search box value. Held locally so typing stays responsive, then debounced
   * into the URL query below.
   */
  const [searchInput, setSearchInput] = useState<string>(filters.q);
  /** Table row density; persisted across visits. */
  const [listDensity, setListDensity] = useState<StagedListDensity>(() => {
    const stored = localStorage.getItem(STAGED_LIST_DENSITY_KEY);
    return stored === 'compact' || stored === 'comfortable' ? stored : 'comfortable';
  });
  /** Rows fetched per infinite-scroll page; persisted so the choice sticks across visits. */
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = Number(localStorage.getItem(STAGED_PRODUCTS_PAGE_SIZE_KEY));
    return STAGED_PRODUCTS_PAGE_SIZE_OPTIONS.includes(stored)
      ? stored
      : STAGED_PRODUCTS_DEFAULT_PAGE_SIZE;
  });
  const [stagedColumnSort, setStagedColumnSort] = useState<{
    orderBy: StagedColumnSortId;
    direction: 'asc' | 'desc';
  }>({ orderBy: 'createdAt', direction: 'desc' });
  const [upcColumnSort, setUpcColumnSort] = useState<{
    orderBy: UpcColumnSortId;
    direction: 'asc' | 'desc';
  }>({ orderBy: 'name', direction: 'asc' });
  const [manualUpcInput, setManualUpcInput] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [pendingLaunchDates, setPendingLaunchDates] = useState<ProductLaunchDatesInput | null | undefined>(undefined);
  const [pendingLifecycle, setPendingLifecycle] = useState<ProductLifecycleInput | null | undefined>(undefined);
  const [launchDatesInvalid, setLaunchDatesInvalid] = useState(false);
  const [lifecycleInvalid, setLifecycleInvalid] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imageUrlLinkOnly, setImageUrlLinkOnly] = useState(false);
  const [makeNewImagePrimary, setMakeNewImagePrimary] = useState(false);
  /** Index into detailProduct.images for full-size preview; null when closed. */
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null);
  const [brokenImageIndexes, setBrokenImageIndexes] = useState<Set<number>>(new Set());
  const [imageSrcOverrides, setImageSrcOverrides] = useState<Record<number, string>>({});
  const prunedImagesForId = useRef<string | null>(null);
  /** Context menu for setting image type (marketing vs product). */
  const [imageTypeMenu, setImageTypeMenu] = useState<{
    anchor: HTMLElement;
    index: number;
  } | null>(null);
  /** Confirm permanent delete for rejected staged rows only */
  const [rejectDeleteConfirm, setRejectDeleteConfirm] = useState<{ id: string; summary: string } | null>(null);
  /** Anchor for the consolidated "AI & Data Tools" batch-actions menu. */
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState<null | HTMLElement>(null);

  // ── Phase 4: remediation drain waves ─────────────────────────────────────
  const [drainDialogOpen, setDrainDialogOpen] = useState(false);
  const [drainBrand, setDrainBrand] = useState('');
  const [drainGap, setDrainGap] = useState('');
  const [drainExclusive, setDrainExclusive] = useState(true);
  const [drainLimit, setDrainLimit] = useState(200);
  const [drainResult, setDrainResult] = useState<{
    dryRun: boolean; moved: number; advanced: number; exhausted: number; filtered: number; scanned: number;
  } | null>(null);

  const remediationSummaryQuery = useQuery({
    queryKey: ['remediation-summary'],
    enabled: drainDialogOpen || activeTab === 'remediating',
    staleTime: 60_000,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/remediation-summary', { headers });
      if (!res.ok) throw new Error('Failed to load remediation summary');
      const json = await res.json();
      return json.data as {
        review: { total: number; runnable: number; advance: number; exhausted: number };
        remediating: { total: number; byLane?: Record<string, number> };
        byGap: Record<string, number>;
        singleGapCohorts: Record<string, number>;
        exhaustedByAction: Record<string, number>;
        byBrand: Record<string, { brand: string; review: number; remediating: number; runnable: number; byGap: Record<string, number> }>;
        budgets: Record<string, { used: number; cap: number | null }>;
      };
    },
  });

  const drainMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/remediation-drain', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(drainBrand ? { brandId: drainBrand } : {}),
          ...(drainGap ? { gap: drainGap, exclusiveGap: drainExclusive } : {}),
          limit: drainLimit,
          dryRun,
        }),
      });
      if (!res.ok) {
        throw await responseError(res, 'Drain wave failed');
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: (data) => {
      setDrainResult(data);
      if (!data.dryRun) {
        queryClient.invalidateQueries({ queryKey: ['staged-products'] });
        queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
        queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
        queryClient.invalidateQueries({ queryKey: ['remediation-summary'] });
      }
    },
  });
  // ── Phase 5: auto-promotion policy ───────────────────────────────────────
  const [autoPromoteOpen, setAutoPromoteOpen] = useState(false);
  /** Local draft of the policy; null until the saved config loads. */
  const [apDraft, setApDraft] = useState<AutoPromoteConfigDraft | null>(null);
  /** Explicit acknowledgement required to leave dry-run for live writes. */
  const [apConfirmLive, setApConfirmLive] = useState(false);
  /** Show every brand, not just those with something ready to promote. */
  const [apShowAllBrands, setApShowAllBrands] = useState(false);
  /** What the Soak hours box shows, so "1." survives; null follows the draft number. */
  const [apSoakText, setApSoakText] = useState<string | null>(null);
  const apSoakInvalid = apSoakText !== null && parseNumberInput(apSoakText) === null;

  const autoPromoteQuery = useQuery({
    queryKey: ['auto-promote-config'],
    enabled: autoPromoteOpen,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/auto-promote-config', { headers });
      if (!res.ok) throw new Error('Failed to load auto-promotion policy');
      const json = await res.json();
      return json.data as {
        config: AutoPromoteConfigDraft;
        scanned: number;
        truncated: boolean;
        candidates: AutoPromoteBrandCandidate[];
        lastRun: Record<string, any> | null;
      };
    },
  });

  // Seed the draft from the server once per open; edits are local until saved.
  useEffect(() => {
    if (autoPromoteQuery.data && !apDraft) setApDraft({ ...autoPromoteQuery.data.config });
  }, [autoPromoteQuery.data, apDraft]);

  const autoPromoteSaveMutation = useMutation({
    mutationFn: async (config: AutoPromoteConfigDraft) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/auto-promote-config', {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        throw await responseError(res, 'Failed to save auto-promotion policy');
      }
      const json = await res.json();
      return json.data.config as AutoPromoteConfigDraft;
    },
    onSuccess: (config) => {
      // Adopt the normalized server result — it may have clamped what we sent.
      setApDraft({ ...config });
      setApSoakText(null);
      setToastMessage({
        type: 'success',
        message: config.enabled && !config.dryRun
          ? `Auto-promotion is LIVE for ${config.brandAllowlist.length} brand(s)`
          : 'Auto-promotion policy saved',
      });
      queryClient.invalidateQueries({ queryKey: ['auto-promote-config'] });
    },
    onError: (err: any) => {
      setToastMessage({ type: 'error', message: err?.message || 'Save failed' });
    },
  });

  /** Anchor for the filter-rules help popover. */
  const [filterHelpAnchor, setFilterHelpAnchor] = useState<null | HTMLElement>(null);
  /** Pipeline Status board starts collapsed; user can expand manually. */
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  /** Selected pipeline card for the detail dialog. */
  const [selectedPipelineKey, setSelectedPipelineKey] = useState<string | null>(null);
  /** Mobile-only: the tab strip becomes a "quick switcher" sheet below this breakpoint. */
  const [tabSwitcherOpen, setTabSwitcherOpen] = useState(false);
  /** Mobile-only: filter controls collapse into a bottom sheet below this breakpoint. */
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const stagedImageFileInputRef = useRef<HTMLInputElement>(null);

  /** Partial AI verification docs should not crash the detail dialog. */
  const verificationSafe = useMemo(() => {
    const v = detailProduct?.verification;
    if (!v) {
      return {
        issues: [] as VerificationResult['issues'],
        scoreEntries: [] as [string, number][],
        overallScore: 0,
        modelUsed: '—',
        suggestedCategory: undefined as string | undefined,
      };
    }
    const rawScores = v.scores != null && typeof v.scores === 'object' ? (v.scores as Record<string, number>) : null;
    const scoreEntries = rawScores ? (Object.entries(rawScores) as [string, number][]) : [];
    const toBrandSlug = (value: unknown) =>
      String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    const productBrandSlugs = new Set(
      [toBrandSlug(detailProduct?.brandId), toBrandSlug(detailProduct?.brand)].filter(Boolean),
    );
    const noiseTags = new Set(['other', 'misc', 'miscellaneous', 'unknown', 'n/a', 'na']);
    // Keep in sync with src/constants/productTags accessory short-forms + brands-in-tags policy
    const allowedCommonTags = new Set([
      'tripod', 'monopod', 'head', 'bag', 'backpack', 'case', 'cage', 'rig', 'grip',
      'handle', 'mount', 'plate', 'clamp', 'bracket', 'monitor', 'matte-box', 'follow-focus',
      'accessory', 'filter', 'microphone', 'light', 'flash', 'carbon-fiber-tripod',
      'aluminum-tripod', 'ball-head', 'gimbal-head', 'camera-backpack', 'shoulder-bag', 'hard-case',
    ]);
    const issues = (Array.isArray(v.issues) ? v.issues : []).filter((issue) => {
      if (issue.field === 'name') {
        const msg = String(issue.message || '').toLowerCase();
        // Brand-in-title is allowed — hide legacy AI "strip brand prefix" findings
        return !(
          msg.includes('should not start with the brand') ||
          msg.includes('starts with brand') ||
          (msg.includes('brand') && msg.includes('stored separately')) ||
          (msg.includes('brand') && msg.includes('handled separately'))
        );
      }
      if (issue.field === 'tags') {
        const msg = String(issue.message || '');
        const lower = msg.toLowerCase();
        const isVocabComplaint =
          lower.includes('vocabulary') ||
          lower.includes('allowed tag') ||
          lower.includes('not valid tag') ||
          lower.includes('invalid entries') ||
          lower.includes('not in the allowed');
        if (!isVocabComplaint) return true;
        const mentioned: string[] = [];
        const tagQuoteRe = /['"]([\w-]+)['"]/g;
        let tagMatch: RegExpExecArray | null;
        while ((tagMatch = tagQuoteRe.exec(msg)) !== null) {
          mentioned.push(tagMatch[1].toLowerCase());
        }
        if (mentioned.length === 0) return true;
        // Hide when every cited tag is noise, the product brand, or a known-good type tag
        const allOk = mentioned.every(
          (t) => noiseTags.has(t) || productBrandSlugs.has(t) || allowedCommonTags.has(t),
        );
        if (allOk) return false;
      }
      return true;
    });
    const catalogShort = String(detailProduct?.shortDescription || '').trim();
    const aiShort = String(detailProduct?.aiShortDescription || '').trim();
    const shortFixCandidate = (() => {
      const clampedAi = aiShort ? clampShortDescription(aiShort) : '';
      if (clampedAi && clampedAi.length <= STAGED_SHORT_DESCRIPTION_MAX_CHARS) return clampedAi;
      if (catalogShort.length > STAGED_SHORT_DESCRIPTION_MAX_CHARS) {
        const clamped = clampShortDescription(catalogShort);
        if (clamped && clamped !== catalogShort) return clamped;
      }
      return '';
    })();
    const shortMissing = !catalogShort;
    const shortOver = catalogShort.length > STAGED_SHORT_DESCRIPTION_MAX_CHARS;
    let foundShortIssue = false;
    const withShortFix = issues.map((issue) => {
      if (!isShortDescriptionIssueField(issue.field)) return issue;
      foundShortIssue = true;
      const fromIssue =
        typeof issue.suggestedFix === 'string' ? clampShortDescription(issue.suggestedFix) : '';
      const suggested =
        (fromIssue && fromIssue.length <= STAGED_SHORT_DESCRIPTION_MAX_CHARS ? fromIssue : '') ||
        shortFixCandidate;
      if ((shortMissing || shortOver) && suggested) {
        return {
          ...issue,
          field: 'shortDescription',
          autoFixable: true,
          suggestedFix: suggested,
        };
      }
      return { ...issue, field: 'shortDescription' };
    });
    if (!foundShortIssue && shortOver && shortFixCandidate) {
      withShortFix.push({
        field: 'shortDescription',
        severity: 'warning',
        message: 'Short description exceeds the recommended 160 characters for SEO meta description.',
        autoFixable: true,
        suggestedFix: shortFixCandidate,
      });
    } else if (!foundShortIssue && shortMissing && shortFixCandidate) {
      withShortFix.push({
        field: 'shortDescription',
        severity: 'error',
        message: 'Missing. Requires a version under 160 characters.',
        autoFixable: true,
        suggestedFix: shortFixCandidate,
      });
    }
    const withCategoryFix = withShortFix.map((issue) =>
      enrichCategoryVerificationIssue(
        issue,
        v.suggestions?.suggestedCategory,
        detailProduct?.category,
      ),
    );
    const categoryIssueFix = withCategoryFix.find(
      (issue) => issue.field === 'category' && typeof issue.suggestedFix === 'string',
    )?.suggestedFix;
    return {
      issues: withCategoryFix,
      scoreEntries,
      overallScore: typeof v.overallScore === 'number' && !Number.isNaN(v.overallScore) ? v.overallScore : 0,
      modelUsed: typeof v.modelUsed === 'string' ? v.modelUsed : '—',
      suggestedCategory:
        (typeof v.suggestions?.suggestedCategory === 'string' && v.suggestions.suggestedCategory.trim()
          ? v.suggestions.suggestedCategory.trim()
          : undefined) ||
        (typeof categoryIssueFix === 'string' ? categoryIssueFix : undefined),
    };
  }, [detailProduct]);

  const sourceSafe = useMemo(
    () =>
      detailProduct?.source ?? {
        vendorId: '',
        vendorName: '—',
        connectorId: '',
        sourceId: '',
        sourceUrl: undefined as string | undefined,
      },
    [detailProduct],
  );

  const storefrontPlatform = useMemo(
    () =>
      detailProduct
        ? stagedStorefrontPlatform({
            brandId: detailProduct.brandId,
            brand: detailProduct.brand,
            source: detailProduct.source,
            variants: detailProduct.variants,
          })
        : undefined,
    [detailProduct],
  );

  /** Catalog `features` vs `aiFeatures` — avoids showing two identical bullet lists. */
  const stagedFeaturesDisplay = useMemo(() => {
    if (!detailProduct) {
      return { feat: [] as string[], ai: [] as string[], identical: false };
    }
    const feat = asStringArray(detailProduct.features);
    const ai = asStringArray(detailProduct.aiFeatures);
    const identical =
      feat.length > 0 && ai.length > 0 && JSON.stringify(feat) === JSON.stringify(ai);
    return { feat, ai, identical };
  }, [detailProduct]);

  // Fetch pipeline status (poll faster while bulk AI verification is active)
  const { data: pipelineStatus } = useQuery({
    queryKey: ['pipeline-status'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/pipeline-status', { headers });
      if (!res.ok) throw new Error(`Pipeline status request failed (${res.status})`);
      const json = await res.json();
      const snapshot = json.budgets as {
        actions?: Record<string, RemediationBudgetLine>;
        lookups?: Record<string, RemediationBudgetLine>;
      } | undefined;
      const budgets = [
        ...Object.entries(snapshot?.actions || {}),
        ...Object.entries(snapshot?.lookups || {}),
      ].map(([action, line]) => ({ ...line, action }));
      return {
        runs: (json.data || {}) as Record<string, PipelineRun>,
        budgets,
      };
    },
    refetchInterval: (query) => {
      const data = query.state.data?.runs;
      const ai = data?.ai_verification;
      const enhance = data?.ai_enhance;
      if (ai && (ai.status === 'running' || ai.status === 'paused')) return 2000;
      if (enhance && enhance.status === 'running') return 2000;
      return 30000;
    },
  });
  const pipelineRuns = pipelineStatus?.runs;

  // Fetch stats
  const { data: stats, isLoading: statsLoading, isFetched: statsFetched } = useQuery({
    queryKey: ['staged-products-stats'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/stats', { headers });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const json = await res.json();
      return json.data as Record<string, number>;
    },
    refetchInterval: 15000,
  });

  // Land on the first pipeline stage that has work, then unlock the product list.
  useEffect(() => {
    if (!statsFetched) return;
    if (stats && !userSelectedTabRef.current) {
      const pendingAi = (stats.pending || 0) + (stats.verifying || 0);
      const landing: StatusTab =
        pendingAi > 0 ? 'pending'
        : (stats.remediating || 0) > 0 ? 'remediating'
        : (stats.review || 0) > 0 ? 'review'
        : (stats.approved || 0) > 0 ? 'approved'
        : 'all';
      setLandingTab(landing);
      // Landing is not a navigation, so it replaces rather than pushes — but it
      // does write `?tab`, so Back leaves the tab the user clicked and a saved
      // view carries the tab it was saved on. This block is skipped entirely
      // when the URL already names a tab, so a shared link is never clobbered.
      setActiveTab(landing, { replace: true });
    }
    setInitialTabReady(true);
  }, [stats, statsFetched]);

  // Wait for stats + landing tab before listing — otherwise Pending AI finishes
  // empty and the table flashes "No products match" while counts still load.
  const queueBootstrapReady = initialTabReady;

  // Fetch products for current tab
  const brandSearchCandidates = useMemo(
    () => [
      ...(brandRegistry || []).map((b) => ({ id: b.id, slug: b.slug, name: b.name })),
      ...BRAND_CATALOG_IDENTITY.map((b) => ({
        id: b.brandId,
        slug: b.brandId,
        name: b.name,
        aliases: b.aliases,
      })),
    ],
    [brandRegistry],
  );
  const queueBrandId = useMemo(() => {
    // Multi-brand checkbox selections OR across brands client-side; a single
    // brandId fetch would drop every brand after the first.
    if (filters.brand.length > 1) return undefined;
    const tokens = [searchInput, filters.q, ...filters.brand];
    for (const token of tokens) {
      const id = resolveStagedSearchBrandId(token, brandSearchCandidates);
      if (id) return id;
    }
    return undefined;
  }, [searchInput, filters.q, filters.brand, brandSearchCandidates]);

  const allQueueStatuses = 'pending,remediating,verifying,review,approved,promoted,rejected,failed';
  // Brand fetch must still honor the active tab; only All requests every status.
  const statusQuery =
    activeTab === 'all'
      ? allQueueStatuses
      : activeTab === 'pending'
        ? 'pending,verifying'
        : activeTab;
  // Known brand tokens use brandId fetch; other 3+ char needles use server full-text search.
  const serverSearchQ =
    !queueBrandId && filters.q.trim().length >= 3 ? filters.q.trim() : '';
  const {
    data: productPages,
    isLoading: productsLoading,
    isFetching: productsFetching,
    refetch: refetchProducts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['staged-products', activeTab, pageSize, queueBrandId || '', serverSearchQ],
    queryFn: async ({ pageParam }) => {
      const headers = await getAuthHeaders();
      // Server search has no cursor; brand fetch pages within that brand.
      // Request a higher limit so brand/name hits are not truncated to the
      // table pageSize (often 25–50).
      const limit = queueBrandId ? 2000 : serverSearchQ ? 200 : pageSize;
      let url = `/staged-products?status=${statusQuery}&limit=${limit}`;
      if (queueBrandId) url += `&brandId=${encodeURIComponent(queueBrandId)}`;
      else if (serverSearchQ) url += `&q=${encodeURIComponent(serverSearchQ)}`;
      if (!serverSearchQ && pageParam) url += `&cursor=${encodeURIComponent(pageParam)}`;
      const res = await makeApiCall(url, { headers });
      if (!res.ok) throw new Error('Failed to fetch staged products');
      const json = await res.json();
      return { data: json.data as StagedProduct[], nextCursor: (json.nextCursor ?? null) as string | null };
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: activeTab !== 'upc_review' && queueBootstrapReady,
  });
  const products = useMemo(() => {
    if (!productPages) return undefined;
    // Dedupe by id: the server replays from the top when a cursor's boundary doc was
    // deleted/promoted mid-pagination, so pages can overlap.
    const seen = new Set<string>();
    return productPages.pages.flatMap((p) => p.data).filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [productPages]);

  // Fetch UPC review queue
  const { data: upcReviewData, isLoading: upcReviewLoading, refetch: refetchUpcReview } = useQuery({
    queryKey: ['upc-review-queue'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/upc-review-queue?limit=100', { headers });
      if (!res.ok) throw new Error('Failed to fetch UPC review queue');
      const json = await res.json();
      return json as { data: any[]; stats: { total: number; unverified: number; ambiguous: number; missing: number } };
    },
    enabled: activeTab === 'upc_review',
    refetchInterval: activeTab === 'upc_review' ? 15000 : false,
  });

  /** Queue-wide promote-blocking duplicate UPC ids (list chips + filter). */
  const { data: upcCollisionIndex } = useQuery({
    queryKey: ['upc-identity-collision-index'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/upc-identity-collision-index', { headers });
      if (!res.ok) throw new Error('Failed to load duplicate UPC index');
      const json = await res.json();
      return json.data as { collisionIds: string[]; count: number; scanned: number };
    },
    enabled: activeTab !== 'upc_review' && queueBootstrapReady,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  // Flag `prerelease_upc_pending` (MOT-651): a pre-release row may promote without a UPC.
  // Read from the promote routes' own answer (the flag's global setting), not the
  // admin's org flag, so the chip never promises a promote the server refuses.
  const { data: preReleaseUpcPending = false } = useQuery({
    queryKey: ['staged-products-prerelease-upc-pending'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/prerelease-upc-pending', { headers });
      if (!res.ok) return false;
      const json = await res.json();
      return json?.data?.enabled === true;
    },
    staleTime: 60_000,
  });
  // Flag `barcode_override` (MOT-838), read from the server's own global answer, as above.
  const { data: barcodeOverrideEnabled = false } = useQuery({
    queryKey: ['staged-products-barcode-override-enabled'],
    queryFn: () => barcodeOverrideApi.enabled().catch(() => false),
    enabled: isPlatformAdmin,
    staleTime: 60_000,
  });
  const [barcodeOverrideOpen, setBarcodeOverrideOpen] = useState(false);
  const duplicateUpcIdSet = useMemo(
    () => new Set(upcCollisionIndex?.collisionIds || []),
    [upcCollisionIndex?.collisionIds],
  );

  const activeStagingStatusesForPromotedSkuWarning = new Set(['review', 'approved']);

  const { data: promotedSkuConflicts } = useQuery({
    queryKey: [
      'staged-promoted-sku-conflicts',
      detailProduct?.id,
      detailProduct?.sku,
      detailProduct?.brandId,
    ],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        sku: detailProduct!.sku,
        brandId: detailProduct!.brandId,
        excludeStagedId: detailProduct!.id,
      });
      const res = await makeApiCall(`/staged-products/promoted-sku-conflicts?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to check promoted staged SKU conflicts');
      const json = await res.json();
      return json.data as Array<{
        stagedId: string;
        name: string;
        brand: string;
        brandId: string;
        promotedAt?: string;
        inferredCanonicalId?: string;
      }>;
    },
    enabled:
      !!detailProduct &&
      activeStagingStatusesForPromotedSkuWarning.has(detailProduct.stagingStatus) &&
      !!detailProduct.sku?.trim() &&
      !!detailProduct.brandId,
  });

  const detailUpcForCollision =
    (detailProduct?.upc || '').trim() ||
    String(detailProduct?.identifierCandidates?.find((c) => c?.upc)?.upc || '').trim();

  const { data: upcIdentityCollisions } = useQuery({
    queryKey: [
      'staged-upc-identity-collisions',
      detailProduct?.id,
      detailUpcForCollision,
      detailProduct?.brandId,
      detailProduct?.existingCanonicalId,
    ],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        upc: detailUpcForCollision,
        brandId: detailProduct!.brandId,
        excludeStagedId: detailProduct!.id,
      });
      if (detailProduct!.existingCanonicalId) {
        params.set('excludeCanonicalId', detailProduct!.existingCanonicalId);
      }
      const res = await makeApiCall(`/staged-products/upc-identity-collisions?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to check UPC identity collisions');
      const json = await res.json();
      return json.data as Array<{
        collection: 'stagedProducts' | 'canonicalProducts';
        id: string;
        name: string;
        brand: string;
        brandId: string;
        sku: string;
        upc: string;
        stagingStatus?: string;
        status?: string;
        skuMismatch?: boolean;
        recommendMerge?: boolean;
        mergeDiff?: MergeDiffResult;
      }>;
    },
    enabled:
      !!detailProduct &&
      activeStagingStatusesForPromotedSkuWarning.has(detailProduct.stagingStatus) &&
      !!detailUpcForCollision &&
      !!detailProduct.brandId,
  });

  const openStagedConflict = async (stagedId: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${stagedId}`, { headers });
      if (!res.ok) throw new Error('Failed to load conflicting staged product');
      const json = await res.json();
      if (json.data) {
        setIsEditing(false);
        setDialogAlert(null);
        setDetailProduct(json.data);
        if (permalinksOn && json.data.id) {
          clickOpenedStagedId.current = json.data.id;
          permalink.seed(json.data.id, json.data as StagedProduct);
          permalinkNav.open(json.data.id, { replace: true });
        }
      }
    } catch (err) {
      setDialogAlert({ type: 'error', message: getErrorMessage(err) });
    }
  };

  const openedDeepLinkId = useRef<string | null>(null);
  /** The row last opened by a click, so a link arrival can be told from a click. */
  const clickOpenedStagedId = useRef<string | null>(null);
  const previousRouteStagedId = useRef('');
  const previousDetailId = useRef<string | null>(null);

  // The older ?id= link moves to the row's own address.
  const legacyIdParam = (searchParams.get('id') || '').trim();
  useEffect(() => {
    if (!permalinksOn || routeStagedId || !legacyIdParam) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete('id');
    const search = rest.toString();
    navigate(
      { pathname: `${permalinkBase}/${encodeURIComponent(legacyIdParam)}`, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [permalinksOn, routeStagedId, legacyIdParam, searchParams, navigate, permalinkBase]);

  // The address decides which row is open. A shared link or refresh fills the drawer from the one
  // by-id read; the queue behind it then lands on that row's tab, brand and SKU like an ?id= link did.
  useEffect(() => {
    if (!permalinksOn) return;
    const previous = previousRouteStagedId.current;
    previousRouteStagedId.current = routeStagedId;
    if (!routeStagedId) {
      if (previous) closeDetailDialog();
      return;
    }
    const item = permalink.item;
    if (!item || item.id !== routeStagedId) return;
    if (detailProduct === item) return;
    if (clickOpenedStagedId.current !== item.id && openedDeepLinkId.current !== item.id && !detailProduct) {
      openedDeepLinkId.current = item.id;
      userSelectedTabRef.current = true;
    }
    setDetailProduct(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permalinksOn, routeStagedId, permalink.item]);

  // The drawer closing on its own (a promote, a reject, the close button) takes the address with it.
  useEffect(() => {
    const previous = previousDetailId.current;
    previousDetailId.current = detailProduct?.id ?? null;
    if (permalinksOn && routeStagedId && previous && !detailProduct) permalinkNav.close();
  }, [permalinksOn, routeStagedId, detailProduct, permalinkNav]);

  // A row the server no longer has leaves the list as well.
  useEffect(() => {
    if (!permalinksOn || !permalink.notFound || !routeStagedId) return;
    evictStagedIdFromListCache(queryClient, routeStagedId);
    if (detailProduct) {
      // Opened from a list row that has since gone: say so, as opening it always did.
      setDetailProduct(null);
      setActionResult({
        type: 'error',
        message: 'That staged row is already gone. It was removed from this list.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permalinksOn, permalink.notFound, routeStagedId, queryClient]);

  useEffect(() => {
    // With direct links on, the redirect above carries the id to the address instead.
    if (permalinksOn || permalinkFlagLoading) return;
    const id = (searchParams.get('id') || '').trim();
    if (!id || openedDeepLinkId.current === id) return;
    openedDeepLinkId.current = id;
    userSelectedTabRef.current = true;
    void (async () => {
      await openStagedConflict(id);
    })();
  }, [searchParams, permalinksOn, permalinkFlagLoading]);

  useEffect(() => {
    const status = detailProduct?.stagingStatus;
    const id = detailProduct?.id;
    if (!id || openedDeepLinkId.current !== id || !status) return;
    const tab: StatusTab =
      status === 'review' || status === 'remediating' || status === 'approved' ||
      status === 'rejected' || status === 'promoted'
        ? status
        : status === 'pending' || status === 'verifying'
          ? 'pending'
          : 'all';
    // Tab, brand and search land in one params write — two would race, and the
    // second updater would be handed the pre-update params.
    const sku = detailProduct.sku ? String(detailProduct.sku) : '';
    userSelectedTabRef.current = true;
    setSearchInput(sku);
    setSearchParams(
      (prev) => {
        const prevState = stagedFiltersFromParams(prev);
        const withBrand = detailProduct.brand
          ? setStagedFilterValue(prevState, 'brand', [String(detailProduct.brand)])
          : prevState;
        const next = stagedFiltersToParams({ ...withBrand, q: sku }, prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  }, [detailProduct]);

  /** Bulk actions update Firestore but leave detail dialog state stale — refetch the open row. */
  const refreshOpenDetailProduct = async () => {
    const id = detailProduct?.id;
    if (!id) return;
    if (permalinksOn && routeStagedId === id) {
      // The open row is the shared by-id entry: re-reading it refreshes the drawer too.
      await permalink.invalidate(id);
      return;
    }
    const gen = detailFetchGen.current;
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}`, { headers });
      if (!res.ok || gen !== detailFetchGen.current) return;
      const json = await res.json();
      if (json.data && gen === detailFetchGen.current) setDetailProduct(json.data);
    } catch {
      /* list invalidate still covers the table */
    }
  };

  /**
   * List rows are a React Query snapshot. After bulk accept the cache can still
   * hold pre-write features/description, so always follow with a fresh GET.
   */
  const openDetailProduct = async (product: StagedProduct) => {
    const gen = ++detailFetchGen.current;
    setIsEditing(false);
    setEditData({});
    setDialogAlert(null);
    setDetailProduct(product);
    if (permalinksOn) {
      // The row opens at once; it is marked stale, so the shared by-id entry re-reads it behind the drawer.
      clickOpenedStagedId.current = product.id;
      permalink.seed(product.id, product, true);
      permalinkNav.open(product.id);
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(stagedProductApiPath(product.id), { headers });
      if (res.status === 404) {
        evictStagedIdFromListCache(queryClient, product.id);
        if (gen === detailFetchGen.current) {
          setDetailProduct(null);
          setActionResult({
            type: 'error',
            message: 'That staged row is already gone. It was removed from this list.',
          });
        }
        return;
      }
      if (!res.ok || gen !== detailFetchGen.current) return;
      const json = await res.json();
      if (json.data && gen === detailFetchGen.current) setDetailProduct(json.data);
    } catch {
      /* list row is already shown */
    }
  };

  const detailMpnForMerge =
    (detailProduct?.mpn || '').trim() ||
    String(detailProduct?.identifierCandidates?.find((c) => c?.mpn)?.mpn || '').trim();

  const { data: mpnMergeCandidates } = useQuery({
    queryKey: [
      'staged-mpn-merge-candidates',
      detailProduct?.id,
      detailMpnForMerge,
      detailProduct?.brandId,
    ],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        mpn: detailMpnForMerge,
        brandId: detailProduct!.brandId,
        excludeStagedId: detailProduct!.id,
      });
      const res = await makeApiCall(`/staged-products/mpn-merge-candidates?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to check MPN merge candidates');
      const json = await res.json();
      return json.data as Array<{
        collection: 'stagedProducts' | 'canonicalProducts';
        id: string;
        name: string;
        brand: string;
        brandId: string;
        sku: string;
        mpn?: string;
        stagingStatus?: string;
        status?: string;
        sourceUrl?: string;
        connectorId?: string;
        fieldsPresentOnCandidate: string[];
        fieldsMissingOnCurrent: string[];
        fieldsPresentOnCurrentMissingOnCandidate: string[];
        recommendMerge: boolean;
        mergeDiff?: MergeDiffResult;
      }>;
    },
    enabled:
      !!detailProduct &&
      activeStagingStatusesForPromotedSkuWarning.has(detailProduct.stagingStatus) &&
      !!detailMpnForMerge &&
      !!detailProduct.brandId,
  });

  const mergeRecommendedCandidates = visibleMpnMergeCandidates(mpnMergeCandidates, {
    existingCanonicalId: detailProduct?.existingCanonicalId,
    promotedStagedIds: (promotedSkuConflicts || []).map((row) => row.stagedId),
  });

  const mergeUpcCollisions = (upcIdentityCollisions || []).filter((row) => row.recommendMerge);
  const blockingUpcCollisions = (upcIdentityCollisions || []).filter((row) => !row.recommendMerge);

  const linkCanonicalMutation = useMutation({
    mutationFn: async ({ productId, canonicalId }: { productId: string; canonicalId: string }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/link-canonical`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ canonicalId }),
      });
      if (!res.ok) {
        let msg = 'Failed to link catalog product';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setDialogAlert({
        type: 'success',
        message: 'Linked as a catalog update. Promote will merge into the existing product.',
      });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-upc-identity-collisions'] });
      queryClient.invalidateQueries({ queryKey: ['upc-identity-collision-index'] });
      queryClient.invalidateQueries({ queryKey: ['staged-merge-preview'] });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const {
    data: promoteMergePreview,
    isLoading: promoteMergePreviewLoading,
    isError: promoteMergePreviewError,
  } = useQuery({
    queryKey: [
      'staged-merge-preview',
      detailProduct?.id,
      detailProduct?.existingCanonicalId,
      mergePreviewRevision,
      canonicalMergeEpoch,
    ],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${detailProduct!.id}/merge-preview`, { headers });
      if (!res.ok) throw new Error('Failed to load merge preview');
      const json = await res.json();
      return json.data as {
        target: { collection: 'canonicalProducts'; id: string; name: string };
        summary: MergeDiffSummary;
        fields: MergeDiffField[];
        kitMismatch?: boolean;
        upcMismatch?: boolean;
        eanMismatch?: boolean;
        mergedIdentifiers?: { upc?: string; ean?: string; gtin?: string };
      };
    },
    enabled: !!detailProduct?.id && !!detailProduct?.existingCanonicalId,
  });

  const detailMergeSelection = useMemo(() => {
    if (!detailProduct?.existingCanonicalId || !promoteMergePreview) return null;
    if (promoteMergeSelection) return promoteMergeSelection;
    return buildMergeSelectionState(promoteMergePreview.fields || []);
  }, [detailProduct?.existingCanonicalId, promoteMergePreview, promoteMergeSelection]);

  const promoteIsNoop = Boolean(
    detailProduct?.existingCanonicalId &&
      promoteMergePreview &&
      !promoteMergePreview.kitMismatch &&
      (promoteMergePreview.summary?.added || 0) === 0 &&
      (promoteMergePreview.summary?.changed || 0) === 0,
  );

  /**
   * The footer button that actually clears a no-diff row, by its rendered
   * label. The dialog's plain Close only dismisses the dialog, so the banner
   * has to name the real one rather than say "close the row".
   */
  const noopClearLabel = detailPromoteButtonLabel(
    detailProduct?.stagingStatus === 'approved' ? 'promote' : 'approve',
    true,
    null,
    true,
  );

  const applyMergeMutation = useMutation({
    mutationFn: async (input: {
      id: string;
      preset?: 'specifications' | 'added_and_changed';
      paths?: string[];
    }) => {
      const headers = await getAuthHeaders();
      const { id, ...body } = input;
      const res = await makeApiCall(`/staged-products/${id}/apply-merge`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = 'Failed to apply merge';
        try {
          const json = await res.json();
          if (typeof json.error === 'string') msg = json.error;
          else if (typeof json.error?.message === 'string') msg = json.error.message;
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }
      return res.json() as Promise<{
        success: boolean;
        data?: { canonicalId: string; appliedPaths: string[]; appliedCount: number };
      }>;
    },
    onSuccess: (data) => {
      const applied = data.data?.appliedCount ?? 0;
      const canonicalId = data.data?.canonicalId || '';
      setDialogAlert({
        type: 'success',
        message: `Applied ${applied} field(s) to catalog ${canonicalId}. Staged row was not promoted.`,
      });
      setCanonicalMergeEpoch((n) => n + 1);
      queryClient.invalidateQueries({ queryKey: ['staged-merge-preview'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      void refreshOpenDetailProduct();
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  // UPC verification mutation
  const upcVerifyMutation = useMutation({
    mutationFn: async ({ id, upc, action }: { id: string; upc?: string; action: 'confirm' | 'reject' | 'set' }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/upc-verify', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, upc, action }),
      });
      if (!res.ok) throw new Error('Failed to verify UPC');
      return res.json();
    },
    onSuccess: (data, variables) => {
      const actionLabel = variables.action === 'confirm' ? 'Confirmed' : variables.action === 'reject' ? 'Rejected' : 'Set';
      const message = `UPC ${actionLabel}${data.upc ? `: ${data.upc}` : ''}`;
      setActionResult({ type: 'success', message });
      setDialogAlert({ type: 'success', message });
      queryClient.invalidateQueries({ queryKey: ['upc-review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      if (detailProduct?.id === variables.id && variables.action === 'reject') {
        setDetailProduct({
          ...detailProduct,
          upc: undefined,
          ean: undefined,
          gtin: undefined,
          upcVerification: 'rejected',
        });
      }
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: async ({
      id,
      autoPromote,
      mergeSelection,
    }: {
      id: string;
      autoPromote: boolean;
      mergeSelection?: MergeApplyInput;
    }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/approve`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoPromote,
          note: reviewNote,
          ...(autoPromote && mergeSelection ? { mergeSelection } : {}),
        }),
      });
      if (!res.ok) {
        let msg = 'Failed to approve';
        try {
          const body = await res.json();
          if (Array.isArray(body.issues) && body.issues.length > 0) {
            msg = `${body.error || 'Cannot approve'}: ${body.issues.join('; ')}`;
          } else if (body.error) {
            msg = body.error;
          }
        } catch {
          /* use default */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setDialogAlert(null);
      setActionResult({ type: 'success', message: data.canonicalId ? `Approved and promoted (${data.canonicalId})` : 'Approved' });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      setDetailProduct(null);
      setReviewNote('');
    },
    onError: (err) => {
      const msg = getErrorMessage(err);
      setDialogAlert({ type: 'error', message: msg });
      setToastMessage({ type: 'error', message: msg });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/reject`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: reviewNote }),
      });
      if (!res.ok) throw new Error('Failed to reject');
      return res.json();
    },
    onSuccess: () => {
      setDialogAlert(null);
      setActionResult({ type: 'success', message: 'Product rejected' });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      setDetailProduct(null);
      setReviewNote('');
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const unrejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/unreject`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to restore product');
      }
      return res.json();
    },
    onSuccess: () => {
      setDialogAlert(null);
      setActionResult({ type: 'success', message: 'Product restored to pending queue' });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      setDetailProduct(null);
      setReviewNote('');
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const sendToReviewMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/send-to-review`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(note ? { note } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to send to review');
      }
      return res.json();
    },
    onSuccess: () => {
      setDialogAlert(null);
      setActionResult({ type: 'success', message: 'Sent to Needs Review' });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      setDetailProduct(null);
      setReviewNote('');
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const deleteRejectedMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(stagedProductApiPath(id), { method: 'DELETE', headers });
      if (res.status === 404) return { alreadyGone: true as const };
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to delete');
      }
      return res.json();
    },
    onSuccess: (data, id) => {
      evictStagedIdFromListCache(queryClient, id);
      setRejectDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      const message =
        data && typeof data === 'object' && 'alreadyGone' in data
          ? 'That row was already gone. Removed it from this list.'
          : 'Rejected product deleted';
      if (detailProduct?.id === id) {
        setDialogAlert(null);
        setActionResult({ type: 'success', message });
        setDetailProduct(null);
        setReviewNote('');
      } else {
        setActionResult({ type: 'success', message });
      }
    },
    onError: (err) => {
      setRejectDeleteConfirm(null);
      setActionResult({ type: 'error', message: getErrorMessage(err) });
      if (detailProduct) {
        setDialogAlert({ type: 'error', message: getErrorMessage(err) });
      }
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async ({
      id,
      mergeSelection,
    }: {
      id: string;
      mergeSelection?: MergeApplyInput;
    }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/promote`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(mergeSelection ? { mergeSelection } : {}),
      });
      if (!res.ok) {
        let msg = 'Failed to promote';
        try {
          const body = await res.json();
          if (typeof body.error === 'string') msg = body.error;
          else if (typeof body.error?.message === 'string') msg = body.error.message;
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setDialogAlert(null);
      setActionResult({ type: 'success', message: `Promoted to canonical: ${data.canonicalId}` });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      queryClient.invalidateQueries({ queryKey: ['upc-identity-collision-index'] });
      setDetailProduct(null);
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const reverifyMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/reverify`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        throw await responseError(res, 'Failed to reverify');
      }
      return res.json();
    },
    onSuccess: async (data, id) => {
      setDialogAlert({ type: 'success', message: `Re-verified → ${data.newStatus} (score: ${data.data?.overallScore ?? '?'})` });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      try {
        const headers = await getAuthHeaders();
        const res = await makeApiCall(`/staged-products/${id}`, { headers });
        if (res.ok) {
          const json = await res.json();
          if (json.data) setDetailProduct(json.data);
        }
      } catch { /* dialog will close on next action anyway */ }
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const enrichMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/enrich`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        throw await responseError(res, 'Failed to enrich');
      }
      return res.json();
    },
    onSuccess: async (data) => {
      const enriched = data.data?.results?.some((r: any) => r.upc || r.mpn || r.ean);
      const linkedId = data.data?.product?.existingCanonicalId;
      const idBits = data.data?.results
        ?.map((r: any) => `${r.source}: ${[r.mpn && `MPN=${r.mpn}`, r.upc && `UPC=${r.upc}`].filter(Boolean).join(', ')}`)
        .join(' | ');
      // The server stops looking before the 60 s gateway limit; say so rather than "nothing found".
      const timedOut = data.data?.timedOut === true;
      const timeNote = timedOut ? ' Some sources ran out of time; run Enrich IDs again to finish the lookup.' : '';
      setDialogAlert({
        type: enriched ? 'success' : 'warning',
        message: enriched
          ? `Identifiers found: ${idBits}${
              linkedId ? ` — linked as update to ${linkedId}` : ''
            }.${timeNote}`
          : timedOut
            ? `No identifiers found yet.${timeNote}`
            : 'No identifiers found from available sources',
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-upc-identity-collisions'] });
      if (data.data?.product) setDetailProduct(data.data.product);
    },
    onError: async (err, id) => {
      setDialogAlert({
        type: 'error',
        message: `${getErrorMessage(err)}${
          /already used by/i.test(getErrorMessage(err))
            ? ' If this is the same camera, use Link as update.'
            : ''
        }`,
      });
      try {
        const headers = await getAuthHeaders();
        const res = await makeApiCall(`/staged-products/${id}`, { headers });
        if (res.ok) {
          const json = await res.json();
          if (json.data) setDetailProduct(json.data);
        }
      } catch {
        /* keep the error banner */
      }
      queryClient.invalidateQueries({ queryKey: ['staged-upc-identity-collisions'] });
    },
  });

  const marketPricingMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/market-pricing`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        throw await responseError(res, 'Failed to fetch market pricing');
      }
      return res.json();
    },
    onSuccess: async (data) => {
      const mp = data.data?.marketPricing;
      const dealers = mp?.dealerPrices?.length || 0;
      setDialogAlert({
        type: 'success',
        message: `Market pricing: MSRP ${mp?.msrp ? `$${mp.msrp}` : '—'}, ${dealers} dealer${dealers !== 1 ? 's' : ''} found ($${mp?.streetPriceLow || '?'}–$${mp?.streetPriceHigh || '?'})`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      if (data.data?.product) setDetailProduct(data.data.product);
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const launchDatesMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/launch-dates`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        throw await responseError(res, 'Failed to fetch launch dates');
      }
      return res.json();
    },
    onSuccess: async (data) => {
      const ld = data.data?.launchDates;
      setDialogAlert({
        type: 'success',
        message: `Launch dates: announced=${ld?.announced || '?'}, US=${ld?.us?.date || '?'}, CA=${ld?.canada?.date || '?'}`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      if (data.data?.product) setDetailProduct(data.data.product);
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const bhFillMutation = useMutation({
    mutationFn: async ({ id, writeOnly }: { id: string; writeOnly?: string[] }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/bh-fill`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(writeOnly?.length ? { writeOnly, missing: writeOnly } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(body, body?.error || 'Failed to start 3P fill'));
      }
      return { ...body, writeOnly };
    },
    onSuccess: (data) => {
      const jobId = data.data?.jobId;
      const localCmd = data.data?.runCommand;
      const boxOnly = data.writeOnly?.includes('inTheBox');
      const queued = Boolean(data.data?.queued);
      const message = queued
        ? '3P fill queued. It will start when the fill already running finishes.'
        : localCmd
          ? `3P fill job created. Run locally: ${localCmd}`
          : boxOnly
            ? '3P In the Box fill started. A longer B&H list replaces the current box. Specs stay put — follow the run on Admin → Jobs.'
            : '3P fill started. Empty fields only — follow the run on Admin → Jobs.';
      setDialogAlert({
        type: 'success',
        message,
        href: jobId ? `/app/admin/jobs?jobId=${encodeURIComponent(jobId)}` : '/app/admin/jobs',
        hrefLabel: 'Open Jobs',
      });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const scrapeMutation = useMutation({
    mutationFn: async ({ id, sourceUrl }: { id: string; sourceUrl: string }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${id}/scrape`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceUrl }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(messageFromApiBody(body, body?.error || 'Failed to queue manufacturer scrape'));
      }
      return body;
    },
    onSuccess: (data) => {
      const jobId = data.data?.jobId;
      const sourceUrl = typeof data.data?.sourceUrl === 'string' ? data.data.sourceUrl : '';
      setScrapeDialogOpen(false);
      if (sourceUrl) {
        setDetailProduct((prev) =>
          prev
            ? { ...prev, source: { ...prev.source, sourceUrl } }
            : prev
        );
      }
      setDialogAlert({
        type: 'success',
        message: data.data?.message || 'Manufacturer scrape queued.',
        href: jobId ? `/app/admin/jobs?jobId=${encodeURIComponent(jobId)}` : '/app/admin/jobs',
        hrefLabel: 'Open Jobs',
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      void refreshOpenDetailProduct();
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const ENRICH_ID_CHUNK = 10;
  const ENRICH_CHUNK_DELAY_MS = 300;

  const bulkEnrichMutation = useMutation({
    mutationFn: async (productIds?: string[]) => {
      const headers = await getAuthHeaders();
      const runBatch = async (ids: string[]) => {
        const res = await makeApiCall('/staged-products/run-enrichment', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: ids, force: true }),
        });
        if (!res.ok) {
          throw await responseError(res, 'Failed to run enrichment');
        }
        return res.json();
      };

      if (productIds && productIds.length > 0) {
        const merged = {
          processed: 0,
          enriched: 0,
          skipped: 0,
          notFound: 0,
        };
        const batches = Math.ceil(productIds.length / ENRICH_ID_CHUNK);
        for (let i = 0; i < productIds.length; i += ENRICH_ID_CHUNK) {
          const chunk = productIds.slice(i, i + ENRICH_ID_CHUNK);
          const data = await runBatch(chunk);
          const d = data.data;
          merged.processed += d.processed ?? 0;
          merged.enriched += d.enriched ?? 0;
          merged.skipped += d.skipped ?? 0;
          merged.notFound += d.notFound ?? 0;
          if (i + ENRICH_ID_CHUNK < productIds.length) {
            await new Promise((r) => setTimeout(r, ENRICH_CHUNK_DELAY_MS));
          }
        }
        return { data: merged, _batches: batches };
      }

      const res = await makeApiCall('/staged-products/run-enrichment', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 20 }),
      });
      if (!res.ok) throw new Error('Failed to run enrichment');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.data;
      const batchNote =
        typeof (data as any)._batches === 'number' && (data as any)._batches > 1
          ? ` (${(data as any)._batches} API batches)`
          : '';
      const nf = d.notFound != null ? `, ${d.notFound} not found` : '';
      setActionResult({
        type: 'success',
        message: `Enrichment: ${d.processed} processed, ${d.enriched} enriched, ${d.skipped} skipped${nf}${batchNote}`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkMarketLaunchMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/run-market-launch-enrichment', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 10, force: false }),
      });
      if (!res.ok) throw new Error('Failed to run market & launch enrichment');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.data;
      setActionResult({
        type: 'success',
        message: `Market & Launch: ${d.processed} processed, ${d.marketEnriched} market, ${d.launchEnriched} launch, ${d.failed} failed`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const backfillVideosMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/backfill-videos', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 60 }),
      });
      if (!res.ok) throw await responseError(res, 'Video backfill failed');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.data || data.summary || data;
      const mirrored = d.mirroredToBothCollections ? `, ${d.mirroredToBothCollections} mirrored` : '';
      setActionResult({
        type: 'success',
        message: `Videos: ${d.processed}/${d.totalMissing || '?'} products scanned, ${d.totalVideosFound} videos found${mirrored}`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const WEIGHT_EXTRACT_CHUNK = 50;

  const bulkExtractWeightMutation = useMutation({
    mutationFn: async (productIds: string[]) => {
      const headers = await getAuthHeaders();
      const merged = { processed: 0, updated: 0, skipped: 0, notFound: 0, noWeight: 0 };
      for (let i = 0; i < productIds.length; i += WEIGHT_EXTRACT_CHUNK) {
        const chunk = productIds.slice(i, i + WEIGHT_EXTRACT_CHUNK);
        const res = await makeApiCall('/staged-products/bulk-extract-weight', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: chunk }),
        });
        if (!res.ok) throw await responseError(res, 'Weight extraction failed');
        const data = await res.json();
        const d = data.data || data;
        merged.processed += d.processed ?? chunk.length;
        merged.updated += d.updated ?? 0;
        merged.skipped += d.skipped ?? 0;
        merged.notFound += d.notFound ?? 0;
        merged.noWeight += d.noWeight ?? 0;
      }
      return merged;
    },
    onSuccess: (d) => {
      setActionResult({
        type: 'success',
        message: `Weights: ${d.updated} updated, ${d.skipped} already set, ${d.noWeight} with no spec weight`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const backfillWeightsMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/backfill-weights', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 200,
          ...(singleBrandFilter ? { brand: singleBrandFilter } : {}),
        }),
      });
      if (!res.ok) throw await responseError(res, 'Weight backfill failed');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.summary || data.data || data;
      setActionResult({
        type: 'success',
        message: `Weights: ${d.updated}/${d.processed} products updated` +
          (d.noWeight ? `, ${d.noWeight} had no spec weight` : ''),
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const backfillInTheBoxMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/backfill-in-the-box', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 200,
          ...(singleBrandFilter ? { brand: singleBrandFilter } : {}),
        }),
      });
      if (!res.ok) throw await responseError(res, 'In-the-box backfill failed');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.data || data.summary || data;
      setActionResult({
        type: 'success',
        message: `In The Box: ${d.fixed}/${d.processed} products fixed`,
      });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkPromoteMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/promote-approved', {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to bulk promote');
      return res.json();
    },
    onSuccess: (data) => {
      setActionResult({ type: 'success', message: `Bulk promoted ${data.promoted}/${data.total} products (${data.failed} failed)` });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkPromoteSelectedMutation = useMutation({
    mutationFn: async ({ productIds, autoApprove }: { productIds: string[]; autoApprove: boolean }) => {
      // API max is 200 per request — chunk large selections (e.g. 600+ Full Updates).
      const BULK_PROMOTE_CHUNK = 200;
      const headers = await getAuthHeaders();
      let promoted = 0;
      let failed = 0;
      let total = 0;
      const results: { id: string; canonicalId?: string; error?: string }[] = [];
      const batches = Math.ceil(productIds.length / BULK_PROMOTE_CHUNK) || 1;

      for (let i = 0; i < productIds.length; i += BULK_PROMOTE_CHUNK) {
        const chunk = productIds.slice(i, i + BULK_PROMOTE_CHUNK);
        const res = await makeApiCall('/staged-products/bulk-promote', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: chunk, autoApprove }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            body?.error || body?.message || `Failed to bulk promote (HTTP ${res.status})`,
          );
        }
        promoted += Number(body.promoted || 0);
        failed += Number(body.failed || 0);
        total += Number(body.total || chunk.length);
        if (Array.isArray(body.results)) results.push(...body.results);
      }

      return { promoted, failed, total, results, _chunks: batches };
    },
    onSuccess: (data) => {
      const chunkNote =
        typeof data._chunks === 'number' && data._chunks > 1
          ? ` in ${data._chunks} batches`
          : '';
      const sampleErrors = (data.results || [])
        .map((r) => r.error)
        .filter((e): e is string => Boolean(e))
        .slice(0, 2);
      const errorNote =
        sampleErrors.length > 0 ? ` — e.g. ${sampleErrors.join('; ')}` : '';
      setActionResult({
        type: data.failed > 0 && data.promoted === 0 ? 'error' : 'success',
        message: `Promoted ${data.promoted}/${data.total} selected products (${data.failed} failed)${chunkNote}${errorNote}`,
      });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      queryClient.invalidateQueries({ queryKey: ['upc-identity-collision-index'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkRejectSelectedMutation = useMutation({
    mutationFn: async (productIds: string[]) => {
      const headers = await getAuthHeaders();
      const results: { id: string; success: boolean; error?: string }[] = [];
      for (const id of productIds) {
        try {
          const res = await makeApiCall(`/staged-products/${id}/reject`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: 'Bulk rejected' }),
          });
          results.push({ id, success: res.ok, error: res.ok ? undefined : 'Failed' });
        } catch (err: any) {
          results.push({ id, success: false, error: err.message });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const succeeded = results.filter((r) => r.success).length;
      const failed = results.length - succeeded;
      setActionResult({ type: failed > 0 ? 'error' : 'success', message: `Rejected ${succeeded}/${results.length} products${failed > 0 ? ` (${failed} failed)` : ''}` });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkAcceptAiFeaturesMutation = useMutation({
    mutationFn: async (productIds?: string[]) => {
      const headers = await getAuthHeaders();
      const body: Record<string, any> = {};
      if (productIds && productIds.length > 0) {
        body.productIds = productIds;
      }
      const res = await makeApiCall('/staged-products/bulk-accept-ai-features', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to bulk accept AI features');
      return res.json();
    },
    onSuccess: async (data) => {
      const d = data.data;
      setActionResult({
        type: 'success',
        message: `AI Features: ${d.accepted} accepted, ${d.skipped} skipped (${d.total} total)`,
      });
      await queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      await queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      await refreshOpenDetailProduct();
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  /** Accept description/features/tags/category drafts already on products — no Gemini re-run. */
  const bulkAcceptAiSuggestionsMutation = useMutation({
    mutationFn: async (productIds?: string[]) => {
      const headers = await getAuthHeaders();
      const body: Record<string, any> = {};
      if (productIds && productIds.length > 0) {
        body.productIds = productIds;
      }
      const res = await makeApiCall('/staged-products/bulk-accept-ai-suggestions', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          messageFromApiBody(payload, `Failed to bulk accept AI suggestions (HTTP ${res.status})`)
        );
      }
      return payload;
    },
    onSuccess: async (data) => {
      const d = data.data || {};
      const failed = Number(d.failed || 0);
      const sample =
        Array.isArray(d.errors) && d.errors.length > 0
          ? ` — e.g. ${d.errors
              .slice(0, 2)
              .map((e: { id?: string; error?: string }) => `${e.id}: ${e.error}`)
              .join('; ')}`
          : '';
      setActionResult({
        type: failed > 0 ? 'error' : 'success',
        message: `AI Suggestions: ${d.accepted || 0} updated, ${d.skipped || 0} skipped${
          failed > 0 ? `, ${failed} failed` : ''
        } (${d.total || 0} total)${sample}`,
      });
      await queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      await queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
      await refreshOpenDetailProduct();
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const bulkAiVerifyMutation = useMutation({
    mutationFn: async (productIds?: string[]) => {
      const headers = await getAuthHeaders();
      const body: Record<string, any> = {};
      if (productIds && productIds.length > 0) {
        body.productIds = productIds;
      }
      const res = await makeApiCall('/staged-products/bulk-ai-verify', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to run bulk AI verification');
      return res.json();
    },
    onSuccess: (data) => {
      const d = data.data;
      setActionResult({
        type: 'success',
        message: d.message || `AI Verify started for ${d.total} products. Products will update as verification completes.`,
      });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 3000);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 10000);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 30000);
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const aiVerifyPauseMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/pipeline/ai-verification/pause', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to pause AI verification');
      }
      return res.json();
    },
    onSuccess: () => {
      setActionResult({ type: 'success', message: 'AI verification pause requested' });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const aiVerifyResumeMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/pipeline/ai-verification/resume', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to resume AI verification');
      }
      return res.json();
    },
    onSuccess: () => {
      setActionResult({ type: 'success', message: 'AI verification resumed' });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const aiVerifyCancelMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/pipeline/ai-verification/cancel', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to stop AI verification');
      }
      return res.json();
    },
    onSuccess: () => {
      setActionResult({ type: 'success', message: 'AI verification stop requested; remaining items will return to pending after the current product' });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] });
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  /** Status filter for tab-wide AI enhance (matches list query: Pending AI = pending + verifying). */
  const aiEnhanceTabStatus = useMemo(() => {
    if (activeTab === 'all') return 'review,approved,pending,remediating';
    if (activeTab === 'pending') return 'pending,verifying';
    return activeTab;
  }, [activeTab]);

  /** Tab-mode batch size (server also caps at 40). */
  const BULK_AI_TAB_BATCH = 20;

  const bulkAiEnhanceMutation = useMutation({
    mutationFn: async (options?: { skipDescriptions?: boolean; skipNameFixes?: boolean; productIds?: string[]; forceRegenerate?: boolean }) => {
      const headers = await getAuthHeaders();
      const skipDescriptions = options?.skipDescriptions === true;
      const forceRegenerate = options?.forceRegenerate === true;
      const skipNameFixes =
        options?.skipNameFixes === true || forceRegenerate === true;
      const ids = options?.productIds;
      const body: Record<string, unknown> = {
        skipDescriptions,
        skipNameFixes,
        forceRegenerate,
      };
      if (ids && ids.length > 0) {
        body.productIds = ids;
      } else {
        body.limit = BULK_AI_TAB_BATCH;
        body.status = aiEnhanceTabStatus;
      }
      const res = await makeApiCall('/staged-products/bulk-ai-enhance', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const raw = await res.text().catch(() => '');
        let msg = 'Failed to start bulk AI enhance';
        try {
          const j = JSON.parse(raw);
          msg = (j.error && String(j.error)) || (j.message && String(j.message)) || msg;
        } catch {
          if (res.status === 502 || res.status === 504 || res.status === 408) {
            msg = 'Bulk AI enhance timed out at the gateway. If Pipeline Status shows AI Enhance running, wait for it to finish.';
          } else if (raw) {
            msg = raw.slice(0, 300);
          }
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setActionResult({
        type: 'success',
        message:
          data.message ||
          `AI Enhance started for ${data.summary?.total ?? 0} products. Watch Pipeline Status for progress.`,
      });
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 3000);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 10000);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['staged-products'] }), 30000);
    },
    onError: (err) => setActionResult({ type: 'error', message: getErrorMessage(err) }),
  });

  const generateDescMutation = useMutation({
    mutationFn: async (productId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/generate-description`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to generate description');
      return res.json();
    },
    onSuccess: (data) => {
      setDialogAlert({
        type: 'success',
        message: `AI description generated (${data.data?.descriptionSource || 'ai_generated'})`,
      });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const copyFromParentMutation = useMutation({
    mutationFn: async (productId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/copy-from-parent`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        let msg = 'Failed to copy from the parent';
        try {
          const body = await res.json();
          if (typeof body.error === 'string') msg = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      const message = data.message || 'Copied content from the parent';
      setDialogAlert({ type: data.copied?.length ? 'success' : 'warning', message });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const saveEditMutation = useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: Record<string, any> }) => {
      const headers = await getAuthHeaders();
      // Default editMeta for callers that forget — keeps audit trail complete.
      const body =
        data.editMeta && typeof data.editMeta === 'object'
          ? data
          : withEditMeta(data, { source: 'admin_edit' });
      const res = await makeApiCall(`/staged-products/${productId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = 'Failed to save changes';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
          if (Array.isArray(j.issues) && j.issues.length > 0) {
            msg = `${j.error || 'Cannot save'}: ${j.issues.join('; ')}`;
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      const cleared = Array.isArray(data.data?.editHistory)
        ? data.data.editHistory[data.data.editHistory.length - 1]?.clearedIssueFields
        : undefined;
      const clearedCount = Array.isArray(cleared) ? cleared.length : 0;
      const message = data.divertedToStoreOverride
        ? 'Saved as a store override. The catalog listing was not changed.'
        : clearedCount > 0
          ? `Saved — cleared ${clearedCount} verification issue${clearedCount === 1 ? '' : 's'}`
          : 'Changes saved successfully';
      setDialogAlert({ type: 'success', message });
      setToastMessage({ type: 'success', message });
      if (data.data) setDetailProduct(data.data);
      setIsEditing(false);
      setEditData({});
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      queryClient.invalidateQueries({ queryKey: ['upc-identity-collision-index'] });
    },
    onError: (err) => {
      const msg = getErrorMessage(err);
      setDialogAlert({ type: 'error', message: msg });
      setToastMessage({ type: 'error', message: msg });
    },
  });

  /** PATCH without closing the main edit form (e.g. remove one variant row). */
  const patchStagedProductMutation = useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: Record<string, any> }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        let msg = 'Failed to save changes';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
          if (Array.isArray(j.issues) && j.issues.length > 0) {
            msg = `${j.error || 'Cannot save'}: ${j.issues.join('; ')}`;
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setDialogAlert({
        type: 'success',
        message: data.divertedToStoreOverride
          ? 'Saved as a store override. The catalog listing was not changed.'
          : 'Saved',
      });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => {
      const msg = getErrorMessage(err);
      setDialogAlert({ type: 'error', message: msg });
      setToastMessage({ type: 'error', message: msg });
    },
  });

  const addImageFromUrlMutation = useMutation({
    mutationFn: async ({
      productId,
      url,
      makePrimary,
      host,
    }: {
      productId: string;
      url: string;
      makePrimary: boolean;
      host: boolean;
    }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/images/from-url`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), makePrimary, host }),
      });
      if (!res.ok) {
        let msg = 'Failed to add image from URL';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json() as Promise<{ data: StagedProduct }>;
    },
    onSuccess: (data) => {
      setDialogAlert({ type: 'success', message: 'Image added' });
      if (data.data) setDetailProduct(data.data);
      setImageUrlInput('');
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const uploadStagedImageMutation = useMutation({
    mutationFn: async ({ productId, file, makePrimary }: { productId: string; file: File; makePrimary: boolean }) => {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      if (makePrimary) formData.append('makePrimary', 'true');
      const res = await makeApiCall(`/staged-products/${productId}/images/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) {
        let msg = 'Image upload failed';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json() as Promise<{ data: StagedProduct }>;
    },
    onSuccess: (data) => {
      setDialogAlert({ type: 'success', message: 'Image uploaded' });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const classifyImagesMutation = useMutation({
    mutationFn: async ({ productId, force }: { productId: string; force?: boolean }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/images/classify`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: force === true }),
      });
      if (!res.ok) {
        let msg = 'Image classification failed';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json() as Promise<{
        data: StagedProduct;
        classified?: number;
        message?: string;
        stats?: {
          aiAttempted?: number;
          aiAssigned?: number;
          aiFetchFailed?: number;
          aiModelFailed?: number;
          heuristicAssigned?: number;
          geminiAvailable?: boolean;
          heuristicsOnly?: boolean;
        };
      }>;
    },
    onSuccess: (data) => {
      const n = data.classified ?? 0;
      const message =
        (typeof data.message === 'string' && data.message.trim()) ||
        (n > 0 ? `Classified ${n} image${n === 1 ? '' : 's'}` : 'No image types changed');
      const alertType = n > 0 ? 'success' : 'warning';
      setDialogAlert({ type: alertType, message });
      // Toast stays visible even when scrolled down to the Images section.
      setToastMessage({ type: alertType, message });
      if (data.data) setDetailProduct(data.data);
      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
    },
    onError: (err) => {
      const message = getErrorMessage(err);
      setDialogAlert({ type: 'error', message });
      setToastMessage({ type: 'error', message });
    },
  });

  const pruneBrokenImagesMutation = useMutation({
    mutationFn: async (productId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${productId}/images/prune-broken`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        let msg = 'Failed to check broken images';
        try {
          const j = await res.json();
          if (typeof j.error === 'string') msg = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return res.json() as Promise<{
        data: StagedProduct;
        dropped?: number;
        restored?: number;
        message?: string | null;
      }>;
    },
    onSuccess: (data) => {
      if (data.data) setDetailProduct(data.data);
      const msg =
        (typeof data.message === 'string' && data.message.trim()) ||
        summarizeImageHealthFix(data.dropped || 0, data.restored || 0);
      if (msg) {
        setDialogAlert({ type: 'success', message: `Images: ${msg}` });
        queryClient.invalidateQueries({ queryKey: ['staged-products'] });
      }
    },
    onError: (err) => setDialogAlert({ type: 'error', message: getErrorMessage(err) }),
  });

  const applyClientBrokenImageFix = () => {
    if (!detailProduct?.images) return;
    const dead = new Set<string>();
    detailProduct.images.forEach((img, i) => {
      if (brokenImageIndexes.has(i) && img.url) dead.add(img.url);
      if (brokenImageIndexes.has(i) && img.sourceUrl) dead.add(img.sourceUrl);
    });
    const withFallbacks = detailProduct.images.map((img, i) => {
      const fallback = imageSrcOverrides[i];
      if (fallback && !brokenImageIndexes.has(i)) return { ...img, url: fallback };
      return img;
    });
    const { images: next, dropped, restored } = applyBrokenImageRemediation(withFallbacks, (url) =>
      dead.has(url),
    );
    if (dropped === 0 && restored === 0) {
      const onlyFallbacks = withFallbacks.some((img, i) => imageSrcOverrides[i] && img.url !== detailProduct.images![i].url);
      if (!onlyFallbacks) return;
    }
    const changed =
      dropped > 0 ||
      restored > 0 ||
      next.some((img, i) => img.url !== detailProduct.images![i]?.url);
    if (!changed) return;
    patchStagedProductMutation.mutate({
      productId: detailProduct.id,
      data: { images: next },
    });
    const msg = summarizeImageHealthFix(dropped, restored) || 'Updated image URLs';
    setDialogAlert({ type: 'success', message: `Images: ${msg}` });
  };

  const detailImageUrlSig = (detailProduct?.images || []).map((img) => img.url || '').join('|');
  useEffect(() => {
    setBrokenImageIndexes(new Set());
    setImageSrcOverrides({});
  }, [detailProduct?.id, detailImageUrlSig]);

  useEffect(() => {
    const id = detailProduct?.id;
    if (!id) {
      prunedImagesForId.current = null;
      return;
    }
    if (!detailProduct.images?.length) return;
    if (prunedImagesForId.current === id) return;
    prunedImagesForId.current = id;
    pruneBrokenImagesMutation.mutate(id);
  }, [detailProduct?.id]);

  const startEditing = () => {
    if (!detailProduct) return;
    setEditData({
      sku: detailProduct.sku || '',
      name: detailProduct.name || '',
      brand: detailProduct.brand || '',
      model: detailProduct.model || '',
      category: detailProduct.category || '',
      productType: detailProduct.productType || '',
      productTypeExpanded: detailProduct.productTypeExpanded || '',
      // No default — an unrelated edit must not stamp `new` onto a used/refurb catalog row.
      condition: detailProduct.condition || '',
      description: catalogText(detailProduct.description) || catalogText(detailProduct.aiDescription),
      shortDescription: catalogText((detailProduct as any).shortDescription) || catalogText((detailProduct as any).aiShortDescription),
      features: detailProduct.features?.length ? detailProduct.features : (detailProduct.aiFeatures || []),
      highlights: asStringArray(detailProduct.highlights),
      documentation: asObjectArray<StagedProductDoc>((detailProduct as any).documentation),
      videos: asObjectArray<StagedProductVideo>((detailProduct as any).videos),
      inTheBox: asObjectArray<StagedProductBoxItem>((detailProduct as any).inTheBox),
      pricing: detailProduct.pricing || {},
      weight: detailProduct.weight || null,
      shippingWeight: detailProduct.shippingWeight || null,
      tags: asStringArray(detailProduct.tags),
      mpn: detailProduct.mpn || '',
      upc: detailProduct.upc || '',
      ean: detailProduct.ean || '',
      gtin: detailProduct.gtin || '',
      asin: detailProduct.asin || '',
      jan: detailProduct.jan || japanArticleNumber(detailProduct) || '',
      countryOfOrigin: countryOfOriginFromProduct(detailProduct) || '',
      detectedStatus: detailProduct.detectedStatus || '',
      launchDate: detailProduct.launchDate || '',
      specifications: detailProduct.specifications
        ? JSON.parse(JSON.stringify(detailProduct.specifications))
        : {},
    });
    setIsEditing(true);
    setPendingLaunchDates(undefined);
    setPendingLifecycle(undefined);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
    setPendingLaunchDates(undefined);
    setPendingLifecycle(undefined);
  };

  const acceptAllAiOnProduct = (product: StagedProduct) => {
    const { patch, applied, skipped } = buildAcceptAllAiPatch(product);
    if (Object.keys(patch).length === 0) {
      setDialogAlert({
        type: 'warning',
        message: 'Nothing to apply — AI drafts and fixable suggestions are already on the product.',
      });
      return;
    }
    saveEditMutation.mutate(
      {
        productId: product.id,
        data: withEditMeta(patch, {
          source: 'accept_all_ai',
          summary: applied.length
            ? `Accepted AI suggestions: ${applied.join(', ')}`
            : 'Accepted AI suggestions',
          clearedIssueFields: Object.keys(patch),
        }),
      },
      {
        onSuccess: () => {
          const parts = [
            applied.length ? `Applied: ${applied.join(', ')}` : null,
            skipped.length ? `Still needs review: ${skipped.slice(0, 2).join('; ')}` : null,
          ].filter(Boolean);
          const message = parts.join('. ') || 'AI suggestions applied.';
          const type = skipped.length ? 'warning' : 'success';
          queueMicrotask(() => {
            setDialogAlert({ type, message });
            setToastMessage({ type, message });
          });
        },
      },
    );
  };

  const submitDetailPromote = (mode: 'promote' | 'approve', confirmed = false) => {
    if (!detailProduct) return;
    const selection = detailProduct.existingCanonicalId ? detailMergeSelection : null;
    const selected = selection?.selectedPaths.length ?? 0;
    const selectable = selection?.selectablePaths.length ?? 0;
    const excluded = selectable - selected;
    if (!confirmed && selection && selectable > 0 && excluded > 0) {
      setPromoteSelectionConfirm({ mode, selected, excluded, selectable });
      return;
    }
    setPromoteSelectionConfirm(null);
    const mergeSelection = resolvePromoteMergeSelection({
      hasCanonical: Boolean(detailProduct.existingCanonicalId),
      previewReady: Boolean(promoteMergePreview) && !promoteMergePreviewError,
      isNoop: promoteIsNoop,
      selection,
    });
    if (mode === 'approve') {
      approveMutation.mutate({
        id: detailProduct.id,
        autoPromote: true,
        mergeSelection,
      });
      return;
    }
    promoteMutation.mutate({
      id: detailProduct.id,
      mergeSelection,
    });
  };

  const detailPromoteBusy =
    Boolean(
      detailProduct?.existingCanonicalId &&
        (promoteMergePreviewLoading || promoteMergePreviewError || !promoteMergePreview),
    ) ||
    approveMutation.isPending ||
    promoteMutation.isPending;

  const runStagedDetailAction = (id: StagedDetailActionId) => {
    if (!detailProduct) return;
    setDetailActionMenuAnchor(null);
    switch (id) {
      case 'accept_all_ai':
        acceptAllAiOnProduct(detailProduct);
        return;
      case 'enrich_ids':
        enrichMutation.mutate(detailProduct.id);
        return;
      case 'generate_ai':
        generateDescMutation.mutate(detailProduct.id);
        return;
      case 'send_to_review':
        sendToReviewMutation.mutate({
          id: detailProduct.id,
          note: reviewNote.trim() || undefined,
        });
        return;
      case 'approve':
        approveMutation.mutate({ id: detailProduct.id, autoPromote: false });
        return;
      case 'approve_and_promote':
        submitDetailPromote('approve');
        return;
      case 'reject':
        rejectMutation.mutate(detailProduct.id);
        return;
    }
  };

  const pipelineOwnedActions = useMemo(() => {
    if (!detailProduct || !PIPELINE_OWNED_STATUSES.has(detailProduct.stagingStatus)) {
      return { primary: null, overflow: [] };
    }
    return pickPipelineOwnedDetailActions({
      stagingStatus: detailProduct.stagingStatus,
      existingCanonicalId: detailProduct.existingCanonicalId,
      hasPendingAi: hasPendingAiSuggestions(detailProduct),
      canReview: canContent('content_review'),
      canApprove: canContent('content_approve'),
      canPromote: canContent('content_promote'),
      remediation: detailProduct.remediation,
    });
  }, [detailProduct, canContent]);

  const detailPromoteBlockers = useMemo(
    () =>
      detailProduct
        ? getPromoteBlockers(overlayMergePreviewBarcodes(detailProduct, promoteMergePreview), {
            duplicateUpc: duplicateUpcIdSet.has(detailProduct.id),
            kitMismatch: !!promoteMergePreview?.kitMismatch,
            // Detail promote uses merge checkboxes and never writes identity barcodes.
            upcMismatch: false,
            preReleaseUpcPending,
          })
        : [],
    [detailProduct, duplicateUpcIdSet, promoteMergePreview, preReleaseUpcPending],
  );

  const identifierReviewInput = useMemo(() => {
    if (!detailProduct) return null;
    return {
      brand: detailProduct.brand,
      brandId: detailProduct.brandId,
      sku: detailProduct.sku,
      mpn: detailProduct.mpn,
      upc: detailProduct.upc || promoteMergePreview?.mergedIdentifiers?.upc,
      ean: detailProduct.ean || promoteMergePreview?.mergedIdentifiers?.ean,
      gtin: detailProduct.gtin || promoteMergePreview?.mergedIdentifiers?.gtin,
      sourceUrl: detailProduct.source?.sourceUrl,
      skuSource: detailProduct.skuSource,
      acknowledgedReviewFlags: detailProduct.acknowledgedReviewFlags,
    };
  }, [detailProduct, promoteMergePreview]);

  const identifierNotesByField = useMemo(() => {
    const map = {} as Partial<Record<IdentifierReviewField, IdentifierFieldNote>>;
    if (!identifierReviewInput) return map;
    for (const note of annotateIdentifiers(identifierReviewInput)) {
      map[note.field] = note;
    }
    return map;
  }, [identifierReviewInput]);

  const identifierPrimers = useMemo(
    () => identifierAreaPrimers(detailProduct?.brandId, detailProduct?.brand),
    [detailProduct?.brandId, detailProduct?.brand],
  );

  const identifierPolicy = useMemo(
    () => brandIdentifierPolicy(detailProduct?.brandId, detailProduct?.brand),
    [detailProduct?.brandId, detailProduct?.brand],
  );
  const barcodeChipLabel =
    identifierPolicy.barcodeModel === 'ean-as-upc' ||
    identifierPolicy.barcodeModel === 'independent-regional'
      ? barcodeModelLabel(identifierPolicy.barcodeModel)
      : undefined;

  const pipelineActionBusy =
    saveEditMutation.isPending ||
    enrichMutation.isPending ||
    generateDescMutation.isPending ||
    sendToReviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending;

  const closeDetailDialog = () => {
    detailFetchGen.current += 1;
    setImageUrlInput('');
    setImageUrlLinkOnly(false);
    setMakeNewImagePrimary(false);
    setImagePreviewIndex(null);
    cancelEditing();
    setDialogAlert(null);
    setInTheBoxNotice(null);
    setInTheBoxBusy(null);
    setDetailActionMenuAnchor(null);
    setPromoteMergeSelection(null);
    setPromoteSelectionConfirm(null);
    setDetailProduct(null);
  };

  const updateEditField = (field: string, value: any) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const addToEditArray = (field: string, item: any) => {
    setEditData((prev) => ({ ...prev, [field]: [...(prev[field] || []), item] }));
  };

  const removeFromEditArray = (field: string, index: number) => {
    setEditData((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((_: any, i: number) => i !== index),
    }));
  };

  const updateEditArrayItem = (field: string, index: number, value: any) => {
    setEditData((prev) => ({
      ...prev,
      [field]: (prev[field] || []).map((item: any, i: number) => (i === index ? { ...item, ...value } : item)),
    }));
  };

  const uniqueBrands = useMemo(
    () => (products ? Array.from(new Set(products.map((p) => p.brand))).sort() : []),
    [products],
  );
  const brandOptions = useMemo(
    () =>
      mergeBrandLabels([
        ...uniqueBrands,
        ...(brandRegistry || []).map((b) => b.name),
        ...BRAND_CATALOG_IDENTITY.filter((b) => b.carried).map((b) => b.name),
      ]).map((b) => ({ value: b, label: b })),
    [uniqueBrands, brandRegistry],
  );
  /** Server-side tools take one brand; only offer it when the filter names exactly one. */
  const singleBrandFilter = filters.brand.length === 1 ? filters.brand[0] : '';

  // Debounce typing into the URL query; the selects apply at once.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === filters.q) return;
    const timer = setTimeout(() => updateFilters((prev) => ({ ...prev, q: next })), 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.q, updateFilters]);
  // …and follow the query when it changes from elsewhere (Clear, a saved view, Back).
  useEffect(() => {
    setSearchInput((prev) => (prev.trim() === filters.q.trim() ? prev : filters.q));
  }, [filters.q]);

  const applyFilterValue = useCallback(
    (key: StagedFilterKey, value: StagedFilterValue) =>
      updateFilters((prev) => setStagedFilterValue(prev, key, value)),
    [updateFilters],
  );
  const addFilterPill = useCallback(
    (key: StagedFilterKey) =>
      updateFilters((prev) =>
        prev.order.includes(key) ? prev : { ...prev, order: [...prev.order, key] },
      ),
    [updateFilters],
  );
  const removeFilterPill = useCallback(
    (key: StagedFilterKey) => updateFilters((prev) => removeStagedFilter(prev, key)),
    [updateFilters],
  );
  /** A saved view is the whole query string — tab, search and filters. */
  const savedViewQuery = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('id');
    return params.toString();
  }, [searchParams]);
  const applySavedView = useCallback(
    (query: string) => {
      const next = new URLSearchParams(query);
      setSearchInput(next.get('q') || '');
      if (isStatusTab(next.get('tab'))) userSelectedTabRef.current = true;
      setSearchParams(next, { replace: false });
    },
    [setSearchParams],
  );

  /** Top issues shortcuts drive the real filters rather than being state of their own. */
  const applyShortcut = useCallback(
    (key: StagedFilterKey, value: string) =>
      updateFilters((prev) => {
        const current = prev[key];
        if (Array.isArray(current)) {
          if (key === 'issues') {
            const mode = stagedIssueFilterMode(current, value);
            if (mode === 'off') return addStagedFilterValue(prev, key, value);
            return setStagedFilterValue(
              prev,
              key,
              current.filter((v) => stagedIssueFilterBase(v) !== value),
            );
          }
          return current.includes(value)
            ? setStagedFilterValue(prev, key, current.filter((v) => v !== value))
            : addStagedFilterValue(prev, key, value);
        }
        return current === value
          ? removeStagedFilter(prev, key)
          : setStagedFilterValue(prev, key, value);
      }),
    [updateFilters],
  );

  // Brand/lifecycle/scrape-kind/issue/AI-score filters and client-side sorts are only
  // correct over the full queue — stream in the remaining pages when active. Only the
  // default sort (createdAt desc) matches the server's page order, so it alone is safe
  // on paged data.
  // Rows filter on every keystroke, as they did before; the 300ms debounce is
  // only on the URL write, so the shareable link does not churn per character.
  const listSearchNorm = searchInput.trim().toLowerCase();
  const hasActiveListFilters = hasActiveStagedFilters({ ...filters, q: '' });

  // Server `?q=` (3+ chars) answers search. Shorter needles still filter on the
  // client and need every page. Other filters/sorts also need the full queue.
  const needsFullQueue =
    !queueBrandId &&
    !serverSearchQ &&
    (!!listSearchNorm ||
      hasActiveListFilters ||
      !(stagedColumnSort.orderBy === 'createdAt' && stagedColumnSort.direction === 'desc'));
  useEffect(() => {
    if (needsFullQueue && hasNextPage && !isFetchingNextPage) fetchNextPage({ cancelRefetch: false });
  }, [needsFullQueue, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Infinite scroll inside the table body scroller: one page per scroll-to-bottom.
  // Header sits outside the vertical scroll root so the scrollbar starts below it.
  // Horizontal scroll is synced to the header container.
  const tableHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadMoreLockRef = useRef(false);
  const syncTableHeaderScroll = (scrollLeft: number) => {
    const header = tableHeaderScrollRef.current;
    if (header && header.scrollLeft !== scrollLeft) header.scrollLeft = scrollLeft;
  };
  const stagedTableColGroup = (
    <colgroup>
      <col style={{ width: 48 }} />
      <col style={{ width: '26%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '11%' }} />
      <col style={{ width: 72 }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '11%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: 128 }} />
    </colgroup>
  );
  useEffect(() => {
    loadMoreLockRef.current = false;
  }, [activeTab, filters, stagedColumnSort]);

  const clearListFilters = useCallback(() => {
    setSearchInput('');
    updateFilters(() => ({ ...EMPTY_STAGED_FILTER_STATE, brand: [], issues: [], order: [] }));
  }, [updateFilters]);

  const stagedFilterRows = useMemo(
    () => (products || []).map((p) => buildStagedFilterRow(p, duplicateUpcIdSet)),
    [products, duplicateUpcIdSet],
  );

  /**
   * Filtered rows plus the facet counts shown in the filter menus. Each field's
   * counts are taken over the query *minus that field*, so a count answers
   * "how many rows would this option give me?" rather than "how many are left".
   */
  const [filteredProducts, facetCounts] = useMemo(() => {
    const searched = listSearchNorm
      ? stagedFilterRows.filter((row) => row.haystack.includes(listSearchNorm))
      : stagedFilterRows;

    // `sort` orders rows rather than filtering them, so it gets no mask and no counts.
    const masks = STAGED_FILTER_KEYS.filter((key) => key !== 'sort').map((key) => ({
      key,
      mask: searched.map((row) => stagedRowMatchesField(row, key, filters[key])),
    }));
    const passesAllExcept = (index: number, exclude: StagedFilterKey | null) =>
      masks.every((m) => m.key === exclude || m.mask[index]);

    const matched = searched.filter((_, i) => passesAllExcept(i, null));

    const counts: Partial<Record<StagedFilterKey, Record<string, number>>> = {};
    for (const { key } of masks) {
      counts[key] = tallyStagedFacet(
        key,
        searched.filter((_, i) => passesAllExcept(i, key)),
      );
    }

    const sorted = matched.slice().sort((a, b) => {
      if (filters.sort === 'new_first' || filters.sort === 'none') {
        const byNew = comparePromotePriority(a.product, b.product);
        if (byNew !== 0) return byNew;
      }
      const byCol = compareStagedProducts(
        a.product,
        b.product,
        stagedColumnSort.orderBy,
        stagedColumnSort.direction,
      );
      if (byCol !== 0) return byCol;
      if (filters.sort === 'most') return b.gapCount - a.gapCount;
      if (filters.sort === 'fewest') return a.gapCount - b.gapCount;
      return 0;
    });
    return [sorted.map((row) => row.product), counts] as const;
  }, [stagedFilterRows, filters, listSearchNorm, stagedColumnSort]);

  const priorityNewCount = useMemo(
    () => (products || []).filter(isPriorityNewStaged).length,
    [products],
  );

  /**
   * One-click shortcuts into the filters they name — quiet, not alerts. Each
   * shows the count it would produce, and a shortcut nobody can act on (count
   * 0 and not already applied) is not rendered at all.
   */
  const topIssueShortcuts = useMemo<TopIssueShortcut[]>(() => {
    const issueCounts = facetCounts.issues || {};
    const dupeCounts = facetCounts.dupe || {};
    const scrapeCounts = facetCounts.scrape || {};
    const candidates: TopIssueShortcut[] = [
      {
        key: 'scrape',
        value: 'new',
        label: 'New products',
        tone: 'critical',
        count: scrapeCounts.new || 0,
      },
      { key: 'issues', value: 'photo', label: 'Missing image', tone: 'critical', count: issueCounts.photo || 0 },
      {
        key: 'issues',
        value: 'longDescription',
        label: 'Description too short',
        tone: 'warning',
        count: issueCounts.longDescription || 0,
      },
      { key: 'issues', value: 'features', label: 'Too few features', tone: 'warning', count: issueCounts.features || 0 },
      { key: 'issues', value: 'upc', label: 'Missing UPC', tone: 'warning', count: issueCounts.upc || 0 },
      // Promote-blocking collisions kept their one-click path off the old red chip.
      {
        key: 'dupe',
        value: 'duplicates_only',
        label: 'Duplicate UPC',
        tone: 'critical',
        count: dupeCounts.duplicates_only || 0,
      },
    ];
    return candidates.filter(
      (s) =>
        (s.count || 0) > 0 ||
        (s.key === 'issues'
          ? stagedIssueFilterMode(filters.issues, s.value) !== 'off'
          : filters[s.key] === s.value),
    );
  }, [facetCounts, filters]);

  const sortedUpcReviewRows = useMemo(() => {
    const rows = (upcReviewData?.data || []) as UpcReviewRow[];
    return [...rows].sort((a, b) =>
      compareUpcRows(a, b, upcColumnSort.orderBy, upcColumnSort.direction),
    );
  }, [upcReviewData?.data, upcColumnSort]);

  const toggleStagedSort = (column: StagedColumnSortId) => {
    setStagedColumnSort((prev) =>
      prev.orderBy === column
        ? { orderBy: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { orderBy: column, direction: 'asc' },
    );
  };

  const toggleUpcSort = (column: UpcColumnSortId) => {
    setUpcColumnSort((prev) =>
      prev.orderBy === column
        ? { orderBy: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { orderBy: column, direction: 'asc' },
    );
  };

  // While needsFullQueue streams the remaining pages, filteredProducts only covers the
  // loaded subset, so select-all would silently miss matching rows still on the server.
  const fullQueueStillLoading = needsFullQueue && (hasNextPage || isFetchingNextPage);

  /** True while the list should not claim "empty" — bootstrap, first page, or filter catch-up. */
  const listWaitingForData =
    activeTab !== 'upc_review' &&
    (!queueBootstrapReady ||
      productsLoading ||
      (productsFetching && (products?.length ?? 0) === 0) ||
      ((filteredProducts?.length ?? 0) === 0 && fullQueueStillLoading));

  const handleSelectAll = () => {
    if (fullQueueStillLoading) return;
    setSelectedIds(new Set(filteredProducts.map((p) => p.id)));
  };

  /** Keep selection order aligned with the visible filtered list (table order). */
  const orderedSelectedIds = useMemo(
    () => filteredProducts.filter((p) => selectedIds.has(p.id)).map((p) => p.id),
    [filteredProducts, selectedIds],
  );

  const handleTrimSelectionToBatchMax = (max = BULK_AI_BATCH_MAX) => {
    setSelectedIds(new Set(orderedSelectedIds.slice(0, max)));
  };

  /**
   * Mobile renders each product as a card with several chips/icon buttons —
   * far heavier per row than a table row. A tab can have thousands of
   * matching products loaded client-side (needsFullQueue), and mounting all
   * of them as cards at once is what was freezing phones. Window the mobile
   * list independently of how many products are loaded, and grow it as the
   * user scrolls.
   */
  const MOBILE_LIST_BATCH = 30;
  // Identifies "which list is this" — any change means the count must drop
  // back to one batch before the new filteredProducts is ever rendered.
  const mobileViewKey = `${activeTab}|${JSON.stringify(filters)}|${listSearchNorm}|${stagedColumnSort.orderBy}:${stagedColumnSort.direction}`;
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_LIST_BATCH);
  const [mobileViewKeySeen, setMobileViewKeySeen] = useState(mobileViewKey);
  // Reset during render (not in an effect) so React re-renders with the
  // reset count before ever committing thousands of cards to the DOM —
  // an effect-based reset would still mount the stale, huge list first.
  if (mobileViewKey !== mobileViewKeySeen) {
    setMobileViewKeySeen(mobileViewKey);
    setMobileVisibleCount(MOBILE_LIST_BATCH);
  }
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isMobile) return;
    const el = mobileLoadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMobileVisibleCount((c) => Math.min(c + MOBILE_LIST_BATCH, filteredProducts.length));
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, filteredProducts.length]);

  /**
   * Desktop table window. Filters, client-side sorts and short searches stream the
   * whole queue in (needsFullQueue) so the result is correct, but mounting every
   * matching row (5k+ rows, ~300k DOM nodes) froze the page on each filter change.
   * Render "Rows / load" rows at a time and grow by that much as the user scrolls,
   * independently of how many products are loaded. Selection, select-all, counts
   * and export still use the full filteredProducts list.
   */
  const desktopViewKey = `${mobileViewKey}|${pageSize}`;
  const [desktopVisibleCount, setDesktopVisibleCount] = useState(pageSize);
  const [desktopViewKeySeen, setDesktopViewKeySeen] = useState(desktopViewKey);
  // Reset during render for the same reason as the mobile list: never commit the
  // stale huge list before the reset lands.
  if (desktopViewKey !== desktopViewKeySeen) {
    setDesktopViewKeySeen(desktopViewKey);
    setDesktopVisibleCount(pageSize);
  }
  const desktopRows = useMemo(
    () => filteredProducts.slice(0, desktopVisibleCount),
    [filteredProducts, desktopVisibleCount],
  );
  const desktopHasHiddenRows = desktopVisibleCount < filteredProducts.length;
  const showMoreDesktopRows = useCallback(() => {
    setDesktopVisibleCount((c) => Math.min(c + pageSize, filteredProducts.length));
  }, [pageSize, filteredProducts.length]);

  // Infinite scroll inside the table body scroller: reveal the next window of
  // already-loaded rows first; only fetch another server page once every loaded
  // row is on screen (and never while needsFullQueue is streaming pages itself).
  useEffect(() => {
    if (isMobile) return;
    const root = tableScrollRef.current;
    const el = loadMoreRef.current;
    if (!root || !el) return;
    if (!desktopHasHiddenRows && (!hasNextPage || needsFullQueue)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (!hit) {
          loadMoreLockRef.current = false;
          return;
        }
        if (desktopHasHiddenRows) {
          showMoreDesktopRows();
          return;
        }
        if (loadMoreLockRef.current || isFetchingNextPage) return;
        loadMoreLockRef.current = true;
        void fetchNextPage({ cancelRefetch: false });
      },
      { root, rootMargin: '400px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    isMobile,
    desktopHasHiddenRows,
    showMoreDesktopRows,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    activeTab,
    needsFullQueue,
  ]);

  /** Cap IDs for bulk AI endpoints that reject >500; trims the UI selection to match. */
  const idsForBulkAiBatch = (ids: string[]): string[] => {
    if (ids.length <= BULK_AI_BATCH_MAX) return ids;
    const capped = ids.slice(0, BULK_AI_BATCH_MAX);
    setSelectedIds(new Set(capped));
    setActionResult({
      type: 'success',
      message: `Using first ${BULK_AI_BATCH_MAX} of ${ids.length} selected (batch limit). Run again for the rest.`,
    });
    return capped;
  };

  const allSelected = filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.has(p.id));
  const someSelected = filteredProducts.some((p) => selectedIds.has(p.id));
  const selectedCount = orderedSelectedIds.length;

  const selectedStatuses = useMemo(() => {
    const statuses = new Set<string>();
    Array.from(selectedIds).forEach((id) => {
      const p = filteredProducts.find((fp) => fp.id === id);
      if (p) statuses.add(p.stagingStatus);
    });
    return statuses;
  }, [selectedIds, filteredProducts]);

  const canPromoteSelected = selectedCount > 0 && (selectedStatuses.has('approved') || selectedStatuses.has('review') || selectedStatuses.has('pending'));
  const needsAutoApprove = selectedStatuses.has('review') || selectedStatuses.has('pending');

  const totalQueue = stats ? (stats.pending || 0) + (stats.remediating || 0) + (stats.verifying || 0) + (stats.review || 0) + (stats.approved || 0) : 0;

  /** Server-side total for the active tab, for the "X of Y loaded" footer. */
  const tabTotalFromStats = useMemo(() => {
    if (!stats) return null;
    switch (activeTab) {
      case 'review': return stats.review || 0;
      case 'remediating': return stats.remediating || 0;
      case 'pending': return (stats.pending || 0) + (stats.verifying || 0);
      case 'approved': return stats.approved || 0;
      case 'promoted': return stats.promoted || 0;
      case 'rejected': return stats.rejected || 0;
      case 'all':
        return totalQueue + (stats.promoted || 0) + (stats.rejected || 0) + (stats.failed || 0);
      default: return null;
    }
  }, [stats, activeTab, totalQueue]);

  /** Any queue/batch tool from the "AI & Data Tools" menu currently running. */
  const anyBatchToolPending =
    bulkEnrichMutation.isPending ||
    bulkMarketLaunchMutation.isPending ||
    bulkAiEnhanceMutation.isPending ||
    bulkAcceptAiFeaturesMutation.isPending ||
    bulkAcceptAiSuggestionsMutation.isPending ||
    bulkAiVerifyMutation.isPending ||
    backfillVideosMutation.isPending ||
    backfillInTheBoxMutation.isPending ||
    backfillWeightsMutation.isPending ||
    bulkExtractWeightMutation.isPending;

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (singleBrandFilter) params.set('brand', singleBrandFilter);
      if (activeTab !== 'all') params.set('status', activeTab);
      // Full nested exports exceed Cloud Functions response size (~32MB) for large brands.
      params.set('profile', 'lean');
      if (filters.dupe === 'duplicates_only') {
        params.set('duplicateUpcOnly', 'true');
      }
      const url = `/staged-products/export/csv${params.toString() ? `?${params}` : ''}`;
      const response = await makeApiCall(url, { headers });
      if (!response.ok) {
        let detail = 'Export failed';
        try {
          const j = await response.json();
          if (typeof j.error === 'string') detail = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `staged-products-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setActionResult({ type: 'error', message: getErrorMessage(err) || 'CSV export failed' });
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Staged Products"
        description="Review, approve, and promote ingested products to the canonical catalog."
        actions={
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={anyBatchToolPending ? <CircularProgress size={16} /> : <GenerateIcon />}
            endIcon={<MenuArrowIcon />}
            onClick={(e) => setToolsMenuAnchor(e.currentTarget)}
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            AI &amp; Data Tools
          </Button>
          <Menu
            anchorEl={toolsMenuAnchor}
            open={Boolean(toolsMenuAnchor)}
            onClose={() => setToolsMenuAnchor(null)}
          >
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, lineHeight: 2 }}>
              Enrichment
            </Typography>
            {/*
              UPC Review is off the tab strip but not gone — this is its entry
              point, so the barcode verify/confirm workflow stays reachable.
            */}
            <MenuItem
              selected={activeTab === 'upc_review'}
              onClick={() => {
                setToolsMenuAnchor(null);
                setActiveTab(activeTab === 'upc_review' ? 'review' : 'upc_review');
              }}
            >
              <ListItemIcon>
                <IdentifierIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={activeTab === 'upc_review' ? 'Back to the staged queue' : 'UPC Review queue'}
                secondary={
                  activeTab === 'upc_review'
                    ? 'Leave barcode review'
                    : 'Confirm, reject or set barcodes flagged by enrichment'
                }
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkEnrichMutation.mutate(undefined); }}
              disabled={bulkEnrichMutation.isPending}
            >
              <ListItemIcon>
                {bulkEnrichMutation.isPending ? <CircularProgress size={18} /> : <IdentifierIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={bulkEnrichMutation.isPending ? 'Running Enrichment...' : 'Run Enrichment'}
                secondary="UPC/EAN/MPN for next queued products missing IDs"
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkMarketLaunchMutation.mutate(); }}
              disabled={bulkMarketLaunchMutation.isPending}
            >
              <ListItemIcon>
                {bulkMarketLaunchMutation.isPending ? <CircularProgress size={18} /> : <PriceCheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={bulkMarketLaunchMutation.isPending ? 'Running Market & Launch...' : 'Market & Launch'}
                secondary="Market pricing + launch date enrichment"
              />
            </MenuItem>
            <Divider />
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, lineHeight: 2 }}>
              AI
            </Typography>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkAiEnhanceMutation.mutate({}); }}
              disabled={bulkAiEnhanceMutation.isPending}
            >
              <ListItemIcon>
                {bulkAiEnhanceMutation.isPending ? <CircularProgress size={18} /> : <AIIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={bulkAiEnhanceMutation.isPending ? 'Enhancing...' : 'AI Enhance'}
                secondary={`Up to ${BULK_AI_TAB_BATCH} products in this tab`}
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkAiEnhanceMutation.mutate({ forceRegenerate: true }); }}
              disabled={bulkAiEnhanceMutation.isPending}
            >
              <ListItemIcon>
                {bulkAiEnhanceMutation.isPending ? <CircularProgress size={18} /> : <AIIcon fontSize="small" color="warning" />}
              </ListItemIcon>
              <ListItemText
                primary="Regen Descriptions"
                secondary={`Force-regenerate, up to ${BULK_AI_TAB_BATCH} products in this tab`}
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkAcceptAiFeaturesMutation.mutate(undefined); }}
              disabled={bulkAcceptAiFeaturesMutation.isPending}
            >
              <ListItemIcon>
                {bulkAcceptAiFeaturesMutation.isPending ? <CircularProgress size={18} /> : <AIIcon fontSize="small" color="secondary" />}
              </ListItemIcon>
              <ListItemText
                primary={bulkAcceptAiFeaturesMutation.isPending ? 'Accepting AI Features...' : 'Accept AI Features'}
                secondary="Copy AI-suggested features into catalog features"
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkAcceptAiSuggestionsMutation.mutate(undefined); }}
              disabled={bulkAcceptAiSuggestionsMutation.isPending}
            >
              <ListItemIcon>
                {bulkAcceptAiSuggestionsMutation.isPending ? (
                  <CircularProgress size={18} />
                ) : (
                  <FixIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  bulkAcceptAiSuggestionsMutation.isPending
                    ? 'Accepting AI Suggestions...'
                    : 'Accept All AI Suggestions'
                }
                secondary="Promote description, features, tags & category fixes — no re-verify"
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); bulkAiVerifyMutation.mutate(undefined); }}
              disabled={bulkAiVerifyMutation.isPending}
            >
              <ListItemIcon>
                {bulkAiVerifyMutation.isPending ? <CircularProgress size={18} /> : <AIIcon fontSize="small" color="info" />}
              </ListItemIcon>
              <ListItemText
                primary={bulkAiVerifyMutation.isPending ? 'Verifying...' : 'Run AI Verify'}
                secondary="AI verification pass over the pending queue"
              />
            </MenuItem>
            <Divider />
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, lineHeight: 2 }}>
              Backfills
            </Typography>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); backfillVideosMutation.mutate(); }}
              disabled={backfillVideosMutation.isPending}
            >
              <ListItemIcon>
                {backfillVideosMutation.isPending ? <CircularProgress size={18} /> : <VideoIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={backfillVideosMutation.isPending ? 'Scanning for Videos...' : 'Backfill Videos'}
                secondary="Scan source pages for product videos"
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); backfillWeightsMutation.mutate(); }}
              disabled={backfillWeightsMutation.isPending}
            >
              <ListItemIcon>
                {backfillWeightsMutation.isPending ? <CircularProgress size={18} /> : <WeightIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={backfillWeightsMutation.isPending ? 'Extracting Weights...' : 'Backfill Weights'}
                secondary={
                  singleBrandFilter
                    ? `Product + shipping weight for ${singleBrandFilter} (200/run)`
                    : 'Product + shipping weight from specs (200/run)'
                }
              />
            </MenuItem>
            <MenuItem
              onClick={() => { setToolsMenuAnchor(null); backfillInTheBoxMutation.mutate(); }}
              disabled={backfillInTheBoxMutation.isPending}
            >
              <ListItemIcon>
                {backfillInTheBoxMutation.isPending ? <CircularProgress size={18} /> : <BoxIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={backfillInTheBoxMutation.isPending ? 'Extracting In The Box...' : 'Backfill In The Box'}
                secondary={
                  singleBrandFilter
                    ? `Box contents for ${singleBrandFilter} from specs, description, or PDFs (200/run)`
                    : 'Extract box contents from specs, description, or PDFs (200/run)'
                }
              />
            </MenuItem>
            <Divider />
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, lineHeight: 2 }}>
              Remediation
            </Typography>
            <MenuItem
              disabled={!canContent('content_promote')}
              onClick={() => {
                setToolsMenuAnchor(null);
                setDrainResult(null);
                setDrainDialogOpen(true);
              }}
            >
              <ListItemIcon><GenerateIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Drain Review Backlog…"
                secondary="Move review docs with runnable actions back to remediation, in waves"
              />
            </MenuItem>
            {canContent('auto_promote_admin') && <Divider />}
            {canContent('auto_promote_admin') && (
              <Typography variant="overline" color="text.secondary" sx={{ px: 2, lineHeight: 2 }}>
                Promotion
              </Typography>
            )}
            {canContent('auto_promote_admin') && (
              <MenuItem
                onClick={() => {
                  setToolsMenuAnchor(null);
                  setApDraft(null);
                  setApSoakText(null);
                  setAutoPromoteOpen(true);
                }}
              >
                <ListItemIcon><PromoteIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText
                  primary="Auto-Promotion Brands…"
                  secondary="Choose which brands promote to canonical without a human"
                />
              </MenuItem>
            )}
          </Menu>

          {/* Phase 4: drain-wave dialog — pick a cohort, preview, launch. */}
          <Dialog open={drainDialogOpen} onClose={() => setDrainDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Drain Review Backlog</DialogTitle>
            <DialogContent>
              {remediationSummaryQuery.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : remediationSummaryQuery.data ? (() => {
                const s = remediationSummaryQuery.data;
                const cohorts = Object.entries(s.singleGapCohorts).sort((a, b) => b[1] - a[1]);
                const brands = Object.entries(s.byBrand)
                  .filter(([, b]) => b.runnable > 0)
                  .sort((a, b) => b[1].runnable - a[1].runnable)
                  .slice(0, 12);
                return (
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {s.review.total} in review — {s.review.runnable} with runnable actions, {s.review.advance} ready
                    to advance, {s.review.exhausted} exhausted (need Phase 5 tiering or manual work).
                    Currently remediating: {s.remediating.total}.
                  </Typography>

                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Single-gap cohorts (best waves first)
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {cohorts.length === 0 && (
                        <Typography variant="caption" color="text.secondary">None</Typography>
                      )}
                      {cohorts.map(([gap, count]) => (
                        <Chip
                          key={gap}
                          size="small"
                          label={`${gap}: ${count}`}
                          variant={drainGap === gap && drainExclusive ? 'filled' : 'outlined'}
                          color={drainGap === gap && drainExclusive ? 'primary' : 'default'}
                          onClick={() => { setDrainGap(gap); setDrainExclusive(true); setDrainResult(null); }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Today's budgets
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {Object.entries(s.budgets).map(([action, b]) => (
                        <Chip
                          key={action}
                          size="small"
                          variant="outlined"
                          label={`${action}: ${b.used}${b.cap ? `/${b.cap}` : ''}`}
                          sx={{ color: b.cap && b.used >= b.cap ? 'warning.main' : 'text.secondary' }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={drainBrand}
                        label="Brand"
                        onChange={(e) => { setDrainBrand(e.target.value); setDrainResult(null); }}
                      >
                        <MenuItem value="">All brands</MenuItem>
                        {brands.map(([brandId, b]) => (
                          <MenuItem key={brandId} value={brandId}>{b.brand} ({b.runnable} runnable)</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Gap</InputLabel>
                      <Select
                        value={drainGap}
                        label="Gap"
                        onChange={(e) => { setDrainGap(e.target.value); setDrainResult(null); }}
                      >
                        <MenuItem value="">Any gap</MenuItem>
                        {Object.keys(s.byGap).sort().map((gap) => (
                          <MenuItem key={gap} value={gap}>{gap}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <NumberField
                      label="Wave size"
                      integer
                      min={1}
                      max={1000}
                      value={String(drainLimit)}
                      onChange={(v) => {
                        setDrainLimit(Math.min(Math.max(parseInt(v, 10) || 1, 1), 1000));
                        setDrainResult(null);
                      }}
                      sx={{ width: 110 }}
                    />
                    {drainGap && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={drainExclusive}
                            onChange={(e) => { setDrainExclusive(e.target.checked); setDrainResult(null); }}
                          />
                        }
                        label={<Typography variant="caption">only gap</Typography>}
                      />
                    )}
                  </Stack>

                  {drainResult && (
                    <Alert severity={drainResult.dryRun ? 'info' : 'success'}>
                      {drainResult.dryRun ? 'Preview: ' : 'Wave launched: '}
                      {drainResult.moved} → remediating, {drainResult.advanced} → pending
                      {drainResult.exhausted > 0 && ` · ${drainResult.exhausted} exhausted stay in review`}
                      {drainResult.filtered > 0 && ` · ${drainResult.filtered} outside cohort`}
                      {!drainResult.dryRun && ' — the dispatcher picks them up within 10 minutes.'}
                    </Alert>
                  )}
                </Stack>
                );
              })() : (
                <Alert severity="error">Failed to load remediation summary.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDrainDialogOpen(false)}>Close</Button>
              <Button
                onClick={() => drainMutation.mutate(true)}
                disabled={drainMutation.isPending || !remediationSummaryQuery.data}
                startIcon={drainMutation.isPending ? <CircularProgress size={14} /> : undefined}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                onClick={() => drainMutation.mutate(false)}
                disabled={drainMutation.isPending || !remediationSummaryQuery.data}
                startIcon={drainMutation.isPending ? <CircularProgress size={14} /> : <GenerateIcon />}
              >
                Start Wave
              </Button>
            </DialogActions>
          </Dialog>

          {/* Phase 5: auto-promotion policy — which brands promote without a human. */}
          <Dialog
            open={autoPromoteOpen}
            onClose={() => { setAutoPromoteOpen(false); setApConfirmLive(false); }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>Auto-Promotion</DialogTitle>
            <DialogContent dividers>
              {autoPromoteQuery.isLoading || !apDraft ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : autoPromoteQuery.data ? (() => {
                const saved = autoPromoteQuery.data.config;
                const draft = apDraft;
                const candidates = autoPromoteQuery.data.candidates;
                const lastRun = autoPromoteQuery.data.lastRun;
                const setDraft = (patch: Partial<AutoPromoteConfigDraft>) =>
                  setApDraft({ ...draft, ...patch });

                const allowed = new Set(draft.brandAllowlist);
                // Allowlisted brands with no approved products left still need a
                // row, otherwise they can never be switched back off here.
                const known = new Set(candidates.map((c) => c.brandId));
                const rows: AutoPromoteBrandCandidate[] = [
                  ...candidates,
                  ...draft.brandAllowlist.filter((b) => !known.has(b)).map((b) => ({
                    brandId: b, brand: b, approved: 0, eligible: 0, allowlisted: true, heldReasons: {},
                  })),
                ];
                const visibleRows = apShowAllBrands
                  ? rows
                  : rows.filter((r) => r.eligible > 0 || allowed.has(r.brandId));

                const readyNow = rows
                  .filter((r) => allowed.has(r.brandId))
                  .reduce((n, r) => n + r.eligible, 0);
                const wouldPromote = Math.min(readyNow, draft.maxPerRun);

                const goingLive = draft.enabled && !draft.dryRun;
                const wasLive = saved.enabled && !saved.dryRun;
                const needsConfirm = goingLive && !wasLive;
                const dirty = autoPromoteDirty(draft, saved);
                const mode = !draft.enabled ? 'off' : draft.dryRun ? 'dry' : 'live';

                return (
                <Stack spacing={2.5}>
                  {/* Where things stand right now. */}
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={mode === 'off' ? 'Disabled' : mode === 'dry' ? 'Dry run' : 'Live'}
                      color={mode === 'off' ? 'default' : mode === 'dry' ? 'info' : 'success'}
                      variant={mode === 'off' ? 'outlined' : 'filled'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {allowed.size} brand{allowed.size === 1 ? '' : 's'} enabled ·{' '}
                      {autoPromoteQuery.data.scanned} approved product
                      {autoPromoteQuery.data.scanned === 1 ? '' : 's'} scanned
                      {autoPromoteQuery.data.truncated && ' (capped)'}
                    </Typography>
                    {lastRun && (
                      <Typography variant="caption" color="text.secondary">
                        · last run {formatFirestoreDate(lastRun.lastRunAt)}: {lastRun.message || lastRun.status}
                      </Typography>
                    )}
                  </Stack>

                  {/* The headline control: brands. */}
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                        Brands
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={apShowAllBrands}
                            onChange={(e) => setApShowAllBrands(e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">show brands with nothing ready</Typography>}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      &quot;Ready&quot; counts what would promote if the brand were enabled, under the
                      saved policy. Change a threshold below and save to refresh these numbers.
                    </Typography>
                    <Paper variant="outlined" sx={{ mt: 1, maxHeight: 300, overflowY: 'auto' }}>
                      {visibleRows.length === 0 && (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            No brand has an approved product ready to promote yet.
                          </Typography>
                        </Box>
                      )}
                      {visibleRows.map((row, idx) => {
                        const on = allowed.has(row.brandId);
                        const reasons = Object.entries(row.heldReasons)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3);
                        return (
                          <Box
                            key={row.brandId}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1.5,
                              py: 1,
                              borderTop: idx === 0 ? 'none' : '1px solid',
                              borderColor: 'divider',
                              bgcolor: on ? 'action.hover' : 'transparent',
                            }}
                          >
                            <Switch
                              size="small"
                              checked={on}
                              onChange={(e) => setDraft({
                                brandAllowlist: e.target.checked
                                  ? [...draft.brandAllowlist, row.brandId]
                                  : draft.brandAllowlist.filter((b) => b !== row.brandId),
                              })}
                            />
                            <Box sx={{ minWidth: 140 }}>
                              <Typography variant="body2" sx={{ fontWeight: on ? 600 : 400 }}>
                                {row.brand}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.eligible} of {row.approved} approved ready
                              </Typography>
                            </Box>
                            <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                              {reasons.map(([reason, count]) => (
                                <Chip
                                  key={reason}
                                  size="small"
                                  variant="outlined"
                                  label={`${AUTO_PROMOTE_REASON_LABELS[reason] || reason}: ${count}`}
                                  sx={{ height: 20, fontSize: '0.68rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Paper>
                  </Box>

                  {/* Thresholds. */}
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Policy
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      <NumberField
                        label="Soak hours"
                        helperText="Time in approved before eligible"
                        min={0}
                        step={0.5}
                        allowEmpty={false}
                        adornment="h"
                        value={apSoakText ?? String(draft.soakHours)}
                        onChange={(v) => {
                          setApSoakText(v);
                          const n = parseNumberInput(v);
                          if (n !== null) setDraft({ soakHours: Math.max(0, n) });
                        }}
                        sx={{ width: 150 }}
                      />
                      <NumberField
                        label="Min score"
                        helperText="Verification bar (verifier passes at 60)"
                        integer
                        min={0}
                        max={100}
                        value={String(draft.minVerificationScore)}
                        onChange={(v) => setDraft({
                          minVerificationScore: Math.min(Math.max(Number(v) || 0, 0), 100),
                        })}
                        sx={{ width: 190 }}
                      />
                      <NumberField
                        label="Max per run"
                        helperText="Ceiling per hour (max 200)"
                        integer
                        min={1}
                        max={200}
                        value={String(draft.maxPerRun)}
                        onChange={(v) => setDraft({
                          maxPerRun: Math.min(Math.max(Number(v) || 1, 1), 200),
                        })}
                        sx={{ width: 170 }}
                      />
                    </Stack>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Switch
                          size="small"
                          checked={draft.allowCanonicalUpdates}
                          onChange={(e) => setDraft({ allowCanonicalUpdates: e.target.checked })}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Allow updates to existing canonical products (riskier — rewrites live catalog data)
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Master switches. */}
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Mode
                    </Typography>
                    <Stack sx={{ mt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={draft.enabled}
                            onChange={(e) => {
                              setDraft({ enabled: e.target.checked });
                              if (!e.target.checked) setApConfirmLive(false);
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Enable the hourly auto-promotion run
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            color="warning"
                            checked={!draft.dryRun}
                            disabled={!draft.enabled}
                            onChange={(e) => {
                              setDraft({ dryRun: !e.target.checked });
                              if (!e.target.checked) setApConfirmLive(false);
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Write to the canonical catalog{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (off = dry run: decisions recorded, nothing promoted)
                            </Typography>
                          </Typography>
                        }
                      />
                    </Stack>
                  </Box>

                  {/* What the next run does under the draft. */}
                  <Alert
                    severity={mode === 'live' ? 'warning' : 'info'}
                    icon={mode === 'live' ? <WarningIcon /> : <InfoIcon />}
                  >
                    {mode === 'off' ? (
                      'Auto-promotion is off. Nothing promotes automatically.'
                    ) : readyNow === 0 ? (
                      `No approved product in the enabled brands is ready yet${
                        allowed.size === 0 ? ' — no brands are enabled.' : '.'
                      }`
                    ) : mode === 'dry' ? (
                      `Dry run: the next hourly run would report ${wouldPromote} promotable product${
                        wouldPromote === 1 ? '' : 's'
                      } and write nothing.`
                    ) : (
                      `${wouldPromote} product${wouldPromote === 1 ? '' : 's'} would be written to the ` +
                      `canonical catalog within the hour, with no human review` +
                      `${readyNow > wouldPromote ? ` (${readyNow} ready, capped at ${draft.maxPerRun}/run)` : ''}.`
                    )}
                  </Alert>

                  {needsConfirm && (
                    <Alert severity="error">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            color="error"
                            checked={apConfirmLive}
                            onChange={(e) => setApConfirmLive(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            I understand this promotes to the live catalog without review. Products
                            reach storefronts and ERP sync.
                          </Typography>
                        }
                      />
                    </Alert>
                  )}
                </Stack>
                );
              })() : (
                <Alert severity="error">Failed to load the auto-promotion policy.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setAutoPromoteOpen(false); setApConfirmLive(false); setApSoakText(null); }}>Close</Button>
              <Button
                onClick={() => {
                  if (autoPromoteQuery.data) setApDraft({ ...autoPromoteQuery.data.config });
                  setApSoakText(null);
                }}
                disabled={
                  !apDraft || !autoPromoteQuery.data ||
                  (!autoPromoteDirty(apDraft, autoPromoteQuery.data.config) && apSoakText === null)
                }
              >
                Revert
              </Button>
              <Button
                variant="contained"
                color={apDraft?.enabled && !apDraft?.dryRun ? 'warning' : 'primary'}
                startIcon={autoPromoteSaveMutation.isPending ? <CircularProgress size={14} /> : <SaveIcon />}
                onClick={() => apDraft && autoPromoteSaveMutation.mutate(apDraft)}
                disabled={
                  !apDraft || !autoPromoteQuery.data ||
                  autoPromoteSaveMutation.isPending ||
                  apSoakInvalid ||
                  !autoPromoteDirty(apDraft, autoPromoteQuery.data.config) ||
                  // Leaving dry-run needs the acknowledgement above.
                  (apDraft.enabled && !apDraft.dryRun &&
                    !(autoPromoteQuery.data.config.enabled && !autoPromoteQuery.data.config.dryRun) &&
                    !apConfirmLive)
                }
              >
                Save Policy
              </Button>
            </DialogActions>
          </Dialog>
          {stats && (stats.approved || 0) > 0 && canContent('content_promote') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PromoteIcon />}
              onClick={() => bulkPromoteMutation.mutate()}
              disabled={bulkPromoteMutation.isPending}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, order: { xs: -1, sm: 0 } }}
            >
              Promote All Approved ({stats.approved})
            </Button>
          )}
          <Tooltip title="Export staged products as CSV">
            <IconButton onClick={handleExportCsv} disabled={exportingCsv}>
              {exportingCsv ? <CircularProgress size={20} /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={() => { refetchProducts(); queryClient.invalidateQueries({ queryKey: ['staged-products-stats'] }); queryClient.invalidateQueries({ queryKey: ['pipeline-status'] }); }}>
            <RefreshIcon />
          </IconButton>
        </Stack>
        }
      />

      {permalinksOn && permalink.notFound && !detailProduct && (
        <PermalinkNotFound what="staged product" onBack={permalinkNav.close} />
      )}

      {permalinksOn && permalink.loadFailed && !detailProduct && (
        <PermalinkLoadFailed what="staged product" onRetry={() => void permalink.refetch()} />
      )}

      {actionResult && !detailProduct && (
        <Alert
          severity={actionResult.type}
          onClose={() => setActionResult(null)}
          sx={{ mb: 2 }}
        >
          {actionResult.message}
        </Alert>
      )}

      {/* Pipeline Status board: grouped by domain, freshness derived from each
          pipeline's real cadence. Healthy cards stay quiet (neutral border,
          muted footer); only attention states carry color, so one red card
          reads instantly instead of drowning in green outlines. */}
      {pipelineRuns && Object.keys(pipelineRuns).length > 0 && (() => {
        const nowMs = Date.now();
        const cards = Object.entries(pipelineRuns).map(([key, run]) => {
          const meta = pipelineMetaFor(key, run);
          return { key, run, meta, ...pipelineHealthFor(run, meta, nowMs) };
        });
        const attention = cards.filter((c) => ATTENTION_HEALTH.includes(c.health));
        const byGroup = PIPELINE_GROUP_ORDER
          .map((group) => ({
            group,
            cards: cards
              .filter((c) => c.meta.group === group)
              .sort((a, b) => {
                const attA = ATTENTION_HEALTH.includes(a.health) ? 0 : 1;
                const attB = ATTENTION_HEALTH.includes(b.health) ? 0 : 1;
                if (attA !== attB) return attA - attB;
                return (b.lastRunMs || 0) - (a.lastRunMs || 0);
              }),
          }))
          .filter((g) => g.cards.length > 0);
        const selectedCard = selectedPipelineKey
          ? cards.find((c) => c.key === selectedPipelineKey) || null
          : null;
        return (
        <>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box
            onClick={() => setPipelineExpanded((open) => !open)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              cursor: 'pointer',
              flexWrap: 'wrap',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <SpeedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ mr: 1 }}>Pipeline Status</Typography>
            {!pipelineExpanded && (
              isMobile ? (
                attention.length > 0 ? (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: attention.some((c) => c.health === 'error') ? 'error.main' : 'warning.main',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {attention.length} {attention.length === 1 ? 'issue needs' : 'issues need'} attention
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    all {cards.length} healthy
                  </Typography>
                )
              ) : attention.length > 0 ? (
                attention.map((c) => (
                  <Chip
                    key={c.key}
                    size="small"
                    variant="outlined"
                    icon={<Box component="span" sx={{ display: 'inline-flex', color: 'inherit', ml: 0.5 }}>{PIPELINE_HEALTH_UI[c.health].icon}</Box>}
                    label={`${c.meta.label}: ${PIPELINE_HEALTH_UI[c.health].label}`}
                    color={c.health === 'error' ? 'error' : c.health === 'running' || c.health === 'paused' ? 'info' : 'warning'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPipelineExpanded(true);
                      setSelectedPipelineKey(c.key);
                    }}
                    sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
                  />
                ))
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`all ${cards.length} healthy`}
                  sx={{ height: 22, fontSize: '0.7rem', color: 'text.secondary' }}
                />
              )
            )}
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" sx={{ pointerEvents: 'none' }}>
              {pipelineExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={pipelineExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Click a pipeline for details, related links, and controls.
          </Typography>
          {byGroup.map(({ group, cards: groupCards }) => (
          <Box key={group} sx={{ '&:not(:first-of-type)': { mt: 2 } }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', letterSpacing: 1, lineHeight: 2, display: 'block' }}
          >
            {group}
          </Typography>
          <Grid container spacing={1.5}>
            {groupCards.map(({ key, run, meta, health, lastRunMs, overdueMs }) => {
                const healthUi = PIPELINE_HEALTH_UI[health];
                const isAttention = ATTENTION_HEALTH.includes(health);

                const aiVerifyActive = key === 'ai_verification' && (run.status === 'running' || run.status === 'paused');
                const aiPaused = run.status === 'paused' || run.pauseRequested === true;
                const aiCancelling = run.cancelRequested === true;
                const aiControlBusy =
                  aiVerifyPauseMutation.isPending || aiVerifyResumeMutation.isPending || aiVerifyCancelMutation.isPending;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                    <Paper
                      variant="outlined"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPipelineKey(key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedPipelineKey(key);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        // Color is reserved for states that need action; healthy
                        // cards keep the neutral divider border.
                        borderLeft: isAttention ? 3 : 1,
                        borderLeftColor: isAttention ? healthUi.color : 'divider',
                        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>{meta.icon}</Box>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>{meta.label}</Typography>
                        <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '2.4em',
                        }}
                      >
                        {run.message || '—'}
                      </Typography>
                      {aiVerifyActive && (
                        <Stack spacing={0.5} sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                          {aiCancelling && (
                            <Typography variant="caption" color="text.secondary">
                              Stop in progress — finishes current product, then resets remaining items to pending.
                            </Typography>
                          )}
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={aiVerifyPauseMutation.isPending ? <CircularProgress size={14} /> : <PauseIcon />}
                              disabled={aiPaused || aiControlBusy || aiCancelling}
                              onClick={() => aiVerifyPauseMutation.mutate()}
                            >
                              Pause
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={aiVerifyResumeMutation.isPending ? <CircularProgress size={14} /> : <ResumeIcon />}
                              disabled={!aiPaused || aiControlBusy || aiCancelling}
                              onClick={() => aiVerifyResumeMutation.mutate()}
                            >
                              Resume
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={aiVerifyCancelMutation.isPending ? <CircularProgress size={14} /> : <StopIcon />}
                              disabled={aiControlBusy || aiCancelling}
                              onClick={() => aiVerifyCancelMutation.mutate()}
                            >
                              Stop
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                        <Box sx={{ color: healthUi.color, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {healthUi.icon}
                          <Typography variant="caption" fontWeight={600} sx={{ color: 'inherit' }}>
                            {health === 'ok' ? (run.status || 'healthy') : healthUi.label}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.disabled">·</Typography>
                        <Tooltip title={lastRunMs ? new Date(lastRunMs).toLocaleString() : 'Never ran'}>
                          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
                            {lastRunMs ? `${shortDuration(nowMs - lastRunMs)} ago` : 'never'}
                          </Typography>
                        </Tooltip>
                        {health === 'overdue' && (
                          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                            · {shortDuration(overdueMs)} overdue
                          </Typography>
                        )}
                      </Stack>
                      {run.pipeline && (
                        <Chip label={`→ ${run.pipeline}`} size="small" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>
          </Box>
          ))}
          </Box>
          </Collapse>
        </Paper>

        <Dialog
          open={!!selectedCard}
          onClose={() => setSelectedPipelineKey(null)}
          maxWidth="sm"
          fullWidth
        >
          {selectedCard && (() => {
            const { key, run, meta, health, lastRunMs, overdueMs } = selectedCard;
            const healthUi = PIPELINE_HEALTH_UI[health];
            const detailEntries = pipelineDetailEntries(run);
            const errorMessages = pipelineMessageList(run.errors);
            const warningMessages = pipelineMessageList(run.warnings);
            const aiVerifyActive = key === 'ai_verification' && (run.status === 'running' || run.status === 'paused');
            const aiPaused = run.status === 'paused' || run.pauseRequested === true;
            const aiCancelling = run.cancelRequested === true;
            const aiControlBusy =
              aiVerifyPauseMutation.isPending || aiVerifyResumeMutation.isPending || aiVerifyCancelMutation.isPending;
            const cadenceLabel = meta.intervalMin
              ? meta.intervalMin >= 1440
                ? `Every ${Math.round(meta.intervalMin / 1440)}d`
                : meta.intervalMin >= 60
                  ? `Every ${Math.round(meta.intervalMin / 60)}h`
                  : `Every ${meta.intervalMin}m`
              : 'Event-driven / retired';
            return (
              <>
                <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>{meta.icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" noWrap>{meta.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{meta.group} · {key}</Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    aria-label="Close"
                    onClick={() => setSelectedPipelineKey(null)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  {meta.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {meta.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    <Chip
                      size="small"
                      icon={<Box component="span" sx={{ display: 'inline-flex', color: 'inherit', ml: 0.5 }}>{healthUi.icon}</Box>}
                      label={health === 'ok' ? (run.status || 'healthy') : healthUi.label}
                      color={health === 'error' ? 'error' : health === 'ok' ? 'default' : health === 'running' || health === 'paused' ? 'info' : 'warning'}
                      variant={health === 'ok' ? 'outlined' : 'filled'}
                    />
                    <Chip size="small" variant="outlined" label={cadenceLabel} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={lastRunMs ? `Last run ${formatFirestoreDate(run.lastRunAt)}` : 'Never ran'}
                    />
                    {health === 'overdue' && (
                      <Chip size="small" color="warning" label={`${shortDuration(overdueMs)} overdue`} />
                    )}
                  </Stack>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Latest message</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{run.message || '—'}</Typography>
                  </Paper>
                  {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2, alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Errors ({errorMessages.length})
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {errorMessages.map((msg, i) => (
                          <Typography component="li" variant="body2" key={`err-${i}`} sx={{ wordBreak: 'break-word' }}>
                            {msg}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                  {warningMessages.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Warnings ({warningMessages.length})
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {warningMessages.map((msg, i) => (
                          <Typography component="li" variant="body2" key={`warn-${i}`} sx={{ wordBreak: 'break-word' }}>
                            {msg}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                  {detailEntries.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Run metrics</Typography>
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {detailEntries.map((entry) => (
                          <Grid size={{ xs: 6, sm: 4 }} key={entry.key}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {entry.key}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} noWrap title={entry.value}>
                                {entry.value}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                  {aiVerifyActive && (
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">AI verification controls</Typography>
                      {aiCancelling && (
                        <Typography variant="caption" color="text.secondary">
                          Stop in progress — finishes current product, then resets remaining items to pending.
                        </Typography>
                      )}
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={aiVerifyPauseMutation.isPending ? <CircularProgress size={14} /> : <PauseIcon />}
                          disabled={aiPaused || aiControlBusy || aiCancelling}
                          onClick={() => aiVerifyPauseMutation.mutate()}
                        >
                          Pause
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={aiVerifyResumeMutation.isPending ? <CircularProgress size={14} /> : <ResumeIcon />}
                          disabled={!aiPaused || aiControlBusy || aiCancelling}
                          onClick={() => aiVerifyResumeMutation.mutate()}
                        >
                          Resume
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={aiVerifyCancelMutation.isPending ? <CircularProgress size={14} /> : <StopIcon />}
                          disabled={aiControlBusy || aiCancelling}
                          onClick={() => aiVerifyCancelMutation.mutate()}
                        >
                          Stop
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
                  {meta.statusTab && (
                    <Button
                      onClick={() => {
                        userSelectedTabRef.current = true;
                        setActiveTab(meta.statusTab!);
                        setSelectedPipelineKey(null);
                        setPipelineExpanded(false);
                      }}
                    >
                      View {STATUS_TABS.find((t) => t.value === meta.statusTab)?.label || meta.statusTab}
                    </Button>
                  )}
                  {meta.href && (
                    <Button
                      startIcon={<OpenInNewIcon />}
                      onClick={() => {
                        setSelectedPipelineKey(null);
                        navigate(meta.href!);
                      }}
                    >
                      {meta.hrefLabel || 'Open related page'}
                    </Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={() => setSelectedPipelineKey(null)}>Close</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>
        </>
        );
      })()}

      {/* Tabs + Filter */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        {/*
          Navigation between mutually exclusive queues, so these read as tabs.
          A filled pill would read as an applied filter — that is what the bar below is for.
          On mobile, a horizontally-scrolling tab strip competes for space and hides
          most queues off-screen, so it becomes a single "quick switcher" button that
          opens a bottom sheet listing every queue and its count.
        */}
        {(() => {
          const tabCount = (value: StatusTab | 'all') =>
            !stats
              ? undefined
              : value === 'review' ? stats.review
              : value === 'remediating' ? stats.remediating
              : value === 'pending' ? (stats.pending || 0) + (stats.verifying || 0)
              : value === 'approved' ? stats.approved
              : value === 'promoted' ? stats.promoted
              : value === 'rejected' ? stats.rejected
              : value === 'all' ? totalQueue + (stats.promoted || 0) + (stats.rejected || 0) + (stats.failed || 0)
              : undefined;
          const countsLoading = statsLoading && !stats;
          if (isMobile) {
            const currentTab = STATUS_TABS.find((t) => t.value === activeTab);
            return (
              <>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTabSwitcherOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setTabSwitcherOpen(true);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    bgcolor: 'grey.50',
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {currentTab?.label || 'View'}
                  </Typography>
                  <StagedTabCount loading={countsLoading} count={tabCount(activeTab)} selected />
                  <Box sx={{ flex: 1 }} />
                  <MenuArrowIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </Box>
                <Drawer
                  anchor="bottom"
                  open={tabSwitcherOpen}
                  onClose={() => setTabSwitcherOpen(false)}
                  PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '75vh' } }}
                >
                  <Box sx={{ width: 36, height: 4, bgcolor: 'grey.300', borderRadius: 3, mx: 'auto', mt: 1.5, mb: 0.5 }} />
                  <Typography variant="subtitle1" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                    Switch view
                  </Typography>
                  <List sx={{ pt: 0, pb: 'env(safe-area-inset-bottom, 8px)' }}>
                    {STATUS_TABS.map((t) => {
                      const selected = activeTab === t.value;
                      return (
                        <ListItemButton
                          key={t.value}
                          selected={selected}
                          onClick={() => {
                            setActiveTab(t.value);
                            setTabSwitcherOpen(false);
                          }}
                          sx={{
                            mx: 1,
                            borderRadius: 1.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography sx={{ fontWeight: selected ? 600 : 400 }}>{t.label}</Typography>
                          <StagedTabCount loading={countsLoading} count={tabCount(t.value)} selected={selected} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Drawer>
              </>
            );
          }
          return (
            <Tabs
              value={STATUS_TABS.some((t) => t.value === activeTab) ? activeTab : false}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-busy={statsLoading && !stats}
              sx={{
                px: 1,
                minWidth: 0,
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.84375rem',
                  minHeight: 44,
                  px: 1.75,
                  color: 'text.secondary',
                },
                '& .MuiTab-root.Mui-selected': { fontWeight: 500, color: 'text.primary' },
              }}
            >
              {STATUS_TABS.map((t) => {
                const selected = activeTab === t.value;
                return (
                  <Tab
                    key={t.value}
                    value={t.value}
                    label={
                      <Stack direction="row" spacing={0.875} alignItems="center">
                        <span>{t.label}</span>
                        <StagedTabCount loading={countsLoading} count={tabCount(t.value)} selected={selected} />
                      </Stack>
                    }
                  />
                );
              })}
            </Tabs>
          );
        })()}
        {pipelineStatus && (() => {
          const run = pipelineRuns?.remediation_dispatcher;
          const last = run?.lastRunAt as { _seconds?: number; seconds?: number } | undefined;
          const seconds = last?._seconds ?? last?.seconds;
          const copy = describeRemediationQueueStatus({
            status: run?.status,
            message: run?.message,
            lastRunMs: typeof seconds === 'number' ? seconds * 1000 : null,
            processed: run?.processed,
            budgetSkips: run?.budgetSkips,
            gapsClosed: run?.gapsClosed,
            sentToReview: run?.sentToReview,
            advanced: (run?.advancedToPending || 0) + (run?.fastTrackedToApproved || 0),
            budgets: pipelineStatus.budgets,
          });
          return (
            <Box sx={{ px: 2, pt: 1.5 }}>
              <InlineMessage severity={copy.severity}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{copy.headline}</Typography>
                <Typography variant="body2">{copy.detail}</Typography>
                {copy.lines.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {copy.lines.map((line) => (
                      <Chip
                        key={line.action}
                        size="small"
                        variant="outlined"
                        color={line.spent ? 'warning' : 'default'}
                        label={`${line.label}: ${line.used.toLocaleString()}${line.cap != null ? `/${line.cap.toLocaleString()}` : ''}`}
                      />
                    ))}
                  </Stack>
                )}
              </InlineMessage>
            </Box>
          );
        })()}
        {priorityNewCount > 0 && activeTab !== 'upc_review' && (
          <Alert
            severity="info"
            sx={{ mx: 2, mt: 1.5, py: 0.25 }}
            action={
              filters.scrape === 'new' ? undefined : (
                <Button color="inherit" size="small" onClick={() => applyFilterValue('scrape', 'new')}>
                  Show new first
                </Button>
              )
            }
          >
            {priorityNewCount} new {priorityNewCount === 1 ? 'product is' : 'products are'} loaded in
            this queue — promote {priorityNewCount === 1 ? 'it' : 'them'} before working updates.
          </Alert>
        )}
        <StagedProductsFilterBar
          isMobile={isMobile}
          mobileSheetOpen={mobileFiltersOpen}
          onMobileSheetOpenChange={setMobileFiltersOpen}
          filters={filters}
          brandOptions={brandOptions}
          onChangeValue={applyFilterValue}
          onAddFilter={addFilterPill}
          onRemoveFilter={removeFilterPill}
          onClearAll={clearListFilters}
          search={searchInput}
          onSearchChange={setSearchInput}
          optionCounts={facetCounts}
          topIssues={topIssueShortcuts}
          onApplyShortcut={applyShortcut}
          resultCount={filteredProducts.length}
          totalCount={tabTotalFromStats ?? undefined}
          loading={listWaitingForData}
          savedViewQuery={savedViewQuery}
          onApplySavedView={applySavedView}
          trailing={
            <>
              {/* Table controls, not part of the query — kept out of the pill row. */}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={listDensity}
                onChange={(_, value: StagedListDensity | null) => {
                  if (!value) return;
                  setListDensity(value);
                  localStorage.setItem(STAGED_LIST_DENSITY_KEY, value);
                }}
                aria-label="Table density"
                sx={{ '& .MuiToggleButton-root': { height: 30, px: 1 } }}
              >
                <ToggleButton value="comfortable" aria-label="Comfortable rows">
                  <Tooltip title="Comfortable">
                    <ComfortableDensityIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="compact" aria-label="Compact rows">
                  <Tooltip title="Compact">
                    <CompactDensityIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 104 }}>
                <InputLabel>Rows / load</InputLabel>
                <Select
                  value={pageSize}
                  label="Rows / load"
                  sx={{ height: 30 }}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPageSize(v);
                    localStorage.setItem(STAGED_PRODUCTS_PAGE_SIZE_KEY, String(v));
                  }}
                >
                  {STAGED_PRODUCTS_PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="How filters and bulk AI batching work">
                <IconButton size="small" onClick={(e) => setFilterHelpAnchor(e.currentTarget)}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Popover
                open={Boolean(filterHelpAnchor)}
                anchorEl={filterHelpAnchor}
                onClose={() => setFilterHelpAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 2, maxWidth: 380 }}>
                  <Typography variant="subtitle2" gutterBottom>Filters &amp; bulk AI</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The bar shows only the filters you have added — use <strong>+ Add filter</strong> for the rest.
                    Filters stack (all must match); several values inside one filter match any of them,
                    except Data issues: a second click on an issue hides rows that have it.
                    The URL carries the whole view, so a filtered queue can be shared or bookmarked.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Data issues use the same approval rules as promote/approve (UPC, photo, descriptions,
                    features, price, weight, etc.). Click an issue to show rows that have it; click again
                    to hide rows that have it. The Top issues row underneath is a shortcut into it.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    &quot;Active only&quot; hides rows where Detected Status or manufacturer stock is discontinued — use it to prioritize currently sold SKUs.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Scrape kind separates brand-new discovery products from re-scrapes of existing catalog SKUs (Full Update, Price Update, etc.) — they usually need different approval attention. Change type narrows further within that set.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    AI score buckets match the ring colors: High ≥80, Medium 60–79, Low &lt;60. &quot;Below 80&quot; is a quick triage for anything that is not green.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    UPC state covers both the barcode itself (UPC or EAN — same rule as approve/promote) and
                    promote-blocking duplicates: &quot;Hide duplicate UPC&quot; clears the queue, &quot;Duplicate UPC only&quot; comes back to them.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                    Bulk AI enhance starts immediately and runs in the background (up to 40 products per run). Watch Pipeline Status for progress.
                  </Typography>
                </Box>
              </Popover>
            </>
          }
        />
      </Paper>

      {activeTab === 'remediating' && (
        <Paper variant="outlined" sx={{ mb: 2, px: 2, py: 1.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="overline" color="text.secondary">
                Lanes
              </Typography>
              {remediationSummaryQuery.isLoading && (
                <Typography variant="caption" color="text.secondary">Counting…</Typography>
              )}
              {remediationSummaryQuery.isError && (
                <Typography variant="caption" color="error.main">Lane counts did not load.</Typography>
              )}
            </Stack>
            {remediationSummaryQuery.data?.remediating.byLane && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {REMEDIATION_LANE_ORDER.map((lane) => {
                  const count = remediationSummaryQuery.data?.remediating.byLane?.[lane] || 0;
                  const copy = REMEDIATION_LANE_COPY[lane];
                  return (
                    <Tooltip key={lane} title={copy.detail}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${copy.label}: ${count.toLocaleString()}`}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
              Free fills run 8 at a time, up to 1,000 a dispatcher run. One run does not clear the whole lane.
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* UPC Review Table */}
      {activeTab === 'upc_review' && (
        <TableContainer component={Paper}>
          {upcReviewLoading && <LinearProgress />}
          {upcReviewData?.stats && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>
                  {upcReviewData.stats.total} products need UPC review
                </Typography>
                <Chip label={`${upcReviewData.stats.unverified} unverified`} size="small" color="warning" variant="outlined" />
                <Chip label={`${upcReviewData.stats.ambiguous} ambiguous`} size="small" color="error" variant="outlined" />
                <Chip label={`${upcReviewData.stats.missing} missing`} size="small" variant="outlined" />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => refetchUpcReview()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          )}
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={upcColumnSort.orderBy === 'name' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'name'}
                    direction={upcColumnSort.orderBy === 'name' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('name')}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'brand' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'brand'}
                    direction={upcColumnSort.orderBy === 'brand' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('brand')}
                  >
                    Brand
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'mpn' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'mpn'}
                    direction={upcColumnSort.orderBy === 'mpn' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('mpn')}
                  >
                    MPN
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'upc' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'upc'}
                    direction={upcColumnSort.orderBy === 'upc' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('upc')}
                  >
                    Current UPC
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'status' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'status'}
                    direction={upcColumnSort.orderBy === 'status' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'source' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'source'}
                    direction={upcColumnSort.orderBy === 'source' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('source')}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'confidence' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'confidence'}
                    direction={upcColumnSort.orderBy === 'confidence' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('confidence')}
                  >
                    Confidence
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={upcColumnSort.orderBy === 'candidates' ? upcColumnSort.direction : false}>
                  <TableSortLabel
                    active={upcColumnSort.orderBy === 'candidates'}
                    direction={upcColumnSort.orderBy === 'candidates' ? upcColumnSort.direction : 'asc'}
                    onClick={() => toggleUpcSort('candidates')}
                  >
                    Candidates
                  </TableSortLabel>
                </TableCell>
                <TableCell>Verify</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!upcReviewData?.data || upcReviewData.data.length === 0) && !upcReviewLoading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No products needing UPC review. Run enrichment first.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedUpcReviewRows.map((product: any) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 220 }}>{brandedProductName(product.brand, product.name)}</Typography>
                    <Typography variant="caption" color="text.secondary">{product.sku}</Typography>
                  </TableCell>
                  <TableCell><Chip label={product.brand} size="small" variant="outlined" /></TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{product.mpn || '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={600}>
                      {product.upc || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.upcVerification}
                      size="small"
                      color={
                        product.upcVerification === 'verified' || product.upcVerification === 'auto_verified' ? 'success'
                        : product.upcVerification === 'ambiguous' ? 'error'
                        : product.upcVerification === 'unverified' ? 'warning'
                        : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell><Typography variant="caption">{product.enrichmentSource || '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={
                        (product.enrichmentConfidence || 0) >= 0.7 ? 'success.main'
                        : (product.enrichmentConfidence || 0) >= 0.4 ? 'warning.main'
                        : 'text.secondary'
                      }
                    >
                      {product.enrichmentConfidence ? `${Math.round(product.enrichmentConfidence * 100)}%` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IdentifierCandidatesChip
                      candidates={product.identifierCandidates || []}
                      currentBarcode={product.upc || product.ean || product.gtin}
                      disabled={upcVerifyMutation.isPending}
                      onUse={(_candidate, barcode) =>
                        upcVerifyMutation.mutate({ id: product.id, upc: barcode, action: 'confirm' })
                      }
                    />
                    {product.enrichmentAmbiguous && (
                      <Tooltip title={product.enrichmentAmbiguityReason || 'Multiple conflicting UPCs'}>
                        <WarningIcon fontSize="small" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {product.upc && (
                        <>
                          <Tooltip title={`Confirm UPC: ${product.upc}`}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => upcVerifyMutation.mutate({ id: product.id, upc: product.upc, action: 'confirm' })}
                              disabled={upcVerifyMutation.isPending}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject this UPC">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => upcVerifyMutation.mutate({ id: product.id, action: 'reject' })}
                              disabled={upcVerifyMutation.isPending}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <BarcodeTextField
                        size="small"
                        placeholder="Enter UPC"
                        value={manualUpcInput[product.id] || ''}
                        onChange={(e) => setManualUpcInput((prev) => ({ ...prev, [product.id]: e.target.value }))}
                        sx={{ width: 130, '& input': { fontSize: '0.75rem', py: 0.5, px: 1, fontFamily: tokens.font.mono } }}
                      />
                      <Tooltip title="Set manual UPC">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!manualUpcInput[product.id] || upcVerifyMutation.isPending}
                            onClick={() => {
                              upcVerifyMutation.mutate({ id: product.id, upc: manualUpcInput[product.id], action: 'set' });
                              setManualUpcInput((prev) => ({ ...prev, [product.id]: '' }));
                            }}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {product.sourceUrl && (
                        <Tooltip title="View source page">
                          <IconButton size="small" component="a" href={product.sourceUrl} target="_blank" rel="noopener noreferrer">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Product Table */}
      {activeTab !== 'upc_review' && (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {listWaitingForData && <LinearProgress />}
        {isMobile ? (
          <Box>
            {filteredProducts.length === 0 && listWaitingForData && (
              <Stack spacing={1.5} alignItems="center" sx={{ py: 6, px: 2 }}>
                <CircularProgress size={32} />
                <Typography variant="subtitle1" fontWeight={600} align="center">
                  {!queueBootstrapReady
                    ? 'Loading queue…'
                    : serverSearchQ && (productsLoading || productsFetching)
                      ? 'Searching…'
                      : fullQueueStillLoading
                      ? 'Loading matching products…'
                      : 'Loading products…'}
                </Typography>
              </Stack>
            )}
            {filteredProducts.length === 0 && !listWaitingForData && (
              <Stack spacing={1.5} alignItems="center" sx={{ py: 6, px: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BoxIcon color="disabled" />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} align="center">
                  No products match this view
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 320 }}>
                  Try another tab, clear filters, or widen search.
                </Typography>
                {hasActiveListFilters && (
                  <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={clearListFilters}>
                    Clear filters
                  </Button>
                )}
              </Stack>
            )}
            {filteredProducts.slice(0, mobileVisibleCount).map((product) => {
              const ct = changeTypeConfig[product.changeType] || changeTypeConfig.full_update;
              const selected = selectedIds.has(product.id);
              const thumb = primaryProductImage(product);
              const gaps = getStagedApprovalGaps(product);
              const rowBlockers = getPromoteBlockers(product, {
                duplicateUpc: duplicateUpcIdSet.has(product.id),
                preReleaseUpcPending,
              });
              return (
                <Box
                  key={product.id}
                  onClick={() => { void openDetailProduct(product); }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: selected ? 'action.selected' : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Checkbox
                      checked={selected}
                      onChange={() => handleToggleSelect(product.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ mt: -0.5, ml: -1 }}
                    />
                    <Avatar
                      variant="rounded"
                      src={thumb}
                      alt=""
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    >
                      <NoImageIcon sx={{ color: 'text.disabled' }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: '-0.01em' }}>
                        {brandedProductName(product.brand, product.name)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: tokens.font.mono }}
                      >
                        {product.sku}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75, pl: '52px' }}>
                    <Chip
                      label={statusDisplayLabel(product.stagingStatus)}
                      size="small"
                      color={statusColor[product.stagingStatus] || 'default'}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    />
                    {product.stagingStatus === 'remediating' && (
                      <RemediationLaneChip lane={product.remediation?.lane} />
                    )}
                    <Chip label={product.brand} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />
                    <Chip
                      icon={ct.icon as React.ReactElement}
                      label={isPriorityNewStaged(product) ? 'Promote first' : ct.label}
                      size="small"
                      color={isPriorityNewStaged(product) ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ borderRadius: 1.5, fontWeight: isPriorityNewStaged(product) ? 700 : 500 }}
                    />
                    {stagedProductIsDiscontinued(product) && (
                      <Chip size="small" color="warning" variant="outlined" label="Discontinued" sx={{ borderRadius: 1.5 }} />
                    )}
                    {duplicateUpcIdSet.has(product.id) && (
                      <Chip size="small" color="error" variant="outlined" label="Dup UPC" sx={{ borderRadius: 1.5 }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: '52px', flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatPrice(product.pricing?.retail || product.pricing?.msrp, product.pricing?.currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(product.ingestedAt || product.createdAt)}
                    </Typography>
                    {gaps.length > 0 && (
                      <Typography variant="caption" color="warning.main">
                        {gaps.length} approval gap{gaps.length === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', pl: '52px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="View Details">
                      <IconButton size="medium" onClick={() => { void openDetailProduct(product); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {CAN_SEND_TO_REVIEW.has(product.stagingStatus) &&
                      canContent('content_review') && (
                      <Tooltip title="Send to Needs Review">
                        <IconButton
                          size="medium"
                          color="info"
                          onClick={() => sendToReviewMutation.mutate({ id: product.id })}
                          disabled={sendToReviewMutation.isPending}
                        >
                          <SendToReviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(product.stagingStatus === 'review' || product.stagingStatus === 'pending') &&
                      canContent('content_approve') && (
                      <Tooltip title="Approve & Promote">
                        <IconButton
                          size="medium"
                          color="success"
                          onClick={() => approveMutation.mutate({ id: product.id, autoPromote: true })}
                          sx={{ position: 'relative' }}
                        >
                          <ApproveIcon fontSize="small" />
                          {rowBlockers.length > 0 && (
                            <WarningIcon
                              color={promoteWillFail(rowBlockers) ? 'error' : 'warning'}
                              sx={{ fontSize: 12, position: 'absolute', right: 0, bottom: 0 }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {product.stagingStatus === 'approved' && canContent('content_promote') && (
                      <Tooltip title="Promote to Canonical">
                        <IconButton
                          size="medium"
                          color="primary"
                          onClick={() => promoteMutation.mutate({ id: product.id })}
                          sx={{ position: 'relative' }}
                        >
                          <PromoteIcon fontSize="small" />
                          {rowBlockers.length > 0 && (
                            <WarningIcon
                              color={promoteWillFail(rowBlockers) ? 'error' : 'warning'}
                              sx={{ fontSize: 12, position: 'absolute', right: 0, bottom: 0 }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {(product.stagingStatus === 'review' || product.stagingStatus === 'pending') &&
                      canContent('content_approve') && (
                      <Tooltip title="Reject">
                        <IconButton size="medium" color="error" onClick={() => rejectMutation.mutate(product.id)}>
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {product.stagingStatus === 'rejected' && canContent('content_approve') && (
                      <Tooltip title="Restore to pending queue">
                        <IconButton
                          size="medium"
                          color="primary"
                          onClick={() => unrejectMutation.mutate(product.id)}
                          disabled={unrejectMutation.isPending}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {product.stagingStatus === 'rejected' && (
                      <Tooltip title="Delete permanently">
                        <IconButton
                          size="medium"
                          color="error"
                          onClick={() =>
                            setRejectDeleteConfirm({
                              id: product.id,
                              summary: `${product.brand} | ${product.sku} | ${product.name}`.slice(0, 200),
                            })
                          }
                          disabled={deleteRejectedMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
            {mobileVisibleCount < filteredProducts.length && (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Box ref={mobileLoadMoreRef} sx={{ height: 1 }} aria-hidden />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setMobileVisibleCount((c) => Math.min(c + MOBILE_LIST_BATCH, filteredProducts.length))
                  }
                >
                  Show more ({filteredProducts.length - mobileVisibleCount} remaining)
                </Button>
              </Box>
            )}
          </Box>
        ) : (
        <>
        {/* Header outside the vertical scroller so the scrollbar starts below the title row */}
        <TableContainer
          ref={tableHeaderScrollRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarGutter: 'stable',
            bgcolor: 'grey.50',
            // Keep gutter for column alignment with the body scroller; hide the bar itself
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Table
            size={listDensity === 'compact' ? 'small' : 'medium'}
            sx={{ minWidth: 1180, tableLayout: 'fixed' }}
          >
            {stagedTableColGroup}
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: 'grey.50', fontWeight: 700, borderBottom: 2, borderColor: 'divider' }}
                >
                  <Tooltip
                    title={
                      fullQueueStillLoading
                        ? 'Select all is available once every matching product has loaded'
                        : filteredProducts.length > BULK_AI_BATCH_MAX
                          ? `Selects all ${filteredProducts.length} visible rows. Bulk AI Accept allows ${BULK_AI_BATCH_MAX} — use “Keep first ${BULK_AI_BATCH_MAX}” in the action bar.`
                          : 'Select all visible rows'
                    }
                  >
                    <span>
                      <Checkbox
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                        onChange={() => allSelected ? handleClearSelection() : handleSelectAll()}
                        size="small"
                        disabled={fullQueueStillLoading}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                {(
                  [
                    { id: 'name' as const, label: 'Product', width: '26%' },
                    { id: 'brand' as const, label: 'Brand', width: '9%' },
                    { id: 'changeType' as const, label: 'Change', width: '9%' },
                    { id: 'stagingStatus' as const, label: 'Status', width: '11%' },
                    { id: 'aiScore' as const, label: 'AI', width: 72 },
                    { id: 'price' as const, label: 'Price', width: '9%' },
                    { id: 'dataRichness' as const, label: 'Completeness', width: '11%' },
                    { id: 'ingestedAt' as const, label: 'Ingested', width: '8%' },
                  ] as const
                ).map((col) => (
                  <TableCell
                    key={col.id}
                    sortDirection={stagedColumnSort.orderBy === col.id ? stagedColumnSort.direction : false}
                    sx={{
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      borderBottom: 2,
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      width: col.width,
                    }}
                  >
                    <TableSortLabel
                      active={stagedColumnSort.orderBy === col.id}
                      direction={stagedColumnSort.orderBy === col.id ? stagedColumnSort.direction : 'asc'}
                      onClick={() => toggleStagedSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: 'grey.50',
                    fontWeight: 700,
                    borderBottom: 2,
                    borderColor: 'divider',
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <TableContainer
          ref={tableScrollRef}
          onScroll={(e) => syncTableHeaderScroll(e.currentTarget.scrollLeft)}
          sx={{
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Table
            size={listDensity === 'compact' ? 'small' : 'medium'}
            sx={{ minWidth: 1180, tableLayout: 'fixed' }}
          >
            {stagedTableColGroup}
            <TableBody>
              {filteredProducts.length === 0 && listWaitingForData && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Stack spacing={1.5} alignItems="center">
                      <CircularProgress size={32} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {!queueBootstrapReady
                          ? 'Loading queue…'
                          : serverSearchQ && (productsLoading || productsFetching)
                            ? 'Searching…'
                            : fullQueueStillLoading
                            ? 'Loading matching products…'
                            : 'Loading products…'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
                        {!queueBootstrapReady
                          ? 'Waiting for staged product counts, then loading this tab.'
                          : serverSearchQ && (productsLoading || productsFetching)
                            ? `Looking up “${serverSearchQ}” on the server instead of paging the queue.`
                            : fullQueueStillLoading
                            ? 'Fetching more pages so filters can match the full queue.'
                            : 'Fetching the product list for this tab.'}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {filteredProducts.length === 0 && !listWaitingForData && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Stack spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BoxIcon color="disabled" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        No products match this view
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                        Try another tab, clear filters, or widen search. Bulk AI and promote actions stay available once rows appear.
                      </Typography>
                      {hasActiveListFilters && (
                        <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={clearListFilters}>
                          Clear filters
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {desktopRows.map((product) => {
                const ct = changeTypeConfig[product.changeType] || changeTypeConfig.full_update;
                const selected = selectedIds.has(product.id);
                const thumb = primaryProductImage(product);
                const score = product.verification?.overallScore;
                const scoreTone = aiScoreTone(score);
                const completeness = dataCompletenessItems(product);
                const completenessOk = completeness.filter((c) => c.ok).length;
                const gaps = getStagedApprovalGaps(product);
                const rowBlockers = getPromoteBlockers(product, {
                  duplicateUpc: duplicateUpcIdSet.has(product.id),
                  preReleaseUpcPending,
                });
                const avatarSize = listDensity === 'compact' ? 36 : 48;
                const rowPy = listDensity === 'compact' ? 0.75 : 1.25;
                return (
                  <TableRow
                    key={product.id}
                    hover
                    selected={selected}
                    sx={{
                      cursor: 'pointer',
                      '& td': { borderColor: 'divider', py: rowPy },
                      '&.Mui-selected': { bgcolor: 'action.selected' },
                      '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                      '&:hover td:last-of-type': { bgcolor: 'action.hover' },
                      '&.Mui-selected:hover td:last-of-type': { bgcolor: 'action.selected' },
                    }}
                    onClick={() => { void openDetailProduct(product); }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected}
                        onChange={() => handleToggleSelect(product.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          src={thumb}
                          alt=""
                          sx={{
                            width: avatarSize,
                            height: avatarSize,
                            bgcolor: 'grey.100',
                            border: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        >
                          <NoImageIcon sx={{ color: 'text.disabled', fontSize: avatarSize * 0.45 }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            title={brandedProductName(product.brand, product.name)}
                            sx={{ letterSpacing: '-0.01em' }}
                          >
                            {brandedProductName(product.brand, product.name)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: tokens.font.mono }}>
                              {product.sku}
                            </Typography>
                            {product.mpn && product.mpn !== product.sku && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`MPN ${product.mpn}`}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {product.productType && (
                              <Typography variant="caption" color="text.disabled">
                                {product.productType}
                              </Typography>
                            )}
                            {stagedProductIsDiscontinued(product) && (
                              <Chip size="small" color="warning" variant="outlined" label="Discontinued" sx={{ height: 20, fontSize: '0.65rem' }} />
                            )}
                            {duplicateUpcIdSet.has(product.id) && (
                              <Tooltip title="Duplicate UPC — promote will fail until resolved">
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label="Dup UPC"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          {gaps.length > 0 && listDensity !== 'compact' && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                              {gaps.length} approval gap{gaps.length === 1 ? '' : 's'}
                            </Typography>
                          )}
                          {gaps.length === 0 && rowBlockers.length > 0 && listDensity !== 'compact' && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                              {summarizePromoteBlockers(rowBlockers)}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.brand}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={ct.icon as React.ReactElement}
                        label={isPriorityNewStaged(product) ? 'Promote first' : ct.label}
                        size="small"
                        color={isPriorityNewStaged(product) ? 'error' : ct.color}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, fontWeight: isPriorityNewStaged(product) ? 700 : 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={statusDisplayLabel(product.stagingStatus)}
                          size="small"
                          color={statusColor[product.stagingStatus] || 'default'}
                          sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                        {product.stagingStatus === 'remediating' && (
                          <RemediationLaneChip lane={product.remediation?.lane} />
                        )}
                        {wasMachineAdvanced(product) && (
                          <Tooltip title="Advanced by automated remediation — open to review what it changed">
                            <GenerateIcon sx={{ fontSize: 14, color: 'info.main' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {product.verification ? (
                        <Tooltip
                          title={
                            product.verification.passedThreshold
                              ? 'Passed verification threshold'
                              : 'Below verification threshold'
                          }
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={Math.max(0, Math.min(100, score ?? 0))}
                                size={32}
                                thickness={5}
                                color={scoreTone === 'default' ? 'inherit' : scoreTone}
                                sx={{ color: scoreTone === 'default' ? 'action.disabled' : undefined }}
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" fontWeight={700} lineHeight={1}>
                                  {score}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatPrice(product.pricing?.retail || product.pricing?.msrp, product.pricing?.currency)}
                      </Typography>
                      {product.pricing?.priceAsOf && (
                        <Typography variant="caption" color="text.disabled" display="block">
                          {formatRelativeTime(product.pricing.priceAsOf)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          <Box>
                            {completeness.map((item) => (
                              <Typography key={item.key} variant="caption" display="block">
                                {item.ok ? '✓' : '○'} {item.label}: {item.detail}
                              </Typography>
                            ))}
                          </Box>
                        }
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {completeness.map((item) => (
                            <Box
                              key={item.key}
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: item.ok ? 'success.main' : 'action.disabledBackground',
                                border: item.ok ? 'none' : '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          ))}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                            {completenessOk}/{completeness.length}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(product.ingestedAt || product.createdAt)}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatRelativeTime(product.ingestedAt || product.createdAt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'sticky',
                        right: 0,
                        bgcolor: 'background.paper',
                        boxShadow: '-8px 0 12px -10px rgba(0,0,0,0.25)',
                      }}
                    >
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => { void openDetailProduct(product); }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {CAN_SEND_TO_REVIEW.has(product.stagingStatus) &&
                          canContent('content_review') && (
                          <Tooltip title="Send to Needs Review">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => sendToReviewMutation.mutate({ id: product.id })}
                              disabled={sendToReviewMutation.isPending}
                            >
                              <SendToReviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(product.stagingStatus === 'review' || product.stagingStatus === 'pending') &&
                          canContent('content_approve') && (
                          <Tooltip
                            title={
                              product.existingCanonicalId
                                ? rowBlockers.length
                                  ? `Approve & apply a full merge. ${summarizePromoteBlockers(rowBlockers)}`
                                  : 'Approve & apply a full merge to the linked canonical (all scrape fields)'
                                : rowBlockers.length
                                  ? `Approve & Promote. ${summarizePromoteBlockers(rowBlockers)}`
                                  : 'Approve & Promote'
                            }
                          >
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => approveMutation.mutate({ id: product.id, autoPromote: true })}
                              sx={{ position: 'relative' }}
                            >
                              <ApproveIcon fontSize="small" />
                              {rowBlockers.length > 0 && (
                                <WarningIcon
                                  color={promoteWillFail(rowBlockers) ? 'error' : 'warning'}
                                  sx={{ fontSize: 12, position: 'absolute', right: 0, bottom: 0 }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {product.stagingStatus === 'approved' && canContent('content_promote') && (
                          <Tooltip
                            title={
                              rowBlockers.length
                                ? summarizePromoteBlockers(rowBlockers)
                                : product.existingCanonicalId
                                  ? `Full merge into ${product.existingCanonicalId} (all scrape fields)`
                                  : 'Promote to Canonical'
                            }
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => promoteMutation.mutate({ id: product.id })}
                              sx={{ position: 'relative' }}
                            >
                              <PromoteIcon fontSize="small" />
                              {rowBlockers.length > 0 && (
                                <WarningIcon
                                  color={promoteWillFail(rowBlockers) ? 'error' : 'warning'}
                                  sx={{ fontSize: 12, position: 'absolute', right: 0, bottom: 0 }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {(product.stagingStatus === 'review' || product.stagingStatus === 'pending') &&
                          canContent('content_approve') && (
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" onClick={() => rejectMutation.mutate(product.id)}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {product.stagingStatus === 'rejected' && canContent('content_approve') && (
                          <Tooltip title="Restore to pending queue">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                unrejectMutation.mutate(product.id);
                              }}
                              disabled={unrejectMutation.isPending}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {product.stagingStatus === 'rejected' && (
                          <Tooltip title="Delete permanently">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRejectDeleteConfirm({
                                  id: product.id,
                                  summary: `${product.brand} | ${product.sku} | ${product.name}`.slice(0, 200),
                                });
                              }}
                              disabled={deleteRejectedMutation.isPending}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Sentinel must stay inside the scroll root so intersection is table-scroll based */}
          <Box
            ref={loadMoreRef}
            sx={{ height: 1, width: '100%' }}
            aria-hidden
          />
        </TableContainer>
        </>
        )}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {listWaitingForData
              ? (!queueBootstrapReady ? 'Waiting for queue counts…' : 'Loading products…')
              : (
                <>
                  {desktopHasHiddenRows
                    ? `Showing ${desktopRows.length} of ${filteredProducts.length} matching`
                    : `${filteredProducts.length} visible`}
                  {products?.length != null && products.length !== filteredProducts.length
                    ? ` · ${products.length} loaded`
                    : ''}
                  {tabTotalFromStats != null ? ` · ${tabTotalFromStats} in tab` : ''}
                </>
              )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && desktopHasHiddenRows && (
              <Button size="small" variant="outlined" onClick={showMoreDesktopRows}>
                Show {Math.min(pageSize, filteredProducts.length - desktopRows.length)} more
              </Button>
            )}
            {isFetchingNextPage ? (
              <>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">Loading more…</Typography>
              </>
            ) : hasNextPage ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  loadMoreLockRef.current = true;
                  void fetchNextPage();
                }}
              >
                Load more
              </Button>
            ) : (
              (products?.length ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {tabTotalFromStats && tabTotalFromStats > (products?.length ?? 0)
                    ? `End of pages · ${products?.length} of ${tabTotalFromStats} loaded`
                    : 'All loaded rows shown'}
                </Typography>
              )
            )}
          </Box>
        </Box>
      </Paper>
      )}

      {/* Floating Bulk Action Bar */}
      <Fade in={selectedCount > 0}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.5,
            maxWidth: 'calc(100vw - 48px)',
            zIndex: 1200,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Chip label={`${selectedCount} selected`} color="primary" size="small" />
          {selectedCount > BULK_AI_BATCH_MAX && (
            <Tooltip title={`Bulk Accept AI Features / Suggestions allow ${BULK_AI_BATCH_MAX} per request`}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleTrimSelectionToBatchMax()}
                sx={{ textTransform: 'none' }}
              >
                Keep first {BULK_AI_BATCH_MAX}
              </Button>
            </Tooltip>
          )}
          {canPromoteSelected && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={bulkPromoteSelectedMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <PromoteIcon />}
              disabled={bulkPromoteSelectedMutation.isPending}
              onClick={() =>
                bulkPromoteSelectedMutation.mutate({
                  productIds: Array.from(selectedIds),
                  autoApprove: needsAutoApprove,
                })
              }
              sx={{ textTransform: 'none' }}
            >
              {needsAutoApprove ? 'Approve & Promote' : 'Promote'} ({selectedCount})
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={bulkRejectSelectedMutation.isPending ? <CircularProgress size={14} /> : <RejectIcon />}
            disabled={bulkRejectSelectedMutation.isPending}
            onClick={() => bulkRejectSelectedMutation.mutate(Array.from(selectedIds))}
            sx={{ textTransform: 'none' }}
          >
            Reject ({selectedCount})
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={bulkAiEnhanceMutation.isPending ? <CircularProgress size={14} /> : <AIIcon />}
            disabled={bulkAiEnhanceMutation.isPending}
            onClick={() =>
              bulkAiEnhanceMutation.mutate({ productIds: Array.from(selectedIds) })
            }
            sx={{ textTransform: 'none' }}
          >
            AI Enhance
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={bulkAiEnhanceMutation.isPending ? <CircularProgress size={14} /> : <AIIcon />}
            disabled={bulkAiEnhanceMutation.isPending}
            onClick={() =>
              bulkAiEnhanceMutation.mutate({ productIds: Array.from(selectedIds), forceRegenerate: true })
            }
            sx={{ textTransform: 'none' }}
          >
            Regen Descriptions
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={bulkAcceptAiFeaturesMutation.isPending ? <CircularProgress size={14} /> : <AIIcon />}
            disabled={bulkAcceptAiFeaturesMutation.isPending}
            onClick={() =>
              bulkAcceptAiFeaturesMutation.mutate(idsForBulkAiBatch(orderedSelectedIds))
            }
            sx={{ textTransform: 'none' }}
          >
            Accept AI Features
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={
              bulkAcceptAiSuggestionsMutation.isPending ? <CircularProgress size={14} /> : <FixIcon />
            }
            disabled={bulkAcceptAiSuggestionsMutation.isPending}
            onClick={() =>
              bulkAcceptAiSuggestionsMutation.mutate(idsForBulkAiBatch(orderedSelectedIds))
            }
            sx={{ textTransform: 'none' }}
          >
            Accept All AI Suggestions
            {selectedCount > BULK_AI_BATCH_MAX ? ` (${BULK_AI_BATCH_MAX})` : ''}
          </Button>
          <Button
            variant="outlined"
            color="info"
            size="small"
            startIcon={bulkAiVerifyMutation.isPending ? <CircularProgress size={14} /> : <AIIcon />}
            disabled={bulkAiVerifyMutation.isPending}
            onClick={() => bulkAiVerifyMutation.mutate(Array.from(selectedIds))}
            sx={{ textTransform: 'none' }}
          >
            Run AI Verify
          </Button>
          <Tooltip title="Fill product + shipping weight from specs">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={bulkExtractWeightMutation.isPending ? <CircularProgress size={14} /> : <WeightIcon />}
                disabled={bulkExtractWeightMutation.isPending}
                onClick={() => bulkExtractWeightMutation.mutate(Array.from(selectedIds))}
                sx={{ textTransform: 'none' }}
              >
                Extract Weights
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Identifier enrichment (UPC/EAN/MPN) for selected rows">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={bulkEnrichMutation.isPending ? <CircularProgress size={14} /> : <IdentifierIcon />}
                disabled={bulkEnrichMutation.isPending}
                onClick={() => bulkEnrichMutation.mutate(Array.from(selectedIds))}
                sx={{ textTransform: 'none' }}
              >
                Enrich IDs
              </Button>
            </span>
          </Tooltip>
          <Button variant="text" size="small" onClick={handleClearSelection} sx={{ textTransform: 'none' }}>
            Clear
          </Button>
        </Paper>
      </Fade>

      {/* Detail Dialog */}
      <Dialog
        open={!!detailProduct}
        onClose={closeDetailDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2.5,
            maxHeight: isMobile ? '100%' : '92vh',
            overflow: 'hidden',
          },
        }}
      >
        {detailProduct && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                py: 2,
                px: { xs: 2, sm: 3 },
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                position: isMobile ? 'sticky' : undefined,
                top: 0,
                zIndex: isMobile ? 2 : undefined,
              }}
            >
              <Stack direction="row" spacing={isMobile ? 1.25 : 2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                <Avatar
                  variant="rounded"
                  src={primaryProductImage(detailProduct)}
                  alt=""
                  sx={{
                    width: isMobile ? 44 : 64,
                    height: isMobile ? 44 : 64,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  <NoImageIcon sx={{ color: 'text.disabled' }} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                    <Typography
                      variant={isMobile ? 'subtitle1' : 'h6'}
                      fontWeight={700}
                      sx={{ letterSpacing: '-0.02em', lineHeight: 1.25 }}
                    >
                      {isEditing ? editData.name : detailProduct.name}
                    </Typography>
                    <StagedSourceBadge
                      source={stagedSectionSource(
                        detailProduct,
                        'Name',
                        !!(isEditing ? editData.name : detailProduct.name),
                      )}
                    />
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip
                      label={statusDisplayLabel(detailProduct.stagingStatus)}
                      size="small"
                      color={statusColor[detailProduct.stagingStatus] || 'default'}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    />
                    {detailPromoteBlockers.length > 0 && (
                      <Tooltip title={promoteBlockerTooltip(detailPromoteBlockers)}>
                        <Chip
                          icon={<WarningIcon fontSize="small" />}
                          label={promoteStateLabel(detailPromoteBlockers)}
                          size="small"
                          color={promoteStateColor(detailPromoteBlockers)}
                          variant="outlined"
                          sx={{ borderRadius: 1.5 }}
                        />
                      </Tooltip>
                    )}
                    <Chip
                      icon={(changeTypeConfig[detailProduct.changeType]?.icon || null) as React.ReactElement}
                      label={
                        isPriorityNewStaged(detailProduct)
                          ? 'Promote first'
                          : changeTypeConfig[detailProduct.changeType]?.label || detailProduct.changeType
                      }
                      size="small"
                      color={isPriorityNewStaged(detailProduct) ? 'error' : 'default'}
                      variant="outlined"
                      sx={{
                        borderRadius: 1.5,
                        fontWeight: isPriorityNewStaged(detailProduct) ? 700 : 500,
                        color: isPriorityNewStaged(detailProduct) ? undefined : 'text.secondary',
                        '& .MuiChip-icon': {
                          color: isPriorityNewStaged(detailProduct) ? undefined : 'text.secondary',
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: tokens.font.mono }}
                    >
                      {detailProduct.sku}
                    </Typography>
                    {detailProduct.brand && (
                      <Chip label={detailProduct.brand} size="small" variant="outlined" sx={{ height: 22, borderRadius: 1.5 }} />
                    )}
                    {detailProduct.existingCanonicalId && (
                      <Chip
                        label={`Updates ${detailProduct.existingCanonicalSku || detailProduct.existingCanonicalId}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ borderRadius: 1.5 }}
                      />
                    )}
                    {isEditing && <Chip label="Editing" size="small" color="warning" sx={{ borderRadius: 1.5 }} />}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {!isEditing ? (
                  <Tooltip title="Edit product data">
                    <IconButton onClick={startEditing} color="primary" size="small"><ContentUpdateIcon /></IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title="Save changes">
                      <IconButton
                        onClick={() => {
                          // Omit unchanged / empty fields so taxonomy rewrite and
                          // promote do not pick up accidental defaults (MOT-647).
                          if (launchDatesInvalid || lifecycleInvalid) {
                            setDialogAlert({
                              type: 'warning',
                              message: 'Fix the date on Launch Dates or Pre-release lifecycle before saving. Those edits stay in the form.',
                            });
                            return;
                          }
                          const payload = withCardPending(editData, pendingLaunchDates, pendingLifecycle);
                          if (
                            String(payload.category || '').trim() ===
                            String(detailProduct.category || '').trim()
                          ) {
                            delete payload.category;
                          }
                          if (!String(payload.productTypeExpanded || '').trim()) {
                            delete payload.productTypeExpanded;
                          }
                          if (!String(payload.condition || '').trim()) {
                            delete payload.condition;
                          } else if (
                            String(payload.condition).trim() ===
                            String(detailProduct.condition || '').trim()
                          ) {
                            delete payload.condition;
                          }
                          saveEditMutation.mutate({
                            productId: detailProduct.id,
                            data: withEditMeta(payload, {
                              source: 'admin_edit',
                              summary: 'Manual edit in staged product detail',
                            }),
                          });
                        }}
                        color="success"
                        size="small"
                        disabled={saveEditMutation.isPending}
                      >
                        {saveEditMutation.isPending ? <CircularProgress size={18} /> : <SaveIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel editing">
                      <IconButton onClick={cancelEditing} color="default" size="small"><EditOffIcon /></IconButton>
                    </Tooltip>
                  </>
                )}
                <IconButton onClick={closeDetailDialog} size="small"><CloseIcon /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                px: { xs: 2, md: 3 },
                py: 2.5,
                bgcolor: 'background.default',
              }}
            >
              {dialogAlert && (
                <Alert
                  severity={dialogAlert.type}
                  onClose={() => setDialogAlertBanner(null)}
                  sx={{ mb: 2 }}
                >
                  {dialogAlert.message}
                  {dialogAlert.href && (
                    <>
                      {' '}
                      <Link component={RouterLink} to={dialogAlert.href} underline="hover">
                        {dialogAlert.hrefLabel || 'Open'}
                      </Link>
                    </>
                  )}
                </Alert>
              )}
              {detailPromoteBlockers.length > 0 && (
                <Alert
                  severity={promoteStateColor(detailPromoteBlockers)}
                  icon={<WarningIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {promoteWillFail(detailPromoteBlockers)
                      ? 'Cannot promote until these are resolved'
                      : detailPromoteBlockers.some((b) => b.id === 'upcPendingPreRelease')
                        ? 'Pre-release, UPC pending: you can promote this product'
                        : 'A promotion rule is holding this product'}
                  </Typography>
                  {detailPromoteBlockers.map((b) => (
                    <Typography key={b.id} variant="body2" display="block">
                      {b.blocksPromote ? '· ' : '· Auto-approve: '}
                      {b.message}
                    </Typography>
                  ))}
                  {detailProduct.parentSku && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={copyFromParentMutation.isPending ? <CircularProgress size={14} /> : <CopyFromParentIcon />}
                      disabled={copyFromParentMutation.isPending}
                      onClick={() => copyFromParentMutation.mutate(detailProduct.id)}
                      sx={{ mt: 1.25 }}
                    >
                      {copyFromParentMutation.isPending
                        ? 'Copying...'
                        : `Copy description from parent ${detailProduct.parentSku}`}
                    </Button>
                  )}
                </Alert>
              )}
              {mergeUpcCollisions.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Possible merge — same camera already in the catalog
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    UPC <code>{detailUpcForCollision}</code> matches an existing {detailProduct.brand} product with the
                    same name. Different SKUs on the same barcode are common (marketing model vs catalog part number).
                    Link this row as an update so promote merges instead of failing.
                  </Typography>
                  <Stack spacing={1.5}>
                    {mergeUpcCollisions.map((row) => (
                      <Box key={`${row.collection}:${row.id}`}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Typography variant="caption" component="div">
                            <strong>{row.name || '—'}</strong>
                            {' — '}
                            {row.collection === 'canonicalProducts' ? 'canonical' : 'staged'}{' '}
                            <code>{row.id}</code>
                            {row.sku ? (
                              <>
                                {' '}
                                · SKU <code>{row.sku}</code>
                              </>
                            ) : null}
                            {row.skuMismatch ? (
                              <>
                                {' '}
                                · SKU differs on this scrape
                              </>
                            ) : null}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            {row.collection === 'stagedProducts' ? (
                              <Button size="small" variant="outlined" onClick={() => openStagedConflict(row.id)}>
                                Open duplicate
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={linkCanonicalMutation.isPending}
                                onClick={() =>
                                  linkCanonicalMutation.mutate({
                                    productId: detailProduct.id,
                                    canonicalId: row.id,
                                  })
                                }
                              >
                                {linkCanonicalMutation.isPending ? 'Linking…' : 'Link as update'}
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        <MergeDiffPanel
                          diff={row.mergeDiff || null}
                          emptyMessage="Could not compute merge preview for this keeper."
                        />
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              )}
              {blockingUpcCollisions.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Duplicate UPC — promote will fail
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    UPC <code>{detailUpcForCollision}</code> is already used by another active staged or catalog row
                    that does not look like the same product. Different SKUs often mean a wrong barcode — clear or
                    fix the UPC instead of merging.
                  </Typography>
                  <Stack spacing={1.5}>
                    {blockingUpcCollisions.map((row) => (
                      <Box key={`${row.collection}:${row.id}`}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Typography variant="caption" component="div">
                            <strong>{row.name || '—'}</strong>
                            {' — '}
                            {row.collection === 'canonicalProducts' ? 'canonical' : 'staged'}{' '}
                            <code>{row.id}</code>
                            {row.sku ? (
                              <>
                                {' '}
                                · SKU <code>{row.sku}</code>
                              </>
                            ) : null}
                            {row.stagingStatus ? <> · {row.stagingStatus}</> : null}
                            {row.status ? <> · {row.status}</> : null}
                            {row.skuMismatch ? (
                              <>
                                {' '}
                                · <strong>different SKU</strong>
                              </>
                            ) : null}
                          </Typography>
                          {row.collection === 'stagedProducts' ? (
                            <Button size="small" variant="outlined" onClick={() => openStagedConflict(row.id)}>
                              Open duplicate
                            </Button>
                          ) : null}
                        </Stack>
                        <MergeDiffPanel
                          diff={row.mergeDiff || null}
                          emptyMessage="Could not compute merge preview for this keeper."
                        />
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              )}
              {promotedSkuConflicts && promotedSkuConflicts.length > 0 && (
                <Alert
                  severity={detailProduct.existingCanonicalId ? 'info' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {detailProduct.existingCanonicalId
                      ? 'Prior promote artifact for this SKU (expected on updates)'
                      : 'Same SKU exists on promoted staged row(s)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    {detailProduct.existingCanonicalId ? (
                      <>
                        This staged row is a <strong>Full Update</strong> for canonical{' '}
                        <code>{detailProduct.existingCanonicalId}</code>. Promoting applies the merge into that
                        existing catalog product (empty staged fields do not wipe canonical data). The older{' '}
                        <code>promoted</code> staged document below is history from a previous promote — not a second
                        catalog row.
                      </>
                    ) : (
                      <>
                        Promoted staged artifacts are not counted toward SKU uniqueness checks. Link this row to the
                        existing canonical (or reject it) before promoting so you do not create overlapping catalog
                        identity.
                      </>
                    )}
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                    {promotedSkuConflicts.map((row) => (
                      <Typography component="li" key={row.stagedId} variant="caption">
                        <strong>{row.name || '—'}</strong> — staged ID <code>{row.stagedId}</code>
                        {row.inferredCanonicalId ? (
                          <>
                            {' '}
                            · canonical <code>{row.inferredCanonicalId}</code>
                          </>
                        ) : null}
                        {row.promotedAt ? <> · promoted {formatDate(row.promotedAt)}</> : null}
                      </Typography>
                    ))}
                  </Stack>
                  {promoteMergePreview?.upcMismatch && !promoteMergePreview?.kitMismatch ? (
                    <Alert severity="warning" sx={{ mt: 1, mb: 1, py: 0.5 }}>
                      <Typography variant="caption">
                        SKU matches but UPC/EAN would change. List-view full promote is blocked. Apply
                        selected or Apply Update to Canonical uses the checked fields and keeps the
                        catalog barcode. Clear the staged barcode to keep the catalog UPC, or reject the row.
                      </Typography>
                    </Alert>
                  ) : null}
                  {detailProduct.existingCanonicalId ? (
                    <MergeDiffPanel
                      loading={promoteMergePreviewLoading}
                      diff={
                        promoteMergePreview
                          ? { summary: promoteMergePreview.summary, fields: promoteMergePreview.fields }
                          : null
                      }
                      emptyMessage={
                        !promoteMergePreviewLoading && !promoteMergePreview
                          ? 'Could not load promote merge preview. Selected apply is disabled — retry, or use the list-row action for a full merge.'
                          : undefined
                      }
                      selectable
                      canApply={canContent('content_promote')}
                      applyBlockedReason={
                        promoteMergePreview?.kitMismatch ? KIT_VS_BODY_APPLY_BLOCK : undefined
                      }
                      applying={applyMergeMutation.isPending}
                      canonicalId={detailProduct.existingCanonicalId}
                      onApply={(input) =>
                        applyMergeMutation.mutate({ id: detailProduct.id, ...input })
                      }
                      onSelectionChange={setPromoteMergeSelection}
                    />
                  ) : null}
                </Alert>
              )}
              {barcodeOverrideEnabled ? <BarcodeOverrideHistory stagedId={detailProduct.id} /> : null}
              {barcodeOverrideEnabled && detailProduct.existingCanonicalId ? (
                <BarcodeOverrideDialog
                  open={barcodeOverrideOpen}
                  stagedId={detailProduct.id}
                  onClose={() => setBarcodeOverrideOpen(false)}
                  onApplied={() => {
                    setBarcodeOverrideOpen(false);
                    // Recheck: the stale "already used by" warnings clear from the same queries that raised them.
                    queryClient.invalidateQueries({ queryKey: ['staged-merge-preview'] });
                    queryClient.invalidateQueries({ queryKey: ['upc-identity-collision-index'] });
                    queryClient.invalidateQueries({ queryKey: ['staged-upc-identity-collisions'] });
                    queryClient.invalidateQueries({ queryKey: ['barcode-overrides', detailProduct.id] });
                    queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                  }}
                />
              ) : null}
              {detailProduct.existingCanonicalId &&
                !(promotedSkuConflicts && promotedSkuConflicts.length > 0) && (
                <Alert
                  severity={
                    mergeIdentityIssue(promoteMergePreview) === 'kit'
                      ? 'error'
                      : mergeIdentityIssue(promoteMergePreview) === 'upc' ||
                          mergeIdentityIssue(promoteMergePreview) === 'ean'
                        ? 'warning'
                        : 'info'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {mergeIdentityIssue(promoteMergePreview) === 'kit'
                      ? 'Kit vs body — do not merge'
                      : mergeIdentityIssue(promoteMergePreview) === 'upc'
                        ? 'Barcode conflict — same SKU, different UPC'
                        : mergeIdentityIssue(promoteMergePreview) === 'ean'
                          ? 'EAN/GTIN would change — review required'
                          : promoteIsNoop
                            ? 'No catalog changes'
                            : 'Full Update merge preview'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {mergeIdentityIssue(promoteMergePreview) === 'kit' ? (
                      <>
                        This scrape does not look like canonical{' '}
                        <code>{detailProduct.existingCanonicalId}</code>
                        {promoteMergePreview?.target?.name ? (
                          <> ({promoteMergePreview.target.name})</>
                        ) : null}
                        . One side is a lens kit and the other is not. Do not apply this merge; fix the SKU
                        or reject the row.
                      </>
                    ) : mergeIdentityIssue(promoteMergePreview) === 'upc' ? (
                      <>
                        SKU matches canonical <code>{detailProduct.existingCanonicalId}</code>
                        {promoteMergePreview?.target?.name ? (
                          <> ({promoteMergePreview.target.name})</>
                        ) : null}
                        , but UPC/EAN would change. That usually means a different sellable item.
                        List-view full promote is blocked. Apply selected or Apply Update to Canonical
                        uses the checked fields and keeps the catalog barcode. Clear the staged barcode
                        to keep the catalog UPC, or reject the row.
                      </>
                    ) : mergeIdentityIssue(promoteMergePreview) === 'ean' ? (
                      <>
                        SKU and UPC match canonical <code>{detailProduct.existingCanonicalId}</code>
                        {promoteMergePreview?.target?.name ? (
                          <> ({promoteMergePreview.target.name})</>
                        ) : null}
                        , but EAN/GTIN is a different GS1 number — not the US UPC with extra zeros.
                        Nikon Japan JAN (<code>49…</code>) and the padded UPC are both valid and must
                        not be auto-applied. This row will not auto-approve. Apply selected keeps the
                        catalog EAN/GTIN; only a full promote writes the scrape values.
                      </>
                    ) : (
                      <>
                        Promoting merges into canonical <code>{detailProduct.existingCanonicalId}</code>
                        {promoteMergePreview?.target?.name ? (
                          <>
                            {' '}
                            ({promoteMergePreview.target.name})
                          </>
                        ) : null}
                        {promoteIsNoop ? (
                          detailProduct.stagingStatus === 'promoted' ? (
                            <>
                              . This row matches the catalog on every compared field, so there is
                              nothing to apply.
                            </>
                          ) : (
                            <>
                              . This scrape matches the catalog on every compared field, so nothing
                              would be written. Use <strong>{noopClearLabel}</strong> below to clear
                              it from the queue and leave canonical as-is — the plain Close button
                              only dismisses this dialog.
                            </>
                          )
                        ) : (
                          <>
                            . Empty staged fields do not wipe canonical data. Specifications merge — a thinner
                            or chrome-only scrape does not remove catalog spec sections. Uncheck fields you
                            do not want; Apply Update to Canonical writes only the checked rows.
                          </>
                        )}
                      </>
                    )}
                  </Typography>
                  {barcodeOverrideEnabled &&
                  (mergeIdentityIssue(promoteMergePreview) === 'upc' ||
                    mergeIdentityIssue(promoteMergePreview) === 'ean') ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ mt: 1, mb: 1 }}
                      onClick={() => setBarcodeOverrideOpen(true)}
                    >
                      Override catalog barcode
                    </Button>
                  ) : null}
                  <MergeDiffPanel
                    loading={promoteMergePreviewLoading}
                    diff={
                      promoteMergePreview
                        ? { summary: promoteMergePreview.summary, fields: promoteMergePreview.fields }
                        : null
                    }
                    emptyMessage={
                      !promoteMergePreviewLoading && !promoteMergePreview
                        ? 'Could not load promote merge preview. Selected apply is disabled — retry, or use the list-row action for a full merge.'
                        : undefined
                    }
                    selectable
                    canApply={canContent('content_promote')}
                    applyBlockedReason={
                      promoteMergePreview?.kitMismatch ? KIT_VS_BODY_APPLY_BLOCK : undefined
                    }
                    applying={applyMergeMutation.isPending}
                    canonicalId={detailProduct.existingCanonicalId}
                    onApply={(input) =>
                      applyMergeMutation.mutate({ id: detailProduct.id, ...input })
                    }
                    onSelectionChange={setPromoteMergeSelection}
                  />
                </Alert>
              )}
              {mergeRecommendedCandidates.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {detailProduct.existingCanonicalId
                      ? `Other leftover rows also use MPN ${detailMpnForMerge}`
                      : `Same MPN ${detailMpnForMerge} already exists`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    {detailProduct.existingCanonicalId ? (
                      <>
                        This scrape already updates the catalog product in the panel above. The rows below are
                        older or alternate copies of the same part number — not extra steps for this update.
                        Open one only if you need to reject a duplicate.
                      </>
                    ) : (
                      <>
                        Promoting this as a new product would create a second catalog row for the same part
                        number. Link it to the catalog product, or open the other staged scrape. Previews below
                        are not applied unless you link or promote.
                      </>
                    )}
                  </Typography>
                  <Stack spacing={1.5}>
                    {mergeRecommendedCandidates.map((row) => (
                      <Box
                        key={`${row.collection}:${row.id}`}
                        sx={{
                          p: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'flex-start' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={mpnMergeCandidateKind(row)}
                                variant="outlined"
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                              />
                              {row.stagingStatus ? (
                                <Chip
                                  size="small"
                                  label={row.stagingStatus}
                                  variant="outlined"
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                                />
                              ) : null}
                            </Stack>
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              <strong>{row.name || '—'}</strong>
                              {row.sku ? (
                                <>
                                  {' '}
                                  · SKU <code>{row.sku}</code>
                                </>
                              ) : null}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                              {row.collection === 'canonicalProducts' ? 'canonical' : 'staged'}{' '}
                              <code>{row.id}</code>
                              {row.fieldsPresentOnCandidate.length > 0 ? (
                                <> · that record already has {row.fieldsPresentOnCandidate.join(', ')}</>
                              ) : null}
                              {row.fieldsPresentOnCurrentMissingOnCandidate.length > 0 ? (
                                <> · this scrape can add {row.fieldsPresentOnCurrentMissingOnCandidate.join(', ')}</>
                              ) : null}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} flexShrink={0}>
                            {row.collection === 'stagedProducts' ? (
                              <Button size="small" variant="outlined" onClick={() => openStagedConflict(row.id)}>
                                Open this copy
                              </Button>
                            ) : detailProduct.existingCanonicalId ? null : (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={linkCanonicalMutation.isPending}
                                onClick={() =>
                                  linkCanonicalMutation.mutate({
                                    productId: detailProduct.id,
                                    canonicalId: row.id,
                                  })
                                }
                              >
                                {linkCanonicalMutation.isPending ? 'Linking…' : 'Link as update target'}
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        {!detailProduct.existingCanonicalId ? (
                          <MergeDiffPanel
                            diff={row.mergeDiff || null}
                            summaryLabel="Preview if this scrape were merged into that record"
                            emptyMessage="No field-level preview for this copy."
                          />
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              )}
              {detailProduct?.source?.sourceUrl?.includes('bedfords.com') &&
                detailProduct?.source?.connectorId?.includes('scraper') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Retailer scrape attributed to brand connector
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Source URL is Bedfords, but the connector is labeled{' '}
                    <code>{detailProduct.source.connectorId}</code> because multi-brand retailer scrapes stage under
                    the product brand (Canon). Confirm MPN against the retailer page before promote — Gemini may guess
                    a nearby Canon part number.
                  </Typography>
                </Alert>
              )}
              <Grid container spacing={2.5} alignItems="flex-start">
                {/* Left Column — Product Info */}
                <Grid size={{ xs: 12, md: 7 }} sx={{ minWidth: 0 }}>
                  {/* What the automated pipeline did — first, so spot-checks
                      start from provenance rather than hunting for it. */}
                  <RemediationPanel product={detailProduct} />
                  <StagedVariantLinkCard
                    productId={detailProduct.id}
                    sku={detailProduct.sku}
                    brand={detailProduct.brand}
                    brandId={detailProduct.brandId}
                    parentSku={detailProduct.parentSku}
                    variantGroupId={detailProduct.variantGroupId}
                    onSaved={(product) => {
                      setDetailProduct(product as StagedProduct);
                      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                    }}
                  />
                  {/* Basic Info */}
                  <DetailSection
                    title="Product Info"
                    action={
                      <BrandCatalogProfileButton
                        brandId={detailProduct.brandId}
                        brand={detailProduct.brand}
                      />
                    }
                  >
                  {identifierPolicy.scraperHint ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
                      {identifierPolicy.scraperHint}
                    </Typography>
                  ) : null}
                  {isEditing ? (
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Name" value={editData.name || ''} onChange={(e) => updateEditField('name', e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Model" value={editData.model || ''} onChange={(e) => updateEditField('model', e.target.value)} />
                      </Grid>
                      {(['mpn', 'upc', 'ean', 'gtin', 'sku'] as IdentifierReviewField[]).map((field) => {
                        const liveNotes = annotateIdentifiers({
                          brand: editData.brand ?? detailProduct.brand,
                          brandId: detailProduct.brandId,
                          sku: editData.sku ?? detailProduct.sku,
                          mpn: editData.mpn ?? detailProduct.mpn,
                          upc: editData.upc ?? detailProduct.upc,
                          ean: editData.ean ?? detailProduct.ean,
                          gtin: editData.gtin ?? detailProduct.gtin,
                          sourceUrl: detailProduct.source?.sourceUrl,
                          skuSource: detailProduct.skuSource,
                          acknowledgedReviewFlags: detailProduct.acknowledgedReviewFlags,
                        });
                        const note = liveNotes.find((n) => n.field === field);
                        const collisionHelper =
                          field === 'upc' && blockingUpcCollisions.length > 0
                            ? 'Duplicate UPC — promote will fail'
                            : field === 'upc' && mergeUpcCollisions.length > 0
                              ? 'Same catalog UPC — link as an update'
                              : undefined;
                        const helper = note?.blocks
                          ? [note.message, note.suggestion ? `Suggested: ${note.suggestion}` : null]
                              .filter(Boolean)
                              .join(' ')
                          : collisionHelper ||
                            (field === 'sku'
                              ? `${identifierPrimers.sku} Vendor source id is updated to match on save.`
                              : identifierPrimers[field]);
                        return (
                          <Grid size={{ xs: 6 }} key={field}>
                            <BarcodeTextField
                              fullWidth
                              size="small"
                              label={field.toUpperCase()}
                              validate={field === 'upc' || field === 'ean' || field === 'gtin'}
                              value={editData[field] ?? detailProduct[field] ?? ''}
                              onChange={(e) => updateEditField(field, e.target.value)}
                              placeholder={
                                (field === 'sku' || field === 'mpn') && identifierPolicy.skuExample
                                  ? identifierPolicy.skuExample
                                  : undefined
                              }
                              error={Boolean(note?.blocks) || (field === 'upc' && blockingUpcCollisions.length > 0)}
                              helperText={helper}
                            />
                            {note?.suggestion && (
                              <Button
                                size="small"
                                sx={{ mt: 0.25, textTransform: 'none', fontSize: 11 }}
                                onClick={() => updateEditField(field, note.suggestion)}
                              >
                                Use {note.suggestion}
                              </Button>
                            )}
                          </Grid>
                        );
                      })}
                      <Grid size={{ xs: 6 }}>
                        <BarcodeTextField
                          fullWidth
                          size="small"
                          label="JAN"
                          value={editData.jan ?? detailProduct.jan ?? japanArticleNumber(detailProduct) ?? ''}
                          onChange={(e) => updateEditField('jan', e.target.value)}
                          helperText="Japanese Article Number. Filled from a 45/49 barcode when the listing has one."
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Country of Origin"
                          value={editData.countryOfOrigin ?? countryOfOriginFromProduct(detailProduct) ?? ''}
                          onChange={(e) => updateEditField('countryOfOrigin', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <BrandSelect label="Brand" value={editData.brand || ''} onChange={(v) => updateEditField('brand', v)} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Category</InputLabel>
                          <Select value={editData.category || ''} label="Category" onChange={(e) => updateEditField('category', e.target.value)}>
                            <MenuItem value="">—</MenuItem>
                            {PRODUCT_CATEGORY_SELECT_GROUPS.flatMap((group) => [
                              <MenuItem key={`${group.label}-header`} disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem' }}>{group.label}</MenuItem>,
                              ...group.options.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              )),
                            ])}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Product Type</InputLabel>
                          <Select value={editData.productType || ''} label="Product Type" onChange={(e) => updateEditField('productType', e.target.value)}>
                            <MenuItem value="">—</MenuItem>
                            <MenuItem value="camera">Camera</MenuItem>
                            <MenuItem value="lens">Lens</MenuItem>
                            <MenuItem value="accessory">Accessory</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type (expanded)</InputLabel>
                          <Select
                            value={editData.productTypeExpanded || ''}
                            label="Type (expanded)"
                            onChange={(e) => updateEditField('productTypeExpanded', e.target.value)}
                          >
                            <MenuItem value="">—</MenuItem>
                            {['camera', 'lens', 'lens-accessory', 'flash', 'tripod', 'bag', 'battery', 'memory', 'filter', 'strap', 'grip', 'remote', 'monitor', 'audio', 'cable', 'drone', 'software', 'cleaning', 'accessory'].map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Condition</InputLabel>
                          <Select
                            value={editData.condition || ''}
                            label="Condition"
                            onChange={(e) => updateEditField('condition', e.target.value)}
                          >
                            <MenuItem value="">—</MenuItem>
                            <MenuItem value="new">New</MenuItem>
                            <MenuItem value="used">Used</MenuItem>
                            <MenuItem value="refurbished">Refurbished</MenuItem>
                            <MenuItem value="open-box">Open box</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select value={editData.detectedStatus || ''} label="Status" onChange={(e) => updateEditField('detectedStatus', e.target.value)}>
                            <MenuItem value="">Unknown</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="discontinued">Discontinued</MenuItem>
                            <MenuItem value="coming_soon">Coming Soon</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="ASIN"
                          value={editData.asin || ''}
                          onChange={(e) => updateEditField('asin', e.target.value)}
                          helperText="Amazon ASIN when known"
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Legacy launchDate"
                          value={editData.launchDate || ''}
                          onChange={(e) => updateEditField('launchDate', e.target.value)}
                          placeholder="YYYY-MM-DD"
                          helperText="Single legacy date; prefer Launch Dates below"
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={1.5}>
                      {[
                        { label: 'SKU', value: detailProduct.sku, mono: true },
                        { label: 'MPN', value: detailProduct.mpn, mono: true },
                        {
                          label: 'UPC',
                          value: detailProduct.upc || promoteMergePreview?.mergedIdentifiers?.upc,
                          catalogKept: !detailProduct.upc && !!promoteMergePreview?.mergedIdentifiers?.upc,
                          mono: true,
                        },
                        {
                          label: 'EAN',
                          value: detailProduct.ean || promoteMergePreview?.mergedIdentifiers?.ean,
                          catalogKept: !detailProduct.ean && !!promoteMergePreview?.mergedIdentifiers?.ean,
                          mono: true,
                        },
                        { label: 'Brand', value: detailProduct.brand },
                        { label: 'Model', value: detailProduct.model },
                        { label: 'Category', value: detailProduct.category },
                        // Only worth a slot when it disagrees with Category — that
                        // mismatch is what shows up as an unclearable merge-diff row.
                        ...(detailProduct.categorySlug && detailProduct.categorySlug !== detailProduct.category
                          ? [{
                              label: 'Category Slug',
                              value: detailProduct.categorySlug,
                              mono: true,
                              primer:
                                'Out of sync with Category. Re-save the category in edit mode to rewrite the slug, taxonomy path, and product type together.',
                            }]
                          : []),
                        { label: 'Type', value: detailProduct.productType },
                        ...(detailProduct.productTypeExpanded &&
                        detailProduct.productTypeExpanded !== detailProduct.productType
                          ? [{ label: 'Type (expanded)', value: detailProduct.productTypeExpanded }]
                          : []),
                        ...(detailProduct.condition
                          ? [{ label: 'Condition', value: detailProduct.condition }]
                          : []),
                        { label: 'Detected Status', value: detailProduct.detectedStatus },
                        {
                          label: 'GTIN',
                          value: detailProduct.gtin || promoteMergePreview?.mergedIdentifiers?.gtin,
                          catalogKept: !detailProduct.gtin && !!promoteMergePreview?.mergedIdentifiers?.gtin,
                          mono: true,
                        },
                        {
                          label: 'JAN',
                          value: japanArticleNumber({
                            jan: detailProduct.jan,
                            ean: detailProduct.ean || promoteMergePreview?.mergedIdentifiers?.ean,
                            gtin: detailProduct.gtin || promoteMergePreview?.mergedIdentifiers?.gtin,
                          }),
                          mono: true,
                        },
                        ...(detailProduct.asin
                          ? [{ label: 'ASIN', value: detailProduct.asin, mono: true }]
                          : []),
                        ...(detailProduct.launchDate
                          ? [{ label: 'Legacy launchDate', value: detailProduct.launchDate, mono: true }]
                          : []),
                        ...(countryOfOriginFromProduct(detailProduct)
                          ? [{
                              label: 'Country of Origin',
                              value: countryOfOriginFromProduct(detailProduct),
                            }]
                          : []),
                      ].map((f) => {
                        const primerKey = PRODUCT_INFO_PRIMER_KEYS[f.label];
                        const reviewField = primerKey as IdentifierReviewField | undefined;
                        const note = reviewField ? identifierNotesByField[reviewField] : undefined;
                        const ack =
                          note?.flagId &&
                          identifierReviewInput &&
                          isIdentifierFlagAcknowledged(
                            note.flagId,
                            identifierReviewInput,
                            detailProduct.acknowledgedReviewFlags,
                          )
                            ? detailProduct.acknowledgedReviewFlags?.[note.flagId]
                            : null;
                        const extraChips = (
                          <>
                            {barcodeChipLabel && (f.label === 'UPC' || f.label === 'EAN') ? (
                              <Chip
                                label={barcodeChipLabel}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ mt: 0.4, height: 18, fontSize: '0.62rem', mr: 0.4 }}
                              />
                            ) : null}
                            {f.label === 'UPC' && blockingUpcCollisions.length > 0 ? (
                              <Chip
                                label="Duplicate UPC"
                                size="small"
                                color="error"
                                sx={{ mt: 0.4, height: 18, fontSize: '0.62rem' }}
                              />
                            ) : null}
                            {f.label === 'UPC' && mergeUpcCollisions.length > 0 ? (
                              <Chip
                                label="Merge candidate"
                                size="small"
                                color="info"
                                sx={{ mt: 0.4, height: 18, fontSize: '0.62rem' }}
                              />
                            ) : null}
                            {f.catalogKept && f.value ? (
                              <Chip
                                label="kept on catalog"
                                size="small"
                                color="info"
                                sx={{ mt: 0.4, height: 18, fontSize: '0.62rem' }}
                              />
                            ) : null}
                          </>
                        );
                        return (
                          <Grid size={{ xs: 6, sm: 4 }} key={f.label}>
                            <InfoField
                              label={f.label}
                              value={f.value}
                              source={stagedFieldSource(detailProduct, f.label)}
                              mono={f.mono}
                              primer={f.primer || (primerKey ? identifierPrimers[primerKey] : undefined)}
                              note={note}
                              acknowledged={ack}
                              extraChips={extraChips}
                              acknowledging={patchStagedProductMutation.isPending}
                              onAcknowledge={
                                note?.flagId && note.blocks && f.value
                                  ? () =>
                                      patchStagedProductMutation.mutate({
                                        productId: detailProduct.id,
                                        data: {
                                          acknowledgedReviewFlags: {
                                            [note.flagId!]: { value: String(f.value) },
                                          },
                                        },
                                      })
                                  : undefined
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                  </DetailSection>

                  {/* Identifier Enrichment */}
                  {(detailProduct.enrichmentAmbiguous || (detailProduct.identifierCandidates && detailProduct.identifierCandidates.length > 0)) && (
                    <DetailSection
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <IdentifierIcon fontSize="small" color={detailProduct.enrichmentAmbiguous ? 'warning' : 'primary'} />
                          <span>Identifier Candidates</span>
                          {detailProduct.enrichmentAmbiguous && (
                            <Chip label="Ambiguous" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Stack>
                      }
                      sx={{ borderColor: detailProduct.enrichmentAmbiguous ? 'warning.main' : 'divider' }}
                    >
                      {detailProduct.enrichmentAmbiguityReason && (
                        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                          <Typography variant="caption">{detailProduct.enrichmentAmbiguityReason}</Typography>
                        </Alert>
                      )}
                      <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Source</Typography></TableCell>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>MPN</Typography></TableCell>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>UPC</Typography></TableCell>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Title</Typography></TableCell>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Conf.</Typography></TableCell>
                            <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600}>Use</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(detailProduct.identifierCandidates || []).map((c, i) => (
                            <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                              <TableCell sx={{ py: 0.25 }}>
                                <Chip label={c.source} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                              </TableCell>
                              <TableCell sx={{ py: 0.25 }}><Typography variant="caption">{c.mpn || '—'}</Typography></TableCell>
                              <TableCell sx={{ py: 0.25 }}><Typography variant="caption">{c.upc || '—'}</Typography></TableCell>
                              <TableCell sx={{ py: 0.25 }}>
                                <Tooltip title={c.matchReason || ''}>
                                  <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{c.title || '—'}</Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ py: 0.25 }}>
                                <Typography variant="caption" fontWeight={600} color={c.confidence >= 0.7 ? 'success.main' : c.confidence >= 0.4 ? 'warning.main' : 'text.secondary'}>
                                  {Math.round(c.confidence * 100)}%
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.25 }}>
                                {c.upc && (
                                  <Tooltip title={`Set UPC to ${c.upc}`}>
                                    <Chip
                                      label="UPC"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.6rem', cursor: 'pointer' }}
                                      onClick={async () => {
                                        try {
                                          const headers = await getAuthHeaders();
                                          const res = await makeApiCall(`/staged-products/${detailProduct.id}`, {
                                            method: 'PATCH',
                                            headers: { ...headers, 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ upc: c.upc, enrichmentAmbiguous: false, enrichmentNeedsReview: false }),
                                          });
                                          if (res.ok) {
                                            setDetailProduct({ ...detailProduct, upc: c.upc, enrichmentAmbiguous: false, enrichmentNeedsReview: false });
                                            setDialogAlert({ type: 'success', message: `UPC set to ${c.upc}` });
                                          }
                                        } catch { /* ignore */ }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </TableContainer>
                      {detailProduct.enrichmentSource && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Best match from: {detailProduct.enrichmentSource} ({Math.round((detailProduct.enrichmentConfidence || 0) * 100)}% confidence)
                        </Typography>
                      )}
                      {detailProduct.upc && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          disabled={upcVerifyMutation.isPending}
                          onClick={() =>
                            upcVerifyMutation.mutate({ id: detailProduct.id, action: 'reject' })
                          }
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Reject this UPC
                        </Button>
                      )}
                    </DetailSection>
                  )}

                  {/* Color/Package Variants */}
                  {(() => {
                    const variants = normalizeProductVariants(detailProduct.variants);
                    if (variants.length === 0) return null;
                    return (
                    <DetailSection
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>Variants</span>
                          <Chip label={`${variants.length}`} size="small" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Stack>
                      }
                      action={
                        <Tooltip title="Remove all variant rows (e.g. wrong barcode matches)">
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={patchStagedProductMutation.isPending}
                            onClick={() => {
                              patchStagedProductMutation.mutate({
                                productId: detailProduct.id,
                                data: { variants: [] },
                              });
                            }}
                          >
                            Clear all
                          </Button>
                        </Tooltip>
                      }
                      sx={{ borderColor: 'info.light' }}
                    >
                      <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <Table size="small" sx={{ minWidth: 640, tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5, width: '34%' }}><Typography variant="caption" fontWeight={600}>{String(variants[0]?.optionName || 'Style')}</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, width: '14%' }}><Typography variant="caption" fontWeight={600}>SKU/MPN</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, width: '22%' }}><Typography variant="caption" fontWeight={600}>UPC</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, width: '22%' }}><Typography variant="caption" fontWeight={600}>EAN</Typography></TableCell>
                            <TableCell align="right" sx={{ py: 0.5, width: 48 }}><Typography variant="caption" fontWeight={600}> </Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {variants.map((v: any, i: number) => {
                            const optionLabel = String(v.optionValue || v.title || '—');
                            const isSelf = isSelfVariantRow(detailProduct, v);
                            return (
                            <TableRow key={`${optionLabel}-${i}`} sx={{
                              '&:last-child td': { borderBottom: 0 },
                              bgcolor: isSelf ? 'action.selected' : undefined,
                            }}>
                              <TableCell sx={{ py: 0.5, overflow: 'hidden' }}>
                                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                  <Tooltip title={optionLabel} disableHoverListener={optionLabel.length < 28}>
                                    <Chip
                                      label={optionLabel}
                                      size="small"
                                      variant={isSelf ? 'filled' : 'outlined'}
                                      color={isSelf ? 'primary' : 'default'}
                                      sx={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        maxWidth: '100%',
                                        justifyContent: 'flex-start',
                                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' },
                                      }}
                                    />
                                  </Tooltip>
                                  {isSelf && (
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                      This product
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, overflow: 'hidden' }}>
                                <Typography variant="caption" fontFamily="monospace" noWrap title={String(v.mpn || v.sku || '')}>
                                  {v.mpn || v.sku || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, overflow: 'hidden' }}>
                                <Typography variant="caption" fontFamily="monospace" noWrap title={String(v.upc || '')}>
                                  {v.upc || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, overflow: 'hidden' }}>
                                <Typography variant="caption" fontFamily="monospace" noWrap title={String(v.ean || '')}>
                                  {v.ean || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.25 }}>
                                <Tooltip title="Remove this row">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Remove variant"
                                      disabled={patchStagedProductMutation.isPending}
                                      onClick={() => {
                                        const next = variants.filter((_, j) => j !== i);
                                        patchStagedProductMutation.mutate({
                                          productId: detailProduct.id,
                                          data: { variants: next },
                                        });
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      </TableContainer>
                    </DetailSection>
                    );
                  })()}

                  {/* Launch Dates (editable, MOT-647) — always shown so an admin can set them when missing. */}
                  <DetailSection
                    title={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>Launch Dates</span>
                        {detailProduct.launchDates?.checkedAt && (
                          <Typography component="span" variant="caption" color="text.disabled">
                            checked {formatDate(detailProduct.launchDates.checkedAt)}
                          </Typography>
                        )}
                      </Stack>
                    }
                  >
                    <ProductLaunchDatesEditor
                      key={detailProduct.id}
                      productId={detailProduct.id}
                      launchDates={detailProduct.launchDates as ProductLaunchDatesValue | null | undefined}
                      editMode={isEditing}
                      onPendingChange={setPendingLaunchDates}
                      onInvalidChange={setLaunchDatesInvalid}
                      onSave={async (input: ProductLaunchDatesInput | null) => {
                        const headers = await getAuthHeaders();
                        const res = await makeApiCall(`/staged-products/${detailProduct.id}`, {
                          method: 'PATCH',
                          headers: { ...headers, 'Content-Type': 'application/json' },
                          body: JSON.stringify(
                            withEditMeta(
                              { launchDates: input },
                              {
                                source: 'admin_edit',
                                summary: input ? 'Updated launch dates' : 'Cleared launch dates',
                              },
                            ),
                          ),
                        });
                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(json?.error || 'Failed to save launch dates');
                        // Prefer the full returned product so updatedAt / launchDates
                        // bust the promote merge-preview cache (MOT-647).
                        const legacyLaunchDate = (value: unknown) => (value == null || value === '' ? '' : String(value));
                        if (json?.data && typeof json.data === 'object' && json.data.id) {
                          setDetailProduct(json.data);
                          // The card save rewrites legacy launchDate on the server.
                          // Keep the open panel draft on that value so its Save does not put the old one back.
                          const nextLaunchDate = legacyLaunchDate(json.data.launchDate);
                          setEditData((prev) => (Object.keys(prev).length === 0 ? prev : { ...prev, launchDate: nextLaunchDate }));
                        } else {
                          const saved = json?.data?.launchDates ?? (input ? undefined : null);
                          const nextLaunchDate = input ? (input.us?.date || input.global?.date || '') : '';
                          setDetailProduct({
                            ...detailProduct,
                            launchDates: input ? (saved ?? detailProduct.launchDates) : { source: 'manual' },
                            launchDate: nextLaunchDate || detailProduct.launchDate,
                            updatedAt: new Date().toISOString(),
                          });
                          setEditData((prev) => (Object.keys(prev).length === 0 ? prev : { ...prev, launchDate: nextLaunchDate }));
                        }
                        setDialogAlert({
                          type: 'success',
                          message: input ? 'Launch dates saved' : 'Launch dates cleared',
                        });
                        queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                      }}
                    />
                  </DetailSection>

                  {/* Pre-release lifecycle (flag `preorder_lifecycle`, MOT-143) */}
                  <DetailSection title="Pre-release lifecycle">
                    <ProductLifecycleEditor
                      key={`lifecycle-${detailProduct.id}`}
                      lifecycle={detailProduct.lifecycle}
                      launchDates={detailProduct.launchDates}
                      editMode={isEditing}
                      onPendingChange={setPendingLifecycle}
                      onInvalidChange={setLifecycleInvalid}
                      onSave={async (input) => {
                        const headers = await getAuthHeaders();
                        const res = await makeApiCall(`/staged-products/${detailProduct.id}`, {
                          method: 'PATCH',
                          headers: { ...headers, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ lifecycle: input }),
                        });
                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(json?.error || 'Failed to save the lifecycle');
                        if (json?.data && typeof json.data === 'object' && json.data.id) {
                          setDetailProduct(json.data);
                        } else {
                          const saved = json?.data?.lifecycle ?? json?.lifecycle;
                          setDetailProduct({
                            ...detailProduct,
                            lifecycle: input ? (saved ?? { ...input, source: 'admin' }) : null,
                            updatedAt: new Date().toISOString(),
                          });
                        }
                        setDialogAlert({ type: 'success', message: input ? `Lifecycle saved as ${input.stage}` : 'Lifecycle cleared' });
                        queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                      }}
                    />
                  </DetailSection>

                  {/* Pricing & Weight */}
                  <DetailSection
                    title="Pricing & Weight"
                    action={
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {!isEditing && (
                          <Tooltip title="Parse weight from specs, manuals, or a grounded AI pass with a source quote">
                            <Button
                              size="small"
                              startIcon={<WeightIcon fontSize="small" />}
                              onClick={async () => {
                                try {
                                  const headers = await getAuthHeaders();
                                  const res = await makeApiCall(`/staged-products/${detailProduct.id}/extract-weight`, {
                                    method: 'POST',
                                    headers: { ...headers, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ apply: true, includeAi: true }),
                                  });
                                  const data = await res.json();
                                  const d = data.data || data;
                                  const report = d.reportLine || formatWeightEvidenceLine(d.report);
                                  if (d.weight || d.shippingWeight) {
                                    const parts = [
                                      d.weight ? `product ${formatStagedWeightDisplay(d.weight)}` : null,
                                      d.shippingWeight
                                        ? `shipping ${formatStagedWeightDisplay(d.shippingWeight)}`
                                        : null,
                                    ].filter(Boolean);
                                    setDialogAlert({
                                      type: d.applied ? 'success' : 'warning',
                                      message: [`Extracted ${parts.join(', ')}`, report].filter(Boolean).join(' — '),
                                    });
                                  } else if (d.clearedJunk || d.report) {
                                    setDialogAlert({
                                      type: 'warning',
                                      message: report || d.message || 'Cleared unusable weight (no unit in source)',
                                    });
                                  } else {
                                    setDialogAlert({
                                      type: 'warning',
                                      message: d.message || 'No weight found in specifications',
                                    });
                                  }
                                  queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                                  setDetailProduct((prev: any) => (prev ? {
                                    ...prev,
                                    ...(d.weight ? { weight: d.weight } : d.clearedJunk ? { weight: undefined } : {}),
                                    ...(d.shippingWeight ? { shippingWeight: d.shippingWeight } : {}),
                                    ...(d.report ? { weightEvidence: d.report } : {}),
                                    ...(d.aiWeight ? { aiWeight: d.aiWeight } : {}),
                                  } : prev));
                                } catch (err) {
                                  setDialogAlert({ type: 'error', message: getErrorMessage(err) });
                                }
                              }}
                            >
                              {detailProduct.weight?.value ? 'Re-extract Weight' : 'Extract Weight'}
                            </Button>
                          </Tooltip>
                        )}
                        {isEditing && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => updateEditField('pricing', null)}
                          >
                            Clear All Pricing
                          </Button>
                        )}
                      </Stack>
                    }
                  >
                  {!isEditing && (detailProduct.pricing?.vendorPriceAsOf || detailProduct.pricing?.priceLastChecked || detailProduct.pricing?.priceAsOf) && (
                    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                      {detailProduct.pricing?.vendorPriceAsOf && (
                        <Typography variant="caption" color="text.disabled">
                          Vendor updated {formatDate(detailProduct.pricing.vendorPriceAsOf)}
                        </Typography>
                      )}
                      {detailProduct.pricing?.priceLastChecked && (
                        <Typography variant="caption" color="text.disabled">
                          Last checked {formatDate(detailProduct.pricing.priceLastChecked)}
                        </Typography>
                      )}
                      {!detailProduct.pricing?.vendorPriceAsOf && detailProduct.pricing?.priceAsOf && (
                        <Typography variant="caption" color="text.disabled">
                          Price changed {formatDate(detailProduct.pricing.priceAsOf)}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {isEditing ? (
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 3 }}>
                        <StagedMoneyField
                          label="MSRP"
                          value={editData.pricing?.msrp}
                          onChange={(next) =>
                            updateEditField('pricing', { ...editData.pricing, msrp: next })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <StagedMoneyField
                          label="Retail"
                          value={editData.pricing?.retail}
                          onChange={(next) =>
                            updateEditField('pricing', { ...editData.pricing, retail: next })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <StagedMoneyField
                          label="Sale"
                          value={editData.pricing?.salePrice}
                          onChange={(next) =>
                            updateEditField('pricing', { ...editData.pricing, salePrice: next })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <StagedWeightField
                          label="Weight"
                          helperText="Product / item weight"
                          weight={editData.weight}
                          fallbackUnit="g"
                          onChange={(next) => updateEditField('weight', next)}
                        />
                      </Grid>
                      <Grid size={{ xs: 3 }}>
                        <StagedWeightField
                          label="Shipping weight"
                          helperText="Boxed / carton weight"
                          weight={editData.shippingWeight}
                          fallbackUnit="lb"
                          onChange={(next) => updateEditField('shippingWeight', next)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoField
                          label="MSRP"
                          value={formatPrice(detailProduct.pricing?.msrp)}
                          source={stagedFieldSource(detailProduct, 'MSRP')}
                        />
                        {detailProduct.pricing?.msrpAsOf && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            as of {formatDate(detailProduct.pricing.msrpAsOf)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoField
                          label="Retail"
                          value={formatPrice(detailProduct.pricing?.retail)}
                          source={stagedFieldSource(detailProduct, 'Retail')}
                        />
                        {detailProduct.pricing?.retailAsOf && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            as of {formatDate(detailProduct.pricing.retailAsOf)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoField
                          label="Sale"
                          value={formatPrice(detailProduct.pricing?.salePrice)}
                          source={stagedFieldSource(detailProduct, 'Sale')}
                        />
                        {detailProduct.pricing?.salePriceAsOf && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            as of {formatDate(detailProduct.pricing.salePriceAsOf)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoField
                          label="Weight"
                          value={detailProduct.weight ? formatStagedWeightDisplay(detailProduct.weight) : undefined}
                          source={stagedFieldSource(detailProduct, 'Weight')}
                          extraChips={
                            detailProduct.aiWeight &&
                            !(Number(detailProduct.weight?.value) > 0) ? (
                              <Button
                                size="small"
                                color="inherit"
                                disabled={saveEditMutation.isPending}
                                onClick={() => {
                                  const suggested = detailProduct.aiWeight!;
                                  saveEditMutation.mutate({
                                    productId: detailProduct.id,
                                    data: withEditMeta(
                                      {
                                        weight: suggested,
                                        weightEvidence: {
                                          ...(detailProduct.weightEvidence || {}),
                                          method: 'ai',
                                          applied: true,
                                          value: suggested.value,
                                          unit: suggested.unit,
                                          quote: suggested.raw,
                                        },
                                        aiWeight: null,
                                      },
                                      {
                                        source: 'ai_suggestion',
                                        summary: `Accepted AI weight ${suggested.value} ${suggested.unit}`,
                                      },
                                    ),
                                  });
                                }}
                                sx={{ mt: 0.25, px: 0.5, minHeight: 22, fontSize: 10.5, textTransform: 'none' }}
                              >
                                Accept {detailProduct.aiWeight.value} {detailProduct.aiWeight.unit}
                              </Button>
                            ) : undefined
                          }
                        />
                        {detailProduct.weightEvidence?.quote && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.35, fontSize: 10.5, lineHeight: 1.35 }}
                          >
                            {formatWeightEvidenceLine(detailProduct.weightEvidence)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoField
                          label="Shipping weight"
                          value={
                            detailProduct.shippingWeight
                              ? formatStagedWeightDisplay(detailProduct.shippingWeight)
                              : undefined
                          }
                          source={stagedFieldSource(detailProduct, 'Shipping Weight')}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/*
                    The capture published several different boxed weights for sibling
                    variants and none could be tied to this row's SKU, so a human picks.
                    Nothing is applied until they do — a guess here would go on to quote
                    carrier rates.
                  */}
                  {(detailProduct.shippingWeightCandidates || []).length > 0 && (
                    <Alert
                      severity="warning"
                      sx={{ mt: 1.5, '& .MuiAlert-message': { width: '100%' } }}
                      action={
                        <Button
                          size="small"
                          color="inherit"
                          disabled={saveEditMutation.isPending}
                          sx={{ fontSize: '0.7rem' }}
                          onClick={() => {
                            saveEditMutation.mutate({
                              productId: detailProduct.id,
                              data: withEditMeta(
                                { shippingWeightCandidates: null },
                                {
                                  source: 'admin_edit',
                                  summary: 'Dismissed ambiguous variant shipping weights',
                                },
                              ),
                            });
                          }}
                        >
                          Dismiss
                        </Button>
                      }
                    >
                      <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                        Shipping weight needs review — variants disagree
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        The source page listed a different boxed weight per variant and none
                        matched this SKU. Pick the one for this product.
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(detailProduct.shippingWeightCandidates || []).map((candidate, i) => (
                          <Button
                            key={i}
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={saveEditMutation.isPending}
                            sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                            onClick={() => {
                              saveEditMutation.mutate({
                                productId: detailProduct.id,
                                data: withEditMeta(
                                  {
                                    shippingWeight: {
                                      value: candidate.value,
                                      unit: candidate.unit,
                                      raw: candidate.raw,
                                    },
                                    shippingWeightCandidates: null,
                                  },
                                  {
                                    source: 'admin_edit',
                                    summary: `Accepted variant shipping weight ${candidate.raw}`,
                                  },
                                ),
                              });
                            }}
                          >
                            {formatStagedWeightDisplay(candidate)}
                            {candidate.variantSku || candidate.variantTitle
                              ? ` — ${candidate.variantSku || candidate.variantTitle}`
                              : ''}
                          </Button>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                  </DetailSection>

                  {/* Market Pricing */}
                  {detailProduct.marketPricing && (
                    <DetailSection
                      title={
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <span>Market Pricing</span>
                          {detailProduct.marketPricing.checkedAt && (
                            <Typography
                              component="span"
                              variant="caption"
                              color={isMarketPricingStale(detailProduct.marketPricing) ? 'warning.dark' : 'text.disabled'}
                            >
                              checked {formatRelativeDate(detailProduct.marketPricing.checkedAt) || formatDate(detailProduct.marketPricing.checkedAt)}
                            </Typography>
                          )}
                          {isMarketPricingStale(detailProduct.marketPricing) && (
                            <Chip size="small" color="warning" variant="outlined" label="Stale" />
                          )}
                        </Stack>
                      }
                    >
                        {isMarketPricingStale(detailProduct.marketPricing) && (
                          <Alert severity="warning" sx={{ mb: 1.5 }}>
                            Street prices are stale
                            {detailProduct.marketPricing.checkedAt
                              ? ` (${formatRelativeDate(detailProduct.marketPricing.checkedAt) || formatDate(detailProduct.marketPricing.checkedAt)})`
                              : ''}
                            . Refresh before using them for MAP or list-price decisions.
                          </Alert>
                        )}
                        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">MSRP (Market)</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {detailProduct.marketPricing.msrp ? formatPrice(detailProduct.marketPricing.msrp) : '—'}
                            </Typography>
                            {detailProduct.marketPricing.msrpSource && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                                via {detailProduct.marketPricing.msrpSource}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Street Low</Typography>
                            <Typography
                              variant="body2"
                              color={isMarketPricingStale(detailProduct.marketPricing) ? 'text.primary' : 'success.main'}
                              fontWeight={600}
                            >
                              {detailProduct.marketPricing.streetPriceLow ? formatPrice(detailProduct.marketPricing.streetPriceLow) : '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Street High</Typography>
                            <Typography variant="body2">
                              {detailProduct.marketPricing.streetPriceHigh ? formatPrice(detailProduct.marketPricing.streetPriceHigh) : '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Avg</Typography>
                            <Typography variant="body2">
                              {detailProduct.marketPricing.streetPriceAvg ? formatPrice(detailProduct.marketPricing.streetPriceAvg) : '—'}
                            </Typography>
                          </Box>
                        </Stack>
                        {Array.isArray(detailProduct.marketPricing.dealerPrices) && detailProduct.marketPricing.dealerPrices.length > 0 && (
                          <Box component="table" sx={{ width: '100%', '& td, & th': { py: 0.25, pr: 1.5, verticalAlign: 'top' } }}>
                            <thead>
                              <tr>
                                <Box component="th" sx={{ textAlign: 'left' }}><Typography variant="caption" fontWeight={600}>Dealer</Typography></Box>
                                <Box component="th" sx={{ textAlign: 'right' }}><Typography variant="caption" fontWeight={600}>Price</Typography></Box>
                                <Box component="th" sx={{ textAlign: 'center' }}><Typography variant="caption" fontWeight={600}>Stock</Typography></Box>
                              </tr>
                            </thead>
                            <tbody>
                              {detailProduct.marketPricing.dealerPrices.map((dp, i) => {
                                const validatedUrl = getDealerUrl(dp.dealer, dp.url, detailProduct.brand, detailProduct.model || detailProduct.sku || '');
                                return (
                                <tr key={i}>
                                  <td>
                                    {validatedUrl ? (
                                      <Typography variant="caption" component="a" href={validatedUrl} target="_blank" rel="noopener" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                        {dp.dealer}
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption">{dp.dealer}</Typography>
                                    )}
                                  </td>
                                  <Box component="td" sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" fontWeight={500}>{formatPrice(dp.price)}</Typography>
                                  </Box>
                                  <Box component="td" sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color={dp.inStock ? 'success.main' : 'error.main'}>
                                      {dp.inStock == null ? '—' : dp.inStock ? 'In Stock' : 'Out'}
                                    </Typography>
                                  </Box>
                                </tr>
                                );
                              })}
                            </tbody>
                          </Box>
                        )}
                    </DetailSection>
                  )}

                  {/* Description */}
                  <DetailSection
                    title={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>Description</span>
                        {!isEditing && detailProduct.verification?.scores?.descriptionQuality != null && (
                          <Chip
                            size="small"
                            label={`${detailProduct.verification.scores.descriptionQuality}%`}
                            color={detailProduct.verification.scores.descriptionQuality >= 70 ? 'success' : detailProduct.verification.scores.descriptionQuality >= 40 ? 'warning' : 'error'}
                            variant="outlined"
                            sx={{ height: 22 }}
                          />
                        )}
                      </Stack>
                    }
                    action={
                      !isEditing ? (
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          {detailProduct.parentSku && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={copyFromParentMutation.isPending ? <CircularProgress size={14} /> : <CopyFromParentIcon />}
                              disabled={copyFromParentMutation.isPending}
                              onClick={() => copyFromParentMutation.mutate(detailProduct.id)}
                            >
                              {copyFromParentMutation.isPending ? 'Copying...' : 'Copy from parent'}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={generateDescMutation.isPending ? <CircularProgress size={14} /> : <GenerateIcon />}
                            disabled={generateDescMutation.isPending}
                            onClick={() => generateDescMutation.mutate(detailProduct.id)}
                          >
                            {generateDescMutation.isPending ? 'Generating...' : 'Generate AI Description'}
                          </Button>
                        </Stack>
                      ) : undefined
                    }
                  >

                    {isEditing ? (
                      <Stack spacing={1}>
                        <TextField fullWidth size="small" label="Description" multiline rows={4} value={editData.description || ''} onChange={(e) => updateEditField('description', e.target.value)} />
                        <TextField
                          fullWidth
                          size="small"
                          label="Short Description"
                          value={editData.shortDescription || ''}
                          onChange={(e) => updateEditField('shortDescription', e.target.value)}
                          error={(editData.shortDescription || '').trim().length > STAGED_SHORT_DESCRIPTION_MAX_CHARS}
                          helperText={`${(editData.shortDescription || '').trim().length}/${STAGED_SHORT_DESCRIPTION_MAX_CHARS} — SEO meta description`}
                        />
                      </Stack>
                    ) : (
                      <>
                        {/* Active Description */}
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.75, mb: 1, borderRadius: 1.5, bgcolor: 'grey.50', borderColor: 'divider' }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}
                            >
                              Active Description
                            </Typography>
                            <StagedSourceBadge
                              source={
                                catalogText(detailProduct.description)
                                  ? stagedFieldSource(detailProduct, 'Description')
                                  : { source: 'missing' }
                              }
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ maxHeight: 140, overflow: 'auto', whiteSpace: 'pre-line', lineHeight: 1.55 }}>
                            {catalogText(detailProduct.description) || <em>No description</em>}
                          </Typography>
                        </Paper>
                        {(() => {
                          const catalogShort = String(detailProduct.shortDescription || '').trim();
                          const aiShort = String(detailProduct.aiShortDescription || '').trim();
                          // Applying an over-length AI short stores the clamped
                          // version, so a clamped match means already accepted.
                          const aiShortApplied =
                            aiShort === catalogShort || clampShortDescription(aiShort) === catalogShort;
                          const shortOver = catalogShort.length > STAGED_SHORT_DESCRIPTION_MAX_CHARS;
                          return (
                            <Paper
                              variant="outlined"
                              sx={{ p: 1.75, mb: 1, borderRadius: 1.5, bgcolor: 'grey.50', borderColor: shortOver ? 'warning.light' : 'divider' }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  color="text.secondary"
                                  sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}
                                >
                                  Short Description
                                </Typography>
                                <StagedSourceBadge
                                  source={
                                    catalogShort
                                      ? stagedFieldSource(detailProduct, 'Short Description')
                                      : { source: 'missing' }
                                  }
                                />
                                {catalogShort ? (
                                  <Chip
                                    size="small"
                                    label={`${catalogShort.length}/${STAGED_SHORT_DESCRIPTION_MAX_CHARS}`}
                                    color={shortOver ? 'warning' : 'default'}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                                  />
                                ) : null}
                              </Stack>
                              <Typography variant="body2" sx={{ lineHeight: 1.55, color: catalogShort ? 'text.primary' : 'text.secondary' }}>
                                {catalogShort || <em>No short description</em>}
                              </Typography>
                              {aiShort && !aiShortApplied && (
                                <Alert
                                  severity="info"
                                  icon={<AIIcon fontSize="small" />}
                                  sx={{ mt: 1.25, '& .MuiAlert-message': { width: '100%' } }}
                                  action={
                                    <Button
                                      size="small"
                                      color="success"
                                      variant="contained"
                                      disabled={saveEditMutation.isPending}
                                      sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                      onClick={() => {
                                        const nextShort = clampShortDescription(aiShort);
                                        const patch = snapshotScrapedFieldsIfNeeded(detailProduct, {
                                          shortDescription: nextShort,
                                        });
                                        saveEditMutation.mutate({
                                          productId: detailProduct.id,
                                          data: withEditMeta(patch, {
                                            source: 'ai_suggestion',
                                            summary: 'Applied AI short description',
                                            clearedIssueFields: ['shortDescription', 'Description.Short'],
                                          }),
                                        });
                                      }}
                                    >
                                      Use AI Short
                                    </Button>
                                  }
                                >
                                  <Typography variant="caption" fontWeight="bold">
                                    AI short — {aiShort.length}/{STAGED_SHORT_DESCRIPTION_MAX_CHARS}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {aiShort}
                                  </Typography>
                                </Alert>
                              )}
                            </Paper>
                          );
                        })()}

                        {/* AI Suggestion (only show if AI content exists and differs from active) */}
                        {catalogText(detailProduct.aiDescription) &&
                          catalogText(detailProduct.aiDescription) !== catalogText(detailProduct.description) && (
                          <Alert
                            severity="info"
                            icon={<AIIcon fontSize="small" />}
                            sx={{ mb: 1, '& .MuiAlert-message': { width: '100%' } }}
                            action={
                              <Button
                                size="small"
                                color="success"
                                variant="contained"
                                disabled={saveEditMutation.isPending}
                                sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  const patch = snapshotScrapedFieldsIfNeeded(detailProduct, {
                                    description: catalogText(detailProduct.aiDescription),
                                    descriptionSource: 'ai_generated',
                                    ...(catalogText(detailProduct.aiShortDescription)
                                      ? { shortDescription: clampShortDescription(catalogText(detailProduct.aiShortDescription)) }
                                      : {}),
                                  });
                                  saveEditMutation.mutate({
                                    productId: detailProduct.id,
                                    data: withEditMeta(patch, {
                                      source: 'ai_suggestion',
                                      summary: 'Applied AI description draft',
                                      clearedIssueFields: ['Description', 'Description.Short'],
                                    }),
                                  });
                                }}
                              >
                                Use AI Version
                              </Button>
                            }
                          >
                            <Typography variant="caption" fontWeight="bold">AI Suggestion — Not yet applied</Typography>
                            <Typography variant="body2" sx={{ maxHeight: 100, overflow: 'auto', mt: 0.5 }}>
                              {catalogText(detailProduct.aiDescription)}
                            </Typography>
                          </Alert>
                        )}
                        {/* Show confirmation when AI content IS the active description */}
                        {catalogText(detailProduct.aiDescription) &&
                          catalogText(detailProduct.aiDescription) === catalogText(detailProduct.description) && (
                          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SuccessOutlineIcon sx={{ fontSize: 14 }} /> AI description is active
                          </Typography>
                        )}
                        {!!catalogText(detailProduct.scrapedDescription).trim() &&
                          catalogText(detailProduct.scrapedDescription).trim() !==
                            catalogText(detailProduct.description).trim() && (
                          <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 1 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={600}>
                                Scraped original (backup)
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={saveEditMutation.isPending}
                                onClick={() => {
                                  saveEditMutation.mutate({
                                    productId: detailProduct.id,
                                    data: withEditMeta(
                                      {
                                        description: detailProduct.scrapedDescription,
                                        descriptionSource: 'scraped',
                                        ...(detailProduct.scrapedShortDescription
                                          ? { shortDescription: detailProduct.scrapedShortDescription }
                                          : {}),
                                      },
                                      {
                                        source: 'admin_edit',
                                        summary: 'Restored scraped description backup',
                                      },
                                    ),
                                  });
                                }}
                              >
                                Restore scraped
                              </Button>
                            </Stack>
                            <Typography variant="body2" sx={{ maxHeight: 100, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                              {detailProduct.scrapedDescription}
                            </Typography>
                          </Alert>
                        )}
                      </>
                    )}
                  </DetailSection>

                  {/* Features — catalog `features` vs AI draft `aiFeatures` (avoid duplicate lists when identical) */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title="Features"
                        extra={
                          <Typography component="span" variant="caption" color="text.secondary" fontWeight={500}>
                            {isEditing
                              ? `(${(editData.features || []).length || 0})`
                              : stagedFeaturesDisplay.feat.length > 0 && stagedFeaturesDisplay.ai.length > 0 && !stagedFeaturesDisplay.identical
                                ? `(${stagedFeaturesDisplay.feat.length} catalog · ${stagedFeaturesDisplay.ai.length} AI draft)`
                                : `(${
                                    stagedFeaturesDisplay.identical
                                      ? stagedFeaturesDisplay.feat.length
                                      : stagedFeaturesDisplay.feat.length || stagedFeaturesDisplay.ai.length
                                  })`}
                          </Typography>
                        }
                        source={stagedSectionSource(
                          detailProduct,
                          'Features',
                          (isEditing ? (editData.features || []).length : stagedFeaturesDisplay.feat.length) > 0,
                        )}
                      />
                    }
                    action={
                      isEditing ? (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addToEditArray('features', '')}>
                          Add
                        </Button>
                      ) : undefined
                    }
                  >
                    {!isEditing &&
                      stagedFeaturesDisplay.feat.length > 0 &&
                      stagedFeaturesDisplay.ai.length > 0 &&
                      stagedFeaturesDisplay.identical && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <SuccessOutlineIcon sx={{ fontSize: 14 }} /> Catalog matches AI draft
                        </Typography>
                      )}
                    {isEditing ? (
                      <Stack spacing={0.5}>
                        {(editData.features || []).map((f: string, i: number) => (
                          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                            <TextField fullWidth size="small" value={f} onChange={(e) => { const arr = [...editData.features]; arr[i] = e.target.value; updateEditField('features', arr); }} />
                            <IconButton size="small" color="error" onClick={() => removeFromEditArray('features', i)}><DeleteIcon fontSize="small" /></IconButton>
                          </Stack>
                        ))}
                        {asStringArray(detailProduct.aiFeatures).length > 0 && (!editData.features || editData.features.length === 0 || editData.features.every((f: string) => !f)) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<AIIcon fontSize="small" />}
                            onClick={() => updateEditField('features', [...asStringArray(detailProduct.aiFeatures)])}
                          >
                            Use AI Features
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <>
                        {stagedFeaturesDisplay.identical && stagedFeaturesDisplay.feat.length > 0 && (
                          <Box component="ul" sx={{ pl: 2, maxHeight: 160, overflow: 'auto' }}>
                            {stagedFeaturesDisplay.feat.map((f, i) => (
                              <li key={i}><Typography variant="body2">{f}</Typography></li>
                            ))}
                          </Box>
                        )}
                        {!stagedFeaturesDisplay.identical &&
                          stagedFeaturesDisplay.feat.length > 0 &&
                          stagedFeaturesDisplay.ai.length > 0 && (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                                Catalog / scraped ({stagedFeaturesDisplay.feat.length})
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Stored on this staged record and used when promoted (unless you replace it).
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, maxHeight: 160, overflow: 'auto' }}>
                                {stagedFeaturesDisplay.feat.map((f, i) => (
                                  <li key={i}><Typography variant="body2">{f}</Typography></li>
                                ))}
                              </Box>
                            </Box>
                            <Alert severity="info" variant="outlined" icon={<AIIcon fontSize="small" />} sx={{ py: 1 }}>
                              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                                AI draft ({stagedFeaturesDisplay.ai.length}) — different wording from catalog
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                The AI draft is stored separately from catalog features. Use the button to replace catalog with this draft, or edit either list in edit mode.
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => {
                                    saveEditMutation.mutate({
                                      productId: detailProduct.id,
                                      data: withEditMeta(
                                        snapshotScrapedFieldsIfNeeded(detailProduct, {
                                          features: [...stagedFeaturesDisplay.ai],
                                        }),
                                        {
                                          source: 'ai_suggestion',
                                          summary: 'Replaced catalog features with AI draft',
                                          clearedIssueFields: ['Features', 'FeaturesCount'],
                                        },
                                      ),
                                    });
                                  }}
                                  disabled={saveEditMutation.isPending}
                                >
                                  Replace catalog with this draft
                                </Button>
                              </Stack>
                              <Box component="ul" sx={{ pl: 2, m: 0, maxHeight: 160, overflow: 'auto' }}>
                                {stagedFeaturesDisplay.ai.map((f, i) => (
                                  <li key={i}><Typography variant="body2">{f}</Typography></li>
                                ))}
                              </Box>
                            </Alert>
                          </>
                        )}
                        {!stagedFeaturesDisplay.identical &&
                          stagedFeaturesDisplay.feat.length > 0 &&
                          stagedFeaturesDisplay.ai.length === 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              Catalog features only (no separate AI draft).
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, maxHeight: 160, overflow: 'auto' }}>
                              {stagedFeaturesDisplay.feat.map((f, i) => (
                                <li key={i}><Typography variant="body2">{f}</Typography></li>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {!stagedFeaturesDisplay.identical &&
                          stagedFeaturesDisplay.feat.length === 0 &&
                          stagedFeaturesDisplay.ai.length > 0 && (
                          <Alert severity="info" variant="outlined" icon={<AIIcon fontSize="small" />} sx={{ py: 1 }}>
                            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                              AI draft only — catalog features are empty
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Accept to copy this draft into catalog features.
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                  saveEditMutation.mutate({
                                    productId: detailProduct.id,
                                    data: withEditMeta(
                                      { features: [...stagedFeaturesDisplay.ai] },
                                      {
                                        source: 'ai_suggestion',
                                        summary: 'Accepted AI features as catalog features',
                                        clearedIssueFields: ['Features', 'FeaturesCount'],
                                      },
                                    ),
                                  });
                                }}
                                disabled={saveEditMutation.isPending}
                              >
                                Accept as catalog features
                              </Button>
                            </Stack>
                            <Box component="ul" sx={{ pl: 2, m: 0, maxHeight: 160, overflow: 'auto' }}>
                              {stagedFeaturesDisplay.ai.map((f, i) => (
                                <li key={i}><Typography variant="body2">{f}</Typography></li>
                              ))}
                            </Box>
                          </Alert>
                        )}
                        {stagedFeaturesDisplay.feat.length === 0 && stagedFeaturesDisplay.ai.length === 0 && (
                          <Typography variant="body2" color="text.secondary">No features listed.</Typography>
                        )}
                      </>
                    )}
                  </DetailSection>

                  {/* Highlights (marketing bullets; distinct from Features) */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`Highlights (${(isEditing ? editData.highlights : asStringArray(detailProduct.highlights)).length || 0})`}
                        source={stagedSectionSource(
                          detailProduct,
                          'Highlights',
                          (isEditing ? (editData.highlights || []).length : asStringArray(detailProduct.highlights).length) > 0,
                        )}
                      />
                    }
                    action={
                      isEditing ? (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addToEditArray('highlights', '')}>
                          Add
                        </Button>
                      ) : undefined
                    }
                  >
                    {isEditing ? (
                      <Stack spacing={0.5}>
                        {(editData.highlights || []).map((h: string, i: number) => (
                          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                            <TextField
                              fullWidth
                              size="small"
                              value={h}
                              onChange={(e) => {
                                const arr = [...(editData.highlights || [])];
                                arr[i] = e.target.value;
                                updateEditField('highlights', arr);
                              }}
                            />
                            <IconButton size="small" color="error" onClick={() => removeFromEditArray('highlights', i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                        {(editData.highlights || []).length === 0 && (
                          <Typography variant="body2" color="text.secondary">No highlights. Add short marketing bullets here.</Typography>
                        )}
                      </Stack>
                    ) : asStringArray(detailProduct.highlights).length > 0 ? (
                      <Box component="ul" sx={{ pl: 2, maxHeight: 160, overflow: 'auto' }}>
                        {asStringArray(detailProduct.highlights).map((h, i) => (
                          <li key={i}><Typography variant="body2">{h}</Typography></li>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No highlights listed.</Typography>
                    )}
                  </DetailSection>

                  {/* Documentation / Manuals */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`Documentation (${(isEditing ? editData.documentation : asObjectArray<StagedProductDoc>(detailProduct.documentation))?.length || 0})`}
                        source={stagedSectionSource(
                          detailProduct,
                          'Documentation',
                          asObjectArray<StagedProductDoc>(
                            isEditing ? editData.documentation : detailProduct.documentation,
                          ).length > 0,
                        )}
                      />
                    }
                    action={
                      isEditing ? (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addToEditArray('documentation', { url: '', title: '', type: 'manual' })}>
                          Add
                        </Button>
                      ) : undefined
                    }
                  >
                    {isEditing ? (
                      <Stack spacing={1}>
                        {(editData.documentation || []).map((doc: any, i: number) => (
                          <Paper key={i} variant="outlined" sx={{ p: 1 }}>
                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <TextField fullWidth size="small" label="Title" value={doc.title || ''} onChange={(e) => updateEditArrayItem('documentation', i, { title: e.target.value })} />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select value={doc.type || 'manual'} onChange={(e) => updateEditArrayItem('documentation', i, { type: e.target.value })}>
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="firmware">Firmware</MenuItem>
                                    <MenuItem value="driver">Driver</MenuItem>
                                    <MenuItem value="spec-sheet">Spec Sheet</MenuItem>
                                    <MenuItem value="brochure">Brochure</MenuItem>
                                    <MenuItem value="support-page">Support Page</MenuItem>
                                    <MenuItem value="warranty">Warranty</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                  </Select>
                                </FormControl>
                                <IconButton size="small" color="error" onClick={() => removeFromEditArray('documentation', i)}><DeleteIcon fontSize="small" /></IconButton>
                              </Stack>
                              <TextField fullWidth size="small" label="URL" value={doc.url || ''} onChange={(e) => updateEditArrayItem('documentation', i, { url: e.target.value })} />
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <>
                        {asObjectArray<StagedProductDoc>(detailProduct.documentation).length > 0 ? (
                          <Stack spacing={0.5}>
                            {asObjectArray<StagedProductDoc>(detailProduct.documentation).map((doc, i) => (
                              <Stack key={i} direction="row" spacing={1} alignItems="center">
                                <Chip label={doc.type ?? ''} size="small" variant="outlined" sx={{ minWidth: 70 }} />
                                <Typography variant="body2" component="a" href={doc.url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>
                                  {doc.title || doc.url}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No documentation attached</Typography>
                        )}
                      </>
                    )}
                  </DetailSection>

                  {/* Videos */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`Videos (${(isEditing ? editData.videos : asObjectArray<StagedProductVideo>(detailProduct.videos))?.length || 0})`}
                        source={stagedSectionSource(
                          detailProduct,
                          'Videos',
                          asObjectArray<StagedProductVideo>(
                            isEditing ? editData.videos : detailProduct.videos,
                          ).length > 0,
                        )}
                      />
                    }
                    action={
                      isEditing ? (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addToEditArray('videos', { url: '', title: '', type: 'product' })}>
                          Add
                        </Button>
                      ) : undefined
                    }
                  >
                    {isEditing ? (
                      <Stack spacing={1}>
                        {(editData.videos || []).map((vid: any, i: number) => (
                          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                            <TextField size="small" label="Title" value={vid.title || ''} onChange={(e) => updateEditArrayItem('videos', i, { title: e.target.value })} sx={{ flex: 1 }} />
                            <TextField size="small" label="URL" value={vid.url || ''} onChange={(e) => updateEditArrayItem('videos', i, { url: e.target.value })} sx={{ flex: 2 }} />
                            <IconButton size="small" color="error" onClick={() => removeFromEditArray('videos', i)}><DeleteIcon fontSize="small" /></IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <>
                        {asObjectArray<StagedProductVideo>(detailProduct.videos).length > 0 ? (
                          <Stack spacing={0.5}>
                            {asObjectArray<StagedProductVideo>(detailProduct.videos).map((vid, i) => (
                              <Typography key={i} variant="body2" component="a" href={vid.url ?? '#'} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: 'primary.main' }}>
                                {vid.title || vid.url || ''}
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No videos attached</Typography>
                        )}
                      </>
                    )}
                  </DetailSection>

                  {detailProduct.isBundle && (
                    <DetailSection
                      title={`Bundle components (${asObjectArray<StagedBundleComponentRow>(detailProduct.bundleComponents).length})`}
                    >
                      <StagedBundleComponents
                        resolution={detailProduct.bundleResolution}
                        reason={detailProduct.bundleResolutionReason}
                        optionName={detailProduct.bundleOptionName}
                        contentsText={detailProduct.bundleContentsText}
                        components={asObjectArray<StagedBundleComponentRow>(detailProduct.bundleComponents)}
                        componentTotal={detailProduct.bundleComponentTotal}
                        compareAtPrice={detailProduct.bundleCompareAtPrice}
                      />
                    </DetailSection>
                  )}

                  {/* In The Box */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`In The Box (${(isEditing ? editData.inTheBox : asObjectArray<StagedProductBoxItem>(detailProduct.inTheBox))?.length || 0})`}
                        source={stagedSectionSource(
                          detailProduct,
                          'In The Box',
                          asObjectArray<StagedProductBoxItem>(
                            isEditing ? editData.inTheBox : detailProduct.inTheBox,
                          ).length > 0,
                        )}
                      />
                    }
                    action={
                      <Stack direction="row" spacing={0.5}>
                        {isEditing && (
                          <Button size="small" startIcon={<AddIcon />} onClick={() => addToEditArray('inTheBox', { name: '', quantity: 1 })}>
                            Add
                          </Button>
                        )}
                        {!isEditing && (
                          <>
                            <Tooltip title="Extract from specs, description, or documentation PDFs">
                              <Button
                                size="small"
                                disabled={!!inTheBoxBusy}
                                startIcon={
                                  inTheBoxBusy === 'extract' ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <BoxIcon fontSize="small" />
                                  )
                                }
                                onClick={async () => {
                                  setInTheBoxBusy('extract');
                                  setInTheBoxNotice(null);
                                  try {
                                    const headers = await getAuthHeaders();
                                    const res = await makeApiCall(`/staged-products/${detailProduct.id}/extract-in-the-box`, {
                                      method: 'POST',
                                      headers: { ...headers, 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ apply: true }),
                                    });
                                    const data = await res.json();
                                    const d = data.data || data;
                                    if (d.found && d.applied === false && d.message) {
                                      const notice = { type: 'warning' as const, message: d.message };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      if (d.descriptionUpdated) {
                                        queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                                        setDetailProduct((prev: any) =>
                                          prev
                                            ? {
                                                ...prev,
                                                ...(typeof d.description === 'string'
                                                  ? { description: d.description }
                                                  : {}),
                                              }
                                            : prev,
                                        );
                                      }
                                    } else if (d.found) {
                                      const notice = {
                                        type: 'success' as const,
                                        message: `Extracted ${d.inTheBox?.length || 0} items from ${d.source}`,
                                      };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                                      setDetailProduct((prev: any) =>
                                        prev
                                          ? {
                                              ...prev,
                                              inTheBox: d.inTheBox,
                                              ...(typeof d.description === 'string'
                                                ? { description: d.description }
                                                : {}),
                                              ...(d.verification !== undefined
                                                ? { verification: d.verification }
                                                : {}),
                                            }
                                          : prev,
                                      );
                                    } else {
                                      const notice = {
                                        type: 'warning' as const,
                                        message:
                                          'No "In The Box" data found in specs, description, or documentation PDFs',
                                      };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                    }
                                  } catch (err) {
                                    const notice = { type: 'error' as const, message: getErrorMessage(err) };
                                    setInTheBoxNotice(notice);
                                    setToastMessage(notice);
                                  } finally {
                                    setInTheBoxBusy(null);
                                  }
                                }}
                              >
                                Extract
                              </Button>
                            </Tooltip>
                            {isPlatformAdmin && (
                              <Tooltip title="Fill In the Box from B&H Items Included. A longer list replaces the current box. Specs are left alone.">
                                <span>
                                  <Button
                                    size="small"
                                    disabled={bhFillMutation.isPending || !!inTheBoxBusy}
                                    startIcon={
                                      bhFillMutation.isPending ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : (
                                        <BhFillIcon fontSize="small" />
                                      )
                                    }
                                    onClick={() => bhFillMutation.mutate({
                                      id: detailProduct.id,
                                      writeOnly: ['inTheBox'],
                                    })}
                                  >
                                    {bhFillMutation.isPending ? 'Queuing...' : '3P Backfill'}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Try extract first; if nothing found, ask AI for a grounded suggestion (not auto-applied)">
                              <Button
                                size="small"
                                disabled={!!inTheBoxBusy}
                                startIcon={
                                  inTheBoxBusy === 'suggest' ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <AIIcon fontSize="small" />
                                  )
                                }
                                onClick={async () => {
                                  setInTheBoxBusy('suggest');
                                  setInTheBoxNotice(null);
                                  try {
                                    const headers = await getAuthHeaders();
                                    const res = await makeApiCall(`/staged-products/${detailProduct.id}/suggest-in-the-box`, {
                                      method: 'POST',
                                      headers: { ...headers, 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ apply: true }),
                                    });
                                    const data = await res.json();
                                    const d = data.data || data;
                                    if (!d.found) {
                                      const notice = {
                                        type: 'warning' as const,
                                        message: d.message || 'No In The Box suggestion available',
                                      };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      return;
                                    }
                                    if (d.mode === 'extract' && d.applied === false && d.message) {
                                      const notice = { type: 'warning' as const, message: d.message };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      if (d.descriptionUpdated && typeof d.description === 'string') {
                                        setDetailProduct((prev: any) =>
                                          prev ? { ...prev, description: d.description } : prev,
                                        );
                                      }
                                    } else if (d.mode === 'extract') {
                                      const notice = {
                                        type: 'success' as const,
                                        message: `Extracted ${d.inTheBox?.length || 0} items from ${d.source}`,
                                      };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      setDetailProduct((prev: any) =>
                                        prev
                                          ? {
                                              ...prev,
                                              inTheBox: d.inTheBox,
                                              ...(typeof d.description === 'string'
                                                ? { description: d.description }
                                                : {}),
                                              ...(d.verification !== undefined
                                                ? { verification: d.verification }
                                                : {}),
                                            }
                                          : prev,
                                      );
                                    } else {
                                      const conf =
                                        typeof d.confidence === 'number'
                                          ? ` (${Math.round(d.confidence * 100)}% confidence)`
                                          : '';
                                      const notice = {
                                        type: 'success' as const,
                                        message: `AI suggested ${d.aiInTheBox?.length || 0} items${conf} — review and Accept`,
                                      };
                                      setInTheBoxNotice(notice);
                                      setToastMessage(notice);
                                      setDetailProduct((prev: any) =>
                                        prev
                                          ? {
                                              ...prev,
                                              aiInTheBox: d.aiInTheBox,
                                              aiContentConfidence: {
                                                ...(prev.aiContentConfidence || {}),
                                                inTheBox: d.confidence,
                                              },
                                            }
                                          : prev,
                                      );
                                    }
                                    queryClient.invalidateQueries({ queryKey: ['staged-products'] });
                                  } catch (err) {
                                    const notice = { type: 'error' as const, message: getErrorMessage(err) };
                                    setInTheBoxNotice(notice);
                                    setToastMessage(notice);
                                  } finally {
                                    setInTheBoxBusy(null);
                                  }
                                }}
                              >
                                Suggest AI
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    }
                  >
                    <Stack spacing={1}>
                    {inTheBoxNotice && (
                      <Alert
                        severity={inTheBoxNotice.type}
                        onClose={() => setInTheBoxNotice(null)}
                      >
                        {inTheBoxNotice.message}
                      </Alert>
                    )}
                    {isEditing ? (
                      <Stack spacing={0.5}>
                        {(editData.inTheBox || []).map((item: any, i: number) => (
                          <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                            <TextField size="small" label="Item" value={item.name || ''} onChange={(e) => updateEditArrayItem('inTheBox', i, { name: e.target.value })} sx={{ flex: 3 }} />
                            <NumberField label="Qty" integer min={1} value={String(item.quantity ?? 1)} onChange={(v) => updateEditArrayItem('inTheBox', i, { quantity: parseInt(v, 10) || 1 })} sx={{ width: 90 }} />
                            <IconButton size="small" color="error" onClick={() => removeFromEditArray('inTheBox', i)}><DeleteIcon fontSize="small" /></IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <>
                        {asObjectArray<StagedProductBoxItem>(detailProduct.inTheBox).length > 0 ? (
                          <Box component="ul" sx={{ pl: 2, mb: asObjectArray<StagedProductBoxItem>(detailProduct.aiInTheBox).length ? 1 : 0 }}>
                            {asObjectArray<StagedProductBoxItem>(detailProduct.inTheBox).map((item, i) => (
                              <li key={i}>
                                <Typography variant="body2">
                                  {item.name || item.item || ''}
                                  {item.quantity != null && item.quantity > 1 ? ` (x${item.quantity})` : ''}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: asObjectArray<StagedProductBoxItem>(detailProduct.aiInTheBox).length ? 1 : 0 }}>
                            Not specified
                          </Typography>
                        )}
                        {asObjectArray<StagedProductBoxItem>(detailProduct.aiInTheBox).length > 0 && (
                          <Alert
                            severity="info"
                            icon={<AIIcon fontSize="small" />}
                            sx={{ '& .MuiAlert-message': { width: '100%' } }}
                            action={
                              <Stack direction="row" spacing={0.5}>
                                <Button
                                  size="small"
                                  color="success"
                                  variant="contained"
                                  disabled={saveEditMutation.isPending}
                                  sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                  onClick={() => {
                                    const items = asObjectArray<StagedProductBoxItem>(detailProduct.aiInTheBox).map(
                                      (i) => ({
                                        name: i.name || '',
                                        ...(i.quantity != null ? { quantity: i.quantity } : {}),
                                      }),
                                    );
                                    saveEditMutation.mutate({
                                      productId: detailProduct.id,
                                      data: withEditMeta(
                                        {
                                          inTheBox: items,
                                          aiInTheBox: null,
                                          aiContentConfidence: { inTheBox: null },
                                        },
                                        {
                                          source: 'ai_suggestion',
                                          summary: 'Accepted AI In The Box suggestion',
                                        },
                                      ),
                                    });
                                  }}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="small"
                                  color="inherit"
                                  disabled={saveEditMutation.isPending}
                                  sx={{ fontSize: '0.7rem' }}
                                  onClick={() => {
                                    saveEditMutation.mutate({
                                      productId: detailProduct.id,
                                      data: withEditMeta(
                                        {
                                          aiInTheBox: null,
                                          aiContentConfidence: { inTheBox: null },
                                        },
                                        {
                                          source: 'ai_suggestion',
                                          summary: 'Dismissed AI In The Box suggestion',
                                        },
                                      ),
                                    });
                                  }}
                                >
                                  Dismiss
                                </Button>
                              </Stack>
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" fontWeight="bold">
                                AI Suggestion — Not yet applied
                              </Typography>
                              {typeof detailProduct.aiContentConfidence?.inTheBox === 'number' && (
                                <Chip
                                  size="small"
                                  label={`${Math.round(detailProduct.aiContentConfidence.inTheBox * 100)}% conf`}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                              {asObjectArray<StagedProductBoxItem>(detailProduct.aiInTheBox).map((item, i) => (
                                <li key={i}>
                                  <Typography variant="body2">
                                    {item.name ?? ''}
                                    {item.quantity != null && item.quantity > 1 ? ` (x${item.quantity})` : ''}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          </Alert>
                        )}
                      </>
                    )}
                    </Stack>
                  </DetailSection>

                  {/* Tags */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`Tags (${(isEditing ? editData.tags : asStringArray(detailProduct.tags)).length || 0})`}
                        source={stagedTagSources(
                          detailProduct,
                          asStringArray(isEditing ? editData.tags : detailProduct.tags).length > 0,
                        )}
                      />
                    }
                  >
                    {isEditing ? (
                      <ChipInput
                        aria-label="Tags"
                        placeholder="Type a tag and press Enter"
                        value={asStringArray(editData.tags)}
                        onChange={(next) => updateEditField('tags', next)}
                      />
                    ) : (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {asStringArray(detailProduct.tags).length > 0
                          ? asStringArray(detailProduct.tags).map((t) => <Chip key={t} label={t} size="small" variant="outlined" />)
                          : <Typography variant="body2" color="text.secondary">No tags</Typography>
                        }
                      </Stack>
                    )}
                  </DetailSection>

                  {/* Images */}
                  <DetailSection
                    title={
                      <SectionTitleWithSource
                        title={`Images (${detailProduct.images?.length ?? 0})`}
                        source={stagedSectionSource(
                          detailProduct,
                          'Images',
                          (detailProduct.images?.length ?? 0) > 0,
                        )}
                      />
                    }
                  >
                    <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Image URL (CDN or direct link)"
                          placeholder="https://..."
                          value={imageUrlInput}
                          onChange={(e) => setImageUrlInput(e.target.value)}
                          disabled={addImageFromUrlMutation.isPending}
                        />
                        <Button
                          variant="contained"
                          sx={{ flexShrink: 0 }}
                          disabled={
                            !imageUrlInput.trim() ||
                            addImageFromUrlMutation.isPending ||
                            uploadStagedImageMutation.isPending
                          }
                          onClick={() => {
                            if (!detailProduct) return;
                            addImageFromUrlMutation.mutate({
                              productId: detailProduct.id,
                              url: imageUrlInput.trim(),
                              makePrimary: makeNewImagePrimary,
                              host: !imageUrlLinkOnly,
                            });
                          }}
                        >
                          {addImageFromUrlMutation.isPending ? 'Adding…' : 'Add from URL'}
                        </Button>
                      </Stack>
                      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={makeNewImagePrimary}
                              onChange={(_, v) => setMakeNewImagePrimary(v)}
                              disabled={addImageFromUrlMutation.isPending || uploadStagedImageMutation.isPending}
                            />
                          }
                          label={<Typography variant="body2">Set next image as primary</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={imageUrlLinkOnly}
                              onChange={(_, v) => setImageUrlLinkOnly(v)}
                              disabled={addImageFromUrlMutation.isPending}
                            />
                          }
                          label={<Typography variant="body2">Store URL only (no download)</Typography>}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <input
                          ref={stagedImageFileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f && detailProduct) {
                              uploadStagedImageMutation.mutate({
                                productId: detailProduct.id,
                                file: f,
                                makePrimary: makeNewImagePrimary,
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          disabled={uploadStagedImageMutation.isPending || addImageFromUrlMutation.isPending}
                          onClick={() => stagedImageFileInputRef.current?.click()}
                        >
                          {uploadStagedImageMutation.isPending ? 'Uploading…' : 'Upload file'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={
                            classifyImagesMutation.isPending ? <CircularProgress size={16} /> : <AIIcon />
                          }
                          disabled={
                            classifyImagesMutation.isPending ||
                            !detailProduct.images?.length ||
                            uploadStagedImageMutation.isPending
                          }
                          onClick={() =>
                            classifyImagesMutation.mutate({ productId: detailProduct.id, force: true })
                          }
                        >
                          {classifyImagesMutation.isPending ? 'Classifying…' : 'Classify images'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={
                            pruneBrokenImagesMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <BrokenImageIcon />
                            )
                          }
                          disabled={
                            pruneBrokenImagesMutation.isPending ||
                            patchStagedProductMutation.isPending ||
                            !detailProduct.images?.length
                          }
                          onClick={() => {
                            if (brokenImageIndexes.size > 0 || Object.keys(imageSrcOverrides).length > 0) {
                              applyClientBrokenImageFix();
                              return;
                            }
                            pruneBrokenImagesMutation.mutate(detailProduct.id);
                          }}
                        >
                          {pruneBrokenImagesMutation.isPending ? 'Checking…' : 'Drop broken'}
                        </Button>
                      </Stack>
                      {classifyImagesMutation.isPending && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Classifying images with heuristics + AI. CDN images can take a minute — keep this dialog open.
                        </Alert>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StorageHostedIcon sx={{ fontSize: 14, color: 'success.main' }} /> Storage = Firebase
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CdnLinkIcon sx={{ fontSize: 14, color: 'warning.main' }} /> CDN = external URL
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click a thumbnail to view full size · right-click chip to set type
                      </Typography>
                    </Stack>
                    {(brokenImageIndexes.size > 0 || Object.keys(imageSrcOverrides).length > 0) && (
                      <Alert
                        severity={brokenImageIndexes.size > 0 ? 'warning' : 'info'}
                        sx={{ mb: 1 }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            disabled={patchStagedProductMutation.isPending || pruneBrokenImagesMutation.isPending}
                            onClick={() => {
                              if (brokenImageIndexes.size > 0 || Object.keys(imageSrcOverrides).length > 0) {
                                applyClientBrokenImageFix();
                                return;
                              }
                              pruneBrokenImagesMutation.mutate(detailProduct.id);
                            }}
                          >
                            Fix images
                          </Button>
                        }
                      >
                        {brokenImageIndexes.size > 0
                          ? `${brokenImageIndexes.size} image${brokenImageIndexes.size === 1 ? '' : 's'} failed to load.`
                          : `${Object.keys(imageSrcOverrides).length} Storage file${Object.keys(imageSrcOverrides).length === 1 ? '' : 's'} missing — showing manufacturer URL.`}
                        {' '}
                        Broken Storage files are dropped; manufacturer URLs are kept when they still work.
                      </Alert>
                    )}
                    {Array.isArray(detailProduct.images) && detailProduct.images.length > 0 ? (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {detailProduct.images.map((img, i) => {
                          const host = stagedImageHostMeta(img);
                          const displayUrl = imageSrcOverrides[i] || img.url || '';
                          const isBroken = brokenImageIndexes.has(i) || !displayUrl;
                          return (
                          <Box
                            key={`${img.url}-${i}`}
                            sx={{
                              position: 'relative',
                              width: 104,
                              pt: 0.5,
                            }}
                          >
                            <Box
                              onClick={() => {
                                if (!isBroken) setImagePreviewIndex(i);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (!isBroken) setImagePreviewIndex(i);
                                }
                              }}
                              aria-label={
                                isBroken
                                  ? `Image ${i + 1} failed to load`
                                  : `View image ${i + 1} larger (${host.caption})`
                              }
                              sx={{
                                position: 'relative',
                                width: 96,
                                height: 96,
                                mx: 'auto',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: isBroken ? 'error.main' : imageSrcOverrides[i] ? 'warning.main' : 'divider',
                                bgcolor: 'grey.50',
                                cursor: isBroken ? 'default' : 'zoom-in',
                                overflow: 'hidden',
                                '&:hover .img-zoom-hint': { opacity: isBroken ? 0 : 1 },
                              }}
                            >
                              {displayUrl ? (
                                <Box
                                  component="img"
                                  src={displayUrl}
                                  alt={img.alt || ''}
                                  onError={() => {
                                    const source = String(img.sourceUrl || '').trim();
                                    if (source && source !== displayUrl && !imageSrcOverrides[i]) {
                                      setImageSrcOverrides((prev) => ({ ...prev, [i]: source }));
                                      return;
                                    }
                                    setBrokenImageIndexes((prev) => {
                                      const next = new Set(prev);
                                      next.add(i);
                                      return next;
                                    });
                                  }}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: isBroken ? 'none' : 'block',
                                  }}
                                />
                              ) : null}
                              {isBroken ? (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 0.25,
                                    bgcolor: 'grey.100',
                                  }}
                                >
                                  <BrokenImageIcon sx={{ color: 'error.main', fontSize: 28 }} />
                                </Box>
                              ) : null}
                              <Box
                                className="img-zoom-hint"
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(0,0,0,0.35)',
                                  opacity: 0,
                                  transition: 'opacity 0.15s',
                                  pointerEvents: 'none',
                                }}
                              >
                                <ZoomInIcon sx={{ color: 'common.white', fontSize: 28 }} />
                              </Box>
                            </Box>
                            <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                              {isBroken ? (
                                <Chip
                                  size="small"
                                  icon={<BrokenImageIcon />}
                                  label="Broken"
                                  color="error"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12, ml: 0.5 } }}
                                />
                              ) : imageSrcOverrides[i] ? (
                                <Tooltip title="Firebase file is missing; showing the original manufacturer URL">
                                  <Chip
                                    size="small"
                                    icon={<CdnLinkIcon />}
                                    label="CDN fallback"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12, ml: 0.5 } }}
                                  />
                                </Tooltip>
                              ) : (
                              <Tooltip title={host.detail}>
                                <Chip
                                  size="small"
                                  icon={host.hosted ? <StorageHostedIcon /> : <CdnLinkIcon />}
                                  label={host.shortLabel}
                                  color={host.hosted ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12, ml: 0.5 } }}
                                />
                              </Tooltip>
                              )}
                              {img.isPrimary ? (
                                <Chip label="Primary" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                              ) : null}
                              {imageTypeChipLabel(img.type) ? (
                                <Tooltip title={`Type: ${img.type}${img.typeSource ? ` (${img.typeSource})` : ''} — click to change`}>
                                  <Chip
                                    label={imageTypeChipLabel(img.type)!}
                                    size="small"
                                    color={
                                      img.type === 'lifestyle' || img.type === 'promotional' || img.type === 'icon'
                                        ? 'secondary'
                                        : 'default'
                                    }
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImageTypeMenu({ anchor: e.currentTarget, index: i });
                                    }}
                                    sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              ) : (
                                <Chip
                                  label="Type"
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setImageTypeMenu({ anchor: e.currentTarget, index: i });
                                  }}
                                  sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                                />
                              )}
                            </Stack>
                            <IconButton
                              size="small"
                              color="error"
                              sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'background.paper', zIndex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!detailProduct?.images) return;
                                const next = detailProduct.images.filter((_, j) => j !== i);
                                patchStagedProductMutation.mutate({ productId: detailProduct.id, data: { images: next } });
                              }}
                              disabled={patchStagedProductMutation.isPending}
                              aria-label="Remove image"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No images yet.</Typography>
                    )}
                    <Menu
                      anchorEl={imageTypeMenu?.anchor ?? null}
                      open={Boolean(imageTypeMenu)}
                      onClose={() => setImageTypeMenu(null)}
                    >
                      {STAGED_IMAGE_TYPE_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.label}
                          selected={
                            opt.value === ''
                              ? !detailProduct.images?.[imageTypeMenu?.index ?? -1]?.type
                              : detailProduct.images?.[imageTypeMenu?.index ?? -1]?.type === opt.value
                          }
                          onClick={() => {
                            if (!detailProduct?.images || imageTypeMenu == null) return;
                            const next = detailProduct.images.map((row, j) => {
                              if (j !== imageTypeMenu.index) return row;
                              if (!opt.value) {
                                const rest = { ...row };
                                delete rest.type;
                                delete rest.typeSource;
                                return rest;
                              }
                              return { ...row, type: opt.value, typeSource: 'admin' as const };
                            });
                            patchStagedProductMutation.mutate({
                              productId: detailProduct.id,
                              data: { images: next },
                            });
                            setImageTypeMenu(null);
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </DetailSection>

                  {/* Specifications */}
                  {(() => {
                    const specsSource = isEditing ? editData.specifications : detailProduct.specifications;
                    const hasSpecs = isPlainRecord(specsSource) && Object.keys(specsSource).filter(k => k !== 'raw' && k !== '_meta').length > 0;
                    const hasRawSpecs =
                      isPlainRecord(detailProduct.specificationsRaw) &&
                      Object.keys(detailProduct.specificationsRaw).length > 0;
                    const specSourceBadge = stagedSectionSource(
                      detailProduct,
                      'Specifications',
                      hasSpecs || hasRawSpecs,
                    );

                    const updateSpec = (section: string, key: string, value: string) => {
                      const specs = { ...(editData.specifications || {}) };
                      if (!specs[section]) specs[section] = {};
                      specs[section] = { ...specs[section], [key]: value };
                      updateEditField('specifications', specs);
                    };
                    const deleteSpecField = (section: string, key: string) => {
                      const specs = { ...(editData.specifications || {}) };
                      if (specs[section]) {
                        const s = { ...specs[section] };
                        delete s[key];
                        specs[section] = s;
                        if (Object.keys(s).length === 0) delete specs[section];
                      }
                      updateEditField('specifications', specs);
                    };
                    const addSpecField = (section: string) => {
                      const specs = { ...(editData.specifications || {}) };
                      if (!specs[section]) specs[section] = {};
                      const newKey = `New Field ${Object.keys(specs[section]).length + 1}`;
                      specs[section] = { ...specs[section], [newKey]: '' };
                      updateEditField('specifications', specs);
                    };
                    const renameSpecField = (section: string, oldKey: string, newKey: string) => {
                      if (oldKey === newKey || !newKey.trim()) return;
                      const specs = { ...(editData.specifications || {}) };
                      if (!specs[section]) return;
                      const s = { ...specs[section] };
                      const val = s[oldKey];
                      delete s[oldKey];
                      s[newKey] = val;
                      specs[section] = s;
                      updateEditField('specifications', specs);
                    };
                    const addSpecSection = () => {
                      const specs = { ...(editData.specifications || {}) };
                      const name = `New Section ${Object.keys(specs).filter(k => k !== 'raw' && k !== '_meta').length + 1}`;
                      specs[name] = { 'New Field 1': '' };
                      updateEditField('specifications', specs);
                    };
                    const deleteSpecSection = (section: string) => {
                      const specs = { ...(editData.specifications || {}) };
                      delete specs[section];
                      updateEditField('specifications', specs);
                    };
                    const renameSpecSection = (oldName: string, newName: string) => {
                      if (oldName === newName || !newName.trim()) return;
                      const specs = { ...(editData.specifications || {}) };
                      const data = specs[oldName];
                      delete specs[oldName];
                      specs[newName] = data;
                      updateEditField('specifications', specs);
                    };

                    if (hasSpecs) {
                      // Box contents live in the dedicated In The Box section — hide from view.
                      const isBoxContentsSpecKey = (k: string) =>
                        /^(in\s*the\s*box|package\s*(includes|contents)|what'?s?\s*(in\s*the\s*box|included)|box\s*contents)$/i.test(
                          k.trim(),
                        );
                      const entries = Object.entries(specsSource as Record<string, unknown>)
                        .filter(([k]) => k !== 'raw' && k !== '_meta')
                        .filter(([k]) => isEditing || !isBoxContentsSpecKey(k));
                      const isScalarSpecValue = (v: unknown) =>
                        typeof v === 'string' ||
                        typeof v === 'number' ||
                        typeof v === 'boolean' ||
                        v === null ||
                        v === undefined;
                      const flatScalarCount = entries.filter(([, v]) => isScalarSpecValue(v)).length;
                      // SmallRig-style flat key→value maps (often <20 fields) should use the
                      // label/value table — not nested "section" headers with the value jammed under.
                      const isFlatSpec =
                        entries.length > 0 &&
                        (flatScalarCount === entries.length ||
                          (flatScalarCount > entries.length * 0.7 && entries.length > 20));

                      const specTableEntries =
                        isFlatSpec && !isEditing
                          ? overlayExtractedWeightsOnSpecs(entries, detailProduct)
                          : entries;

                      const formatFlatSpecLabel = (raw: string) =>
                        stripUnicodeSuperscripts(
                          collapseLetterSpacedWords(
                            raw.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim(),
                          ),
                        );

                      const setFlatSpecValue = (key: string, value: string) => {
                        const specs = { ...(editData.specifications || {}) };
                        specs[key] = value;
                        updateEditField('specifications', specs);
                      };
                      const renameFlatSpecKey = (oldKey: string, newKey: string) => {
                        if (oldKey === newKey || !newKey.trim()) return;
                        const specs = { ...(editData.specifications || {}) };
                        const val = specs[oldKey];
                        delete specs[oldKey];
                        specs[newKey] = val;
                        updateEditField('specifications', specs);
                      };
                      const deleteFlatSpecKey = (key: string) => {
                        const specs = { ...(editData.specifications || {}) };
                        delete specs[key];
                        updateEditField('specifications', specs);
                      };
                      const addFlatSpecField = () => {
                        const specs = { ...(editData.specifications || {}) };
                        const name = `New Field ${Object.keys(specs).filter((k) => k !== 'raw' && k !== '_meta').length + 1}`;
                        specs[name] = '';
                        updateEditField('specifications', specs);
                      };

                      return (
                        <DetailSection
                          title={
                            <SectionTitleWithSource
                              title={`Specifications (${specTableEntries.length} ${isFlatSpec ? 'fields' : 'sections'})`}
                              source={specSourceBadge}
                            />
                          }
                          action={
                            isEditing ? (
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={isFlatSpec ? addFlatSpecField : addSpecSection}
                              >
                                {isFlatSpec ? 'Add Field' : 'Add Section'}
                              </Button>
                            ) : undefined
                          }
                          sx={{ mb: 2 }}
                        >
                          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: isEditing ? 500 : 300, overflow: 'auto', bgcolor: 'grey.50', border: 'none' }}>
                            {isFlatSpec ? (
                              <Box
                                component="table"
                                sx={{
                                  width: '100%',
                                  borderCollapse: 'separate',
                                  borderSpacing: 0,
                                  tableLayout: 'fixed',
                                  '& td': { py: isEditing ? 0.5 : 0.45, verticalAlign: 'top', borderBottom: '1px solid', borderColor: 'divider' },
                                }}
                              >
                                <tbody>
                                  {specTableEntries.map(([key, val]) => {
                                    const splitSpec = (raw: string, v: any): { label: string; value: string } => {
                                      const sVal =
                                        v != null && typeof v !== 'object' && String(v) !== raw
                                          ? String(v)
                                          : '';
                                      if (sVal) {
                                        return { label: formatFlatSpecLabel(raw), value: sVal };
                                      }
                                      // Legacy mangled keys where label+value were concatenated into the key
                                      const m = raw.match(/^(.+?[a-z)])([A-Z0-9].+)$/);
                                      if (m && m[1].length > 4) {
                                        return {
                                          label: formatFlatSpecLabel(m[1]),
                                          value: m[2].trim(),
                                        };
                                      }
                                      if (/^[A-Z]\s[A-Z]/.test(raw)) {
                                        return {
                                          label: stripUnicodeSuperscripts(collapseLetterSpacedWords(raw.replace(/\s+/g, ''))),
                                          value: '-',
                                        };
                                      }
                                      return {
                                        label: formatFlatSpecLabel(raw),
                                        value: v == null || v === '' ? '-' : String(v),
                                      };
                                    };
                                    const { label, value } = splitSpec(key, val);
                                    return (
                                      <tr key={key}>
                                        <Box component="td" sx={{ pr: 2, width: isEditing ? '35%' : '40%', maxWidth: isEditing ? '35%' : '40%', wordBreak: 'break-word' }}>
                                          {isEditing ? (
                                            <TextField
                                              size="small"
                                              variant="standard"
                                              fullWidth
                                              defaultValue={key}
                                              onBlur={(e) => renameFlatSpecKey(key, e.target.value.trim())}
                                              inputProps={{ style: { fontSize: '0.75rem', fontWeight: 600 } }}
                                            />
                                          ) : (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                              {label}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Box component="td" sx={{ wordBreak: 'break-word', pl: 0.5 }}>
                                          {isEditing ? (
                                            <TextField
                                              size="small"
                                              variant="standard"
                                              fullWidth
                                              multiline
                                              value={typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                                              onChange={(e) => setFlatSpecValue(key, e.target.value)}
                                              inputProps={{ style: { fontSize: '0.75rem' } }}
                                            />
                                          ) : (
                                            <Typography variant="caption" component="span" sx={{ display: 'inline-block' }}>
                                              {value}
                                            </Typography>
                                          )}
                                        </Box>
                                        {isEditing && (
                                          <Box component="td" sx={{ width: 32, pl: 0 }}>
                                            <IconButton size="small" color="error" onClick={() => deleteFlatSpecKey(key)}>
                                              <DeleteIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </Box>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Box>
                            ) : (
                              entries.map(([section, values]) => (
                                <Box key={section} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                                  {isEditing ? (
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                                      <TextField
                                        size="small"
                                        variant="standard"
                                        defaultValue={section}
                                        onBlur={(e) => renameSpecSection(section, e.target.value.trim())}
                                        inputProps={{ style: { fontWeight: 700, fontSize: '0.75rem', textTransform: 'capitalize', color: theme.palette.primary.main } }}
                                        sx={{ flex: 1 }}
                                      />
                                      {values && typeof values === 'object' ? (
                                        <Tooltip title="Add field">
                                          <IconButton size="small" onClick={() => addSpecField(section)}><AddIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                      ) : null}
                                      <Tooltip title="Delete section">
                                        <IconButton size="small" color="error" onClick={() => deleteSpecSection(section)}><DeleteIcon fontSize="small" /></IconButton>
                                      </Tooltip>
                                    </Stack>
                                  ) : values && typeof values === 'object' ? (
                                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'capitalize', color: 'primary.main' }}>
                                      {formatSpecSectionTitle(section)}
                                    </Typography>
                                  ) : null}
                                  {values && typeof values === 'object' ? (
                                    <Box component="table" sx={{ width: '100%', mt: 0.25, tableLayout: 'fixed', '& td': { py: isEditing ? 0.5 : 0.2, verticalAlign: 'top' } }}>
                                      <tbody>
                                        {Object.entries(values as Record<string, any>)
                                          .filter(([, v]) => isEditing || (v !== undefined && v !== null && v !== ''))
                                          .map(([key, val]) => (
                                            <tr key={key}>
                                              <Box component="td" sx={{ pr: 1, width: isEditing ? '35%' : '42%', maxWidth: isEditing ? '35%' : '42%', wordBreak: 'break-word' }}>
                                                {isEditing ? (
                                                  <TextField
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    defaultValue={key}
                                                    onBlur={(e) => renameSpecField(section, key, e.target.value.trim())}
                                                    inputProps={{ style: { fontSize: '0.75rem' } }}
                                                  />
                                                ) : (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {formatSpecFieldLabel(section, key)}
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Box component="td" sx={{ wordBreak: 'break-word', pl: 0.5 }}>
                                                {isEditing ? (
                                                  <TextField
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    multiline
                                                    value={typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                                                    onChange={(e) => updateSpec(section, key, e.target.value)}
                                                    inputProps={{ style: { fontSize: '0.75rem' } }}
                                                  />
                                                ) : (
                                                  <Typography variant="caption" component="span">
                                                    {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                                  </Typography>
                                                )}
                                              </Box>
                                              {isEditing && (
                                                <Box component="td" sx={{ width: 32, pl: 0 }}>
                                                  <IconButton size="small" color="error" onClick={() => deleteSpecField(section, key)}>
                                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Box>
                                              )}
                                            </tr>
                                          ))}
                                      </tbody>
                                    </Box>
                                  ) : (
                                    // Scalar top-level value mixed into nested specs — render as a KV row
                                    <Box
                                      component="table"
                                      sx={{
                                        width: '100%',
                                        tableLayout: 'fixed',
                                        '& td': {
                                          py: isEditing ? 0.5 : 0.35,
                                          verticalAlign: 'top',
                                          borderBottom: '1px solid',
                                          borderColor: 'divider',
                                        },
                                      }}
                                    >
                                      <tbody>
                                        <tr>
                                          <Box component="td" sx={{ pr: 2, width: '40%', wordBreak: 'break-word' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                              {formatSpecSectionTitle(section)}
                                            </Typography>
                                          </Box>
                                          <Box component="td" sx={{ wordBreak: 'break-word', pl: 0.5 }}>
                                            {isEditing ? (
                                              <TextField
                                                size="small"
                                                variant="standard"
                                                fullWidth
                                                multiline
                                                value={String(values ?? '')}
                                                onChange={(e) => {
                                                  const specs = { ...(editData.specifications || {}) };
                                                  specs[section] = e.target.value;
                                                  updateEditField('specifications', specs);
                                                }}
                                                inputProps={{ style: { fontSize: '0.75rem' } }}
                                              />
                                            ) : (
                                              <Typography variant="caption" component="span">
                                                {String(values ?? '')}
                                              </Typography>
                                            )}
                                          </Box>
                                        </tr>
                                      </tbody>
                                    </Box>
                                  )}
                                </Box>
                              ))
                            )}
                          </Paper>
                        </DetailSection>
                      );
                    }

                    // No normalized specs — show raw or empty
                    return (
                      <DetailSection
                        title={<SectionTitleWithSource title="Specifications" source={specSourceBadge} />}
                        action={
                          isEditing ? (
                            <Button size="small" startIcon={<AddIcon />} onClick={addSpecSection}>
                              Add Section
                            </Button>
                          ) : undefined
                        }
                      >
                        {isPlainRecord(detailProduct.specificationsRaw) && Object.keys(detailProduct.specificationsRaw).length > 0 ? (
                          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50', border: 'none' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Raw specifications (not normalized)</Typography>
                            <Box component="table" sx={{ width: '100%', '& td': { py: 0.15, verticalAlign: 'top' } }}>
                              <tbody>
                                {Object.entries(detailProduct.specificationsRaw).map(([key, val]) => (
                                  <tr key={key}>
                                    <Box component="td" sx={{ pr: 2, width: '42%', wordBreak: 'break-word' }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {stripUnicodeSuperscripts(collapseLetterSpacedWords(key.replace(/_/g, ' ')))}
                                      </Typography>
                                    </Box>
                                    <Box component="td" sx={{ wordBreak: 'break-word', pl: 0.5 }}>
                                      <Typography variant="caption">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</Typography>
                                    </Box>
                                  </tr>
                                ))}
                              </tbody>
                            </Box>
                          </Paper>
                        ) : (
                          <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
                            <Typography variant="caption">No specifications data available</Typography>
                          </Alert>
                        )}
                      </DetailSection>
                    );
                  })()}

                  {/* Source */}
                  <DetailSection title="Source" sx={{ mb: 0 }}>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: storefrontPlatform ? 3 : 4 }}>
                        <InfoField label="Vendor" value={sourceSafe.vendorName} />
                      </Grid>
                      <Grid size={{ xs: storefrontPlatform ? 3 : 4 }}>
                        <InfoField label="Source ID" value={sourceSafe.sourceId} mono />
                      </Grid>
                      <Grid size={{ xs: storefrontPlatform ? 3 : 4 }}>
                        <InfoField label="Connector" value={sourceSafe.connectorId} mono />
                      </Grid>
                      {storefrontPlatform ? (
                        <Grid size={{ xs: 3 }}>
                          <InfoField label="Storefront" value={storefrontPlatform.label} />
                        </Grid>
                      ) : null}
                      {detailProduct?.distributor?.name ? (
                        // A product the source resells (MOT-633): filed under its maker, sold by the source.
                        <Grid size={{ xs: 12 }}>
                          <InfoField
                            label="Distributor"
                            value={`${detailProduct.distributor.name} resells this ${detailProduct.brand} product`}
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                    {(sourceSafe.sourceUrl || storefrontPlatform || isPlatformAdmin) && (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {sourceSafe.sourceUrl ? (
                          <Typography
                            variant="body2"
                            component="a"
                            href={sourceSafe.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                          >
                            View source page →
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No manufacturer page stored. Sheet rows often need one pasted in.
                          </Typography>
                        )}
                        {storefrontPlatform ? (
                          <Tooltip title={storefrontPlatform.hint}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: 20,
                                px: 0.75,
                                borderRadius: '4px',
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                color: 'text.secondary',
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                              }}
                            >
                              {storefrontPlatform.label}
                            </Box>
                          </Tooltip>
                        ) : null}
                        {isPlatformAdmin ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={scrapeMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <ScraperIcon fontSize="small" />}
                            disabled={scrapeMutation.isPending}
                            onClick={() => setScrapeDialogOpen(true)}
                            sx={{ textTransform: 'none' }}
                          >
                            Scrape page
                          </Button>
                        ) : null}
                      </Stack>
                    )}
                  </DetailSection>
                </Grid>

                {/* Right Column — AI Verification */}
                <Grid
                  size={{ xs: 12, md: 5 }}
                  sx={{
                    minWidth: 0,
                    position: { md: 'sticky' },
                    top: { md: 8 },
                    alignSelf: 'flex-start',
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                      <AIIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
                        AI Verification
                      </Typography>
                    </Stack>

                    {hasPendingAiSuggestions(detailProduct) && (
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<FixIcon />}
                        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
                        disabled={saveEditMutation.isPending}
                        onClick={() => acceptAllAiOnProduct(detailProduct)}
                      >
                        Accept All AI Suggestions
                      </Button>
                    )}

                    {detailPromoteBlockers.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem', display: 'block', mb: 1 }}
                        >
                          Promotion ({detailPromoteBlockers.length})
                        </Typography>
                        {detailPromoteBlockers.map((b) => (
                          <Alert
                            key={b.id}
                            severity={b.blocksPromote ? 'error' : 'warning'}
                            variant="outlined"
                            icon={<WarningIcon fontSize="small" />}
                            sx={{ mb: 0.75, py: 0, '& .MuiAlert-message': { py: 0.75 } }}
                          >
                            <Typography variant="caption" fontWeight={600} display="block">
                              {b.label}
                            </Typography>
                            <Typography variant="caption">{b.message}</Typography>
                          </Alert>
                        ))}
                      </Box>
                    )}

                    {detailProduct.verification ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                              variant="determinate"
                              value={100}
                              size={72}
                              thickness={4}
                              sx={{ color: 'action.hover', position: 'absolute' }}
                            />
                            <CircularProgress
                              variant="determinate"
                              value={verificationSafe.overallScore}
                              size={72}
                              thickness={4}
                              color={detailProduct.verification.passedThreshold ? 'success' : 'warning'}
                            />
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1 }}>
                                {detailProduct.verification.overallScore ?? verificationSafe.overallScore}
                              </Typography>
                            </Box>
                          </Box>
                          <Box>
                            <Chip
                              label={detailProduct.verification.passedThreshold ? 'Passed' : 'Below Threshold'}
                              size="small"
                              color={detailProduct.verification.passedThreshold ? 'success' : 'warning'}
                              sx={{ borderRadius: 1.5, fontWeight: 600 }}
                            />
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.75 }}>
                              Model: {verificationSafe.modelUsed}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Score Breakdown */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem', display: 'block', mb: 1 }}
                        >
                          Score Breakdown
                        </Typography>
                        {verificationSafe.scoreEntries.map(([key, score]) => {
                          const num = typeof score === 'number' && !Number.isNaN(score) ? score : Number(score) || 0;
                          return (
                          <Box key={key} sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">{key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</Typography>
                              <Typography variant="caption" fontWeight={600}>{num}/100</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={num}
                              color={num >= 70 ? 'success' : num >= 40 ? 'warning' : 'error'}
                              sx={{ height: 5, borderRadius: 2, bgcolor: 'action.hover' }}
                            />
                          </Box>
                          );
                        })}

                        {/* Issues */}
                        {verificationSafe.issues.length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem' }}
                              >
                                Issues ({verificationSafe.issues.length})
                              </Typography>
                              {(() => {
                                const fixableIssues = verificationSafe.issues.filter(i => i.autoFixable && i.suggestedFix != null);
                                if (fixableIssues.length < 2) return null;
                                return (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<FixIcon sx={{ fontSize: 14 }} />}
                                    sx={{ height: 22, fontSize: '0.65rem', textTransform: 'none' }}
                                    disabled={saveEditMutation.isPending}
                                    onClick={() => {
                                      const patchData: Record<string, any> = {};
                                      let tagsAccum = [...asStringArray(detailProduct.tags)];
                                      for (const issue of fixableIssues) {
                                        if (issue.field === 'tags') {
                                          const n = buildTagsFromAiSuggestedFix(tagsAccum, issue.suggestedFix);
                                          if (n) tagsAccum = n;
                                          continue;
                                        }
                                        if (isShortDescriptionIssueField(issue.field)) {
                                          const raw = typeof issue.suggestedFix === 'string'
                                            ? issue.suggestedFix
                                            : String(issue.suggestedFix ?? '');
                                          patchData.shortDescription = clampShortDescription(raw);
                                          continue;
                                        }
                                        if (issue.field.includes('.')) {
                                          const parts = issue.field.split('.');
                                          const current =
                                            patchData[parts[0]] || (detailProduct as any)[parts[0]] || {};
                                          if (current && typeof current === 'object' && !Array.isArray(current)) {
                                            patchData[parts[0]] = {
                                              ...current,
                                              [parts[1]]: issue.suggestedFix,
                                            };
                                          } else {
                                            patchData[parts[0]] = issue.suggestedFix;
                                          }
                                        } else {
                                          patchData[issue.field] = issue.suggestedFix;
                                        }
                                      }
                                      const origKey = [...asStringArray(detailProduct.tags)]
                                        .map((s) => s.toLowerCase())
                                        .sort()
                                        .join('|');
                                      const nextKey = [...tagsAccum].map((s) => s.toLowerCase()).sort().join('|');
                                      if (origKey !== nextKey) patchData.tags = tagsAccum;
                                      if (Object.keys(patchData).length === 0) {
                                        setDialogAlert({
                                          type: 'warning',
                                          message:
                                            'No fixes applied — tag suggestions may be prose only. Use Suggestions → Merge All or edit tags manually.',
                                        });
                                        return;
                                      }
                                      saveEditMutation.mutate({
                                        productId: detailProduct.id,
                                        data: withEditMeta(patchData, {
                                          source: 'ai_fix',
                                          summary: `Applied ${fixableIssues.length} AI verification fix(es)`,
                                          clearedIssueFields: fixableIssues.map((i) => i.field),
                                        }),
                                      });
                                    }}
                                  >
                                    Apply All Fixes ({fixableIssues.length})
                                  </Button>
                                );
                              })()}
                            </Stack>
                            {verificationSafe.issues.map((issue, i) => (
                              <Alert
                                key={i}
                                severity={issue.severity}
                                variant="outlined"
                                icon={issue.severity === 'error' ? <ErrorIcon fontSize="small" /> : issue.severity === 'warning' ? <WarningIcon fontSize="small" /> : <InfoIcon fontSize="small" />}
                                sx={{
                                  mb: 0.75,
                                  py: 0.25,
                                  borderRadius: 1.5,
                                  bgcolor: 'background.paper',
                                  '& .MuiAlert-message': { py: 0.5, width: '100%' },
                                }}
                              >
                                <Typography variant="caption">
                                  <Box component="span" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{issue.field}</Box>
                                  {': '}
                                  {issue.message}
                                </Typography>
                                {issue.autoFixable && issue.suggestedFix != null && (
                                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      Suggested: {typeof issue.suggestedFix === 'string' ? issue.suggestedFix : JSON.stringify(issue.suggestedFix)}
                                    </Typography>
                                    <Tooltip title={`Apply fix: ${typeof issue.suggestedFix === 'string' ? issue.suggestedFix : JSON.stringify(issue.suggestedFix)}`}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<FixIcon sx={{ fontSize: 14 }} />}
                                        sx={{ height: 22, fontSize: '0.65rem', textTransform: 'none', minWidth: 'auto', whiteSpace: 'nowrap' }}
                                        disabled={saveEditMutation.isPending}
                                        onClick={async () => {
                                          const topField = issue.field.split('.')[0];
                                          const patchData: Record<string, any> = {};
                                          if (issue.field === 'tags') {
                                            const next = buildTagsFromAiSuggestedFix(
                                              asStringArray(detailProduct.tags),
                                              issue.suggestedFix,
                                            );
                                            if (!next) {
                                              setDialogAlert({
                                                type: 'warning',
                                                message:
                                                  'This tag fix is not a concrete tag list (e.g. only a sentence). Use Suggestions → Merge All or edit tags manually.',
                                              });
                                              return;
                                            }
                                            patchData.tags = next;
                                          } else if (isShortDescriptionIssueField(issue.field)) {
                                            const raw = typeof issue.suggestedFix === 'string'
                                              ? issue.suggestedFix
                                              : String(issue.suggestedFix ?? '');
                                            patchData.shortDescription = clampShortDescription(raw);
                                          } else if (issue.field.includes('.')) {
                                            const parts = issue.field.split('.');
                                            const current = (detailProduct as any)[parts[0]] || {};
                                            if (current && typeof current === 'object' && !Array.isArray(current)) {
                                              patchData[parts[0]] = { ...current, [parts[1]]: issue.suggestedFix };
                                            } else {
                                              patchData[parts[0]] = issue.suggestedFix;
                                            }
                                          } else {
                                            patchData[topField] = issue.suggestedFix;
                                          }
                                          saveEditMutation.mutate({
                                            productId: detailProduct.id,
                                            data: withEditMeta(patchData, {
                                              source: 'ai_fix',
                                              summary: `Applied AI fix for ${issue.field}`,
                                              clearedIssueFields: [issue.field],
                                            }),
                                          });
                                        }}
                                      >
                                        Apply Fix
                                      </Button>
                                    </Tooltip>
                                  </Stack>
                                )}
                                {issue.autoFixable && issue.suggestedFix == null && (
                                  <Chip label="Auto-fixable" size="small" sx={{ mt: 0.5, height: 16, fontSize: '0.6rem' }} />
                                )}
                              </Alert>
                            ))}
                          </>
                        )}

                        {/* Suggestions */}
                        {(detailProduct.verification.suggestions || verificationSafe.suggestedCategory) && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} gutterBottom>Suggestions</Typography>
                            {verificationSafe.suggestedCategory && (
                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                <Typography variant="caption">Category:</Typography>
                                <Chip label={verificationSafe.suggestedCategory} size="small" />
                                {verificationSafe.suggestedCategory !== detailProduct.category && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    sx={{ height: 22, fontSize: '0.65rem', textTransform: 'none', minWidth: 'auto' }}
                                    disabled={saveEditMutation.isPending}
                                    onClick={() =>
                                      saveEditMutation.mutate({
                                        productId: detailProduct.id,
                                        data: withEditMeta(
                                          {
                                            category: verificationSafe.suggestedCategory,
                                          },
                                          {
                                            source: 'ai_suggestion',
                                            summary: 'Accepted suggested category',
                                            clearedIssueFields: ['Category'],
                                          },
                                        ),
                                      })
                                    }
                                  >
                                    Accept
                                  </Button>
                                )}
                              </Stack>
                            )}
                            {asStringArray(detailProduct.verification.suggestions?.suggestedTags).length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption">Tags:</Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    sx={{ height: 22, fontSize: '0.65rem', textTransform: 'none', minWidth: 'auto' }}
                                    disabled={saveEditMutation.isPending}
                                    onClick={async () => {
                                      const existing = asStringArray(detailProduct.tags);
                                      const suggested = asStringArray(detailProduct.verification!.suggestions?.suggestedTags);
                                      const merged = Array.from(new Set(existing.concat(suggested)));
                                      const newTags = suggested.filter((t) => !existing.includes(t));
                                      if (newTags.length === 0) {
                                        setDialogAlert({ type: 'warning', message: 'All suggested tags are already on the product.' });
                                        return;
                                      }
                                      saveEditMutation.mutate(
                                        {
                                          productId: detailProduct.id,
                                          data: withEditMeta(
                                            { tags: merged },
                                            {
                                              source: 'ai_suggestion',
                                              summary: 'Merged suggested tags',
                                              clearedIssueFields: ['Tags'],
                                            },
                                          ),
                                        },
                                        {
                                          onSuccess: (data) => {
                                            const savedTags = asStringArray(data.data?.tags);
                                            const dropped = newTags.filter((t) => !savedTags.includes(t.toLowerCase()));
                                            if (dropped.length > 0 && dropped.length === newTags.length) {
                                              setDialogAlert({ type: 'warning', message: `Tags not in vocabulary and were dropped: ${dropped.join(', ')}. Edit tags manually instead.` });
                                            } else if (dropped.length > 0) {
                                              setDialogAlert({ type: 'warning', message: `Merged, but some tags were dropped (not in vocabulary): ${dropped.join(', ')}` });
                                            }
                                          },
                                        },
                                      );
                                    }}
                                  >
                                    Merge All
                                  </Button>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                  {asStringArray(detailProduct.verification.suggestions?.suggestedTags).map((t) => {
                                    const alreadyHas = asStringArray(detailProduct.tags).includes(t);
                                    return (
                                      <Chip
                                        key={t}
                                        label={t}
                                        size="small"
                                        color={alreadyHas ? 'default' : 'info'}
                                        variant={alreadyHas ? 'filled' : 'outlined'}
                                        deleteIcon={alreadyHas ? undefined : <FixIcon sx={{ fontSize: '14px !important' }} />}
                                        onDelete={alreadyHas ? undefined : () => {
                                          const merged = Array.from(new Set(asStringArray(detailProduct.tags).concat(t)));
                                          saveEditMutation.mutate({
                                            productId: detailProduct.id,
                                            data: withEditMeta(
                                              { tags: merged },
                                              {
                                                source: 'ai_suggestion',
                                                summary: `Added suggested tag: ${t}`,
                                                clearedIssueFields: ['Tags'],
                                              },
                                            ),
                                          });
                                        }}
                                        sx={alreadyHas ? { opacity: 0.6 } : {}}
                                      />
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}
                            {detailProduct.verification.suggestions.discontinuedLikelihood != null && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Discontinued likelihood: {Math.min(Math.round(detailProduct.verification.suggestions.discontinuedLikelihood), 100)}%
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not yet verified by AI.
                      </Typography>
                    )}

                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={reverifyMutation.isPending ? <CircularProgress size={16} /> : <AIIcon />}
                        onClick={() => reverifyMutation.mutate(detailProduct.id)}
                        disabled={reverifyMutation.isPending}
                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {reverifyMutation.isPending ? 'Verifying...' : 'AI Verify'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={enrichMutation.isPending ? <CircularProgress size={16} /> : <IdentifierIcon />}
                        onClick={() => enrichMutation.mutate(detailProduct.id)}
                        disabled={enrichMutation.isPending}
                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {enrichMutation.isPending ? 'Enriching...' : 'Enrich IDs'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={marketPricingMutation.isPending ? <CircularProgress size={16} /> : <PriceCheckIcon />}
                        onClick={() => marketPricingMutation.mutate(detailProduct.id)}
                        disabled={marketPricingMutation.isPending}
                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {marketPricingMutation.isPending ? 'Checking...' : 'Market Pricing'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={launchDatesMutation.isPending ? <CircularProgress size={16} /> : <CalendarIcon />}
                        onClick={() => launchDatesMutation.mutate(detailProduct.id)}
                        disabled={launchDatesMutation.isPending}
                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {launchDatesMutation.isPending ? 'Looking up...' : 'Launch Dates'}
                      </Button>
                      {isPlatformAdmin && (
                        <Tooltip title="Queue a one-page manufacturer scrape for images, specs, and copy. Paste a URL if this row came from a sheet.">
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={scrapeMutation.isPending ? <CircularProgress size={16} /> : <ScraperIcon />}
                              onClick={() => setScrapeDialogOpen(true)}
                              disabled={scrapeMutation.isPending}
                              sx={{ textTransform: 'none', borderRadius: 1.5 }}
                            >
                              {scrapeMutation.isPending ? 'Queuing...' : 'Scrape page'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isPlatformAdmin && (
                        <Tooltip title="Fill empty images, specs, identifiers, and In the Box from a third-party listing. Does not overwrite existing fields.">
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={bhFillMutation.isPending ? <CircularProgress size={16} /> : <BhFillIcon />}
                              onClick={() => bhFillMutation.mutate({ id: detailProduct.id })}
                              disabled={bhFillMutation.isPending}
                              sx={{ textTransform: 'none', borderRadius: 1.5 }}
                            >
                              {bhFillMutation.isPending ? 'Queuing...' : '3P Backfill'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                    {reverifyMutation.isError && (
                      <Alert severity="error" variant="outlined" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">{getErrorMessage(reverifyMutation.error)}</Typography>
                      </Alert>
                    )}
                    {enrichMutation.isError && (
                      <Alert severity="error" variant="outlined" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">{getErrorMessage(enrichMutation.error)}</Typography>
                      </Alert>
                    )}
                  </Paper>

                  {/* Admin / AI change log */}
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'divider' }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ letterSpacing: '-0.01em' }}>
                      Change log ({detailProduct.editHistory?.length || 0})
                    </Typography>
                    {(detailProduct.editHistory?.length || 0) === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        AI fixes and admin edits will appear here for review.
                      </Typography>
                    ) : (
                      <Stack spacing={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                        {[...(detailProduct.editHistory || [])]
                          .slice()
                          .reverse()
                          .slice(0, 20)
                          .map((entry, idx) => (
                            <Box
                              key={`${entry.at || idx}-${entry.source}-${idx}`}
                              sx={{
                                py: 0.75,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': { borderBottom: 0 },
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                                <Chip
                                  size="small"
                                  label={editHistorySourceLabel(entry.source)}
                                  color={
                                    entry.source === 'admin_edit'
                                      ? 'default'
                                      : entry.source === 'ai_fix' || entry.source === 'accept_all_ai'
                                        ? 'success'
                                        : 'info'
                                  }
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(entry.at)}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" display="block">
                                {(entry.summary || entry.fields?.join(', ') || 'Updated fields').slice(0, 160)}
                              </Typography>
                              {(entry.changes?.length || 0) > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {entry.changes!
                                    .map((change) => `${change.field}: ${change.from || '(none)'} → ${change.to || '(none)'}`)
                                    .join('; ')}
                                </Typography>
                              )}
                              {(entry.byName || entry.byEmail) && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  by {entry.byName || entry.byEmail}
                                </Typography>
                              )}
                              {(entry.clearedIssueFields?.length || 0) > 0 && (
                                <Typography variant="caption" color="success.main" display="block">
                                  Cleared issues: {entry.clearedIssueFields!.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          ))}
                      </Stack>
                    )}
                  </Paper>

                  {/* Timestamps */}
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'divider' }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ letterSpacing: '-0.01em' }}>
                      Timeline
                    </Typography>
                    {[
                      { label: 'Ingested', value: formatDate(detailProduct.ingestedAt || detailProduct.createdAt) },
                      { label: 'Verified', value: formatDate(detailProduct.verifiedAt) },
                      { label: 'Promoted', value: formatDate(detailProduct.promotedAt) },
                      { label: 'Updated', value: formatDate(detailProduct.updatedAt) },
                    ].map((t) => (
                      <Box
                        key={t.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          py: 0.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 0 },
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {t.value}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>

                  {/* Review Note */}
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Review note (optional)"
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    sx={{
                      mb: 0,
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1.75,
                bgcolor: 'grey.50',
                borderTop: 1,
                borderColor: 'divider',
                gap: 1,
                flexWrap: 'wrap',
                position: isMobile ? 'sticky' : undefined,
                bottom: 0,
                zIndex: isMobile ? 2 : undefined,
              }}
            >
              {promoteSelectionConfirm ? (
                <Alert
                  severity="warning"
                  sx={{ width: '100%', py: 0.5 }}
                  action={
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setPromoteSelectionConfirm(null)}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={detailPromoteBusy}
                        onClick={() => submitDetailPromote(promoteSelectionConfirm.mode, true)}
                        sx={{ textTransform: 'none', boxShadow: 'none' }}
                      >
                        Confirm
                      </Button>
                    </Stack>
                  }
                >
                  <Typography variant="caption">
                    {promoteSelectionConfirm.selected === 0
                      ? `Promote this row without writing ${promoteSelectionConfirm.excluded} unchecked scrape field(s)? Catalog values stay as they are.`
                      : `Apply ${promoteSelectionConfirm.selected} checked field(s) and keep the catalog value for ${promoteSelectionConfirm.excluded} unchecked field(s)? This promotes the staged row.`}
                  </Typography>
                </Alert>
              ) : null}
              <Button onClick={closeDetailDialog} sx={{ textTransform: 'none' }}>
                Close
              </Button>
              <Box sx={{ flex: 1 }} />
              {PIPELINE_OWNED_STATUSES.has(detailProduct.stagingStatus) && (
                <PipelineOwnedActionSplit
                  primary={pipelineOwnedActions.primary}
                  overflow={pipelineOwnedActions.overflow}
                  busy={pipelineActionBusy}
                  menuAnchor={detailActionMenuAnchor}
                  onMenuAnchor={setDetailActionMenuAnchor}
                  onRun={runStagedDetailAction}
                  promoteBlockers={detailPromoteBlockers}
                />
              )}
              {!PIPELINE_OWNED_STATUSES.has(detailProduct.stagingStatus) &&
                CAN_SEND_TO_REVIEW.has(detailProduct.stagingStatus) &&
                canContent('content_review') && (
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<SendToReviewIcon />}
                  onClick={() =>
                    sendToReviewMutation.mutate({
                      id: detailProduct.id,
                      note: reviewNote.trim() || undefined,
                    })
                  }
                  disabled={sendToReviewMutation.isPending}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  Send to review
                </Button>
              )}
              {(detailProduct.stagingStatus === 'review' || detailProduct.stagingStatus === 'pending') &&
                canContent('content_approve') && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => rejectMutation.mutate(detailProduct.id)}
                    disabled={rejectMutation.isPending}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => approveMutation.mutate({ id: detailProduct.id, autoPromote: false })}
                    disabled={approveMutation.isPending}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Approve
                  </Button>
                  {/* Approves *and* writes to canonical, so it needs both grants. */}
                  {canContent('content_promote') && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PromoteIcon />}
                        onClick={() => submitDetailPromote('approve')}
                        disabled={detailPromoteBusy}
                        sx={{ textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
                      >
                        {detailPromoteButtonLabel(
                          'approve',
                          Boolean(detailProduct.existingCanonicalId),
                          detailMergeSelection,
                          promoteIsNoop,
                        )}
                      </Button>
                      <PromoteHoldIcon blockers={detailPromoteBlockers} />
                    </Stack>
                  )}
                </>
              )}
              {detailProduct.stagingStatus === 'approved' && canContent('content_promote') && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PromoteIcon />}
                    onClick={() => submitDetailPromote('promote')}
                    disabled={detailPromoteBusy}
                    sx={{ textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
                  >
                    {detailPromoteButtonLabel(
                      'promote',
                      Boolean(detailProduct.existingCanonicalId),
                      detailMergeSelection,
                      promoteIsNoop,
                    )}
                  </Button>
                  <PromoteHoldIcon blockers={detailPromoteBlockers} />
                </Stack>
              )}
              {/*
                A promoted row is normally finished, but admins keep editing
                them to clear flagged issues. Without a way back to the catalog
                those edits are stranded on a row whose footer looks empty.
              */}
              {detailProduct.stagingStatus === 'promoted' && (
                promoteIsNoop || !detailProduct.existingCanonicalId ? (
                  <Typography variant="caption" color="text.secondary">
                    Already in the catalog — this row matches on every compared field, so there is
                    nothing left to apply.
                  </Typography>
                ) : canContent('content_promote') ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PromoteIcon />}
                      onClick={() => submitDetailPromote('promote')}
                      disabled={detailPromoteBusy}
                      sx={{ textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
                    >
                      Apply edits to catalog
                    </Button>
                    <PromoteHoldIcon blockers={detailPromoteBlockers} />
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    This row has edits the catalog does not have yet. Applying them needs the
                    promote permission.
                  </Typography>
                )
              )}
              {detailProduct.stagingStatus === 'rejected' && canContent('content_approve') && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UndoIcon />}
                    onClick={() => unrejectMutation.mutate(detailProduct.id)}
                    disabled={unrejectMutation.isPending}
                    sx={{ textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
                  >
                    Restore to queue
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() =>
                      setRejectDeleteConfirm({
                        id: detailProduct.id,
                        summary: `${detailProduct.brand} | ${detailProduct.sku} | ${detailProduct.name}`.slice(0, 200),
                      })
                    }
                    disabled={deleteRejectedMutation.isPending}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Delete permanently
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <StagedProductScrapeDialog
        open={scrapeDialogOpen && !!detailProduct}
        productName={detailProduct?.name}
        sku={detailProduct?.sku}
        initialUrl={detailProduct?.source?.sourceUrl || ''}
        loading={scrapeMutation.isPending}
        onClose={() => !scrapeMutation.isPending && setScrapeDialogOpen(false)}
        onSubmit={(sourceUrl) => {
          if (!detailProduct) return;
          scrapeMutation.mutate({ id: detailProduct.id, sourceUrl });
        }}
      />

      {detailProduct?.images && imagePreviewIndex != null && (
        <ImageGallery
          open
          onClose={() => setImagePreviewIndex(null)}
          images={detailProduct.images.map((img, i) => imageSrcOverrides[i] || img.url || '')}
          initialIndex={Math.min(imagePreviewIndex, Math.max(0, detailProduct.images.length - 1))}
          title={detailProduct.name || 'Product images'}
          captions={detailProduct.images.map((img) => stagedImageHostMeta(img).caption)}
        />
      )}

      <Dialog
        open={!!rejectDeleteConfirm}
        onClose={() => !deleteRejectedMutation.isPending && setRejectDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete permanently?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes the staged product from the queue. It cannot be undone. Only rejected items can be deleted.
          </Typography>
          {rejectDeleteConfirm && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, wordBreak: 'break-word' }}>
              {rejectDeleteConfirm.summary}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRejectDeleteConfirm(null)} disabled={deleteRejectedMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteRejectedMutation.isPending}
            onClick={() => {
              if (!rejectDeleteConfirm) return;
              // Evict / close only on success — a 400/network error must not leave
              // a still-present rejected row missing from the list cache.
              deleteRejectedMutation.mutate(rejectDeleteConfirm.id);
            }}
          >
            {deleteRejectedMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notifications — above Detail Dialog (modal zIndex).
          When the drawer is open, pin to the top so the sticky footer does not
          cover it; ignore clickaway so a click in the drawer does not dismiss it. */}
      <Snackbar
        open={!!toastMessage || (!!actionResult && !!detailProduct)}
        autoHideDuration={
          (toastMessage?.type || actionResult?.type) === 'error' ? 8000 : 4000
        }
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setToastMessage(null);
          if (detailProduct) setActionResult(null);
        }}
        anchorOrigin={{
          vertical: detailProduct ? 'top' : 'bottom',
          horizontal: 'center',
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert
          onClose={() => {
            setToastMessage(null);
            if (detailProduct) setActionResult(null);
          }}
          severity={toastMessage?.type || actionResult?.type || 'info'}
          variant="filled"
          sx={{ width: '100%', maxWidth: 600 }}
        >
          {toastMessage?.message || actionResult?.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AdminStagedProductsPage;
